
/*
 * @license @wistia/vhs v4.0.1
 *
 * Copyright (c) 2021-2025, Wistia, Inc. and its affiliates.
 *
 * This source code is unlicensed, all rights reserved.
 */

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/helpers/copyToClipboard/copyToClipboard.ts
var copyToClipboard = async (textToCopy) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(textToCopy).catch((error) => {
      if (error instanceof Error) {
        throw error;
      }
      throw new DOMException("The clipboard is not accessible", "NotAllowedError");
    });
  }
  const span = document.createElement("span");
  span.textContent = textToCopy;
  span.style.whiteSpace = "pre";
  document.body.appendChild(span);
  const selection = window.getSelection();
  const range = window.document.createRange();
  selection?.removeAllRanges();
  range.selectNode(span);
  selection?.addRange(range);
  let success = false;
  try {
    success = window.document.execCommand("copy");
  } catch (error) {
    console.error("error", error);
  }
  selection?.removeAllRanges();
  window.document.body.removeChild(span);
  if (success) {
    return Promise.resolve();
  }
  return Promise.reject(new DOMException("The request is not allowed", "NotAllowedError"));
};

// src/helpers/datetimeHelpers/datetimeHelpers.ts
import { isNil } from "@wistia/type-guards";
import { differenceInCalendarDays } from "date-fns";
import { daysInWeek } from "date-fns/constants";
var milisecondsInSecond = 1e3;
var secondsInMinute = 60;
var minutesInHour = 60;
var halfAnHourInMinutes = 30;
var defaultLocales = ["en-US"];
var defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
var padTimeInteger = (num) => num.toString().padStart(2, "0");
var isDate = (date) => date instanceof Date;
var isInvalidDate = (date) => !isDate(date) || date.getTime() === 0;
var dateOnlyString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      dateStyle: "medium",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dateOnlyStringNumeric = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dateOnlyISOString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    const formatter = new Intl.DateTimeFormat(defaultLocales, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone
    });
    const parts = formatter.formatToParts(date);
    const partsAsObject = parts.reduce(
      (map, obj) => {
        map[obj.type] = obj.value;
        return map;
      },
      {}
    );
    return `${partsAsObject.year}-${partsAsObject.month}-${partsAsObject.day}`;
  } catch (error) {
    console.error(error);
    return "";
  }
};
var monthDayStringNumeric = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      month: "2-digit",
      day: "2-digit",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dayOfWeekString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (date === null) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      weekday: "long",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var timeOnlyString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      timeStyle: "short",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dateTimeString = (date, { timeZone = defaultTimeZone, omitYear = false } = {}) => {
  if (isNil(date)) {
    return "";
  }
  const formattedDate = new Date(date);
  if (isInvalidDate(formattedDate)) {
    return "";
  }
  try {
    const baseOptions = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone
    };
    const options = omitYear ? baseOptions : { ...baseOptions, year: "numeric" };
    return formattedDate.toLocaleDateString(defaultLocales, options);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dateTimeStringForSentence = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    const { year, month, day, hour, minute, dayPeriod } = new Intl.DateTimeFormat(defaultLocales, {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone
    }).formatToParts(date).reduce((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
    return `${month} ${day}, ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch (error) {
    console.error(error);
    return "";
  }
};
var dateOnlyStringForSentence = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      dateStyle: "long",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};
var timeAgoString = (date, { nowAnchor = /* @__PURE__ */ new Date(), includeTime = true } = {}) => {
  if (isInvalidDate(date)) return "";
  const minutesAgo = (nowAnchor.valueOf() - date.valueOf()) / (secondsInMinute * milisecondsInSecond);
  const minutesAgoRounded = Math.round(minutesAgo);
  const differenceInDays2 = differenceInCalendarDays(nowAnchor, date);
  const isSameYear = date.getFullYear() === nowAnchor.getFullYear();
  if (minutesAgo < 0) {
    return dateTimeString(date);
  }
  if (minutesAgo < 1) {
    return "< 1 minute ago";
  }
  if (minutesAgoRounded < 2) {
    return "1 minute ago";
  }
  if (minutesAgoRounded <= minutesInHour) {
    return `${minutesAgoRounded} minutes ago`;
  }
  if (differenceInDays2 === 0) {
    return includeTime ? `Today, ${Intl.DateTimeFormat(defaultLocales, { hour: "numeric", minute: "2-digit" }).format(date)}` : "Today";
  }
  if (differenceInDays2 === 1) {
    return includeTime ? `Yesterday, ${Intl.DateTimeFormat(defaultLocales, { hour: "numeric", minute: "2-digit" }).format(date)}` : "Yesterday";
  }
  if (differenceInDays2 < daysInWeek && !includeTime) {
    return `on ${dayOfWeekString(date)}`;
  }
  return includeTime ? dateTimeString(date, { omitYear: isSameYear }) : dateOnlyString(date, { omitYear: isSameYear });
};
var dateUTCOffset = (date) => {
  const offsetInHours = date.getTimezoneOffset() / minutesInHour * -1;
  const hours = Math.round(offsetInHours);
  const minutes = (offsetInHours - hours) * minutesInHour;
  const prefix = hours >= 0 ? "+" : "";
  const hoursString = `${hours}`.padStart(2, "0");
  const minutesString = `${minutes}`.padStart(2, "0");
  return `${prefix}${hoursString}:${minutesString}`;
};
var buildTimeDuration = (numberOfMilliseconds) => {
  const numberOfSeconds = Math.floor(numberOfMilliseconds / milisecondsInSecond);
  const numberOfMinutes = Math.floor(numberOfSeconds / secondsInMinute);
  const numberOfHours = Math.floor(numberOfMinutes / minutesInHour);
  const seconds = numberOfSeconds - numberOfMinutes * secondsInMinute;
  const minutes = numberOfMinutes - numberOfHours * minutesInHour;
  return { seconds, minutes, hours: numberOfHours };
};
var mediaDurationString = (numberOfMilliseconds) => {
  const { hours, minutes, seconds } = buildTimeDuration(numberOfMilliseconds);
  if (hours < 1) {
    return `${minutes}:${padTimeInteger(seconds)}`;
  }
  return `${hours}:${padTimeInteger(minutes)}:${padTimeInteger(seconds)}`;
};
var sessionDurationString = (numberOfMilliseconds) => {
  const { hours, minutes, seconds } = buildTimeDuration(numberOfMilliseconds);
  return `${hours}:${padTimeInteger(minutes)}:${padTimeInteger(seconds)}`;
};
var dateToDateTime = (date) => {
  if (!isDate(date)) {
    return null;
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    dayOfMonth: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes()
  };
};
var dateTimeToDate = (dateTime) => {
  const { year, month, dayOfMonth, hours, minutes } = dateTime ?? {};
  if (year !== void 0 && month !== void 0 && dayOfMonth !== void 0) {
    return new Date(year, month, dayOfMonth, hours ?? 0, minutes ?? 0);
  }
  return null;
};
var dateTimeToISO = (dateTime) => dateTimeToDate(dateTime)?.toISOString() ?? null;
var dateTimeRounded = (dateTime, toISOString = true) => {
  const first30Min = dateTime.getMinutes() <= halfAnHourInMinutes;
  const hours = first30Min ? dateTime.getHours() : dateTime.getHours() + 1;
  const minutes = first30Min ? halfAnHourInMinutes : 0;
  const dateWithTime = {
    ...dateToDateTime(dateTime),
    hours,
    minutes
  };
  if (toISOString) {
    return dateTimeToISO(dateWithTime);
  }
  return dateTimeToDate(dateWithTime);
};
var millisecondsToDurationISOString = (numberOfMilliseconds) => {
  const { seconds, minutes, hours } = buildTimeDuration(numberOfMilliseconds);
  let string = "PT";
  if (hours) {
    string += `${hours}H`;
  }
  if (minutes) {
    string += `${minutes}M`;
  }
  if (seconds) {
    string += `${seconds}S`;
  }
  if (!(seconds || minutes || hours)) {
    string += "0S";
  }
  return string;
};

// src/helpers/datetimeHelpers/index.ts
var datetimeHelpers = {
  isDate,
  dateOnlyString,
  dateOnlyStringNumeric,
  dateOnlyISOString,
  monthDayStringNumeric,
  dayOfWeekString,
  timeOnlyString,
  dateTimeString,
  dateTimeStringForSentence,
  dateOnlyStringForSentence,
  timeAgoString,
  dateUTCOffset,
  buildTimeDuration,
  mediaDurationString,
  sessionDurationString,
  dateToDateTime,
  dateTimeToDate,
  dateTimeToISO,
  dateTimeRounded,
  millisecondsToDurationISOString
};

// src/helpers/getBackgroundGradient/getBackgroundGradient.ts
import { isNotNil } from "@wistia/type-guards";
import { css } from "styled-components";
var gradients = {
  defaultDarkOne: css`
    background-color: #222d66;
    background-image:
      radial-gradient(farthest-corner at top right, #222d66, transparent 70%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #6b84ff, transparent 57%),
      radial-gradient(farthest-corner at top left, #2949e5, transparent 68%);
  `,
  defaultDarkTwo: css`
    background-color: #222d66;
    background-image:
      radial-gradient(farthest-corner at top left, #6b84ff, transparent 100%),
      radial-gradient(farthest-corner at bottom left, #222d66, transparent 57%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  defaultLightOne: css`
    background-color: #ccd5ff;
    background-image:
      radial-gradient(farthest-corner at bottom right, #ccd5ff, transparent 55%),
      radial-gradient(farthest-corner at top left, #ccd5ff, transparent 65%),
      radial-gradient(farthest-corner at top right, #6b84ff, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #f7f8ff, transparent 50%);
  `,
  defaultLightTwo: css`
    background-color: #ccd5ff;
    background-image:
      radial-gradient(ellipse at top, #ccd5ff, transparent),
      radial-gradient(ellipse at bottom, #6b84ff, transparent);
  `,
  defaultMidOne: css`
    background-color: #6b84ff;
    background-image:
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top left, #6b84ff, transparent 80%),
      radial-gradient(farthest-corner at bottom left, #222d66, transparent 57%);
  `,
  defaultMidTwo: css`
    background-color: #6b84ff;
    background-image:
      radial-gradient(ellipse at top, #2949e5, transparent),
      radial-gradient(ellipse at bottom, #ccd5ff, transparent);
  `,
  green: css`
    background-color: #fafffa;
    background-image:
      radial-gradient(farthest-corner at bottom left, #b0e5a5, transparent 50%),
      radial-gradient(farthest-corner at top right, #268713, transparent 50%),
      radial-gradient(farthest-corner at top left, #44b62d, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #44b62d, transparent 55%);
  `,
  greenWithPop: css`
    background-color: #fafffa;
    background-image:
      radial-gradient(farthest-corner at bottom left, #b0e5a5, transparent 50%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top left, #44b62d, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #44b62d, transparent 55%);
  `,
  pink: css`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #ffc7e8, transparent 50%),
      radial-gradient(farthest-corner at top right, #e0128e, transparent 70%),
      radial-gradient(farthest-corner at top left, #ff40b3, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #ff40b3, transparent 55%);
  `,
  pinkWithPop: css`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at top right, #e0128e, transparent 70%),
      radial-gradient(farthest-corner at top left, #ff40b3, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #ff40b3, transparent 55%),
      radial-gradient(farthest-corner at bottom left, #2949e5, transparent 50%);
  `,
  playfulGradientOne: css`
    background-color: #f7f8ff;
    background-image:
      radial-gradient(farthest-corner at top left, #d65cff, transparent 68%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #ffc7e8, transparent 57%),
      radial-gradient(farthest-corner at top right, #ffcaba, transparent 70%);
  `,
  playfulGradientTwo: css`
    background-color: #f7f8ff;
    background-image:
      radial-gradient(farthest-corner at top left, #44b62d, transparent 68%),
      radial-gradient(farthest-corner at bottom right, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #ccd5ff, transparent 57%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  purple: css`
    background-color: #f2caff;
    background-image:
      radial-gradient(ellipse at 0% 100%, #f9e5ff, transparent 50%),
      radial-gradient(ellipse at 100% 0%, #e093fa, transparent 70%);
  `,
  purpleWithPop: css`
    background-color: #f2caff;
    background-image:
      radial-gradient(farthest-corner at bottom left, #f2caff, transparent 50%),
      radial-gradient(farthest-corner at top left, #d65cff, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #d65cff, transparent 55%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  yellow: css`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at top right, #bcbf19, transparent 70%),
      radial-gradient(farthest-corner at top left, #e8ec1e, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #e8ec1e, transparent 55%);
  `,
  yellowWithPop: css`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at top right, #bcbf19, transparent 70%),
      radial-gradient(farthest-corner at top left, #e8ec1e, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 55%);
  `
};
var gradientMap = Object.keys(gradients);
var getBackgroundGradient = (gradientName = void 0) => {
  return isNotNil(gradientName) ? gradients[gradientName] : gradients.defaultDarkOne;
};

// src/helpers/getBackgroundGradient/getSemiRandomBackgroundGradient.ts
var MULTIPLIER = 31;
var getSemiRandomBackgroundGradient = (id) => {
  let hashCode2 = null;
  if (typeof id === "string" && id) {
    hashCode2 = Array.from(id).reduce(
      (num, char) => Math.imul(MULTIPLIER, num) + char.charCodeAt(0) || 0,
      0
    );
  } else {
    return gradientMap[0];
  }
  const index = Math.abs(hashCode2) % gradientMap.length;
  return gradientMap[index];
};

// src/helpers/mq/mq.ts
import { getValueAndUnit } from "polished";
var getMaxWidth = (breakpoint) => {
  const breakpointBuffer = 25e-4;
  const [value, unit] = getValueAndUnit(breakpoint);
  return `@media (max-width: ${Math.max(value - breakpointBuffer, 0)}${unit})`;
};
var xsAndUp = ({ theme: theme2 }) => `@media (min-width: ${theme2.breakpoint.xs})`;
var smAndUp = ({ theme: theme2 }) => `@media (min-width: ${theme2.breakpoint.sm})`;
var mdAndUp = ({ theme: theme2 }) => `@media (min-width: ${theme2.breakpoint.md})`;
var lgAndUp = ({ theme: theme2 }) => `@media (min-width: ${theme2.breakpoint.lg})`;
var xlAndUp = ({ theme: theme2 }) => `@media (min-width: ${theme2.breakpoint.xl})`;
var xsAndDown = ({ theme: theme2 }) => getMaxWidth(theme2.breakpoint.xs);
var smAndDown = ({ theme: theme2 }) => getMaxWidth(theme2.breakpoint.sm);
var mdAndDown = ({ theme: theme2 }) => getMaxWidth(theme2.breakpoint.md);
var lgAndDown = ({ theme: theme2 }) => getMaxWidth(theme2.breakpoint.lg);
var xlAndDown = ({ theme: theme2 }) => getMaxWidth(theme2.breakpoint.xl);
var mq = {
  xsAndUp,
  smAndUp,
  mdAndUp,
  lgAndUp,
  xlAndUp,
  xsAndDown,
  smAndDown,
  mdAndDown,
  lgAndDown,
  xlAndDown
};

// src/helpers/objectHelpers/isObject.ts
var isObject = (value) => value !== null && value !== void 0 && (Object.getPrototypeOf(value) === null || value.constructor === Object);

// src/helpers/objectHelpers/deepFreeze.ts
var deepFreeze = (obj) => {
  Object.freeze(obj);
  Object.getOwnPropertyNames(obj).forEach((prop) => {
    if (
      // eslint-disable-next-line prefer-object-has-own
      Object.prototype.hasOwnProperty.call(obj, prop) && // avoids error in strict mode
      (typeof obj === "function" ? prop !== "caller" && prop !== "callee" && prop !== "arguments" : true) && obj[prop] !== null && (typeof obj[prop] === "object" || typeof obj[prop] === "function") && !Object.isFrozen(obj[prop])
    ) {
      deepFreeze(obj[prop]);
    }
  });
  return obj;
};

// src/helpers/objectHelpers/merge.ts
var merge = (target, ...sources) => {
  if (!sources.length) {
    return target;
  }
  const output = { ...target };
  sources.forEach((source) => {
    if (isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (isObject(source[key])) {
          if (output[key] === null || output[key] === void 0) {
            output[key] = { ...source[key] };
          } else {
            output[key] = merge(output[key], source[key]);
          }
        } else {
          output[key] = source[key];
        }
      });
    }
  });
  return output;
};

// src/helpers/objectHelpers/selectKeys.ts
var selectKeys = (obj, keys2) => keys2.reduce((obj2, key) => {
  if (Object.prototype.hasOwnProperty.call(obj, key)) {
    obj2[key] = obj[key];
  }
  return obj2;
}, {});

// src/helpers/objectHelpers/index.ts
var objectHelpers = { isObject, deepFreeze, merge, selectKeys };

// src/helpers/stringHelpers/camelCase.ts
var camelCase = (str = "") => (
  // https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
  str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) {
      return "";
    }
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  })
);

// src/helpers/stringHelpers/coerceToBoolean.ts
var coerceToBoolean = (value) => {
  if (value === "true" || value === "false") {
    return value === "true";
  }
  return Boolean(value);
};

// src/helpers/stringHelpers/coerceToString.ts
var coerceToString = (value) => value === null || value === void 0 ? "" : String(value);

// src/helpers/stringHelpers/isString.ts
var isString = (value) => typeof value === "string" || value instanceof String;

// src/helpers/stringHelpers/isHttpUrl.ts
var isHttpUrl = (str) => {
  if (!isString(str)) {
    return false;
  }
  try {
    const url = new URL(str);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

// src/helpers/stringHelpers/isUrl.ts
var isUrl = (str) => {
  const protocolAndDomain = /^(?:\w+:)?\/\/(\S+)$/;
  const localhostDomain = /^localhost[:?\d]*(?:[^:?\d]\S*)?$/;
  const nonLocalhostDomain = /^[^\s.]+\.\S{2,}$/;
  if (!isString(str)) {
    return false;
  }
  const match = protocolAndDomain.exec(str);
  if (!match) {
    return false;
  }
  const remaining = match[1];
  if (remaining === void 0 || !remaining) {
    return false;
  }
  return localhostDomain.test(remaining) || nonLocalhostDomain.test(remaining);
};

// src/helpers/stringHelpers/lowerFirst.ts
var lowerFirst = (str = "") => (str[0]?.toLowerCase() ?? "") + str.slice(1);

// src/helpers/stringHelpers/removeNewlines.ts
var removeNewlines = (str) => str.replace(/\n/g, "");

// src/helpers/stringHelpers/stripExtension.ts
var stripExtension = (str) => (str || "").replace(/\.[^/.]{1,4}$/, "");

// src/helpers/stringHelpers/truncate.ts
var truncate = (str, maxChars) => {
  if (str.length <= maxChars) {
    return str;
  }
  return `${str.slice(0, maxChars - 3)}...`;
};

// src/helpers/stringHelpers/upperFirst.ts
var upperFirst = (str = "") => (str[0]?.toUpperCase() ?? "") + str.slice(1);

// src/helpers/stringHelpers/index.ts
var stringHelpers = {
  camelCase,
  coerceToBoolean,
  coerceToString,
  isHttpUrl,
  isString,
  isUrl,
  lowerFirst,
  removeNewlines,
  stripExtension,
  truncate,
  upperFirst
};

// src/providers/VHSProvider.tsx
import { LiveAnnouncer } from "react-aria-live";
import { MotionConfig } from "motion/react";

// src/providers/VHSThemeProvider.tsx
import { ThemeProvider } from "styled-components";

// ../../node_modules/@wistia/vhs-design-tokens/dist/js/designTokens.mjs
var designTokens = {
  breakpoint: {
    xs: "0em",
    sm: "37.5em",
    md: "56.5625em",
    lg: "77.5em",
    xl: "90em"
  },
  color: {
    brandBlue100: "#E5E9FF",
    brandBlue200: "#CCD5FF",
    brandBlue300: "#99ABFF",
    brandBlue400: "#6B84FF",
    brandBlue500: "#2949E5",
    brandBlue600: "#1F3399",
    brandBlue700: "#222D66",
    brandBlue800: "#000934",
    purple100: "#FDF7FF",
    purple200: "#F9E5FF",
    purple300: "#F2CAFF",
    purple400: "#E093FA",
    purple500: "#D65CFF",
    purple600: "#AC12E0",
    purple700: "#752E8C",
    purple800: "#39004D",
    green100: "#F4FFF2",
    green200: "#DFFFD9",
    green300: "#B0E5A5",
    green400: "#76D962",
    green500: "#44B62D",
    green600: "#268713",
    green700: "#38662E",
    green800: "#093300",
    blue100: "#E9F5FF",
    blue200: "#C7E7FF",
    blue300: "#91CEFF",
    blue400: "#58B7FE",
    blue500: "#229FFF",
    blue600: "#1B7FDE",
    blue700: "#165FAC",
    blue800: "#0D407A",
    pink100: "#FFF8FC",
    pink200: "#FFE5F4",
    pink300: "#FFC7E8",
    pink400: "#FF8CD0",
    pink500: "#FF40B3",
    pink600: "#E0128E",
    pink700: "#661F4A",
    pink800: "#4D002E",
    red100: "#FFF7F9",
    red200: "#FFCCD5",
    red300: "#FF99AB",
    red400: "#FF6682",
    red500: "#FF2B51",
    red600: "#D60027",
    red700: "#99001C",
    red800: "#660013",
    yellow100: "#FFFFF0",
    yellow200: "#EFF18D",
    yellow300: "#E8EC1E",
    yellow400: "#D9DC25",
    yellow500: "#BCBF19",
    yellow600: "#9EA00F",
    yellow700: "#7C7E07",
    yellow800: "#4A4D00",
    orange100: "#FFF9F8",
    orange200: "#FFCABA",
    orange300: "#FF9D80",
    orange400: "#FF6C40",
    orange500: "#D1451A",
    orange600: "#B23009",
    orange700: "#80280D",
    orange800: "#4D1200",
    grey100: "#F8F8F9",
    grey200: "#EBEBED",
    grey300: "#DEDEE1",
    grey400: "#C4C4C8",
    grey500: "#A3A3AA",
    grey600: "#7B7B87",
    grey700: "#62626C",
    grey800: "#4A4A51",
    grey900: "#37373C",
    grey1000: "#1F1F22",
    grey1100: "#0B0D0E",
    error100: "#FFECEC",
    error200: "#FFCACA",
    error300: "#FF9A9A",
    error400: "#FF6962",
    error500: "#FA4040",
    error600: "#D63727",
    error700: "#AA0D15",
    error800: "#860011",
    success100: "#E6F9F3",
    success200: "#BFEFE0",
    success300: "#81E0C1",
    success400: "#41D0A3",
    success500: "#00C185",
    success600: "#00966E",
    success700: "#047063",
    success800: "#025050",
    warning100: "#FFF8E6",
    warning200: "#FFEFC8",
    warning300: "#FFDF96",
    warning400: "#FFCE5A",
    warning500: "#FABE1F",
    warning600: "#DC9601",
    warning700: "#BE7200",
    warning800: "#B45101"
  },
  duration: {
    productive: {
      fast: "70ms",
      moderate: "110ms",
      slow: "150ms"
    },
    expressive: {
      fast: "240ms",
      moderate: "400ms",
      slow: "700ms"
    }
  },
  easing: {
    standard: {
      productive: "cubic-bezier(0.2, 0, 0.38, 0.9)",
      expressive: "cubic-bezier(0.4, 0.14, 0.3, 1)"
    },
    entrance: {
      productive: "cubic-bezier(0, 0, 0.38, 0.9)",
      expressive: "cubic-bezier(0, 0, 0.3, 1)"
    },
    exit: {
      productive: "cubic-bezier(0.2, 0, 1, 0.9)",
      expressive: "cubic-bezier(0.4, 0.14, 1, 1)"
    }
  },
  font: {
    family: {
      default: "InterVariable, Helvetica, Arial, sans-serif",
      monospace: '"IBM Plex Mono", Consolas, Monaco, monospace'
    },
    weight: {
      thin: 100,
      extraLight: 200,
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800
    },
    size: {
      default: "16px",
      small: "14px"
    }
  },
  shadow: {
    elevation0: "none",
    elevation1: "0 2px 4px rgba(0, 0, 0, 0.1)",
    elevation2: "0 0 64px 0 rgba(0, 0, 0, 0.08)"
  },
  spacing: {
    space01: "4px",
    space02: "8px",
    space03: "12px",
    space04: "16px",
    space05: "24px",
    space06: "32px",
    space07: "48px",
    space08: "64px",
    space09: "80px",
    space10: "96px",
    space11: "120px"
  }
};

// src/theme.ts
var zIndex1 = 100;
var zIndex2 = 200;
var zIndex3 = 300;
var zIndex5 = 500;
var theme = {
  breakpoint: {
    ...designTokens.breakpoint
  },
  color: {
    ...designTokens.color
  },
  duration: {
    ...designTokens.duration
  },
  easing: {
    ...designTokens.easing
  },
  font: {
    family: {
      default: "InterVariable, Helvetica, Arial, sans-serif",
      monospace: designTokens.font.family.monospace
    },
    size: designTokens.font.size,
    weight: Object.fromEntries(
      Object.entries(designTokens.font.weight).map(([key, value]) => [key, Number(value)])
    )
  },
  shadow: {
    ...designTokens.shadow
  },
  spacing: {
    ...designTokens.spacing
  },
  zIndex: {
    ModalCloseButton: zIndex1,
    Backdrop: zIndex2,
    ActionModal: zIndex3,
    FullScreenModal: zIndex3,
    RangeSelector: zIndex5,
    MenuComponent: zIndex5,
    Toast: zIndex5,
    Tooltip: zIndex5,
    BackdropElevated: zIndex5
  },
  button: {
    borderRadius: "4px"
  },
  buttonGroup: {
    gap: "16px"
  },
  headline: {
    family: designTokens.font.family,
    headline1: {
      weight: designTokens.font.weight.bold
    },
    headline2: {
      weight: designTokens.font.weight.medium
    },
    headline3: {
      weight: designTokens.font.weight.bold
    }
  },
  paper: {
    elevation0: designTokens.shadow.elevation0,
    elevation1: designTokens.shadow.elevation1,
    elevation2: designTokens.shadow.elevation2
  }
};

// src/providers/VHSThemeProvider.tsx
import { jsx } from "react/jsx-runtime";
var VHSThemeProvider = ({
  children,
  theme: overrideTheme
}) => {
  const theme2 = merge(theme, overrideTheme);
  return /* @__PURE__ */ jsx(ThemeProvider, { theme: theme2, children });
};

// src/providers/WrapperProvider.tsx
import { createContext, useContext } from "react";
var WrapperContext = createContext({});
var useWrappers = () => useContext(WrapperContext);
var WrapperProvider = WrapperContext.Provider;

// src/components/Toast/ToastProvider.tsx
import { useState as useState3, useCallback as useCallback2 } from "react";

// src/components/Toast/Toast.tsx
import { memo, useState as useState2, useEffect, useCallback, Fragment as Fragment3 } from "react";
import { createPortal } from "react-dom";
import { LiveMessage as LiveMessage2 } from "react-aria-live";
import { styled as styled3 } from "styled-components";
import { isNonEmptyString as isNonEmptyString2 } from "@wistia/type-guards";

// src/components/Toast/ToastAnimation.tsx
import { motion, AnimatePresence } from "motion/react";

// src/hooks/useTheme/useTheme.ts
import { useTheme as useStyledComponentsTheme } from "styled-components";
var useTheme = () => useStyledComponentsTheme();

// src/hooks/useWindowSize/useWindowSize.ts
import { useState, useLayoutEffect } from "react";
import { debounce } from "throttle-debounce";

// src/private/helpers/isClient/isClient.ts
var isClient = () => typeof window !== "undefined" && typeof document !== "undefined";

// src/hooks/useWindowSize/useWindowSize.ts
var useWindowSize = (interval = 0) => {
  const [dimensions, setDimensions] = useState({
    width: isClient() ? window.innerWidth : 0,
    height: isClient() ? window.innerHeight : 0
  });
  useLayoutEffect(() => {
    const handleResize = debounce(
      interval,
      () => setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })
    );
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [interval]);
  return dimensions;
};

// src/hooks/useMq/useMq.ts
var REM_SIZE = 16;
var useMq = () => {
  const viewportWidth = useWindowSize().width / REM_SIZE;
  const theme2 = useTheme();
  const isXsAndUp = true;
  const isSmAndUp = viewportWidth >= Number.parseFloat(theme2.breakpoint.sm);
  const isMdAndUp = viewportWidth >= Number.parseFloat(theme2.breakpoint.md);
  const isLgAndUp = viewportWidth >= Number.parseFloat(theme2.breakpoint.lg);
  const isXlAndUp = viewportWidth >= Number.parseFloat(theme2.breakpoint.xl);
  const isXsAndDown = viewportWidth < Number.parseFloat(theme2.breakpoint.sm);
  const isSmAndDown = viewportWidth < Number.parseFloat(theme2.breakpoint.md);
  const isMdAndDown = viewportWidth < Number.parseFloat(theme2.breakpoint.lg);
  const isLgAndDown = viewportWidth < Number.parseFloat(theme2.breakpoint.xl);
  const isXlAndDown = true;
  return {
    isXsAndUp,
    isSmAndUp,
    isMdAndUp,
    isLgAndUp,
    isXlAndUp,
    isXsAndDown,
    isSmAndDown,
    isMdAndDown,
    isLgAndDown,
    isXlAndDown
  };
};

// src/components/Toast/ToastAnimation.tsx
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
var toastPositionOffset = 24;
var toastPositionStyles = {
  bottom: toastPositionOffset,
  left: toastPositionOffset,
  position: "fixed"
};
var toastWidth = {
  minWidth: "352px",
  width: "400px"
};
var toastMobileWidth = {
  width: `calc(100% - ${toastPositionOffset * 2}px)`
};
var ToastAnimation = ({ children, in: inProp, renderInPlace }) => {
  const { isSmAndUp } = useMq();
  const { duration, zIndex } = useTheme();
  const durationInSeconds = Number.parseInt(duration.expressive.fast, 10) / 1e3;
  const toastStyle = {
    ...toastPositionStyles,
    ...isSmAndUp ? toastWidth : toastMobileWidth,
    // adjust Toast width based on viewport size
    willChange: "auto",
    zIndex: zIndex.Toast
  };
  if (renderInPlace) {
    return /* @__PURE__ */ jsx2(Fragment, { children });
  }
  return /* @__PURE__ */ jsx2(AnimatePresence, { children: inProp ? /* @__PURE__ */ jsx2(
    motion.div,
    {
      animate: { opacity: 1, y: "0%" },
      exit: { opacity: 0, y: "100%" },
      initial: { opacity: 0, y: "100%" },
      style: toastStyle,
      transition: { duration: durationInSeconds },
      children
    }
  ) : null });
};

// src/hooks/useThemeColor/useThemeColor.ts
var getColor = (colors, colorName) => {
  if (!colors.hasOwnProperty(colorName)) {
    throw new Error(
      `\`${String(
        colorName
      )}\` is not a defined theme color, check argument passed to \`useThemeColor\``
    );
  }
  return colors[colorName];
};
var useThemeColor = (colorName = null) => {
  const theme2 = useTheme();
  const colors = theme2.color;
  const noColorsFound = !Object.keys({ ...colors }).length;
  if (noColorsFound) {
    throw new Error(
      "The `useThemeColor` hook could not find any defined colors. A possible cause of this error is that your component is not wrapped in a theme provider such as `<VHSProvider />`"
    );
  }
  if (colorName === null) {
    return colors;
  }
  if (Array.isArray(colorName)) {
    return colorName.map((color) => getColor(colors, color));
  }
  return getColor(colors, colorName);
};

// src/components/Button/Button.tsx
import { forwardRef } from "react";
import { isNotNil as isNotNil3 } from "@wistia/type-guards";

// src/components/Button/ButtonContent.tsx
import { styled } from "styled-components";
import { isNotNil as isNotNil2 } from "@wistia/type-guards";
import { LiveMessage } from "react-aria-live";

// src/components/Icon/Icon.tsx
import { isNil as isNil2 } from "@wistia/type-guards";

// src/components/Icon/iconMap.ts
var iconMap = {
  "ab-test": "AbTestIcon",
  "all-content": "AllContentIcon",
  archived: "ArchivedIcon",
  "arrow-down": "ArrowDownIcon",
  "arrow-left": "ArrowLeftIcon",
  "arrow-right": "ArrowRightIcon",
  "arrow-up": "ArrowUpIcon",
  asterisk: "AsteriskIcon",
  audience: "AudienceIcon",
  calendar: "CalendarIcon",
  camera: "CameraIcon",
  "caret-down": "CaretDownIcon",
  "caret-left": "CaretLeftIcon",
  "caret-right": "CaretRightIcon",
  "caret-up": "CaretUpIcon",
  channel: "ChannelIcon",
  checkmark: "CheckmarkIcon",
  "checkmark-circle": "CheckmarkCircleIcon",
  "checkmark-circle-outline": "CheckmarkCircleOutlineIcon",
  "checkmark-circle-three-quarters": "CheckmarkCircleThreeQuartersIcon",
  "checkmark-underlined": "CheckmarkUnderlinedIcon",
  close: "CloseIcon",
  "close-circle": "CloseCircleIcon",
  "collect-video": "CollectVideoIcon",
  collection: "CollectionIcon",
  comment: "CommentIcon",
  community: "CommunityIcon",
  "contact-us": "ContactUsIcon",
  copy: "CopyIcon",
  "copy-disabled": "CopyDisabledIcon",
  create: "CreateIcon",
  delete: "DeleteIcon",
  "delete-sm": "DeleteSmIcon",
  "dottie-clock": "DottieClockIcon",
  download: "DownloadIcon",
  draggable: "DraggableIcon",
  edit: "PencilIcon",
  ellipsis: "EllipsisIcon",
  "ellipsis-vertical": "EllipsisVerticalIcon",
  embed: "EmbedIcon",
  episodes: "EpisodesIcon",
  error: "TriangleIcon",
  expand: "ExpandIcon",
  export: "DownloadIcon",
  favorite: "FavoriteIcon",
  "favorite-outline": "FavoriteOutlineIcon",
  filter: "FilterIcon",
  gear: "GearIcon",
  "getting-started": "GettingStartedIcon",
  globe: "GlobeIcon",
  "grid-view": "GridViewIcon",
  "group-record": "GroupRecordIcon",
  hamburger: "HamburgerIcon",
  hazard: "HazardIcon",
  "help-center": "HelpCenterIcon",
  hide: "HideIcon",
  home: "HomeIcon",
  hourglass: "HourglassIcon",
  import: "ImportIcon",
  info: "InfoIcon",
  integrations: "IntegrationsIcon",
  invite: "InviteIcon",
  "invite-to-record": "InviteToRecordIcon",
  "lightning-bolt": "LightningBoltIcon",
  link: "LinkIcon",
  "list-view": "ListViewIcon",
  live: "LiveIcon",
  lock: "LockIcon",
  logout: "LogoutIcon",
  media: "MediaIcon",
  megaphone: "MegaphoneIcon",
  "more-options": "MoreOptionsIcon",
  "open-new": "OpenNewIcon",
  overview: "OverviewIcon",
  paint: "PaintIcon",
  pencil: "PencilIcon",
  play: "PlayIcon",
  playlist: "PlaylistIcon",
  podcast: "PodcastIcon",
  preview: "PreviewIcon",
  "private-user-sessions": "PrivateUserSessionsIcon",
  project: "ProjectIcon",
  "question-mark": "QuestionMarkIcon",
  react: "ReactIcon",
  record: "RecordIcon",
  redo: "RedoIcon",
  "remove-from-channel": "RemoveFromChannelIcon",
  replace: "ReplaceIcon",
  reply: "ReplyIcon",
  "save-copy": "CopyIcon",
  scissors: "ScissorsIcon",
  search: "SearchIcon",
  send: "SendIcon",
  settings: "SettingsIcon",
  share: "ShareIcon",
  soapbox: "SoapboxIcon",
  sort: "SortIcon",
  sparkle: "SparkleIcon",
  spinner: "SpinnerIcon",
  stats: "StatsIcon",
  "switch-accounts": "SwitchAccountsIcon",
  tag: "TagIcon",
  team: "TeamIcon",
  "thumbs-down": "ThumbsDownIcon",
  "thumbs-up": "ThumbsUpIcon",
  triangle: "TriangleIcon",
  undo: "UndoIcon",
  unlock: "UnlockIcon",
  upload: "UploadIcon",
  "users-permissions": "UsersPermissionsIcon",
  "view-stream": "ViewStreamIcon",
  wand: "WandIcon",
  "zoom-in": "ZoomInIcon",
  "zoom-out": "ZoomOutIcon"
};

// src/components/Icon/icons/index.ts
var icons_exports = {};
__export(icons_exports, {
  AbTestIcon: () => AbTestIcon,
  AllContentIcon: () => AllContentIcon,
  ArchivedIcon: () => ArchivedIcon,
  ArrowDownIcon: () => ArrowDownIcon,
  ArrowLeftIcon: () => ArrowLeftIcon,
  ArrowRightIcon: () => ArrowRightIcon,
  ArrowUpIcon: () => ArrowUpIcon,
  AsteriskIcon: () => AsteriskIcon,
  AudienceIcon: () => AudienceIcon,
  CalendarIcon: () => CalendarIcon,
  CameraIcon: () => CameraIcon,
  CaretDownIcon: () => CaretDownIcon,
  CaretLeftIcon: () => CaretLeftIcon,
  CaretRightIcon: () => CaretRightIcon,
  CaretUpIcon: () => CaretUpIcon,
  ChannelIcon: () => ChannelIcon,
  CheckmarkCircleIcon: () => CheckmarkCircleIcon,
  CheckmarkCircleOutlineIcon: () => CheckmarkCircleOutlineIcon,
  CheckmarkCircleThreeQuartersIcon: () => CheckmarkCircleThreeQuartersIcon,
  CheckmarkIcon: () => CheckmarkIcon,
  CheckmarkUnderlinedIcon: () => CheckmarkUnderlinedIcon,
  CloseCircleIcon: () => CloseCircleIcon,
  CloseIcon: () => CloseIcon,
  CollectVideoIcon: () => CollectVideoIcon,
  CollectionIcon: () => CollectionIcon,
  CommentIcon: () => CommentIcon,
  CommunityIcon: () => CommunityIcon,
  ContactUsIcon: () => ContactUsIcon,
  CopyDisabledIcon: () => CopyDisabledIcon,
  CopyIcon: () => CopyIcon,
  CreateIcon: () => CreateIcon,
  DeleteIcon: () => DeleteIcon,
  DeleteSmIcon: () => DeleteSmIcon,
  DottieClockIcon: () => DottieClockIcon,
  DownloadIcon: () => DownloadIcon,
  DraggableIcon: () => DraggableIcon,
  EllipsisIcon: () => EllipsisIcon,
  EllipsisVerticalIcon: () => EllipsisVerticalIcon,
  EmbedIcon: () => EmbedIcon,
  EpisodesIcon: () => EpisodesIcon,
  ExpandIcon: () => ExpandIcon,
  FavoriteIcon: () => FavoriteIcon,
  FavoriteOutlineIcon: () => FavoriteOutlineIcon,
  FilterIcon: () => FilterIcon,
  GearIcon: () => GearIcon,
  GettingStartedIcon: () => GettingStartedIcon,
  GlobeIcon: () => GlobeIcon,
  GridViewIcon: () => GridViewIcon,
  GroupRecordIcon: () => GroupRecordIcon,
  HamburgerIcon: () => HamburgerIcon,
  HazardIcon: () => HazardIcon,
  HelpCenterIcon: () => HelpCenterIcon,
  HideIcon: () => HideIcon,
  HomeIcon: () => HomeIcon,
  HourglassIcon: () => HourglassIcon,
  ImportIcon: () => ImportIcon,
  InfoIcon: () => InfoIcon,
  IntegrationsIcon: () => IntegrationsIcon,
  InviteIcon: () => InviteIcon,
  InviteToRecordIcon: () => InviteToRecordIcon,
  LightningBoltIcon: () => LightningBoltIcon,
  LinkIcon: () => LinkIcon,
  ListViewIcon: () => ListViewIcon,
  LiveIcon: () => LiveIcon,
  LockIcon: () => LockIcon,
  LogoutIcon: () => LogoutIcon,
  MediaIcon: () => MediaIcon,
  MegaphoneIcon: () => MegaphoneIcon,
  MoreOptionsIcon: () => MoreOptionsIcon,
  OpenNewIcon: () => OpenNewIcon,
  OverviewIcon: () => OverviewIcon,
  PaintIcon: () => PaintIcon,
  PencilIcon: () => PencilIcon,
  PlayIcon: () => PlayIcon,
  PlaylistIcon: () => PlaylistIcon,
  PodcastIcon: () => PodcastIcon,
  PreviewIcon: () => PreviewIcon,
  PrivateUserSessionsIcon: () => PrivateUserSessionsIcon,
  ProjectIcon: () => ProjectIcon,
  QuestionMarkIcon: () => QuestionMarkIcon,
  ReactIcon: () => ReactIcon,
  RecordIcon: () => RecordIcon,
  RedoIcon: () => RedoIcon,
  RemoveFromChannelIcon: () => RemoveFromChannelIcon,
  ReplaceIcon: () => ReplaceIcon,
  ReplyIcon: () => ReplyIcon,
  ScissorsIcon: () => ScissorsIcon,
  SearchIcon: () => SearchIcon,
  SendIcon: () => SendIcon,
  SettingsIcon: () => SettingsIcon,
  ShareIcon: () => ShareIcon,
  SoapboxIcon: () => SoapboxIcon,
  SortIcon: () => SortIcon,
  SparkleIcon: () => SparkleIcon,
  SpinnerIcon: () => SpinnerIcon,
  StatsIcon: () => StatsIcon,
  SwitchAccountsIcon: () => SwitchAccountsIcon,
  TagIcon: () => TagIcon,
  TeamIcon: () => TeamIcon,
  ThumbsDownIcon: () => ThumbsDownIcon,
  ThumbsUpIcon: () => ThumbsUpIcon,
  TriangleIcon: () => TriangleIcon,
  UndoIcon: () => UndoIcon,
  UnlockIcon: () => UnlockIcon,
  UploadIcon: () => UploadIcon,
  UsersPermissionsIcon: () => UsersPermissionsIcon,
  ViewStreamIcon: () => ViewStreamIcon,
  WandIcon: () => WandIcon,
  ZoomInIcon: () => ZoomInIcon,
  ZoomOutIcon: () => ZoomOutIcon
});

// src/components/Icon/icons/AbTestIcon.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var AbTestIcon = (props) => /* @__PURE__ */ jsx3("svg", { ...props, children: /* @__PURE__ */ jsx3(
  "path",
  {
    d: "M8.78042 2.11118C6.86612 2.34876 6.94137 3.15354 6.99208 3.69591V3.69599C6.99476 3.72465 6.99738 3.75258 6.99962 3.77965C7.04436 4.31904 7.55048 5.39025 8.78042 5.39025H8.79735L8.79731 5.40433C8.79731 5.40433 9.05197 8.22842 8.79731 9.662C8.63183 10.5935 8.18982 11.2329 7.69474 11.865C8.39927 11.9016 9.14688 11.9764 9.93037 12.0994C10.4699 12.1842 10.8383 12.6879 10.753 13.2246C10.6678 13.7612 10.1613 14.1276 9.62166 14.0428C8.33927 13.8413 7.1731 13.7835 6.16316 13.812C5.48376 14.6589 4.44391 15.9821 3.58432 17.1925C5.12252 17.2343 7.05517 17.2692 9.48309 17.1438C10.0287 17.1157 10.4939 17.5327 10.5222 18.0753C10.5504 18.6178 10.1312 19.0805 9.58566 19.1086C6.78798 19.253 4.53527 19.189 2.90579 19.1426C2.71222 19.1371 2.52744 19.1319 2.35159 19.1273C1.49048 20.8558 2.24968 21.573 4.20714 21.7855C6.33564 22.0165 18.0133 22.1217 19.9478 21.7855C21.8825 21.4493 22.5323 19.9214 21.5397 18.6499C20.547 17.3783 17.7768 13.4113 16.9172 11.9984C16.7004 11.642 16.4948 11.3282 16.3083 11.0435C15.7556 10.1997 15.3708 9.61242 15.36 8.93404L15.3601 5.40433L15.36 5.39025H15.3746C16.8928 5.34284 17.0231 4.6562 17.0231 3.75071C17.0231 2.84523 16.285 2.11118 15.3746 2.11118C15.3746 2.11118 10.7959 1.86103 8.78042 2.11118Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/AllContentIcon.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var AllContentIcon = (props) => /* @__PURE__ */ jsx4("svg", { ...props, children: /* @__PURE__ */ jsx4(
  "path",
  {
    d: "m3.09059 3.5023c.5523 0 1 .4477 1 1l-.00092.70188c-.01018 3.93536-.10142 7.15092-.08848 9.77572.0068 1.3587.0426 2.4727.1299 3.3301.071.6968.16857 1.1263.25679 1.3643l.00412.0107.04589.0175c.01953.007.04171.0145.06676.0224l.08404.025c.2655.0742.6435.1409 1.1387.1952.9865.1081 2.3084.1539 3.8524.1644 3.14371.0213 6.85261-.0993 10.44111-.0993l.1166.0067c.4974.0578.8834.4805.8834.9933 0 .5522-.4477 1-1 1l-.738.0017c-3.232.0147-6.8753.1168-9.71671.0976-1.558-.0106-2.9641-.0566-4.0568-.1763-.5444-.0597-1.0437-.141-1.4591-.2571-.3702-.1035-.8555-.2808-1.185-.649l-.05731-.0703-.05079-.0751-.10286-.186c-.29118-.5812-.4296-1.3656-.51294-2.183-.0974-.9568-.1333-2.1494-.1402-3.5229-.0133-2.6853.0894-6.4106.0894-10.4876l.00673-.11652c.05776-.49735.48042-.88338.99327-.88338zm6.5557-1.37637.53051.02595c1.8616.09188 3.7138.19142 3.7138.19142s1.0423-.01829 2.2175-.02072l.4451-.0001c.8957.00155 1.802.01442 2.3355.05302 2.564.2222 2.6272 1.1409 2.7026 2.237.0118.172.024.3485.0461.5273.2722 2.1214.4627 4.9141.4627 6.5213 0 1.6393-.0545 5.1864-.245 5.4757-.2721.4178-.7348.9964-2.6398.8357-1.5154-.1278-7.0949-.2353-9.79337-.2416l-.43309-.0001c-.46931.0011-.80752.0065-.95905.0167-.9525.0643-2.0683-.0643-2.2588-1.125-.17942-.9182-.22215-3.2602-.2137-5.7009l.00427-.7725c.01347-1.80295.04985-3.58372.07493-4.8122.0148-.7225.0256-1.254.0256-1.4866 0-.0124-.0004-.0308-.001-.0543-.0081-.3442-.0425-1.796.8683-1.796.39926 0 1.70989.05778 3.1169.12593zm1.68581 5.01357c-.0356.94577-.0413 3.5445-.0421 5.0123l-.0002.688c0 .1695.1692.2755.3172.1907.9725-.551 3.89-2.2463 4.7145-3.327.0846-.106.0423-.2543-.0634-.3179l-4.6089-2.4156c-.148-.0848-.3171.0212-.3171.1695z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ArchivedIcon.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var ArchivedIcon = (props) => /* @__PURE__ */ jsx5("svg", { ...props, children: /* @__PURE__ */ jsx5(
  "path",
  {
    clipRule: "evenodd",
    d: "m1.73061 4.12674c-.00015-.04078-.00031-.08299-.0006-.12674.00196-.01564.00388-.03112.00579-.04645.14457-1.16135.18151-1.45809 3.76421-1.95355 4.033-.558 11.67499-.278 15.49699.892.433.133 1.003.608 1.003 1.108 0 .63366.0008.64812.0016.66226.0007.01356.0014.02683.0014.58474-.1073.53269-.783.38874-1.8549.16039-.8357-.17804-1.9122-.40739-3.1481-.40739-.9352 0-2.0855-.03207-3.3103-.06622-2.7041-.07539-5.7715-.16091-7.68969.06622-.93175.05647-1.70731.17102-2.3234.26202-.91133.1346-1.47374.21767-1.6766-.01502-.26635-.30625-.26743-.59442-.2694-1.12026zm.38218 9.34776.0002.0065c.027.913.053 1.802.033 2.573l-.01.316-.00009.0031c-.05397 1.901-.10965 3.8622.37509 4.4189.34.39 1.277.743 3.591.743 2.315 0 11.63801.264 12.80501.403 1.168.14 2.548.112 2.739-.836.303-1.4255.306-4.3452.308-6.2904v-.0386c0-.625 0-1.147.01-1.48.031-1.25-.024-1.967-.081-2.702-.021-.266-.042-.534-.059-.83-.005-.146-.008-.281-.01-.406l-.0001-.00311c-.0169-.82786-.0249-1.21719-.8169-1.45989-3.822-1.171-11.46401-1.45-15.49701-.892-3.195.442-3.233.992-3.297 1.935-.00056.00797-.00112.01597-.00168.024-.00848.12097-.01744.24897-.03432.384-.14378 1.1792-.09814 2.6835-.0542 4.1315zm5.0572-1.8505c-.316-.349-.254-1.079-.149-1.376.114-.297 3.05201-.268 5.00901-.214 1.5006.0502 2.997.1864 4.482.408.298.052.613.705.465 1.175l-.023.075c-.037.13-.075.263-.17.274-.36.012-6.747-.068-6.747-.068s-2.55201.074-2.86701-.274z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ArrowDownIcon.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var ArrowDownIcon = (props) => /* @__PURE__ */ jsx6("svg", { ...props, children: /* @__PURE__ */ jsx6(
  "path",
  {
    d: "m19.7685 14.8636c.615-.555.6637-1.5035.1087-2.1185-.555-.6151-1.5035-.6637-2.1185-.1088l-4.2587 3.8429v-12.27446c0-.82845-.6716-1.5-1.5-1.5s-1.5.67155-1.5 1.5v12.27446l-4.25874-3.8429c-.615-.555-1.56345-.5063-2.11845.1088-.555.615-.5064 1.5635.10875 2.1185l6.76354 6.1031c.5709.5152 1.4389.5152 2.0098 0z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ArrowLeftIcon.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var ArrowLeftIcon = (props) => /* @__PURE__ */ jsx7("svg", { ...props, children: /* @__PURE__ */ jsx7(
  "path",
  {
    d: "M9.1653 19.7974C9.72028 20.4124 10.6688 20.4611 11.2838 19.9061C11.8989 19.3512 11.9476 18.4027 11.3926 17.7876L7.54974 13.5289L19.8242 13.5289C20.6526 13.5289 21.3242 12.8573 21.3242 12.0289C21.3242 11.2005 20.6526 10.5289 19.8242 10.5289L7.54975 10.5289L11.3926 6.27019C11.9476 5.65519 11.8989 4.70674 11.2838 4.15174C10.6688 3.59674 9.72028 3.64534 9.16529 4.26049L3.06217 11.024C2.54703 11.5949 2.54703 12.4629 3.06217 13.0338L9.1653 19.7974Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ArrowRightIcon.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var ArrowRightIcon = (props) => /* @__PURE__ */ jsx8("svg", { ...props, children: /* @__PURE__ */ jsx8(
  "path",
  {
    d: "M14.8347 4.26044C14.2797 3.6454 13.3312 3.59671 12.7162 4.15169C12.1011 4.70668 12.0524 5.65517 12.6074 6.27022L16.4502 10.5289L4.17581 10.5289C3.34736 10.5289 2.67581 11.2005 2.67581 12.0289C2.67581 12.8573 3.34736 13.5289 4.17581 13.5289L16.4502 13.5289L12.6074 17.7877C12.0524 18.4027 12.1011 19.3511 12.7161 19.9061C13.3312 20.4611 14.2797 20.4125 14.8347 19.7974L20.9378 13.0338C21.453 12.4629 21.453 11.5949 20.9378 11.024L14.8347 4.26044Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ArrowUpIcon.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var ArrowUpIcon = (props) => /* @__PURE__ */ jsx9("svg", { ...props, children: /* @__PURE__ */ jsx9(
  "path",
  {
    d: "M4.23151 9.19422C3.61647 9.7492 3.56778 10.6977 4.12276 11.3127C4.67775 11.9278 5.62624 11.9765 6.24129 11.4215L10.5 7.57866L10.5 19.8531C10.5 20.6815 11.1716 21.3531 12 21.3531C12.8284 21.3531 13.5 20.6815 13.5 19.8531L13.5 7.57867L17.7587 11.4215C18.3737 11.9765 19.3222 11.9278 19.8772 11.3128C20.4322 10.6977 20.3836 9.7492 19.7684 9.19422L13.0049 3.09109C12.434 2.57595 11.566 2.57595 10.9951 3.09109L4.23151 9.19422",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/AsteriskIcon.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var AsteriskIcon = (props) => /* @__PURE__ */ jsx10("svg", { ...props, children: /* @__PURE__ */ jsx10(
  "path",
  {
    d: "M14.4425 22L13.9358 15.3633L19.5575 19.0908L22 14.9542L15.3642 11.9542L22 9L19.5575 4.86333L13.9358 8.63667L14.4425 2H9.5575L10.0183 8.68167L4.39667 4.90917L2 9.04583L8.4975 12L2 14.9092L4.39667 19L10.0183 15.2725L9.5575 22H14.4425Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/AudienceIcon.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var AudienceIcon = (props) => /* @__PURE__ */ jsx11("svg", { ...props, children: /* @__PURE__ */ jsx11(
  "path",
  {
    d: "m11.1744 3.04659c2.6835 2.33688 2.1425 8.06531-2.85234 7.99341-4.99483-.0719-5.03082-5.26362-3.69418-7.27726 1.33665-2.01364 4.64706-2.37021 6.54652-.71615zm8.511 8.47051c1.505 1.3105 1.2016 4.523-1.5995 4.4827-2.8011-.0404-2.8213-2.9519-2.0717-4.0811s2.6061-1.3292 3.6712-.4016zm-16.99799 10.3336c-.32545.0141-.61759-.3577-.6444-.8086-.15145-2.3574.04693-5.2652 1.4129-7.0804 2.86859-3.7922 9.46089-1.5366 10.60059 2.1134.236.7357.4008 1.5352.4961 2.3645.1371-.16.2718-.3099.3984-.4466 1.7601-1.8904 5.8048-.766 6.5041 1.0535.2965.751.4096 1.6354.3492 2.5077-.0098.2438-.2089.4477-.4506.4458h-7.4492-.0132-.0022-.4484c-.0235.0008-.0471-.0016-.0706-.0069z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CalendarIcon.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var CalendarIcon = (props) => /* @__PURE__ */ jsx12("svg", { ...props, children: /* @__PURE__ */ jsx12(
  "path",
  {
    clipRule: "evenodd",
    d: "m7.5 2.9895c.82843 0 1.5.67157 1.5 1.5v1.51041l6 .00003c0-.00001 0 .00001 0 0v-1.51044c0-.82843.6716-1.5 1.5-1.5s1.5.67157 1.5 1.5v1.51041c0-.00001 0 .00001 0 0l1.5.00003c.8284 0 1.5.67157 1.5 1.5v1.5c0 .82843-.6716 1.49996-1.5 1.49996h-15c-.82843 0-1.5-.67153-1.5-1.49996v-1.5c0-.82843.67157-1.5 1.5-1.5h1.5v-1.51044c0-.82843.67157-1.5 1.5-1.5zm-3 9.0104c-.82843 0-1.5.6716-1.5 1.5v6c0 .8285.67157 1.5 1.5 1.5h15c.8284 0 1.5-.6715 1.5-1.5v-6c0-.8284-.6716-1.5-1.5-1.5z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CameraIcon.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var CameraIcon = (props) => /* @__PURE__ */ jsx13("svg", { ...props, children: /* @__PURE__ */ jsx13(
  "path",
  {
    clipRule: "evenodd",
    d: "M11.3162 20.979C11.6222 20.9857 11.9282 20.9924 12.2342 21V20.992C12.276 20.992 12.3178 20.9924 12.3595 20.9928C12.443 20.9935 12.5264 20.9942 12.6096 20.992C12.9666 20.9825 13.3235 20.9735 13.6805 20.9645C14.6678 20.9395 15.6552 20.9144 16.6423 20.8803C17.3374 20.8559 18.032 20.8127 18.7251 20.7569C19.3748 20.7048 20.0238 20.6415 20.6727 20.5783C20.9177 20.5544 21.1626 20.5305 21.4076 20.5072C21.8887 20.4612 22.2848 20.0971 22.3749 19.624C22.5973 18.4536 22.7728 17.2761 22.8625 16.0887C22.9258 15.2478 22.9671 14.403 22.9784 13.5597C22.9883 12.8257 22.9578 12.0898 22.9174 11.3563C22.8803 10.6899 22.8278 10.023 22.7433 9.36129C22.6605 8.71089 22.5531 8.06362 22.4457 7.41667C22.4215 7.27097 22.3974 7.12528 22.3735 6.97958C22.2928 6.48916 21.8624 6.12123 21.3663 6.08228C21.0761 6.05955 20.7863 6.03115 20.4965 6.00274C20.2656 5.98012 20.0347 5.9575 19.8036 5.93773C19.2277 5.88845 18.6514 5.84434 18.0747 5.80586C17.9794 5.79929 17.9418 5.76737 17.9151 5.67868L17.8955 5.61308C17.7345 5.07457 17.5734 4.53581 17.3998 4.00139C17.2445 3.5227 16.7784 3.15664 16.3077 3.1313C16.1265 3.12155 15.9453 3.11126 15.7641 3.10098C15.1608 3.06675 14.5575 3.03253 13.9537 3.01773C12.2178 2.97502 10.4823 3.00787 8.75008 3.13365C8.62103 3.14303 8.48634 3.17917 8.36901 3.23455C8.02173 3.39833 7.773 3.66208 7.65567 4.03236C7.56181 4.32848 7.47105 4.62568 7.38028 4.92287C7.3013 5.18146 7.22232 5.44004 7.14131 5.69792C7.12817 5.73922 7.07937 5.79131 7.04041 5.79741C6.95316 5.81131 6.86434 5.81585 6.77551 5.82041C6.73533 5.82246 6.69516 5.82452 6.65512 5.82744C6.53247 5.83652 6.40978 5.84531 6.28709 5.8541C5.96567 5.87712 5.64421 5.90015 5.32324 5.92834C5.07994 5.94972 4.83701 5.97418 4.59413 5.99864C4.29308 6.02896 3.99209 6.05927 3.69053 6.08368C3.21982 6.1217 2.78478 6.49432 2.70453 6.96316C2.66943 7.16876 2.63372 7.37429 2.59802 7.57982C2.47619 8.28117 2.35434 8.98263 2.25634 9.68746C2.15263 10.4327 2.06674 11.1841 2.03202 11.9354C1.99259 12.782 1.9879 13.6329 2.02732 14.479C2.06674 15.3322 2.1545 16.1854 2.26714 17.032C2.35101 17.6614 2.46316 18.287 2.5753 18.9124C2.61877 19.1549 2.66224 19.3973 2.70406 19.64C2.77868 20.0708 3.19542 20.4636 3.61263 20.5044C3.69683 20.5127 3.78099 20.5213 3.86514 20.5298C4.03332 20.5469 4.20149 20.564 4.37008 20.579C4.58912 20.5986 4.80813 20.6188 5.02715 20.639C5.58193 20.6903 6.13681 20.7416 6.69266 20.7813C7.51863 20.8404 8.34601 20.8897 9.17386 20.9207C9.88781 20.9476 10.602 20.9633 11.3162 20.979ZM6.3909 12.7131C6.41718 9.38992 9.19264 6.63089 12.5083 6.64872C15.8811 6.66703 18.5796 9.38664 18.5707 12.7375C18.5618 16.0676 15.807 18.8534 12.4721 18.8328C9.15556 18.8121 6.36462 16.0512 6.3909 12.7131ZM16.2261 12.7248C16.2383 10.658 14.5539 8.99148 12.4754 8.99524L12.4749 8.99571C10.4006 8.99899 8.72848 10.6655 8.73318 12.7243C8.73834 14.7935 10.4217 16.4942 12.4806 16.4867C14.5619 16.4792 16.2139 14.8104 16.2261 12.7248Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CaretDownIcon.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var CaretDownIcon = (props) => /* @__PURE__ */ jsx14("svg", { ...props, children: /* @__PURE__ */ jsx14(
  "path",
  {
    d: "m2.68581 7.2381c.52393-.57331 1.39057-.64667 2.00074-.19312l.11843.09775 7.19402 6.57527 7.196-6.57527c.5733-.52393 1.443-.51915 2.01-.01262l.1092.10799c.5239.57331.5191 1.44304.0126 2.01l-.108.10918-8.2069 7.50002c-.5348.4887-1.3351.5213-1.9053.0977l-.1185-.0977-8.20691-7.50002c-.61154-.55886-.65424-1.50764-.09538-2.11918z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CaretLeftIcon.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var CaretLeftIcon = (props) => /* @__PURE__ */ jsx15("svg", { ...props, children: /* @__PURE__ */ jsx15(
  "path",
  {
    d: "m16.7619 2.68582c.5733.52393.6467 1.39057.1931 2.00074l-.0977.11844-6.5753 7.194 6.5753 7.196c.5239.5733.5191 1.443.0126 2.01l-.108.1092c-.5733.5239-1.443.5191-2.01.0126l-.1092-.108-7.49998-8.2069c-.48873-.5348-.52131-1.3351-.09775-1.9053l.09775-.1185 7.49998-8.2069c.5589-.61153 1.5077-.65423 2.1192-.09538z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CaretRightIcon.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var CaretRightIcon = (props) => /* @__PURE__ */ jsx16("svg", { ...props, children: /* @__PURE__ */ jsx16(
  "path",
  {
    d: "m7.23808 21.3142c-.57331-.524-.64667-1.3906-.19312-2.0008l.09774-.1184 6.5753-7.194-6.57529-7.196c-.52393-.57331-.51915-1.44303-.01262-2.01l.10799-.10917c.57331-.52393 1.44303-.51915 2.01-.01262l.10917.10799 7.50005 8.2069c.4887.5348.5213 1.3351.0977 1.9053l-.0977.1185-7.50004 8.2069c-.55886.6115-1.50765.6542-2.11918.0954z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CaretUpIcon.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var CaretUpIcon = (props) => /* @__PURE__ */ jsx17("svg", { ...props, children: /* @__PURE__ */ jsx17(
  "path",
  {
    d: "m21.3141 16.7619c-.5239.5733-1.3905.6467-2.0007.1931l-.1184-.0977-7.194-6.5753-7.19602 6.5753c-.57331.5239-1.44304.5191-2.01.0126l-.10918-.108c-.52393-.5733-.51915-1.443-.01261-2.01l.10798-.1092 8.20683-7.49998c.5348-.48873 1.3352-.52131 1.9054-.09775l.1184.09775 8.207 7.49998c.6115.5589.6542 1.5077.0953 2.1192z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ChannelIcon.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var ChannelIcon = (props) => /* @__PURE__ */ jsx18("svg", { ...props, children: /* @__PURE__ */ jsx18(
  "path",
  {
    clipRule: "evenodd",
    d: "m5.64571 1.19092c-.44678-.324663-1.07216-.225666-1.39682.22112-.32466.44678-.22567 1.07215.22111 1.39682 1.28625.93467 2.48092 2.19849 3.54704 3.39817-1.62947-.00332-2.92814.02027-3.46778.08086-1.98082.22241-2.2359 1.3046-2.45624 2.97122-.22035 1.66659.01731 8.61489.2821 10.20679s1.03019 2.2578 3.23903 2.2185c1.36389-.0243 5.14395.102 8.22435.2049 1.9081.0637 3.5484.1185 4.1789.123l.0126.0001c1.6426.0116 2.973.0211 3.5839-1.2509.6125-1.2752.3643-9.5706.2662-11.15071-.0982-1.58008-.6322-2.71681-1.8871-2.99303-.4032-.08875-1.9372-.17748-3.8995-.24967.9793-1.0846 2.1361-2.34437 3.5284-3.63478.405-.37542.4291-1.00813.0536-1.41319-.3754-.405062-1.0081-.429086-1.4132-.05366-1.61 1.49224-2.9274 2.95491-3.9684 4.11064-.1937.21508-.3778.41955-.5525.61158-.0947.10407-.1859.20373-.2738.29896-.9155-.02314-1.8546-.0422-2.7663-.056-.1596-.18054-.3281-.37314-.5061-.57663l-.1089-.12441c-1.18229-1.35084-2.71275-3.08411-4.44059-4.33968zm9.86039 12.42148c-1.0777 1.4126-4.8912 3.6285-6.16234 4.3486-.19344.1108-.41451-.0277-.41451-.2493 0-1.468 0-5.9827.05527-7.4507 0-.1939.22107-.3324.4145-.2216l6.02418 3.1576c.1382.0831.1934.2769.0829.4154z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CheckmarkCircleIcon.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var CheckmarkCircleIcon = (props) => /* @__PURE__ */ jsx19("svg", { ...props, children: /* @__PURE__ */ jsx19(
  "path",
  {
    clipRule: "evenodd",
    d: "m21.8158 14.5648c3.0982-10.94829-8.8224-15.41821-15.39746-10.85188-4.65388 3.23212-6.673329 10.24658-3.15895 14.34028 3.51439 4.0936 15.45811 7.4598 18.55641-3.4884zm-4.1007-5.8658c.3861-.39496.3788-1.02808-.0161-1.41413-.395-.38604-1.0281-.37882-1.4141.01613l-6.127 6.2684-2.44277-2.4992c-.38605-.3949-1.01917-.4021-1.41413-.0161-.39495.3861-.40217 1.0192-.01612 1.4141l3.15789 3.2308c.18815.1925.44596.301.71513.301.2692 0 .527-.1085.7151-.301z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CheckmarkCircleOutlineIcon.tsx
import { jsx as jsx20, jsxs } from "react/jsx-runtime";
var CheckmarkCircleOutlineIcon = ({ ...props }) => /* @__PURE__ */ jsxs("svg", { ...props, children: [
  /* @__PURE__ */ jsx20(
    "path",
    {
      clipRule: "evenodd",
      d: "M11.25 2.5C6.41751 2.5 2.5 6.41751 2.5 11.25C2.5 16.0825 6.41751 20 11.25 20C16.0825 20 20 16.0825 20 11.25C20 6.41751 16.0825 2.5 11.25 2.5ZM0 11.25C0 5.0368 5.0368 0 11.25 0C17.4632 0 22.5 5.0368 22.5 11.25C22.5 17.4632 17.4632 22.5 11.25 22.5C5.0368 22.5 0 17.4632 0 11.25Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx20(
    "path",
    {
      clipRule: "evenodd",
      d: "M17.1237 7.35609C17.6174 7.83865 17.6265 8.63005 17.1439 9.12374L10.3018 16.1237C10.0666 16.3644 9.74436 16.5 9.4079 16.5C9.07143 16.5 8.74918 16.3644 8.51399 16.1237L5.35609 12.893C4.87354 12.3993 4.88256 11.6079 5.37626 11.1253C5.86995 10.6428 6.66135 10.6518 7.14391 11.1455L9.4079 13.4617L15.3561 7.37626C15.8386 6.88256 16.6301 6.87354 17.1237 7.35609Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  )
] });

// src/components/Icon/icons/CheckmarkCircleThreeQuartersIcon.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var CheckmarkCircleThreeQuartersIcon = (props) => /* @__PURE__ */ jsx21("svg", { ...props, children: /* @__PURE__ */ jsx21(
  "path",
  {
    d: "m5.06066 10.1406c1.02693-3.83255 4.96634-6.10695 8.79884-5.08003.8844.23699 1.7935-.28787 2.0305-1.1723.237-.88444-.2879-1.79352-1.1723-2.0305-5.60142-1.500892-11.35896 1.82322-12.85985 7.42462-1.500884 5.60141 1.82323 11.35891 7.42463 12.85981 5.60142 1.5009 11.35892-1.8232 12.85982-7.4246.237-.8844-.2879-1.7935-1.1723-2.0305s-1.7935.2879-2.0305 1.1723c-1.0269 3.8325-4.9663 6.1069-8.7988 5.08-3.83257-1.0269-6.10696-4.9663-5.08004-8.7988zm16.57534-2.98696c.6582-.63651.6758-1.68609.0392-2.34429-.6365-.65821-1.686-.67579-2.3442-.03928l-7.5698 7.32023-2.17928-2.46975c-.60581-.68657-1.65349-.75203-2.34005-.14621-.68656.60576-.75202 1.65346-.14621 2.34006l2.94474 3.3372c.8339.9451 2.2881 1.0027 3.1942.1265z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CheckmarkIcon.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var CheckmarkIcon = (props) => /* @__PURE__ */ jsx22("svg", { ...props, children: /* @__PURE__ */ jsx22(
  "path",
  {
    d: "m8.79289 18.7054-6.50001-6.5374c-.39051-.3927-.39051-1.0295 0-1.4223l1.41418-1.42238c.39051-.39279 1.02371-.39279 1.41422 0l4.37872 4.40388 9.3787-9.43263c.3905-.39276 1.0237-.39276 1.4142 0l1.4142 1.42235c.3905.39275.3905 1.02956 0 1.42235l-11.5 11.56613c-.39054.3928-1.0237.3928-1.41421 0z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CheckmarkUnderlinedIcon.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var CheckmarkUnderlinedIcon = (props) => /* @__PURE__ */ jsx23("svg", { ...props, children: /* @__PURE__ */ jsx23(
  "path",
  {
    d: "M9.54401 12.95L17.994 4.49995C18.194 4.29995 18.4315 4.19995 18.7065 4.19995C18.9815 4.19995 19.219 4.29995 19.419 4.49995C19.619 4.69995 19.719 4.93745 19.719 5.21245C19.719 5.48745 19.619 5.72495 19.419 5.92495L10.244 15.1C10.044 15.3 9.81067 15.4 9.54401 15.4C9.27734 15.4 9.04401 15.3 8.84401 15.1L4.56901 10.825C4.36901 10.625 4.27317 10.3875 4.28151 10.1125C4.28984 9.83745 4.39401 9.59995 4.59401 9.39995C4.79401 9.19995 5.03151 9.09995 5.30651 9.09995C5.58151 9.09995 5.81901 9.19995 6.01901 9.39995L9.54401 12.95ZM5.99401 19.8C5.71067 19.8 5.47317 19.7041 5.28151 19.5125C5.08984 19.3208 4.99401 19.0833 4.99401 18.8C4.99401 18.5166 5.08984 18.2791 5.28151 18.0875C5.47317 17.8958 5.71067 17.8 5.99401 17.8H17.994C18.2773 17.8 18.5148 17.8958 18.7065 18.0875C18.8982 18.2791 18.994 18.5166 18.994 18.8C18.994 19.0833 18.8982 19.3208 18.7065 19.5125C18.5148 19.7041 18.2773 19.8 17.994 19.8H5.99401Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CloseCircleIcon.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var CloseCircleIcon = (props) => /* @__PURE__ */ jsx24("svg", { ...props, children: /* @__PURE__ */ jsx24(
  "path",
  {
    clipRule: "evenodd",
    d: "M13.8186 20.4731C4.60239 22.893 -2.98829 15.5818 3.31523 6.74072C9.6261 -2.10038 19.5705 4.8504 21.402 9.84467C23.2334 14.8389 19.0556 19.105 13.8186 20.4731Z \n       M15.0413 8.87414C15.4318 8.48355 15.4318 7.85028 15.0413 7.45969C14.6508 7.0691 14.0178 7.0691 13.6273 7.45969L11.0001 10.0878L8.37284 7.4597C7.98238 7.06911 7.34932 7.06911 6.95886 7.4597C6.5684 7.85029 6.5684 8.48356 6.95886 8.87415L9.5861 11.5023L6.95886 14.1304C6.5684 14.5209 6.5684 15.1542 6.95886 15.5448C7.34932 15.9354 7.98238 15.9354 8.37284 15.5448L11.0001 12.9167L13.6273 15.5448C14.0178 15.9354 14.6508 15.9354 15.0413 15.5448C15.4318 15.1542 15.4318 14.521 15.0413 14.1304L12.4141 11.5023L15.0413 8.87414Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CloseIcon.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var CloseIcon = (props) => /* @__PURE__ */ jsx25("svg", { ...props, children: /* @__PURE__ */ jsx25(
  "path",
  {
    d: "m17.7742 19.618c.5091.5093 1.3347.5093 1.8439 0 .5091-.5091.5091-1.3347 0-1.8438l-5.7742-5.7742 5.7742-5.77422c.5091-.50918.5091-1.3347 0-1.84387-.5092-.50917-1.3348-.50917-1.8439 0l-5.7742 5.77419-5.77426-5.77422c-.50917-.50917-1.33469-.50917-1.84387 0-.50916.50917-.50916 1.3347 0 1.84386l5.77423 5.77426-5.77421 5.7742c-.50918.5092-.50918 1.3347 0 1.8439.50916.5092 1.33469.5092 1.84385 0l5.77426-5.7742z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CollectVideoIcon.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var CollectVideoIcon = (props) => /* @__PURE__ */ jsx26("svg", { ...props, children: /* @__PURE__ */ jsx26(
  "path",
  {
    clipRule: "evenodd",
    d: "M12 6.85714H3V4.92857C3 3.864 3.864 3 4.92857 3H8.74072C9.17014 3 9.57257 3.21471 9.81043 3.57214L12 6.85714ZM3 6.85718H19.0714C20.136 6.85718 21 7.72118 21 8.78575V13.1438C20.9149 13.1202 20.8253 13.1077 20.7328 13.1077H13.3066L14.7071 11.7071C15.0976 11.3166 15.0976 10.6834 14.7071 10.2929C14.3166 9.90237 13.6834 9.90237 13.2929 10.2929L10.2929 13.2929C10.1054 13.4804 10 13.7348 10 14C10 14.2652 10.1054 14.5196 10.2929 14.7071L13.2929 17.7071C13.6834 18.0976 14.3166 18.0976 14.7071 17.7071C15.0976 17.3166 15.0976 16.6834 14.7071 16.2929L13.5219 15.1077H20.7328C20.8253 15.1077 20.9149 15.0951 21 15.0716V17.1429C21 19.272 19.272 21 17.1429 21H6.85714C4.728 21 3 19.272 3 17.1429V6.85718Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CommentIcon.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var CommentIcon = (props) => /* @__PURE__ */ jsx27("svg", { ...props, children: /* @__PURE__ */ jsx27(
  "path",
  {
    clipRule: "evenodd",
    d: "M2.0994 10.6931C2.04002 8.22692 1.96942 5.29509 2.11584 4.25593C2.13365 4.13216 2.14292 4.01528 2.1517 3.90469C2.21613 3.09248 2.25369 2.61894 5.44865 2.23843C9.48199 1.75807 17.1241 1.99825 20.9452 3.007C21.7385 3.21574 21.7461 3.55147 21.7624 4.26645C21.7648 4.37414 21.7675 4.49044 21.773 4.6162C21.8367 5.57691 22.0066 10.2604 21.9641 11.7255C21.9538 12.0123 21.9535 12.4616 21.9532 13.001V13.001C21.9522 14.6749 21.9507 17.2153 21.6457 18.4505C21.4546 19.2671 20.0748 19.2911 18.9073 19.171C18.4972 19.1289 17.0813 19.0734 15.3164 19.0193C14.4586 19.7428 13.2551 20.8268 12.6703 21.669C12.4667 21.9623 12.1951 22 11.9708 22C11.7566 22 11.6986 21.9343 11.3756 21.5684L11.3281 21.5147C10.6506 20.7484 9.58149 19.5267 9.00085 18.8624C7.74516 18.8383 6.69432 18.8234 6.10233 18.8234C3.78846 18.8234 2.85054 18.5199 2.51089 18.1837C2.02619 17.7038 2.08219 16.014 2.13647 14.3759L2.14543 14.1031C2.16606 13.462 2.13471 12.1599 2.0994 10.6931ZM4.33635 7.16713C4.33635 7.16713 3.83065 8.24157 4.01326 8.52809C4.14949 8.74182 8.25659 8.84926 10.9526 8.91979L10.9527 8.91979C11.8709 8.94381 12.6253 8.96354 13.0036 8.98174C14.1695 9.05337 17.7376 8.83848 19.2126 8.74298C19.24 8.74161 19.2672 8.74047 19.2943 8.73935C19.7381 8.72089 20.1152 8.7052 19.943 7.69241C19.9149 7.5014 19.8166 7.38202 19.634 7.38202C19.044 7.38202 9.89913 7.38202 7.90439 7.191L7.64546 7.16615C5.81529 6.99024 4.8063 6.89326 4.33635 7.16713ZM19.8977 11.4732C20.0908 11.7527 19.6254 12.8452 19.6254 12.8452C19.166 13.1363 18.1541 13.0769 16.3186 12.9692C16.2338 12.9642 16.1473 12.9591 16.059 12.9539C14.0586 12.8372 4.92114 13.177 4.33049 13.199L4.33037 13.199C4.14788 13.2058 4.04518 13.0901 4.01001 12.9003C3.80035 11.8946 4.17656 11.8649 4.61942 11.83C4.64638 11.8278 4.67359 11.8257 4.70093 11.8233C6.17134 11.673 9.72893 11.3257 10.8967 11.3539C11.2754 11.3581 12.0301 11.3498 12.9486 11.3396C15.6454 11.3099 19.7536 11.2646 19.8977 11.4732Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/CommunityIcon.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var CommunityIcon = (props) => /* @__PURE__ */ jsx28("svg", { ...props, children: /* @__PURE__ */ jsx28(
  "path",
  {
    d: "m14.8307 3.14077c.3645.16757.686.31534.9541.40981l.0186.00652c1.2355.43528 5.3747 1.89346 6.0303 4.36204.5612 2.11356-.3999 4.17296-1.3677 6.24656-.1682.3606-.3367.7217-.4975 1.0835-.0379.0854-.0756.1704-.1132.255-1.4657 3.3022-2.6018 5.8617-4.6582 6.3731-2.1092.5247-5.1606-.7312-7.71054-1.9147-.05102-.0244-.10251-.0491-.15441-.0738-1.99154-.951-4.60465-2.1987-5.20489-4.1987-.61588-2.0523 1.14517-6.02627 2.25161-7.92519.20562-.35289.40011-.72469.59807-1.10314.86724-1.65795 1.80113-3.44328 4.02973-4.32392 2.19233-.86633 4.35703.12865 5.82403.80292zm-2.2269 2.13686c-.5027-.24679-1.1143-.04716-1.366.44591l-.6538 1.281-1.30983-.64294c-.50277-.24679-1.11436-.04714-1.36603.44591-.25167.49306-.04812 1.09282.45465 1.33961l1.3098.64293-1.50554 2.94955-1.44199-.7078c-.50276-.2468-1.11435-.0471-1.36602.4459-.25167.4931-.04812 1.0928.45465 1.3396l1.44198.7078-.77998 1.5281c-.25167.4931-.04812 1.0929.45464 1.3396.50277.2468 1.11436.0472 1.36603-.4459l.77999-1.5281 2.98935 1.4674-.7622 1.4931c-.2517.4931-.0481 1.0929.4547 1.3397.5027.2467 1.1143.0471 1.366-.446l.7622-1.4931 1.4413.7075c.5028.2468 1.1144.0471 1.366-.446.2517-.4929.0482-1.0928-.4546-1.3396l-1.4414-.7074 1.5055-2.9496 1.5735.7724c.5028.2468 1.1144.0472 1.3661-.4459.2516-.4931.0481-1.0928-.4546-1.3396l-1.5736-.7724.6717-1.31597c.2518-.49306.0482-1.09282-.4546-1.33961-.5027-.24679-1.1143-.04715-1.366.4459l-.6718 1.31598-2.9892-1.46736.6538-1.281c.2517-.49306.0481-1.09283-.4547-1.33961zm1.8788 5.87347-1.5055 2.9496-2.98937-1.4674 1.50557-2.94954z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ContactUsIcon.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var ContactUsIcon = (props) => /* @__PURE__ */ jsx29("svg", { ...props, children: /* @__PURE__ */ jsx29(
  "path",
  {
    d: "m2 7.13053c1.02794.75462 2.30848 1.82137 3.61006 2.90567 0 0 5.06424 3.9629 5.81564 3.9629 1.8549 0 3.7992-1.8146 5.1678-2.8746 1.3176-1.0205 2.7467-2.21282 4.0625-3.31055.1651-.13774.3285-.27408.4895-.40831-.0011.22999.0035.44719.0315.64131.3947 2.63975.4326 8.17435 0 11.09695-.3145.4144-.8493.9882-3.0513.8288s-11.82778-.2869-12.92877-.2231c-1.101.0637-2.39073-.0638-2.61093-1.1158-.40264-1.7682-.5511-8.74247-.586-11.50327zm16.4311-2.99936c1.2812.09529 1.9619.48357 2.322 1.00077-.4574.37757-.9338.77506-1.4207 1.18138-1.2989 1.0838-2.6732 2.23056-3.9636 3.22999-.9655.74789-1.8517 1.38729-2.5896 1.83539-.3691.2242-.6815.3884-.9342.4939-.2367.0989-.3632.1358-.5917.0084-.2306-.1286-.5322-.3283-.8998-.5958-.73248-.5331-1.63926-1.2708-2.61739-2.08202-.26991-.22385-.54604-.45404-.8253-.68686-.21657-.18055-.43504-.36268-.65398-.54466l-.65679-.54374c-1.20143-.98965-2.37365-1.92213-3.27827-2.50838.22838-.42619.59195-.78832 1.14091-.78831 1.57531.00002 3.21902-.03191 4.885-.06428 3.39902-.06604 6.89072-.13388 10.08342.06422z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CopyDisabledIcon.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var CopyDisabledIcon = (props) => /* @__PURE__ */ jsx30("svg", { ...props, children: /* @__PURE__ */ jsx30(
  "path",
  {
    d: "m23.7071 1.70711c.3905-.39053.3905-1.023692 0-1.414217-.3905-.3905241-1.0237-.3905241-1.4142 0l-22.000007 22.000007c-.3905238.3905-.3905242 1.0237 0 1.4142.390524.3905 1.023687.3905 1.414217 0zm-5.0845.29289c.1737 0 .3443.01298.5109.03803l-3.3066 3.30661h-8.21351c-.93263 0-1.68869-.74873-1.68869-1.67232 0-.92361.75606-1.67232 1.68869-1.67232zm-14.9339 4.94911h10.5338l-12.2225 12.22249v-10.55017c0-.92359.75605-1.67232 1.6887-1.67232zm13.5095 2.68113-12.36977 12.36976h10.68107c.9327 0 1.6887-.7487 1.6887-1.6723zm4.7671-4.76707-3.3427 3.34265v8.10658c0 .9235.756 1.6724 1.6887 1.6724s1.6887-.7489 1.6887-1.6724v-10.96776c0-.1635-.0118-.32425-.0347-.48147z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CopyIcon.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
var CopyIcon = (props) => /* @__PURE__ */ jsx31("svg", { ...props, children: /* @__PURE__ */ jsx31(
  "path",
  {
    d: "m18.6226 16.3124c0 .9235.756 1.6724 1.6887 1.6724s1.6887-.7489 1.6887-1.6724v-10.96776c0-1.8472-1.5122-3.34464-3.3774-3.34464h-11.00921c-.93263 0-1.68869.74871-1.68869 1.67232 0 .92359.75606 1.67232 1.68869 1.67232h11.00921zm-3.1131 5.6876c.9327 0 1.6887-.7487 1.6887-1.6723v-11.70627c0-.92359-.756-1.67232-1.6887-1.67232h-11.8208c-.93265 0-1.6887.74873-1.6887 1.67232v11.70627c0 .9236.75605 1.6723 1.6887 1.6723z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/CreateIcon.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var CreateIcon = (props) => /* @__PURE__ */ jsx32("svg", { ...props, children: /* @__PURE__ */ jsx32(
  "path",
  {
    d: "m10.3966 3.60341c0-.88553.7179-1.60341 1.6034-1.60341s1.6034.71788 1.6034 1.60341v6.79319h6.7932c.8856 0 1.6034.7179 1.6034 1.6034 0 .8856-.7178 1.6034-1.6034 1.6034h-6.7932v6.7932c0 .8856-.7179 1.6034-1.6034 1.6034s-1.6034-.7178-1.6034-1.6034v-6.7932h-6.7932c-.88554 0-1.6034-.7178-1.6034-1.6034 0-.8855.71786-1.6034 1.6034-1.6034h6.7932z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/DeleteIcon.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var DeleteIcon = (props) => /* @__PURE__ */ jsx33("svg", { ...props, children: /* @__PURE__ */ jsx33(
  "path",
  {
    d: "m14.904 2.36857c.6808.18014 1.0637 1.89327 1.3326 3.11843 1.0626.02303 2.1802.06975 3.3652.15126.5509.0379.9669.51528.929 1.06626-.0379.55099-.5153.96692-1.0663.92902-3.2573-.22405-5.9961-.17824-8.5308-.1036-.3474.01023-.6918.02104-1.03385.03178-2.1416.06726-4.18778.13153-6.28166.02989-.55163-.02678-.97711-.49568-.95034-1.04731.02678-.55164.49568-.97712 1.04731-.95034 1.04212.05058 2.0709.0584 3.11959.04482.08961-.94493.83902-2.66045 1.49748-3.27021.72917-.67525 5.56337-.2668 6.57177 0zm-8.53971 18.93913c-.38411-1.7907-1.43613-8.7724-1.60784-10.1112-.01239-.0967-.02523-.1887-.03746-.2764-.1573-1.12763-.21516-1.5424 2.05023-1.84784 2.71293-.36579 9.32588-.45722 11.89608.31093.5336.15895.5387.4146.5496.95901.0017.082.0035.1706.0072.2664 0 2.8358-.6932 5.8331-1.3554 8.6963-.1564.6763-.3112 1.3453-.4546 2.0028-.1285.6219-1.0566.6401-1.842.5487-.7853-.0915-5.4362-.2926-6.99265-.2926-1.55635 0-1.98471 0-2.21316-.2561zm8.97891-9.2246c-.0298-.5515-.501-.9744-1.0525-.9446s-.9744.501-.9446 1.0525c.0309.5711-.027 1.5863-.123 2.7715-.0544.6721-.1166 1.3496-.1763 2.0013-.0459.5008-.0905.9868-.1291 1.4427-.0465.5504.3619 1.0342.9122 1.0807.5503.0466 1.0342-.3618 1.0807-.9122.0351-.4152.0789-.8939.1249-1.3962l.0002-.0017.0001-.0015c.0616-.6734.1271-1.3892.1808-2.0516.094-1.1611.166-2.312.1266-3.0409zm-5.56465 7.4093c.55025.0479 1.03505-.3594 1.08295-.9096.0632-.7272.0291-1.8798-.0269-3.0434-.0319-.6643-.0739-1.3823-.1135-2.0578-.0295-.504-.0576-.985-.0791-1.4014-.0285-.5516-.4987-.9756-1.05025-.9472-.55156.0285-.97562.4987-.94716 1.0502.02358.4571.05217.944.08167 1.4462.03835.653.07827 1.3328.11063 2.006.0571 1.1878.08163 2.2042.03206 2.7741-.04786.5502.3594 1.035.9096 1.0829z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/DeleteSmIcon.tsx
import { jsx as jsx34, jsxs as jsxs2 } from "react/jsx-runtime";
var DeleteSmIcon = (props) => /* @__PURE__ */ jsx34("svg", { ...props, children: /* @__PURE__ */ jsxs2("g", { fill: "currentColor", children: [
  /* @__PURE__ */ jsx34("path", { d: "m9.05042 2c-.64391 0-1.23256.35667-1.52052.92132l-1.23015 2.41201h-1.59974c-.9389 0-1.70001.74619-1.70001 1.66667 0 .92047.76111 1.66667 1.70001 1.66667h14.59999c.9389 0 1.7-.7462 1.7-1.66667 0-.92048-.7611-1.66667-1.7-1.66667h-1.8003l-1.2301-2.41201c-.288-.56465-.8765-.92132-1.5205-.92132z" }),
  /* @__PURE__ */ jsx34("path", { d: "m6.63212 10c-.9389 0-1.63212.7462-1.63212 1.6667v8.8333c0 .9205.36086 1.5 1.29975 1.5h11.19995c.9389 0 1.5003-.5795 1.5003-1.5l-.1-8.8333c0-.9205-.4611-1.6667-1.4-1.6667z" })
] }) });

// src/components/Icon/icons/DottieClockIcon.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var DottieClockIcon = (props) => /* @__PURE__ */ jsx35("svg", { ...props, children: /* @__PURE__ */ jsx35(
  "path",
  {
    clipRule: "evenodd",
    d: "M10 18.8889C9.54004 18.8889 9.16671 18.5155 9.16671 18.0555C9.16671 17.5955 9.54004 17.2222 10 17.2222C13.9823 17.2222 17.2223 13.9822 17.2223 9.99997C17.2223 6.01775 13.9823 2.77775 10 2.77775C9.54004 2.77775 9.16671 2.40442 9.16671 1.94442C9.16671 1.48442 9.54004 1.11108 10 1.11108C14.9012 1.11108 18.8889 5.09886 18.8889 9.99997C18.8889 14.9011 14.9012 18.8889 10 18.8889ZM13.61 13.3333C13.4478 13.3333 13.2812 13.2855 13.1367 13.1855L9.5256 10.6855C9.30115 10.53 9.16671 10.2733 9.16671 9.99997V5.27775C9.16671 4.81775 9.54004 4.44442 10 4.44442C10.46 4.44442 10.8334 4.81775 10.8334 5.27775V9.56331L14.0856 11.8144C14.4645 12.0766 14.5589 12.5955 14.2956 12.9733C14.1334 13.2066 13.8745 13.3322 13.61 13.3322V13.3333ZM5.13676 15.6966C5.13676 16.1568 4.76366 16.5299 4.30343 16.5299C3.84319 16.5299 3.47009 16.1568 3.47009 15.6966C3.47009 15.2363 3.84319 14.8632 4.30343 14.8632C4.76366 14.8632 5.13676 15.2363 5.13676 15.6966ZM1.94442 10.8333C2.40465 10.8333 2.77775 10.4602 2.77775 9.99994C2.77775 9.5397 2.40465 9.16661 1.94442 9.16661C1.48418 9.16661 1.11108 9.5397 1.11108 9.99994C1.11108 10.4602 1.48418 10.8333 1.94442 10.8333ZM5.13676 4.30332C5.13676 4.76356 4.76366 5.13666 4.30343 5.13666C3.84319 5.13666 3.47009 4.76356 3.47009 4.30332C3.47009 3.84309 3.84319 3.46999 4.30343 3.46999C4.76366 3.46999 5.13676 3.84309 5.13676 4.30332ZM6.91777 18.2755C7.378 18.2755 7.7511 17.9024 7.7511 17.4422C7.7511 16.9819 7.378 16.6088 6.91777 16.6088C6.45753 16.6088 6.08443 16.9819 6.08443 17.4422C6.08443 17.9024 6.45753 18.2755 6.91777 18.2755ZM3.39115 13.0822C3.39115 13.5425 3.01806 13.9155 2.55782 13.9155C2.09758 13.9155 1.72449 13.5425 1.72449 13.0822C1.72449 12.622 2.09758 12.2489 2.55782 12.2489C3.01806 12.2489 3.39115 12.622 3.39115 13.0822ZM2.55782 7.751C3.01806 7.751 3.39115 7.3779 3.39115 6.91766C3.39115 6.45743 3.01806 6.08433 2.55782 6.08433C2.09758 6.08433 1.72449 6.45743 1.72449 6.91766C1.72449 7.3779 2.09758 7.751 2.55782 7.751ZM7.7511 2.55772C7.7511 3.01796 7.378 3.39105 6.91777 3.39105C6.45753 3.39105 6.08443 3.01796 6.08443 2.55772C6.08443 2.09748 6.45753 1.72439 6.91777 1.72439C7.378 1.72439 7.7511 2.09748 7.7511 2.55772Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/DownloadIcon.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
var DownloadIcon = (props) => /* @__PURE__ */ jsx36("svg", { ...props, children: /* @__PURE__ */ jsx36(
  "path",
  {
    d: "m8.60831 3.62342c0-.89747.72755-1.625 1.62499-1.625h3.25c.8974 0 1.6249.72753 1.6249 1.625l-.0733 5.37819h2.169c1.5012 0 2.1996 1.86149 1.0688 2.84899l-5.4188 4.7324c-.6123.5347-1.5256.5347-2.1378 0l-5.41894-4.7324c-1.13072-.9875-.43233-2.84899 1.06891-2.84899h2.16889zm-4.94834 15.12818c-.89747 0-1.625.7275-1.625 1.625s.72753 1.625 1.625 1.625h16.68003c.8975 0 1.625-.7275 1.625-1.625s-.7275-1.625-1.625-1.625z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/DraggableIcon.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var DraggableIcon = (props) => /* @__PURE__ */ jsx37("svg", { ...props, children: /* @__PURE__ */ jsx37(
  "path",
  {
    d: "m8.4 22c-.66 0-1.225-.2448-1.695-.7344s-.705-1.0781-.705-1.7656.235-1.276.705-1.7656 1.035-.7344 1.695-.7344 1.225.2448 1.695.7344.705 1.0781.705 1.7656-.235 1.276-.705 1.7656-1.035.7344-1.695.7344zm7.2 0c-.66 0-1.225-.2448-1.695-.7344s-.705-1.0781-.705-1.7656.235-1.276.705-1.7656 1.035-.7344 1.695-.7344 1.225.2448 1.695.7344.705 1.0781.705 1.7656-.235 1.276-.705 1.7656-1.035.7344-1.695.7344zm-7.2-7.5c-.66 0-1.225-.2448-1.695-.7344s-.705-1.0781-.705-1.7656.235-1.276.705-1.7656c.47-.48961 1.035-.7344 1.695-.7344s1.225.24479 1.695.7344c.47.4896.705 1.0781.705 1.7656s-.235 1.276-.705 1.7656-1.035.7344-1.695.7344zm7.2 0c-.66 0-1.225-.2448-1.695-.7344s-.705-1.0781-.705-1.7656.235-1.276.705-1.7656c.47-.48961 1.035-.7344 1.695-.7344s1.225.24479 1.695.7344c.47.4896.705 1.0781.705 1.7656s-.235 1.276-.705 1.7656-1.035.7344-1.695.7344zm-7.2-7.5c-.66 0-1.225-.24479-1.695-.73437-.47-.48959-.705-1.07813-.705-1.76563s.235-1.27604.705-1.76562c.47-.48959 1.035-.73438 1.695-.73438s1.225.24479 1.695.73438c.47.48958.705 1.07812.705 1.76562s-.235 1.27604-.705 1.76563c-.47.48958-1.035.73437-1.695.73437zm7.2 0c-.66 0-1.225-.24479-1.695-.73437-.47-.48959-.705-1.07813-.705-1.76563s.235-1.27604.705-1.76562c.47-.48959 1.035-.73438 1.695-.73438s1.225.24479 1.695.73438c.47.48958.705 1.07812.705 1.76562s-.235 1.27604-.705 1.76563c-.47.48958-1.035.73437-1.695.73437z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/EllipsisIcon.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var EllipsisIcon = (props) => /* @__PURE__ */ jsx38("svg", { ...props, children: /* @__PURE__ */ jsx38(
  "path",
  {
    d: "m6.8654 12.0577c0 1.2745-1.03319 2.3077-2.3077 2.3077s-2.3077-1.0332-2.3077-2.3077 1.03319-2.3077 2.3077-2.3077 2.3077 1.0332 2.3077 2.3077zm7.6923 0c0 1.2745-1.0331 2.3077-2.3077 2.3077-1.2745 0-2.30766-1.0332-2.30766-2.3077s1.03316-2.3077 2.30766-2.3077c1.2746 0 2.3077 1.0332 2.3077 2.3077zm5.3846 2.3077c1.2746 0 2.3077-1.0332 2.3077-2.3077s-1.0331-2.3077-2.3077-2.3077c-1.2745 0-2.3077 1.0332-2.3077 2.3077s1.0332 2.3077 2.3077 2.3077z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/EllipsisVerticalIcon.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var EllipsisVerticalIcon = (props) => /* @__PURE__ */ jsx39("svg", { ...props, children: /* @__PURE__ */ jsx39(
  "path",
  {
    d: "m12.25 6.67311c-1.2745 0-2.30769-1.03319-2.30769-2.30771 0-1.27451 1.03319-2.3077 2.30769-2.3077s2.3077 1.03319 2.3077 2.3077c0 1.27452-1.0332 2.30771-2.3077 2.30771zm0 7.69229c-1.2745 0-2.30769-1.0331-2.30769-2.3077 0-1.2745 1.03319-2.30766 2.30769-2.30766s2.3077 1.03316 2.3077 2.30766c0 1.2746-1.0332 2.3077-2.3077 2.3077zm-2.30769 5.3846c0 1.2746 1.03319 2.3077 2.30769 2.3077s2.3077-1.0331 2.3077-2.3077c0-1.2745-1.0332-2.3077-2.3077-2.3077s-2.30769 1.0332-2.30769 2.3077z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/EmbedIcon.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
var EmbedIcon = (props) => /* @__PURE__ */ jsx40("svg", { ...props, children: /* @__PURE__ */ jsx40(
  "path",
  {
    d: "m13.7617 2c.0584 0 .12.00417.18.01417.6751.1 1.1442.745 1.0451 1.43166l-2.5243 17.47497c-.09.6259-.6225 1.0792-1.2251 1.0792-.0591 0-.1191-.005-.18-.0142-.6733-.1-1.14336-.745-1.0442-1.4316l2.5226-17.4767c.09-.625.6242-1.0775 1.2251-1.0775zm-7.00456 5c.31886 0 .63772.12667.87943.38167.48343.50833.48343 1.33916 0 1.8475l-2.63657 2.77083 2.63743 2.7708c.48343.5084.48343 1.3409 0 1.8492-.24172.2533-.56057.38-.87943.38-.318 0-.63771-.1267-.87857-.38l-3.51686-3.6967c-.48343-.5083-.48343-1.3391 0-1.8466l3.51686-3.69503c.24086-.255.56057-.38167.87771-.38167zm10.48556 0c.3179 0 .6376.12667.8784.38167l3.5164 3.69503c.4833.5083.4833 1.3391 0 1.8466l-3.5164 3.6967c-.2408.2533-.5605.38-.8784.38-.3188 0-.6376-.1267-.8793-.38-.4834-.5083-.4834-1.3408 0-1.8475l2.6362-2.7725-2.6371-2.77083c-.4833-.50834-.4833-1.33917 0-1.8475.2417-.255.5605-.38167.8793-.38167z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/EpisodesIcon.tsx
import { jsx as jsx41 } from "react/jsx-runtime";
var EpisodesIcon = (props) => /* @__PURE__ */ jsx41("svg", { ...props, children: /* @__PURE__ */ jsx41(
  "path",
  {
    d: "M1.412 6.647c.78 0 1.412.633 1.412 1.412v12.706H16.94c.78 0 1.412.632 1.412 1.411 0 .78-.633 1.412-1.412 1.412H1.411C.633 23.588 0 22.956 0 22.176V8.06c0-.78.632-1.412 1.412-1.412zM22.588 1C23.368 1 24 1.632 24 2.412v14.117c0 .78-.632 1.412-1.412 1.412H7.058c-.778 0-1.41-.632-1.41-1.412V2.412c0-.78.632-1.412 1.41-1.412h15.53zm-1.412 2.824H8.471v11.294h12.705V3.824zm-8.755 2.531c.252-.142.77-.138 1.019.01l4.02 2.416c.24.145.383.406.383.69 0 .282-.162.544-.403.689l-4.052 2.415c-.127.077-.324.115-.466.115-.137 0-.378-.034-.501-.105-.253-.142-.617-.409-.617-.7v-4.83c0-.289.364-.557.617-.7z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ExpandIcon.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var ExpandIcon = (props) => /* @__PURE__ */ jsx42("svg", { ...props, children: /* @__PURE__ */ jsx42(
  "path",
  {
    clipRule: "evenodd",
    d: "m13.0984 3.43938c-.5858-.58578-1.5356-.58578-2.1214 0l-3.76881 3.76885c-.58579.58579-.58579 1.53554 0 2.12132.58578.58579 1.53553.58579 2.12132 0l2.70819-2.70819 2.7082 2.70819c.5858.58579 1.5355.58579 2.1213 0 .5858-.58578.5858-1.53553 0-2.12132zm-5.9543 13.41652 3.8329 3.8329c.5858.5858 1.5356.5858 2.1214 0l3.8329-3.8329c.5858-.5858.5858-1.5356 0-2.1214s-1.5355-.5858-2.1213 0l-2.7723 2.7723-2.77228-2.7723c-.58579-.5858-1.53553-.5858-2.12132 0s-.58579 1.5356 0 2.1214z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/FavoriteIcon.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var FavoriteIcon = (props) => /* @__PURE__ */ jsx43("svg", { ...props, children: /* @__PURE__ */ jsx43(
  "path",
  {
    d: "m10.5016 2.97092c.6129-1.29459 2.3837-1.29459 2.9966.00001l1.7958 3.79325c.2434.51408.7138.8704 1.258.95284l4.0155.60828c1.3705.2076 1.9177 1.9633.926 2.971l-2.9056 2.9526c-.3938.4002-.5735.9767-.4805 1.5418l.6859 4.1691c.2341 1.4229-1.1985 2.508-2.4243 1.8362l-3.5916-1.9684c-.4867-.2668-1.0682-.2668-1.555 0l-3.59154 1.9684c-1.22576.6718-2.65839-.4132-2.42429-1.8361l.68593-4.1692c.09296-.5651-.08673-1.1416-.48052-1.5418l-2.90564-2.9526c-.99167-1.0077-.44446-2.7634.92599-2.971l4.0155-.60828c.5442-.08244 1.01465-.43876 1.25803-.95284z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/FavoriteOutlineIcon.tsx
import { jsx as jsx44 } from "react/jsx-runtime";
var FavoriteOutlineIcon = (props) => /* @__PURE__ */ jsx44("svg", { ...props, children: /* @__PURE__ */ jsx44(
  "path",
  {
    clipRule: "evenodd",
    d: "m11.985 5.812-1.0288 2.17325c-.586 1.23773-1.74668 2.14615-3.14789 2.35845l-2.51017.3802 1.88529 1.9158c.96838.984 1.38702 2.3703 1.16718 3.7065l-.42276 2.5696 2.07515-1.1373c1.2371-.678 2.727-.678 3.9641 0l2.0751 1.1373-.4227-2.5696c-.2199-1.3362.1988-2.7225 1.1672-3.7065l1.8853-1.9158-2.5102-.3802c-1.4012-.2123-2.5619-1.12072-3.1479-2.35845zm1.4983-2.69239c-.6128-1.2946-2.3837-1.2946-2.9965 0l-1.79583 3.79325c-.24338.51408-.71383.87041-1.25803.95284l-4.0155.60828c-1.37045.2076-1.91766 1.96332-.926 2.97102l2.90565 2.9526c.39379.4002.57348.9767.48052 1.5417l-.68593 4.1692c-.2341 1.4229 1.19852 2.508 2.42429 1.8362l3.59153-1.9684c.4868-.2668 1.0683-.2668 1.5551 0l3.5915 1.9684c1.2258.6718 2.6584-.4133 2.4243-1.8362l-.6859-4.1692c-.093-.565.0867-1.1415.4805-1.5417l2.9057-2.9526c.9916-1.0077.4444-2.76342-.926-2.97102l-4.0155-.60828c-.5442-.08243-1.0147-.43875-1.2581-.95284z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/FilterIcon.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
var FilterIcon = (props) => /* @__PURE__ */ jsx45("svg", { ...props, children: /* @__PURE__ */ jsx45(
  "path",
  {
    clipRule: "evenodd",
    d: "M2 6.28571C2 5.49673 2.63959 4.85714 3.42857 4.85714H20.5714C21.3604 4.85714 22 5.49673 22 6.28571C22 7.07469 21.3604 7.71428 20.5714 7.71428H3.42857C2.63959 7.71428 2 7.07469 2 6.28571ZM4.85714 12C4.85714 11.211 5.49674 10.5714 6.28571 10.5714H17.7143C18.5033 10.5714 19.1429 11.211 19.1429 12C19.1429 12.789 18.5033 13.4286 17.7143 13.4286H6.28571C5.49674 13.4286 4.85714 12.789 4.85714 12ZM9.85714 16.2857C9.06816 16.2857 8.42857 16.9253 8.42857 17.7143C8.42857 18.5033 9.06817 19.1429 9.85714 19.1429H14.1429C14.9318 19.1429 15.5714 18.5033 15.5714 17.7143C15.5714 16.9253 14.9318 16.2857 14.1429 16.2857H9.85714Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/GearIcon.tsx
import { jsx as jsx46 } from "react/jsx-runtime";
var GearIcon = (props) => /* @__PURE__ */ jsx46("svg", { ...props, children: /* @__PURE__ */ jsx46(
  "path",
  {
    d: "m8.67556 21.6465 1.89584.3535.3926-1.9249c1.1027.034 2.1897-.0828 3.2074-.367l1.0729 1.7617 1.6555-.9092c.6409-.3283 1.255-.7575 1.7891-1.2373l1.3885-1.2374-1.4908-1.4954c.5245-.7911.9285-1.7273 1.1783-2.8192l2.0748.1226.1335-1.7929c.0268-.3282.0268-.6817.0268-1.01v-.2273-.0001c0-.2525 0-.4545-.0268-.6818l-.1335-1.81815-2.1093.12467c-.3096-1.10015-.8292-1.97313-1.4952-2.64471l1.0411-1.92441-1.7089-.83334c-.6676-.30303-1.3885-.55556-2.1629-.70708l-1.8692-.37878-.4549 2.02227c-1.0724.02298-2.1706.21979-3.21617.5585l-.97506-1.72218-1.68224.85858c-.61415.30303-1.2016.68182-1.76234 1.08586l-1.52203 1.16163 1.46422 1.72321c-.47141.78434-.85454 1.74705-1.11019 2.78393l-2.06298-.1637-.1602 1.8182c-.02671.4041-.05341.808-.05341 1.1869 0 .2524 0 .505.0267.7575l.10681 1.8182 2.10099-.1241c.27942 1.014.75807 1.9261 1.47723 2.6267l-.8813 2.0681 1.76235.6566c.0465.0165.09173.033.13625.0493.16001.0583.31079.1132.4779.1528.48065.1262.98799.2524 1.46863.3282zm.61417-12.55053c2.29637-1.71717 6.72897-1.81818 6.78237 2.34843.0534 4.1668-3.3378 5.2526-6.22162 4.4192-2.88385-.8333-2.13619-5.606-.56075-6.76763z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/GettingStartedIcon.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var GettingStartedIcon = (props) => /* @__PURE__ */ jsx47("svg", { ...props, children: /* @__PURE__ */ jsx47(
  "path",
  {
    d: "m4.217 3.06901c.04358-.54546-.36709-1.02266-.91726-1.06587-.55017-.0432-1.03149.36395-1.07507.9094-.40883 5.11717-.16781 10.44226.00568 14.27546.07054 1.5585.12991 2.8704.12992 3.8213 0 .5472.44739.9907.99929.9907.55188 0 .99928-.4435.99928-.9907 0-1.0818-.06403-2.4873-.13829-4.1175-.1733-3.8042-.40231-8.83162-.00355-13.82279zm9.9563 12.74949c-2.2507.1558-5.3327.2449-6.97506-.1558-.97326-.2449-1.05436-.7346-1.1963-5.3426l-.00276-.09c-.13968-4.53423-.17773-5.76919.12443-6.47688.34469-.8459 1.56125-1.06851 2.98058-1.29111 1.27741-.20035 11.58171-.88811 12.66971 0 .5698.46517-.7384 1.42159-2.4235 2.65368-1.5322 1.12022-3.376 2.46834-4.4034 3.88231 1.3949 1.3481 3.0566 2.4259 4.4165 3.308 1.8792 1.2189 3.1824 2.0642 2.4104 2.7326-.6999.6059-2.7027.6408-4.8097.6775-.9455.0165-1.9119.0333-2.7909.1023z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/GlobeIcon.tsx
import { jsx as jsx48, jsxs as jsxs3 } from "react/jsx-runtime";
var GlobeIcon = ({ ...props }) => {
  return /* @__PURE__ */ jsx48("svg", { ...props, children: /* @__PURE__ */ jsxs3("g", { id: "path", children: [
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M7.65627 1.71562C6.06613 2.32378 4.62011 3.29415 3.51622 4.59949C1.96831 6.4299 1.05108 8.79219 0.845234 11.1393C1.09887 11.1103 1.40015 11.0776 1.74531 11.0428C2.57641 10.9591 3.66238 10.8638 4.95062 10.7817C5.01134 9.27806 5.28616 7.82783 5.67836 6.4794C6.19762 4.69416 6.92703 3.0717 7.65627 1.71562Z",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M6.45598 10.6976C6.52204 9.38849 6.76617 8.11027 7.11867 6.89833C7.77653 4.63656 8.80374 2.63467 9.71201 1.14475C10.3391 1.03232 10.9748 0.972202 11.6088 0.96582C11.6319 1.0021 11.6561 1.04012 11.6811 1.07984C11.9491 1.50469 12.3203 2.12264 12.7281 2.88811C13.5459 4.42302 14.5009 6.53176 15.0748 8.85394C15.2228 9.45317 15.3448 10.0635 15.4326 10.6794C12.1484 10.5285 9.13304 10.5738 6.653 10.6883C6.58695 10.6913 6.52127 10.6944 6.45598 10.6976Z",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M4.9638 12.284C3.73179 12.3635 2.69271 12.455 1.89556 12.5353C1.45914 12.5792 1.09536 12.6198 0.812256 12.6531C0.93317 15.3097 2.01391 17.7905 4.17645 19.2687C5.27269 20.018 6.85073 20.5926 8.60938 20.8635C6.93244 18.9808 5.91554 16.9895 5.38983 14.9839C5.15227 14.0776 5.01657 13.1745 4.9638 12.284Z",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M10.8824 21.0346C8.63427 18.9674 7.40822 16.7683 6.84082 14.6036C6.63042 13.8009 6.50902 12.9969 6.46149 12.1991C6.54769 12.1949 6.63459 12.1907 6.72216 12.1867C9.22193 12.0713 12.2664 12.0277 15.575 12.1878C15.6313 13.4726 15.5143 14.7616 15.1537 16.0051C14.6398 17.7773 13.6207 19.4948 11.8349 21.0003C11.5168 21.0226 11.1988 21.0338 10.8824 21.0346Z",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M16.5944 16.4229C16.1733 17.875 15.4618 19.2766 14.3822 20.5674C17.6751 19.644 20.5203 17.2629 21.094 12.6429C19.7333 12.4812 18.3897 12.3608 17.0794 12.2739C17.1265 13.6484 16.9926 15.0498 16.5944 16.4229Z",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M16.531 8.4941C16.7122 9.22762 16.8596 9.98738 16.9572 10.7629C18.3381 10.8507 19.7575 10.9751 21.1969 11.1446L21.1977 11.0964C21.2835 4.96044 17.6372 1.77532 13.4486 1.10179C13.6348 1.42031 13.8387 1.78254 14.0519 2.18276C14.905 3.78384 15.9176 6.01212 16.531 8.4941Z",
        fill: "currentColor"
      }
    )
  ] }) });
};

// src/components/Icon/icons/GridViewIcon.tsx
import { jsx as jsx49 } from "react/jsx-runtime";
var GridViewIcon = (props) => /* @__PURE__ */ jsx49("svg", { ...props, children: /* @__PURE__ */ jsx49(
  "path",
  {
    clipRule: "evenodd",
    d: "m14.6005 10.65c-.6903 0-1.25-.5597-1.25-1.25002v-6.14997c0-.69036.5597-1.25001 1.25-1.25001h6.1497c.6903 0 1.25.55965 1.25 1.25001v6.14997c0 .69032-.5597 1.25002-1.25 1.25002zm-12.6005 10.1c0 .6904.55965 1.25 1.25001 1.25h6.14961c.69038 0 1.25008-.5596 1.25008-1.25v-6.1499c0-.6903-.5597-1.25-1.25008-1.25h-6.14961c-.69036 0-1.25001.5597-1.25001 1.25zm11.3505 0c0 .6904.5597 1.25 1.25 1.25h6.1497c.6903 0 1.25-.5596 1.25-1.25v-6.1499c0-.6903-.5597-1.25-1.25-1.25h-6.1497c-.6903 0-1.25.5597-1.25 1.25zm-11.3505-11.35002c0 .69032.55965 1.25002 1.25001 1.25002h6.14961c.69038 0 1.25008-.5597 1.25008-1.25002v-6.14997c0-.69036-.5597-1.25001-1.25008-1.25001h-6.14961c-.69036 0-1.25001.55965-1.25001 1.25001z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/HamburgerIcon.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
var HamburgerIcon = (props) => /* @__PURE__ */ jsx50("svg", { ...props, children: /* @__PURE__ */ jsx50(
  "path",
  {
    clipRule: "evenodd",
    d: "m22.0882 7.28569c-.0338.55114-.5079.9675-1.0591.92995-3.7464-.25521-6.8936-.23457-9.7993-.18333-.3986.00703-.7934.01466-1.1851.02223-2.4532.04744-4.78505.09252-7.17257-.02796-.55162-.02783-.97929-.49765-.95522-1.04937s.49076-.97641 1.04239-.94857c2.31236.11668 4.56689.07325 7.02439.0259.39351-.00758.79231-.01527 1.19731-.02241 2.9241-.05155 6.1384-.0734 9.9705.18765.5511.03754.9705.51477.9367 1.06591zm0 5.78541c-.0338.5512-.5079.9675-1.0591.93-3.7464-.2552-6.8936-.2346-9.7993-.1834-.3988.0071-.7938.0147-1.1857.0223-2.45322.0474-4.78446.0925-7.17197-.028-.55162-.0278-.97929-.4976-.95522-1.0493.02407-.5518.49076-.9765 1.04239-.9486 2.31235.1167 4.56688.0732 7.02436.0259.39354-.0076.79234-.0153 1.19734-.0224 2.9241-.0516 6.1384-.0734 9.9705.1876.5511.0376.9705.5148.9367 1.0659zm-.9753 6.5159c.552-.008.99-.4623.9782-1.0147-.0118-.5523-.4689-.9936-1.0209-.9856-6.2773.0914-10.4894-.01-14.77115-.1131-1.10589-.0266-2.21649-.0533-3.36847-.0769-.5521-.0113-1.00577.4273-1.01329.9796-.00753.5523.43393 1.0092.98603 1.0205 1.14246.0234 2.2471.05 3.3496.0765 4.28668.1032 8.54098.2057 14.85998.1137z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/HazardIcon.tsx
import { jsx as jsx51 } from "react/jsx-runtime";
var HazardIcon = (props) => /* @__PURE__ */ jsx51("svg", { ...props, children: /* @__PURE__ */ jsx51(
  "path",
  {
    d: "m12.7865 2.94054c.7413.21443 1.3767.75049 1.8003 1.39377l6.9896 12.43669c.2118.4288.4236.9649.4236 1.5009 0 1.6082-1.2708 2.8948-2.8594 2.8948h-14.19093c-.52951 0-1.05902-.1072-1.48264-.4289-1.37673-.8577-1.90624-2.6803-1.05902-4.0741l7.09548-12.32939c.2118-.42885.63541-.8577 1.05901-1.07213.6354-.42885 1.4826-.53606 2.224-.32164zm-.5295 2.03704h-.2119c-.2118 0-.3177 0-.4236.10721l-.1588.16082-.1589.16082-7.09545 12.43667c-.2118.3216-.1059.8577.31771 1.0721.08825.0893.25005.1042.36282.1067l14.25172.0005c.4236 0 .8472-.3216.8472-.8577 0-.1072 0-.3216-.1059-.4288l-7.2013-12.32947c-.1059-.21442-.2118-.32164-.4236-.42885zm.6354 11.47172c.4236.4289.4236 1.1794 0 1.6082-.3177.2144-.6354.3216-.8473.3216-.1059 0-.3177 0-.4236-.1072s-.2118-.1072-.3177-.2144c-.3953-.4003-.4217-1.0807-.079-1.5183l.079-.0899c.4236-.4288 1.165-.4288 1.5886 0zm.4236-7.93371v.21443l-.4236 5.03898c0 .7505-.2118 1.3938-.8473 1.3938-.7413 0-.9531-.6433-.9531-1.2866l-.4236-5.03897c0-.75049.5295-1.39376 1.2708-1.50097.7414-.10722 1.3768.42885 1.3768 1.17933z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/HelpCenterIcon.tsx
import { jsx as jsx52 } from "react/jsx-runtime";
var HelpCenterIcon = (props) => /* @__PURE__ */ jsx52("svg", { ...props, children: /* @__PURE__ */ jsx52(
  "path",
  {
    d: "m3.81493 6.36059c3.08876-4.51878 10.40947-5.95666 14.79867-2.2448 6.2012 5.24418 4.951 18.09941-6.5912 17.93811-11.542153-.1614-11.296231-11.1745-8.20747-15.69331zm.3161 4.74441c-.21438 1.3577-.18185 2.8247.21769 4.1649.39061 1.3102 1.11527 2.4485 2.26201 3.2804.47657.3457 1.05138.6568 1.74342.9052l.22047-2.8886c-1.45328-.9896-2.00163-2.605-1.99252-4.1767zm15.52497-2.11314-2.0825 1.92114c.4477 2.0742-.1406 4.3636-1.9318 5.6316l.1812 2.9024c.678-.2623 1.2582-.5962 1.7512-.9755 1.2454-.9582 2.0658-2.3068 2.4595-3.8531.4672-1.8346.3121-3.8498-.3776-5.62654zm-5.2445.22952c-.6711-.57069-1.6504-.81143-2.7043-.63891-1.0636.17412-1.97562.73537-2.47215 1.45763-.52483.7633-.86487 2.2708-.50246 3.4986.16442.557.45675 1.0039.90194 1.3287.44947.328 1.20237.6385 2.46407.6595 1.1982.02 1.9722-.2946 2.4683-.6741.5044-.3858.8546-.9408 1.0289-1.6177.3699-1.4361-.1222-3.1105-1.1843-4.01372zm-3.3565-5.09683c-.9833.16372-1.92577.48149-2.77592.92687l2.16822 1.78626c.6128-.20104 1.2593-.30765 1.903-.30943l1.694-2.39577c-.96-.17519-1.9746-.17686-2.9893-.00793z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/HideIcon.tsx
import { jsx as jsx53 } from "react/jsx-runtime";
var HideIcon = (props) => /* @__PURE__ */ jsx53(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    children: /* @__PURE__ */ jsx53(
      "path",
      {
        clipRule: "evenodd",
        d: "m4.99451 4.11717c-.26525.00112-.51919.10753-.70601.29583-.18671.18841-.29096.44324-.28983.70849.00112.26525.10753.51919.29583.70601l.009.009.0045-.0045 13.932 13.8195-.0045.0045c.1909.1788.4442.2758.7056.2701.2615-.0056.5104-.1134.6934-.3003.1829-.1869.2855-.438.2856-.6995.0001-.2616-.1023-.5128-.2851-.6998l-1.4775-1.4655c2.0625-1.6245 3.9225-3.5325 3.84-4.6875.0675-.957-1.209-2.49-3.024-4.179-2.8755-2.6775-7.899-3.9105-11.553-1.7835l-1.7175-1.704c-.18841-.18671-.44325-.29096-.70849-.28983zm5.95399 5.49333-1.464-1.4505c1.5315-.7665 3.6135-.636 4.9635.519 1.533 1.311 1.983 3.738 1.113 5.508l-1.455-1.4445c.2235-.9285-.081-2.01-.825-2.64-.6315-.534-1.5405-.684-2.3325-.492zm-8.946 2.3145c-.06-.8415.915-2.088 2.25-3.33l3.1155 3.09c-.138 2.1285.897 4.5885 4.3065 4.6365.121.0017.2421-.0003.363-.006l2.4315 2.412c-3.3015.792-7.0905-.432-9.4425-2.622-1.8135-1.689-3.0915-3.2205-3.024-4.1805z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/HomeIcon.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var HomeIcon = (props) => /* @__PURE__ */ jsx54("svg", { ...props, children: /* @__PURE__ */ jsx54(
  "path",
  {
    d: "m20.2951 12.8675c.39.3916 1.0223.3916 1.4123 0 .3901-.3915.3901-1.0264 0-1.418-.5441-.5463-1.0963-1.185-1.7355-1.92403-1.5548-1.798-3.6235-4.19026-7.3397-7.29367-.3677-.30698-.9005-.30929-1.2707-.00552-2.80856 2.30436-4.3166 3.73206-9.11644 9.27432-.3619.4179-.31787 1.0512.09835 1.4145.41623.3634 1.04703.3192 1.40893-.0987.22242-.2568.43756-.5046.64588-.7437-.12635 2.67-.31165 6.9451-.23082 7.493.03562.2352.06232.4794.08762.7106.05894.539.11018 1.0073.24856 1.1258.2571.2118.73171.2329 2.49178.2541.43145.0052 1.20203.0212 2.13063.0436-.04005-.8221-.09121-2.9385-.11733-4.0194l-.00919-.3784c-.01478-.1881-.01071-.5289-.00583-.9389.00278-.2332.00583-.4887.00583-.7514.02316-1.5293.99606-2.8171 2.31633-3.0049 1.4361-.2147 2.71.9122 2.9649 2.5488.0232.1342.0463.2683.0694.3756.1622.8049.278 4.9904.139 6.0636-.013.0978-.0151.1825-.0169.2563v.0011c1.1948.0404 2.1271.0788 2.4112.1058.8899.0847 1.9183.0847 2.0567-.4233.2768-1.0162.4548-5.9783.4548-6.6981.0122-.5599.0019-1.8728-.017-2.9434.3011.335.6026.6584.9172.9743z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/HourglassIcon.tsx
import { jsx as jsx55 } from "react/jsx-runtime";
var HourglassIcon = (props) => /* @__PURE__ */ jsx55("svg", { ...props, children: /* @__PURE__ */ jsx55(
  "path",
  {
    d: "m15.7232 10.2988c-.6792.8959-.3199 2.4081.2552 2.9902.575.5821.2595.2692 2.8516 3.3035 2.5921 3.0344-.2717 3.8307-2.8485 3.8203l-7.07912-.0286c-2.54232-.0103-5.19674-1.1217-2.25149-4.8988.67569-.7818 1.33873-1.5406 1.9048-2.1733.77137-.8621.78975-2.2582.04095-3.1399-.89303-1.05163-1.88693-2.29888-2.76384-3.4247-1.43635-2.02599-.07259-3.1681 1.57519-3.16031l9.31711.04403c1.6246.00768 2.5595 1.84424 1.601 3.15606-.8543 1.16929-1.8098 2.46542-2.6029 3.51152zm.8976 8.1748c-1.0924.2669-6.5677.2362-8.11503-.0174-1.54734-.2535-.98222-1.1902-.42583-1.8964.55639-.7061 1.20002-1.166 1.79963-1.8301.45553-.5046.92993-.9558 1.41473-1.4203.0189-.0182.0205-.0197.0076-.0097.3977-.3063 1.1425-.4605 1.6217-.2591.6321.2657 1.1417.8642 1.6292 1.3291.7252.6914 1.6262 1.596 2.4986 2.5248.5045.537.6617 1.3122-.4306 1.5791z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/ImportIcon.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var ImportIcon = (props) => /* @__PURE__ */ jsx56("svg", { ...props, children: /* @__PURE__ */ jsx56(
  "path",
  {
    d: "M20.9995 14.0626C20.9631 16.5163 18.9626 18.4942 16.5002 18.4942H13.263V12.8027L14.6115 14.1068C15.0581 14.5387 15.7702 14.5268 16.2022 14.0802C16.6342 13.6336 16.6222 12.9213 16.1757 12.4894L13.0084 9.42632C12.9532 9.359 12.8904 9.2982 12.8212 9.24525C12.3745 8.81431 11.6631 8.82653 11.2314 9.27282L7.98828 12.6262C7.55634 13.0729 7.56825 13.7851 8.01486 14.217C8.46148 14.649 9.17371 14.6371 9.60565 14.1904L11.013 12.7352V18.4942H7.50003C5.03754 18.4942 3.03702 16.5163 3.00051 14.0626L3 14.0284V13.9943C3.03192 11.8494 4.56491 10.0682 6.59484 9.65361C6.72933 6.7847 9.09789 4.5 12.0001 4.5C14.9886 4.5 17.4114 6.92271 17.4114 9.91127C17.4114 9.98315 17.4099 10.0546 17.4072 10.1256C19.3761 10.1616 20.9661 11.8738 20.9995 13.9943L21 14.0281L20.9995 14.0626Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/InfoIcon.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var InfoIcon = (props) => /* @__PURE__ */ jsx57("svg", { ...props, children: /* @__PURE__ */ jsx57(
  "path",
  {
    d: "m21.9985 12.0962c.156-11.337814-12.26789-12.565873-17.33611-6.47453-3.58731 4.31149-3.71983 11.58443.64734 14.61853 4.36716 3.034 16.53287 3.1937 16.68877-8.144zm-8.5688-1.5609v7.29h-2.4297v-7.29zm-.3492-2.77932c-.2329.23288-.5214.34931-.8656.34931s-.6328-.11643-.8656-.34931c-.2329-.23287-.3493-.52144-.3493-.86569s.1164-.63281.3493-.86568c.2328-.23288.5214-.34932.8656-.34932s.6327.11644.8656.34932c.2328.23287.3492.52143.3492.86568s-.1164.63282-.3492.86569z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/IntegrationsIcon.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var IntegrationsIcon = (props) => /* @__PURE__ */ jsx58("svg", { ...props, children: /* @__PURE__ */ jsx58(
  "path",
  {
    d: "m17.556 2c-.5523 0-1 .44771-1 1v2.43713h-2.4371c-.5523 0-1 .44772-1 1 0 .55229.4477 1 1 1h2.4371v2.43706c0 .55231.4477 1.00001 1 1.00001s1-.4477 1-1.00001v-2.43706h2.4371c.5523 0 1-.44771 1-1 0-.55228-.4477-1-1-1h-2.4371v-2.43713c0-.55229-.4477-1-1-1zm-11.16851 1.26404c1.23017-.04521 2.91132-.02893 3.79511.2616.5235.17684.5544.48675.507 3.38818l-.001.05678c-.0467 2.85493-.0594 3.6326-.2431 4.0706-.2107.5239-.87939.6353-1.65859.742-.70121.096-5.08453.1317-5.9701-.1168-.54681-.1498-.67672-.7304-.73486-3.74861-.06216-3.43916.04971-3.98138.6889-4.17828.6165-.18385 2.06436-.40204 3.61664-.47547zm.12642 18.83116c-1.22709.0981-2.90743.1543-3.80287-.098-.53064-.1541-.57486-.4625-.65224-3.3633l-.00151-.0567c-.07615-2.8543-.0969-3.6317.06784-4.0772.18793-.5325.85123-.6727 1.62507-.8128.69645-.1261 5.07421-.3503 5.96961-.1401.55279.1261.70749.7007.89549 3.7136.21 3.4333.1212 4.1716-.5089 4.3958-.54029.1923-1.46289.2656-2.56636.3533h-.00003-.00022c-.32776.026-.67148.0534-1.02588.0854zm5.85269-7.7969c-.2522.8955-.3521 2.4643-.254 3.6914.1402 1.5478.3059 2.9399.5161 3.5479.2242.6301 1.1312.7485 4.5645.5384 3.0129-.1879 3.5875-.3426 3.7136-.8953.2102-.8955-.014-5.2733-.1401-5.9698-.1402-.7737-.2803-1.4371-.8128-1.625-.4455-.1647-1.2229-.144-4.0773-.0678l-.0567.0015c-2.9008.0774-3.2991.2481-3.4533.7787z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/InviteIcon.tsx
import { jsx as jsx59, jsxs as jsxs4 } from "react/jsx-runtime";
var InviteIcon = (props) => /* @__PURE__ */ jsxs4("svg", { ...props, children: [
  /* @__PURE__ */ jsx59(
    "path",
    {
      clipRule: "evenodd",
      d: "M15.125 3.12081C18.0843 5.62343 17.4877 11.7582 11.9795 11.6812C6.47136 11.6042 6.43167 6.04421 7.90568 3.88776C9.37969 1.73131 13.0303 1.34944 15.125 3.12081ZM3.0867 22.0565C2.57221 22.0699 2.11037 21.7159 2.06799 21.2867C1.82858 19.0425 2.14219 16.2745 4.3016 14.5465C7.60285 11.9186 14.0238 12.3416 17.9678 14.1067C15.7016 14.5823 14 16.5924 14 19C14 20.1954 14.4195 21.2928 15.1193 22.153L3.0867 22.0565Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  ),
  /* @__PURE__ */ jsx59(
    "path",
    {
      clipRule: "evenodd",
      d: "M18.3015 16.7C18.3015 16.3134 18.6149 16 19.0015 16C19.3881 16 19.7015 16.3134 19.7015 16.7V18.3012H21.3025C21.6891 18.3012 22.0025 18.6146 22.0025 19.0012C22.0025 19.3878 21.6891 19.7012 21.3025 19.7012H19.7015V21.3025C19.7015 21.6891 19.3881 22.0025 19.0015 22.0025C18.6149 22.0025 18.3015 21.6891 18.3015 21.3025V19.7012H16.7C16.3134 19.7012 16 19.3878 16 19.0012C16 18.6146 16.3134 18.3012 16.7 18.3012H18.3015V16.7Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  )
] });

// src/components/Icon/icons/LightningBoltIcon.tsx
import { jsx as jsx60 } from "react/jsx-runtime";
var LightningBoltIcon = ({ ...props }) => {
  return /* @__PURE__ */ jsx60("svg", { ...props, children: /* @__PURE__ */ jsx60(
    "path",
    {
      clipRule: "evenodd",
      d: "M10.9912 2.11398C13.1572 1.94882 16.3205 1.97579 18.5935 2.11398C19.3811 2.16116 20.1345 2.60607 19.4239 3.66777C18.2339 5.44738 16.9412 6.85287 15.3189 8.67629C16.6715 8.64259 18.1183 8.58866 19.5865 8.62911C20.8579 8.66618 21.5256 9.28972 20.494 10.3649C16.9027 14.1028 12.2839 17.9788 7.77226 21.0493C5.69192 22.4649 4.57469 22.384 5.34947 20.2337C6.00011 18.4305 7.42982 15.4139 8.80387 12.9399C7.47262 13.0074 5.53782 13.0208 4.18945 12.9939C3.1878 12.9737 2.81539 12.4276 3.08507 11.6962C4.02679 9.1212 6.26123 5.60579 8.21743 3.39476C9.04786 2.45777 9.48448 2.22857 10.9912 2.11398Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  ) });
};

// src/components/Icon/icons/LinkIcon.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
var LinkIcon = (props) => /* @__PURE__ */ jsx61("svg", { ...props, children: /* @__PURE__ */ jsx61(
  "path",
  {
    d: "m19.1411 3.22522c-1.6336-1.63363-4.2823-1.63363-5.9159 0l-4.42241 4.42244c-1.63362 1.63364-1.63362 4.28224 0 5.91594l1.12908 1.1291c.54453.5445 1.42743.5445 1.97203 0 .5445-.5446.5445-1.4275 0-1.972l-1.1291-1.1291c-.5446-.5445-.5446-1.4274 0-1.97196l4.4224-4.42245c.5445-.54454 1.4275-.54454 1.972 0l1.6336 1.63364c.5446.54455.5446 1.42744 0 1.97198l-1.514 1.51399c-.5445.5445-.5445 1.4274 0 1.972.5445.5445 1.4274.5445 1.972 0l1.514-1.514c1.6337-1.63365 1.6337-4.28229 0-5.91593zm-14.28222 17.54948c1.63363 1.6337 4.28228 1.6337 5.91592 0l4.4224-4.4224c1.6337-1.6336 1.6337-4.2822 0-5.9159l-.891-.89097c-.5445-.54454-1.4274-.54454-1.9719 0-.5446.54457-.5446 1.42747 0 1.97197l.891.891c.5445.5445.5445 1.4274 0 1.972l-4.42248 4.4224c-.54455.5445-1.42742.5445-1.97198 0l-1.63365-1.6337c-.54454-.5445-.54454-1.4274 0-1.9719l1.51404-1.5141c.54454-.5445.54454-1.4274 0-1.9719-.54455-.5446-1.42744-.5446-1.97197 0l-1.51405 1.514c-1.63362 1.6337-1.63361 4.2823.00002 5.9159z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ListViewIcon.tsx
import { jsx as jsx62 } from "react/jsx-runtime";
var ListViewIcon = (props) => /* @__PURE__ */ jsx62("svg", { ...props, children: /* @__PURE__ */ jsx62(
  "path",
  {
    clipRule: "evenodd",
    d: "m9.59493 13.25c.70527 0 1.19227.4311 1.25817 1.0932l.0077.1568v6.25c0 .6964-.4366 1.1773-1.10708 1.2424l-.15879.0076h-6.3291c-.70525 0-1.19221-.4311-1.25817-1.0932l-.00766-.1568v-6.25c0-.6964.43658-1.1773 1.10704-1.2424l.15879-.0076zm11.13927 5c.7595 0 1.2658.5 1.2658 1.25 0 .6964-.4366 1.1773-1.107 1.2424l-.1588.0076h-6.3291c-.7595 0-1.2659-.5-1.2659-1.25 0-.6964.4366-1.1773 1.1071-1.2424l.1588-.0076zm0-3.75c.7595 0 1.2658.5 1.2658 1.25 0 .6964-.4366 1.1773-1.107 1.2424l-.1588.0076h-6.3291c-.7595 0-1.2659-.5-1.2659-1.25 0-.6964.4366-1.1773 1.1071-1.2424l.1588-.0076zm-11.13927-12.5c.70527 0 1.19227.43112 1.25817 1.0932l.0077.1568v6.25c0 .6964-.4366 1.1773-1.10708 1.2424l-.15879.0076h-6.3291c-.70525 0-1.19221-.4311-1.25817-1.0932l-.00766-.1568v-6.25c0-.69643.43658-1.1773 1.10704-1.24244l.15879-.00756zm11.13927 5c.7595 0 1.2658.5 1.2658 1.25 0 .69642-.4366 1.17729-1.107 1.24243l-.1588.00757h-6.3291c-.7595 0-1.2659-.5-1.2659-1.25 0-.69642.4366-1.17729 1.1071-1.24243l.1588-.00757zm0-3.75c.7595 0 1.2658.5 1.2658 1.25 0 .69642-.4366 1.1773-1.107 1.24244l-.1588.00756h-6.3291c-.6329 0-1.2659-.5-1.2659-1.25 0-.69643.4366-1.1773 1.1071-1.24244l.1588-.00756z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/LiveIcon.tsx
import { jsx as jsx63 } from "react/jsx-runtime";
var LiveIcon = (props) => /* @__PURE__ */ jsx63("svg", { ...props, children: /* @__PURE__ */ jsx63(
  "path",
  {
    d: "m22.9958 11.8791c-.0478 1.702-.5318 3.3895-1.4008 4.8848-.0956.168-.2708.2707-.4681.2768-.19.0048-.3505-.0762-.4498-.2225-.0464-.0856-.1002-.1603-.1479-.2267l-.0004-.0005c-.0171-.023-.0331-.0459-.0539-.0762-.1373-.1849-.1557-.4073-.0527-.5959.7145-1.2596 1.0981-2.6291 1.1398-4.0809v-.2671c0-2.35354-.929-4.59584-2.6166-6.31354-1.7648-1.79263-4.1375-2.81406-6.6878-2.87571h-.2574c-2.49402 0-4.85688.92593-6.65354 2.60857-1.80157 1.68626-2.83103 3.94308-2.89721 6.35578-.04167 1.6585.36644 3.2819 1.17653 4.6865.1103.2067.09804.4461-.04167.6516-.00792.0135-.0171.0274-.02656.0417-.00665.01-.01343.0203-.02001.0308-.01961.029-.03922.0592-.05393.0822-.11642.1813-.31374.2889-.53434.2841-.21815-.0037-.40566-.1112-.51596-.2938-.99269-1.6838-1.486591-3.5913-1.42899-5.5169.07966-2.82857 1.29786-5.4589 3.4291-7.40627 2.05893-1.87967 4.73186-2.90593 7.55798-2.90593.103 0 .2059 0 .3088.00484 2.9438.07615 5.6731 1.25109 7.6867 3.30604 2.0197 2.05253 3.0884 4.74088 3.0087 7.56822zm-3.391.5476c-.0307 1.1242-.277 2.1855-.7341 3.1598-.0834.1837-.2304.3203-.413.3844-.1778.0604-.3689.0459-.5405-.0399l-.0662-.0339h-.0172c-.3394-.2018-.4644-.6467-.2843-1.0419.3481-.764.5442-1.621.5662-2.4768.0441-1.6742-.5466-3.26741-1.6643-4.48829-1.1263-1.21725-2.6484-1.91352-4.2931-1.95945h-.1581c-1.5969 0-3.10922.63461-4.25878 1.78538-1.14712 1.14956-1.80402 2.68836-1.84569 4.33106-.02206.8462.1152 1.6718.40934 2.4539.14094.3663.0147.7712-.29291.9719l-.07966.035c-.17525.0907-.38482.1016-.57233.0327-.19242-.0726-.34071-.2164-.41547-.4086-.39462-.9949-.57968-2.0525-.54904-3.1465.05515-2.15044.9449-4.1377 2.5038-5.5955 1.46576-1.37077 3.35434-2.09484 5.31404-2.04165 1.9351.04956 3.7428.84374 5.1203 2.2411l-.0086.00846.1937.20912c1.4057 1.52187 2.1471 3.51757 2.0859 5.61967zm-5.3532-2.91072c2.1287 1.76482 1.7071 6.09952-2.2624 6.04882-3.95856-.0508-3.9892-3.989-2.93032-5.5085 1.05892-1.51944 3.68152-1.79505 5.19272-.54032zm-8.66839 13.38252c-.37011.0109-.69979-.2345-.73043-.5403v-.0012c-.17525-1.5811.05148-3.5394 1.60425-4.7639 3.25997-2.5602 10.76767-1.0407 12.06317 1.4179.5453 1.0106.761 2.2145.6483 3.3871-.0111.3263-.3812.6019-.8334.6019z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/LockIcon.tsx
import { jsx as jsx64 } from "react/jsx-runtime";
var LockIcon = (props) => /* @__PURE__ */ jsx64("svg", { ...props, children: /* @__PURE__ */ jsx64(
  "path",
  {
    d: "m12 3c3.3137 0 5.9999 2.68629 5.9999 6 .8285 0 1.5.67158 1.5 1.5v9c0 .8284-.6715 1.5-1.5 1.5h-11.9999c-.82844 0-1.5-.6716-1.5-1.5v-9c0-.82842.67156-1.5 1.5-1.5 0-3.31371 2.68629-6 6-6zm0 3c-1.6569 0-3 1.34314-3 3h5.9999c0-1.65686-1.343-3-2.9999-3zm0 12c1.6569 0 2.9999-1.3431 2.9999-3s-1.343-3-2.9999-3-3 1.3431-3 3 1.3431 3 3 3z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/LogoutIcon.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var LogoutIcon = (props) => /* @__PURE__ */ jsx65("svg", { ...props, children: /* @__PURE__ */ jsx65(
  "path",
  {
    d: "m2.70507 7.67368c1.74622-5.02899 8.66503-6.766142 13.67123-4.38094 7.0728 3.36988 9.1132 15.77696-1.6595 18.53426-10.77276 2.7573-13.757949-9.1243-12.01173-14.15332zm7.80893-.83709c-.3854-.38176-1.00379-.37523-1.38114.0146l-2.96599 3.06372c-.02406.02443-.04687.05013-.06831.07698-.0482.06031-.08835.12501-.12044.19261-.05827.1225-.09227.259-.09573.4033-.00048.0193-.00031.0386.00025.0579.00388.1177.02812.2303.06928.3341.04648.1175.11615.2278.20904.3237l2.9719 3.0699c.37735.3898.99574.3963 1.38114.0145.3853-.3818.3918-1.0072.0143-1.3971l-1.34627-1.3907h1.58317c2.6969 0 4.8831 2.2116 4.8831 4.9397v.4083c0 .5457.4372.988.9766.988s.9766-.4423.9766-.988v-.4083c0-3.8194-3.0607-6.91554-6.8363-6.91554h-1.58306l1.34616-1.39058c.3775-.38982.371-1.01532-.0143-1.39709z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/MediaIcon.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var MediaIcon = (props) => /* @__PURE__ */ jsx66("svg", { ...props, children: /* @__PURE__ */ jsx66(
  "path",
  {
    clipRule: "evenodd",
    d: "m12.0946 20.4146c-2.54091-.0423-5.06588-.0847-6.15454-.0847-3.43484 0-3.51232-.6046-3.74674-2.4432-.024-.1906-.0495-.381-.07648-.5712-.19072-1.292-.10927-9.0787-.02682-10.80108.00993-.23536.01092-.45497.0139-.66079.01689-1.62391.02384-2.35363 3.34445-2.35363 2.48524 0 7.86103.07189 11.47163.12113 1.8247.02462 3.1985.04333 3.5193.04333.9555.03348 1.4194 1.09311 1.474 2.08774.0547.99463.0269 5.566 0 7.0569-.0079.3349.011.8312.0338 1.4103.0586 1.5461.1391 3.6801-.1976 4.8845-.3 1.1935-1.7204 1.3925-2.9213 1.3925-.7103.0177-3.7329-.0325-6.7336-.0818zm-7.78952-14.74217.03775.9769c.00376.10022.04593.19523.11791.26568s.16836.11103.26947.11347l1.28136.03052c.05395.00123.10758-.00845.15762-.02844.05005-.01999.09547-.04988.13348-.08785.03801-.03796.06783-.0832.08763-.13296.0198-.04977.02917-.10301.02753-.15648l-.02781-.92766c-.00278-.09829-.04254-.19199-.11147-.2627-.06892-.0707-.16203-.11329-.26102-.1194l-1.29129-.07878c-.05542-.00335-.11094.00486-.16297.0241-.05203.01923-.09941.04907-.13909.08757-.03969.03851-.07079.08484-.0913.136-.02051.05115-.02998.10502-.0278.16003zm3.23121.9769-.03774-.97591c-.00219-.05501.00728-.10987.0278-.16102.02051-.05116.05161-.09749.09129-.136.03969-.0385.08707-.06834.1391-.08757.05203-.01924.10754-.02745.16297-.0241l1.29129.07878c.09899.00611.19209.0487.26102.1194.06892.07071.10869.16441.11147.2627l.02781.92766c.00164.05356-.00776.10688-.02762.15671s-.04976.09512-.08789.1331c-.03812.03798-.08365.06785-.13382.08778-.05016.01993-.1039.02951-.15792.02814l-1.28037-.03052c-.10112-.00244-.19749-.04302-.26947-.11347s-.11415-.16546-.11792-.26568zm11.65041 11.52887-.0397-.9769c-.004-.1001-.0463-.1948-.1182-.2651-.072-.0702-.1682-.1107-.2692-.1131l-1.2794-.0315c-.054-.0014-.1077.0082-.1579.0282-.0502.0199-.0957.0498-.1338.0877-.0381.038-.068.0833-.0879.1331-.0199.0499-.0293.1032-.0276.1567l.0278.9287c.0028.0983.0425.192.1115.2627.0689.0707.162.1133.261.1194l1.2913.0788c.0555.0035.1111-.0046.1633-.0238.0521-.0192.0996-.049.1394-.0875s.071-.0849.0916-.1362c.0205-.0512.03-.1061.0278-.1612zm-8.4579-11.52887-.0388-.97591c-.0022-.0551.0073-.11004.0279-.16127s.0518-.09761.0916-.13613c.0397-.03852.0873-.06834.1394-.08751s.1078-.02727.1633-.02378l1.2913.07878c.099.00611.1921.0487.261.1194.0689.07071.1087.16441.1115.2627l.0278.92766c.0016.05356-.0078.10688-.0276.15671-.0199.04983-.0498.09512-.0879.1331-.0382.03798-.0837.06785-.1339.08778-.0501.01993-.1039.02951-.1579.02814l-1.2803-.03052c-.1012-.00244-.1975-.04302-.2695-.11347s-.1142-.16546-.1179-.26568zm3.1547 10.56767.0397.9769c.0038.1003.046.1953.1179.2657.072.0705.1684.111.2695.1135l1.2794.0305c.054.0014.1077-.0082.1579-.0281.0502-.02.0957-.0498.1338-.0878.0382-.038.0681-.0833.0879-.1331.0199-.0498.0293-.1032.0276-.1567l-.0278-.9277c-.0028-.0983-.0425-.192-.1114-.2627-.069-.0707-.1621-.1133-.2611-.1194l-1.2913-.0788c-.0554-.0034-.1109.0046-.163.0237-.0521.0192-.0995.0489-.1393.0873s-.071.0847-.0916.1358c-.0207.0511-.0303.1059-.0282.1609zm2.07-11.56033.0388.97691c.0022.0551-.0073.11004-.0279.16126-.0206.05123-.0518.09761-.0916.13613-.0397.03853-.0873.06834-.1394.08751s-.1078.02727-.1633.02378l-1.2913-.07878c-.099-.00611-.1921-.0487-.261-.1194s-.1087-.16441-.1115-.2627l-.0268-.92865c-.0015-.05347.008-.10668.0279-.1564.02-.04971.0499-.09488.088-.13275.0381-.03788.0836-.06766.1337-.08753s.1037-.02942.1577-.02806l1.2793.03053c.101.00242.1972.04286.2692.11311.0719.07024.1142.16501.1182.26504zm-3.1547 12.52153-.0397-.9769c-.004-.1001-.0463-.1948-.1183-.2651-.0719-.0702-.1682-.1107-.2691-.1131l-1.2794-.0315c-.054-.0014-.1077.0082-.1579.0282-.0502.0199-.0957.0498-.1338.0877-.0382.038-.0681.0833-.0879.1331-.0199.0499-.0293.1032-.0276.1567l.0278.9287c.0028.0983.0425.192.1114.2627.069.0707.1621.1133.2611.1194l1.2913.0788c.0555.0035.1111-.0046.1632-.0238.0522-.0192.0997-.049.1395-.0875s.071-.0849.0915-.1362c.0206-.0512.0301-.1061.0279-.1612zm4.3169-11.52887-.0387-.97591c-.0022-.0551.0073-.11004.0278-.16127.0206-.05123.0518-.09761.0916-.13613s.0873-.06834.1394-.08751c.0522-.01917.1078-.02727.1633-.02378l1.2913.07878c.099.00611.1921.0487.261.1194.0689.07071.1087.16441.1115.2627l.0268.92766c.0017.05356-.0077.10688-.0276.15671s-.0498.09512-.0879.1331-.0836.06785-.1338.08778-.1039.02951-.1579.02814l-1.2794-.03052c-.1011-.00244-.1975-.04302-.2695-.11347s-.1141-.16546-.1179-.26568zm-9.61715 10.56767.03774.9769c.00377.1003.04594.1953.11792.2657.07198.0705.16835.111.26947.1135l1.28037.0305c.05402.0014.10776-.0082.15792-.0281.05017-.02.0957-.0498.13382-.0878.03813-.038.06803-.0833.08789-.1331s.02926-.1032.02762-.1567l-.02781-.9277c-.00278-.0983-.04255-.192-.11147-.2627-.06893-.0707-.16203-.1133-.26102-.1194l-1.29129-.0788c-.05534-.0033-.11077.0049-.16273.0241-.05196.0191-.09929.0489-.13896.0873-.03966.0384-.07079.0846-.09136.1356-.02058.0511-.03016.1058-.02811.1607zm-1.12442-.0157.03874.9769c.0022.0551-.0073.11-.02787.1612-.02057.0513-.05177.0977-.09156.1362s-.0873.0683-.13945.0875c-.05214.0192-.10777.0273-.16328.0238l-1.29129-.0788c-.09899-.0061-.19209-.0487-.26102-.1194-.06892-.0707-.10868-.1644-.11147-.2627l-.02682-.9287c-.00163-.0535.00776-.1068.02762-.1567.01987-.0498.04977-.0951.08789-.1331.03812-.0379.08366-.0678.13382-.0877.05017-.02.1039-.0296.15793-.0282l1.27937.0315c.10095.0024.19719.0429.26914.1131.07196.0703.11424.165.11825.2651zm3.80237-2.9248c.7986-.4491 3.1935-1.8288 3.8699-2.7082.0695-.0866.0348-.2068-.0517-.259l-3.7834-1.9656c-.1212-.06893-.26029.01773-.26029.13787-.03477.91383-.03476 3.72543-.03476 4.63833 0 .1379.13905.2245.26025.1556z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/MegaphoneIcon.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var MegaphoneIcon = (props) => /* @__PURE__ */ jsx67("svg", { ...props, children: /* @__PURE__ */ jsx67(
  "path",
  {
    clipRule: "evenodd",
    d: "m17.5195 3.01289c-4.627 1.42268-11.69556 3.59505-15.22532 6.18514-.42573.28958-.38276 3.56277.08595 3.85137l3.70565.9222c0 1.5508.65158 6.8268 4.88692 7.9398 2.7752.5721 4.9538-1.682 6.3529-5.1187 1.3822.3522 2.4765.6347 3.0381.787.9834.4561 1.0593.1413 1.1653-.2986l.028-.113c.7675-5.272.3407-12.86975.1699-14.64054-.042-.41268-.6806-.65887-1.2353-.45404-.7635.26032-1.7888.57512-2.9721.93937zm-1.9957 13.32381c-2.4993-.6302-4.9994-1.2571-7.50025-1.8808.13291 2.0079.52966 4.711 3.14105 5.2478 2.3435.7133 3.1889-.9989 4.1973-3.0391z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/MoreOptionsIcon.tsx
import { jsx as jsx68 } from "react/jsx-runtime";
var MoreOptionsIcon = (props) => /* @__PURE__ */ jsx68("svg", { ...props, children: /* @__PURE__ */ jsx68(
  "path",
  {
    d: "m20.5717 17.2858c.79 0 1.4283.6384 1.4283 1.4284s-.6383 1.4283-1.4283 1.4283h-11.43003c-.78834 0-1.42834-.6383-1.42834-1.4283s.64-1.4284 1.42834-1.4284zm-17.14337 0c.79 0 1.42917.6384 1.42917 1.4284s-.63917 1.4283-1.42917 1.4283c-.78833 0-1.42833-.6383-1.42833-1.4283s.64-1.4284 1.42833-1.4284zm17.14337-7.1433c.79 0 1.4283.6392 1.4283 1.4292s-.6383 1.4283-1.4283 1.4283h-11.43003c-.78834 0-1.42834-.6383-1.42834-1.4283s.64-1.4292 1.42834-1.4292zm-17.14337 0c.79 0 1.42917.6392 1.42917 1.4292s-.63917 1.4283-1.42917 1.4283c-.78833 0-1.42833-.6383-1.42833-1.4283s.64-1.4292 1.42833-1.4292zm17.14337-7.1425c.79 0 1.4283.63833 1.4283 1.42833s-.6383 1.42917-1.4283 1.42917h-11.43003c-.78834 0-1.42834-.63917-1.42834-1.42917.00084-.79.64084-1.42833 1.42917-1.42833zm-17.14337 0c.79 0 1.42917.63833 1.42917 1.42833s-.63917 1.43-1.42917 1.43c-.78833-.00083-1.42833-.64-1.42833-1.43s.64-1.42833 1.42833-1.42833z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/OpenNewIcon.tsx
import { jsx as jsx69 } from "react/jsx-runtime";
var OpenNewIcon = (props) => /* @__PURE__ */ jsx69("svg", { ...props, children: /* @__PURE__ */ jsx69(
  "path",
  {
    d: "m12.3369 3.55763c.0451-.89627.7944-1.58554 1.6736-1.53953l6.1462.32164c.8161.04271 1.4683.7076 1.5101 1.53953l.3155 6.26593c.0452.8963-.6309 1.6602-1.5101 1.7062-.8791.046-1.6284-.6433-1.6735-1.5396l-.1317-2.61568-7.4804 7.62608c-.6225.6346-1.6317.6346-2.25417 0s-.62247-1.6635 0-2.2981l7.48037-7.62606-2.5658-.13428c-.8791-.046-1.5552-.80987-1.5101-1.70613zm8.9774 15.19197c0 1.7778-1.4412 3.219-3.2191 3.219h-12.87616c-1.77784 0-3.21905-1.4412-3.21905-3.219v-13.18963c0-1.77783 1.44122-3.21905 3.21905-3.21905h3.21904c.88892 0 1.60952.72061 1.60952 1.60953s-.7206 1.60952-1.60952 1.60952h-3.21904v13.18963h12.87616v-3.3131c0-.8889.7206-1.6095 1.6095-1.6095.889 0 1.6096.7206 1.6096 1.6095z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/OverviewIcon.tsx
import { jsx as jsx70 } from "react/jsx-runtime";
var OverviewIcon = (props) => /* @__PURE__ */ jsx70("svg", { ...props, children: /* @__PURE__ */ jsx70(
  "path",
  {
    d: "m18.6532 18.6542c-.325.325-.8316.4883-1.5066.4883-.7042 0-1.5059-.1883-2.3484-.505.6984-.5475 1.3859-1.1392 2.0417-1.7958.6533-.6517 1.2375-1.3359 1.785-2.0317.0442.1158.11.2375.1483.3517.5284 1.5833.4834 2.89-.12 3.4925zm-11.79996.4883c-.675 0-1.18166-.1642-1.50583-.4883-.60417-.6025-.64833-1.9084-.12-3.4925.0375-.1142.10417-.2359.1475-.3517.54667.6958 1.1325 1.38 1.78333 2.0317.6575.6566 1.34417 1.2483 2.0425 1.7958-.84166.3167-1.64333.505-2.3475.505zm-1.625-10.305c-.52916-1.58333-.485-2.88917.11917-3.49167.325-.325.83-.48833 1.50583-.48833.70417 0 1.505.18833 2.3475.505-.69833.5475-1.385 1.13917-2.04166 1.795-.65167.6525-1.2375 1.33667-1.78417 2.0325-.04333-.11583-.11-.23833-.1475-.3525zm11.92166 3.1625c-.6558.9717-1.4392 1.9333-2.3283 2.8217-.9.9008-1.8592 1.6808-2.82 2.3333-.9634-.6525-1.9225-1.4325-2.82252-2.3333-.88917-.8892-1.67167-1.85-2.32917-2.8217.6575-.9717 1.44-1.9333 2.32833-2.8225.89996-.9 1.85916-1.68167 2.82246-2.33167.9609.65 1.9209 1.43167 2.82 2.33167.8892.8892 1.6725 1.8508 2.3292 2.8225zm-.0025-7.1425c.675 0 1.1825.16417 1.5067.48833.6025.6025.6483 1.90834.12 3.49167-.0384.11417-.1042.23667-.1484.3525-.5475-.69583-1.1316-1.38-1.7841-2.03333-.6567-.655-1.3442-1.24667-2.0417-1.79417.8417-.31667 1.6433-.505 2.3475-.505zm3.3242 7.1425c.405-.7575.7641-1.5142 1.0108-2.25833.9042-2.71167.6167-4.99-.81-6.41667-.8642-.86667-2.085-1.325-3.5258-1.325-1.5559 0-3.3417.54833-5.1467 1.51583-1.805-.96666-3.59166-1.51583-5.14666-1.51583-1.44 0-2.65916.45833-3.525 1.325-1.42666 1.4275-1.715 3.70583-.81166 6.41667.2475.74413.60583 1.50083 1.01333 2.25833-.4075.7575-.76667 1.5142-1.01333 2.2575-.90334 2.7125-.615 4.9917.81166 6.4167.86584.8675 2.085 1.3258 3.525 1.3258 1.555 0 3.34166-.5483 5.14746-1.5158 1.8034.9666 3.59 1.5158 5.1459 1.5158 1.4408 0 2.6608-.4583 3.5258-1.325 1.4267-1.4267 1.7142-3.705.81-6.4175-.2467-.7433-.6058-1.5-1.0108-2.2575z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/PaintIcon.tsx
import { jsx as jsx71 } from "react/jsx-runtime";
var PaintIcon = (props) => /* @__PURE__ */ jsx71("svg", { ...props, children: /* @__PURE__ */ jsx71(
  "path",
  {
    clipRule: "evenodd",
    d: "m22.0003 11.4141c-.1004-11.304369-11.1037-11.060891-16.78934-6.40002-3.84615 3.16522-5.317726 14.50432 1.80602 16.73042 2.31016.7268 2.59201-.1753 3.02362-1.5568.312-.9985.7022-2.2475 1.9931-3.3128.8231-.6896 2.2559-.6232 3.7827-.5525 2.9397.1361 6.2279.2884 6.1839-4.9083zm-16.32113 5.6348c.23411.3826.63545.5913 1.03679.5913.23411 0 .43478-.0348.602-.1739.56856-.3827.73579-1.1479.40134-1.7392l-.03344-.0348c-.33445-.5913-1.07024-.7652-1.6388-.3826s-.70234 1.1479-.36789 1.7392zm12.14043-5.0087c.0669.6608.5686 1.1478 1.204 1.1478h.0669c.6689-.0696 1.1706-.6609 1.1037-1.3565v-.0696-.0348c-.0669-.6608-.6689-1.1478-1.3378-1.0782-.6354.0695-1.1037.6956-1.0368 1.3913zm-12.17388-.4522c.13378.0348.26756.0696.40134.0696.50167 0 1.00334-.3479 1.13712-.8348.23412-.6609-.10033-1.35655-.73578-1.60002-.63545-.24348-1.30435.10434-1.53846.76521v.03481c-.23412.6261.10033 1.3217.73578 1.5652zm9.63208-3.72175c.2007.13913.4348.2087.6689.2087.4014 0 .7693-.2087 1.0034-.5913.3344-.59131.2006-1.35653-.3679-1.73913l-.0335-.03479c-.5685-.34782-1.2709-.17391-1.6388.41739-.3679.59131-.2006 1.35653.3679 1.73913zm-6.22071-1.32173c.16723.52173.63545.83478 1.13711.83478.1108 0 .2217-.02387.3515-.05184.0269-.00579.0546-.01176.0833-.01773.6355-.24348.9699-.93913.7358-1.6s-.903-1.00869-1.53848-.76521h-.03344c-.63545.24347-.9699.93913-.73579 1.6z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/PencilIcon.tsx
import { jsx as jsx72 } from "react/jsx-runtime";
var PencilIcon = (props) => /* @__PURE__ */ jsx72("svg", { ...props, children: /* @__PURE__ */ jsx72(
  "path",
  {
    d: "m13.4144 6 4.5856 4.5857-9.95735 9.9576-4.0884.4514c-.54731.0605-1.00974-.4023-.94885-.9496l.45491-4.0914zm7.1077-.47944-2.0427-2.04268c-.6371-.63717-1.6705-.63717-2.3077 0l-1.9217 1.9217 4.3504 4.35042 1.9217-1.9217c.6372-.63751.6372-1.67057 0-2.30774z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/PlayIcon.tsx
import { jsx as jsx73 } from "react/jsx-runtime";
var PlayIcon = (props) => /* @__PURE__ */ jsx73("svg", { ...props, children: /* @__PURE__ */ jsx73(
  "path",
  {
    d: "M19.5427 9.71299C21.3396 10.7505 21.3396 13.3441 19.5427 14.3816L6.89773 21.6822C5.10078 22.7196 2.8546 21.4228 2.8546 19.3479L2.8546 4.74675C2.8546 2.67181 5.10078 1.37497 6.89774 2.41244L19.5427 9.71299Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/PodcastIcon.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var PodcastIcon = (props) => /* @__PURE__ */ jsx74("svg", { ...props, children: /* @__PURE__ */ jsx74(
  "path",
  {
    d: "m5.03666 10.8522c.51251.0183.92109.4193.96101.9183l.00255.1168.00044.1828c.01531.6885.18571 1.6057.62783 2.4885.78316 1.5638 2.20547 2.5134 4.58571 2.5969 2.7301.0958 4.4504-.6666 5.4599-1.9712.3259-.4211.5542-.8737.7008-1.3244.0282-.0868.0512-.1658.0695-.2354l.0421-.1798.0185-.1156c.0553-.5495.5457-.9501 1.0952-.8947.5495.0553.9501.5456.8947 1.0951-.0767.7623-.4212 1.8219-1.2391 2.8788-1.1367 1.469-2.8727 2.4274-5.2555 2.6851l-.0013.924 1.001.0008c.5522 0 1 .4477 1 1 0 .5129-.3861.9355-.8834.9933l-.1166.0067h-4.00004c-.55229 0-1-.4477-1-1 0-.5128.38604-.9355.88338-.9932l.11662-.0068.99904-.0008v-.871l-.1745-.008c-2.94807-.1902-4.91745-1.554-5.98428-3.6842-.37658-.7519-.61294-1.5476-.73949-2.3471-.06944-.4386-.09766-.8183-.10067-1.1147l.00144-.1767c.01977-.5519.48323-.9833 1.03516-.9635zm5.14174-8.74976c.3849-.068 1.1957-.02029 2.0375.03843l.5056.03571c.3354.02358.6631.04556.9579.05926 1.319.079 2.426 1.7858 2.3807 4.4442-.0308 1.80785-.0421 2.74853-.0441 3.80146v.6471c.0004.1822.0012.3292.0023.4367.023 1.5111-.5191 3.3964-1.467 3.7293-.4376.1976-1.7443.3194-2.7857.2553-.7982-.047-2.15774-.2241-2.94204-.6081s-1.1882-1.903-1.1703-2.7445c.0117-.942.1799-4.32316.2155-6.00616.0797-2.6608 1.1813-3.878 2.30964-4.0887z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/PreviewIcon.tsx
import { jsx as jsx75 } from "react/jsx-runtime";
var PreviewIcon = (props) => /* @__PURE__ */ jsx75("svg", { ...props, children: /* @__PURE__ */ jsx75(
  "path",
  {
    d: "m10.7498 2.00041c4.8325 0 8.75 3.9175 8.75 8.74999 0 1.9642-.655 3.7733-1.7492 5.2333l3.8834 3.885c.4883.4859.4883 1.2809 0 1.7667-.2417.2433-.5617.3642-.8834.3642-.32 0-.64-.1209-.8833-.3634l-3.8833-3.8833c-1.4617 1.0917-3.2692 1.7467-5.2342 1.7467-4.83335.0008-8.75001-3.9159-8.75001-8.7492 0-4.83165 3.91666-8.74999 8.75001-8.74999zm0 2.5c-3.44668 0-6.25001 2.8025-6.25001 6.24999 0 3.4475 2.80333 6.25 6.25001 6.25 3.4458 0 6.25-2.8033 6.25-6.25 0-3.44665-2.8042-6.24999-6.25-6.24999zm-1.75001 3.49167c.22333-.12667.68166-.1225.90333.00833l3.55748 2.13919c.215.1275.34.36.34.61 0 .2508-.1425.4833-.3566.6108l-3.58755 2.14c-.1125.0667-.28583.1008-.41333.1008-.12 0-.33333-.03-.44417-.0916-.2225-.1267-.545-.3642-.545-.6209v-4.27662c0-.25834.3225-.4925.545-.62z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/PrivateUserSessionsIcon.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var PrivateUserSessionsIcon = (props) => /* @__PURE__ */ jsx76("svg", { ...props, children: /* @__PURE__ */ jsx76(
  "path",
  {
    d: "m16.3564 2c1.5175.00328 2.7654.05959 3.3551.19802 1.2681.28936 1.6232 1.60755 2.0544 8.51998.4819 7.877.2781 9.5709-1.1676 10.0853-1.2394.441-3.3562.6093-5.8879.8106l-.5708.0456c-.5773.0463-1.1734.0953-1.7834.1505-2.81531.2251-6.67048.3537-8.72489-.225-1.21742-.3537-1.31887-1.061-1.49641-7.7162l-.00348-.1303c-.07337-2.7504-.12433-4.66021-.13075-6.02614l.00004-.55296c.00836-1.50623.08983-2.24363.28637-2.77512.43117-1.22173 1.95295-1.54324 3.72836-1.86475 1.11233-.20143 6.31756-.51193 10.07126-.51953zm3.1428 5.19947-15.49271 1.15568c.01637 1.28708.06245 3.01455.12591 5.39325l.00179.067c.09105 3.4133.16207 5.0486.32392 5.9018l.00263.0138c.76761.1673 1.90292.2576 3.26301.268 1.49142.0114 3.09275-.0725 4.46275-.1818.87-.0787 1.6602-.1412 2.401-.1999.8576-.0679 1.6491-.1306 2.4216-.207 1.3162-.1301 2.2225-.2729 2.7833-.4464.0351-.1138.0851-.3222.126-.6736.1469-1.2621.0949-3.4668-.1487-7.4489-.0929-1.4893-.1808-2.68305-.2705-3.64193zm-11.14647 2.93623c2.58087-1.8752 6.39687-1.02448 8.52877.9606 1.2696 1.1821 2.1643 2.255 2.1166 2.9255l-.0003.1105c-.0672.9454-1.7348 2.4982-3.3505 3.6722-2.5808 1.8753-6.39694 1.0245-8.52883-.9606-1.26956-1.1821-2.16431-2.255-2.11656-2.9254l.00028-.1104c.06722-.9455 1.73482-2.4983 3.35054-3.6724zm.90454 1.9887c-.90992 1.3477-.88542 4.8225 2.51483 4.8706 3.4002.0482 3.7685-3.7858 1.9417-5.3499-1.293-1.107-3.5466-.8684-4.45653.4793zm3.63903.5174c1.0567.8936.8436 3.0841-1.1232 3.0566-1.96671-.0275-1.98091-2.0128-1.4546-2.7827.5263-.77 1.8299-.9064 2.5778-.2739z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ProjectIcon.tsx
import { jsx as jsx77 } from "react/jsx-runtime";
var ProjectIcon = (props) => /* @__PURE__ */ jsx77("svg", { ...props, children: /* @__PURE__ */ jsx77(
  "path",
  {
    d: "m2.25954 20.5314c.23026 1.1309 1.57918 1.268 2.7308 1.1994 1.15151-.0685 11.21906.0686 13.52216.2399 2.303.1713 2.8623-.4456 3.1913-.891.2304-.3084.2962-4.0903.2962-5.8381 0-1.7135-.2304-4.691-.5594-6.95284-.1974-1.40511.2632-2.67311-3.3229-2.94722-1.6121-.10278-3.0812-.10278-6.0423-.03433-.296-.78822-1.2946-2.8142-1.9526-3.15687-.98704-.41132-6.54729.13701-7.23818.27412-.75669.17133-.75669.58265-.75669 1.50788v2.98165c0 1.30222-.36187 11.38971.13161 13.61741z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/QuestionMarkIcon.tsx
import { jsx as jsx78 } from "react/jsx-runtime";
var QuestionMarkIcon = (props) => /* @__PURE__ */ jsx78("svg", { ...props, children: /* @__PURE__ */ jsx78(
  "path",
  {
    d: "m21.9985 12.0962c.156-11.337813-12.26789-12.565874-17.33611-6.47453-3.58731 4.31149-3.71983 11.58443.64734 14.61853 4.36716 3.034 16.53287 3.1937 16.68877-8.144zm-8.8773 1.238h-2.3237v-.1495c0-.9268.1764-1.4649 1.0589-2.0629l.6912-.4634c.5736-.3886.8236-.77726.8236-1.40508 0-.7175-.4706-1.16594-1.1472-1.16594-.8236 0-1.4265.46339-1.4265 1.55459h-2.35319c0-1.8984 1.22069-3.37826 3.85329-3.37826 2.0295 0 3.4561 1.01647 3.4561 2.88497 0 1.30052-.6912 2.03292-1.456 2.61592l-.603.4484c-.4412.3439-.5735.5531-.5735.9418zm.3676 2.2571c0 .8371-.6765 1.5397-1.5295 1.5397-.8677 0-1.5295-.7026-1.5295-1.5397s.6618-1.5247 1.5295-1.5247c.853 0 1.5295.6876 1.5295 1.5247z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ReactIcon.tsx
import { jsx as jsx79 } from "react/jsx-runtime";
var ReactIcon = (props) => /* @__PURE__ */ jsx79("svg", { ...props, children: /* @__PURE__ */ jsx79(
  "path",
  {
    clipRule: "evenodd",
    d: "m12 22c5.5228 0 10-4.4772 10-10 0-5.52285-4.4772-10-10-10-5.52285 0-10 4.47715-10 10 0 5.5228 4.47715 10 10 10zm-3.33333-11.6667c.92047 0 1.66663-.74616 1.66663-1.66663 0-.92048-.74616-1.66667-1.66663-1.66667s-1.66667.74619-1.66667 1.66667c0 .92047.7462 1.66663 1.66667 1.66663zm8.33333 3.3334c0 2.7614-2.2386 5-5 5-2.76142 0-5-2.2386-5-5zm0-5.00003c0 .92047-.7462 1.66663-1.6667 1.66663-.9204 0-1.6666-.74616-1.6666-1.66663 0-.92048.7462-1.66667 1.6666-1.66667.9205 0 1.6667.74619 1.6667 1.66667z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/RecordIcon.tsx
import { jsx as jsx80, jsxs as jsxs5 } from "react/jsx-runtime";
var RecordIcon = (props) => /* @__PURE__ */ jsxs5("svg", { ...props, children: [
  /* @__PURE__ */ jsx80(
    "path",
    {
      d: "M5.72808 6.57665L5.72808 6.57666C4.21821 8.39134 3.44905 10.8031 3.50262 13.081C3.5562 15.3591 4.44354 17.634 6.41174 19.0014C8.31623 20.3245 11.728 20.952 14.6917 20.132C16.2039 19.7135 17.6545 18.9039 18.734 17.5601C19.8183 16.2104 20.467 14.3977 20.4988 12.0859C20.5306 9.7716 19.9186 7.89371 18.8485 6.48004C17.7808 5.06955 16.3051 4.18499 14.7177 3.77414C11.5832 2.96286 7.89555 3.97164 5.72808 6.57665Z",
      fill: "transparent",
      stroke: "currentColor",
      strokeWidth: "2"
    }
  ),
  /* @__PURE__ */ jsx80(
    "path",
    {
      d: "M7.66621 8.21881C9.16184 6.42126 11.7691 5.68812 13.9883 6.26249C15.0878 6.54707 16.077 7.14918 16.787 8.08709C17.4957 9.0234 17.9503 10.3264 17.9266 12.0543C17.9028 13.7834 17.4234 15.0307 16.7129 15.9152C16 16.8027 15.0236 17.3629 13.9441 17.6616C11.7547 18.2675 9.2536 17.7685 7.98327 16.886C6.7448 16.0256 6.1109 14.5403 6.07219 12.894C6.03347 11.2477 6.59545 9.50574 7.66621 8.21881Z",
      fill: "currentColor",
      stroke: "transparent"
    }
  )
] });

// src/components/Icon/icons/RedoIcon.tsx
import { jsx as jsx81 } from "react/jsx-runtime";
var RedoIcon = (props) => /* @__PURE__ */ jsx81("svg", { ...props, children: /* @__PURE__ */ jsx81(
  "path",
  {
    d: "m6.87391 20.4397c-1.80165-.6674-3.34245-1.8936-4.39695-3.4997-1.0545-1.6059-1.567498-3.5072-1.46385-5.4256.10365-1.91847.81855-3.7534 2.04-5.23636s2.8854-2.53613 4.74855-3.00542c1.863-.46929 3.82734-.33007 5.60554.39728 1.7783.72735 3.2771 2.00467 4.2772 3.64507.7418 1.21674 1.1804 2.58587 1.2889 3.99293l.642-.536c.6359-.531 1.5818-.4459 2.1128.1899.5309.6359.4459 1.5819-.19 2.1128l-2.996 2.5018c-.5567.4648-1.3662.4648-1.9228 0l-2.996-2.5018c-.6359-.5309-.721-1.4769-.19-2.1128.531-.6358 1.4769-.7209 2.1128-.1899l.385.3215c-.1193-.7805-.3922-1.53458-.8082-2.21675-.6667-1.0936-1.6659-1.94515-2.8514-2.43006-1.1855-.4849-2.49506-.57771-3.73709-.26485-1.242.31287-2.3514 1.01497-3.16575 2.00362-.8142.98864-1.2909 2.21194-1.3599 3.49094-.06915 1.2789.27285 2.5464.9759 3.617.70305 1.0707 1.73025 1.8882 2.9313 2.3331 1.20109.4449 2.51304.4938 3.74384.1394.7961-.2291 1.6273.2304 1.8565 1.0266.2291.796-.2304 1.6272-1.0265 1.8564-1.8463.5314-3.81421.4582-5.61589-.2091z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/RemoveFromChannelIcon.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
var RemoveFromChannelIcon = (props) => /* @__PURE__ */ jsx82("svg", { ...props, children: /* @__PURE__ */ jsx82(
  "path",
  {
    d: "M5.64571 1.19092C5.19893 0.866257 4.57355 0.965254 4.24889 1.41204C3.92423 1.85882 4.02322 2.48419 4.47 2.80886C5.75625 3.74353 6.95092 5.00735 8.01704 6.20703C6.38757 6.20371 5.0889 6.2273 4.54926 6.28789C2.56844 6.5103 2.31336 7.59249 2.09302 9.25911C1.87267 10.9257 2.11033 17.874 2.37512 19.4659C2.63991 21.0578 3.40531 21.7237 5.61415 21.6844C6.9775 21.6601 10.7551 21.7863 13.8349 21.8892L13.8531 21.8898C15.7549 21.9533 17.3885 22.0078 18.0174 22.0123L18.03 22.0124C19.6724 22.024 21.003 22.0334 21.6139 20.7615C22.2264 19.4863 21.9782 11.1909 21.8801 9.61079C21.7819 8.03071 21.2479 6.89398 19.993 6.61776C19.5898 6.52901 18.0558 6.44028 16.0935 6.36809C17.0728 5.28349 18.2296 4.02372 19.6219 2.73331C20.0269 2.35789 20.051 1.72518 19.6755 1.32012C19.3001 0.915058 18.6674 0.891034 18.2623 1.26646C16.6527 2.7583 15.3356 4.22058 14.2947 5.37616L14.2798 5.39271C14.0913 5.60208 13.9119 5.8013 13.7414 5.98868C13.6467 6.09275 13.5555 6.19241 13.4676 6.28764C12.5521 6.2645 11.613 6.24544 10.7013 6.23164C10.5417 6.0511 10.3732 5.8585 10.1952 5.65501L10.0863 5.5306C8.90401 4.17976 7.37355 2.44649 5.64571 1.19092ZM6.2929 14.9216C5.95119 14.5799 5.90848 14.0524 6.16476 13.6642C6.20137 13.6088 6.24408 13.5562 6.2929 13.5074L9.82843 9.97183C10.219 9.5813 10.8521 9.5813 11.2426 9.97183C11.6332 10.3623 11.6332 10.9955 11.2426 11.386L9.46447 13.1642H16.5858C17.1381 13.1642 17.5858 13.6119 17.5858 14.1642C17.5858 14.7165 17.1381 15.1642 16.5858 15.1642H9.36396L11.2426 17.0429C11.6332 17.4334 11.6332 18.0666 11.2426 18.4571C10.8521 18.8476 10.219 18.8476 9.82843 18.4571L6.2929 14.9216Z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ReplaceIcon.tsx
import { jsx as jsx83 } from "react/jsx-runtime";
var ReplaceIcon = (props) => /* @__PURE__ */ jsx83("svg", { ...props, children: /* @__PURE__ */ jsx83(
  "path",
  {
    d: "m11.6953 2c-2.36214 0-4.62753.93836-6.29781 2.60864-.58578.58578-.58578 1.53553 0 2.12131.58579.58579 1.53553.58579 2.12133 0 1.10766-1.10768 2.60998-1.72995 4.17648-1.72995s3.0688.62227 4.1765 1.72995c.7623.76237 1.2948 1.7117 1.5527 2.7402l-.5756-.48053c-.6358-.53098-1.5817-.44591-2.1127.18997-.531.63589-.446 1.58181.1899 2.11281l2.9961 2.5017c.5566.4648 1.3662.4648 1.9228 0l2.996-2.5017c.6358-.531.7209-1.47692.19-2.11281-.531-.63588-1.477-.72095-2.1129-.18997l-.4506.37628c-.3144-1.79011-1.1718-3.45466-2.4744-4.75726-1.6702-1.67028-3.9357-2.60864-6.2978-2.60864zm7.4377 16.8867c-1.5536 1.7794-3.7502 2.869-6.1069 3.0288-2.3567.1599-4.68042-.623-6.45992-2.1765-1.32667-1.1582-2.26984-2.6738-2.73018-4.3469l-.17388.2132c-.52381.6417-1.46874.7374-2.11053.2136-.641801-.5238-.737441-1.4687-.21363-2.1105l2.57465-3.1546c.26245-.3216.64644-.52 1.06057-.5481.41414-.02809.82139.1167 1.12487.3999l2.97703 2.778c.60569.5652.63851 1.5143.07332 2.12-.5652.6057-1.51437.6386-2.12005.0734l-.04151-.0389c.34575.8142.87327 1.5482 1.55225 2.141 1.18009 1.03 2.72111 1.5493 4.28401 1.4433 1.5628-.1061 3.0196-.8285 4.0498-2.0087.5448-.624 1.4924-.6883 2.1165-.1435.624.5448.6884 1.4923.1436 2.1165z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ReplyIcon.tsx
import { jsx as jsx84 } from "react/jsx-runtime";
var ReplyIcon = (props) => /* @__PURE__ */ jsx84("svg", { ...props, children: /* @__PURE__ */ jsx84(
  "path",
  {
    clipRule: "evenodd",
    d: "m3.43934 7.93934c-.58579.58579-.58579 1.53553 0 2.12136l4.5 4.5c.58579.5857 1.53553.5857 2.12136 0 .5857-.5858.5857-1.5356 0-2.1214l-1.93938-1.9393h.87868c4.9706 0 9 4.0294 9 9 0 .8284.6716 1.5 1.5 1.5s1.5-.6716 1.5-1.5c0-6.6274-5.3726-12-12-12h-.87868l1.93938-1.93934c.5857-.58579.5857-1.53553 0-2.12132-.58583-.58579-1.53557-.58579-2.12136 0z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/ScissorsIcon.tsx
import { jsx as jsx85 } from "react/jsx-runtime";
var ScissorsIcon = (props) => /* @__PURE__ */ jsx85("svg", { ...props, children: /* @__PURE__ */ jsx85(
  "path",
  {
    d: "m8.03043 13.6607c-.29507.3401-.77967.4312-1.22762.3856-.26063-.0266-.53261-.0225-.83308-.0225-2.20558 0-3.95014 2.0802-3.992 3.992-.04187 1.9117 1.25486 4.1879 3.992 3.992 2.73715-.1959 3.99201-1.7864 3.99201-3.992 0-.3414-.04361-.6694-.125-.982-.10544-.4049-.03782-.8512.25806-1.1471l1.1558-1.1558c.3906-.3905 1.0237-.3905 1.4142 0l1.1558 1.1558c.296.2959.3636.7422.2581 1.1471-.0813.3126-.125.6406-.125.982 0 2.2056 1.2079 4.1121 3.992 3.992 2.1682 0 3.9313-1.7263 3.9905-3.8801.0021-.0742.0111-.1481.0221-.2214.249-1.6525-2.0736-4.0873-4.0126-3.8825-.3396 0-.6661.0432-.9773.1238-.4074.1055-.8563.036-1.152-.2635l-.2823-.286c-.3353-.3396-.3855-.8665-.1187-1.2621 1.2897-1.9126 4.8975-7.29122 5.5061-8.5084.5957-1.19133-.0999-1.55313-.4001-1.64993-.0817-.02633-.1675-.02743-.2521-.01311-.3067.0519-1.1141.23047-1.8398.75302-.8699.6263-7.2522 7.11612-10.39907 10.74312zm-.0647 4.3551c0 1.0978-.33819 2.1849-2.02833 2.2762-1.69015.0913-1.93466-.9724-1.96367-2.2762s.81534-1.7091 1.92989-1.7429 2.06211.6451 2.06211 1.7429zm11.62977-.1c0 1.0978-.6104 1.7735-1.7181 1.7735-1.1078 0-1.6627-.5095-1.6627-1.6073s.4995-1.7181 1.6072-1.7181c1.1078 0 1.7736.4541 1.7736 1.5519zm-16.00097-13.65035c.36312.61452 4.38321 5.68498 5.13739 6.04815.75418.3631 3.01228-2.35364 2.62128-3.13575-.3911-.78212-6.57133-4.945-7.37947-5.13008-.51609-.11819-.77828.15828-.90039.37355-.07895.13919-.07354.30477-.02904.45848.09686.33451.29621.95576.55023 1.38565z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/SearchIcon.tsx
import { jsx as jsx86 } from "react/jsx-runtime";
var SearchIcon = (props) => /* @__PURE__ */ jsx86("svg", { ...props, children: /* @__PURE__ */ jsx86(
  "path",
  {
    clipRule: "evenodd",
    d: "m18.1805 10.0368c.1262-9.02516-9.92582-10.002742-14.02641-5.15382-2.90243 3.43213-3.00965 9.22162.52374 11.63682 2.18864 1.496 6.79817 2.1141 10.01977.223.1591.1745.3297.3626.5089.5601l.0011.0012.028.0308c.3639.4012.7527.8439 1.1456 1.2911 1.4196 1.6159 2.8916 3.2914 3.4268 3.2876.9201-.0065 1.8533-.8438 1.8058-1.6624-.0358-.617-3.0505-4.1585-4.5332-5.8655.6663-1.1243 1.0748-2.556 1.0999-4.3489zm-3.0735-.0148c.078-5.63543-6.13398-6.24583-8.66808-3.21812-1.79365 2.14306-1.85991 5.75812.32367 7.26612 1.35254.9342 4.20111 1.3201 6.19201.1393.3521-.2089 1.2447-.8717 1.4727-1.4718.0304-.08.062-.1614.0941-.2443h.0001c.2656-.6842.5717-1.4727.5855-2.4712z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/SendIcon.tsx
import { jsx as jsx87 } from "react/jsx-runtime";
var SendIcon = (props) => /* @__PURE__ */ jsx87("svg", { ...props, children: /* @__PURE__ */ jsx87(
  "path",
  {
    d: "m2.08399 13.9107c.0602 3.3015.20898 6.331.4105 7.6243.0499.3202.29259.5164.60534.4312 3.13827-.855 18.95577-8.2148 18.95577-9.9463 0-1.2384-16.56256-9.66456-19.1655-10.02992-.19461-.02731-.34033.09248-.39323.28174-.29629 1.05988-.42832 4.29228-.44124 7.85348l14.16417 1.8946z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/SettingsIcon.tsx
import { jsx as jsx88 } from "react/jsx-runtime";
var SettingsIcon = (props) => /* @__PURE__ */ jsx88("svg", { ...props, children: /* @__PURE__ */ jsx88(
  "path",
  {
    clipRule: "evenodd",
    d: "m6.24843 6.77277c.74955 2.13722 3.90597 2.17402 4.61477.46032.0683-.1408.125-.2811.1707-.42046.6058-.00536 1.1978-.0136 1.7785-.0229.4047-.00648.8032-.01346 1.1969-.02035 2.4581-.04305 4.7202-.08264 7.0401.0237.5014.02298.9266-.36485.9495-.86627.023-.50141-.3648-.92652-.8662-.94951-2.382-.10919-4.7076-.06836-7.1622-.02525-.3921.00689-.7877.01383-1.1872.02023-.57.00912-1.1495.01719-1.7411.02251-.1944-.59562-.5734-1.11691-1.05139-1.50894-.92757-.76087-3.35974-.86696-3.825 1.46603-1.01999-.03054-2.08381-.07744-3.2008-.14657-.50098-.031-.93224.34999-.96324.85097-.03101.50098.34999.93224.85097.96325 1.18803.07352 2.31663.12231 3.39569.15324zm-4.2483 5.54683c0-.5019.4069-.9088.90884-.9088 1.40629 0 2.64238-.0161 3.88561-.0323 1.96291-.0254 3.94372-.0511 6.64042-.0143.5017-2.38934 3.3463-2.2812 4.2812-1.51432.5667.46472.9111 1.01612 1.0122 1.64502.7524.0257 1.5502.0552 2.3993.0894.5015.0202.8917.443.8716.9445-.0202.5016-.4431.8919-.9447.8717-.9361-.0376-1.8091-.0697-2.6268-.097-.0222.0498-.0455.0999-.0699.1503-.7019 1.6972-3.9734 1.7795-4.8058-.2702-2.7348-.0393-4.72571-.0135-6.71331.0124-1.24973.0162-2.49855.0325-3.92982.0325-.50194 0-.90884-.4069-.90884-.9089zm0 6.1328c-.00116-.5019.40481-.9097.90675-.9109 1.07677-.0025 2.10118-.0058 3.08174-.0089.11298-.0004.2258-.0008.33762-.0011.05692-.1747.12895-.3528.21677-.5339.73063-1.7666 3.87449-1.7897 4.45259.5249 3.676.0025 6.8235.0408 10.1372.1927.5014.0229.8892.4481.8662.9495-.0229.5014-.4481.8892-.9495.8663-3.2903-.1508-6.4125-.1885-10.0872-.1907-.5634 2.1212-2.87808 1.9991-3.77763 1.2612-.42273-.3467-.72861-.77-.89095-1.2522-.09774.0003-.19592.0006-.29455.001-.98121.003-2.00765.0064-3.0881.0089-.50195.0011-.90979-.4048-.91094-.9068z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/ShareIcon.tsx
import { jsx as jsx89 } from "react/jsx-runtime";
var ShareIcon = (props) => /* @__PURE__ */ jsx89("svg", { ...props, children: /* @__PURE__ */ jsx89(
  "path",
  {
    d: "m10.6913 7.39911c.578 0 1.0471.46807 1.0471 1.04714 0 .57802-.4691 1.04714-1.0471 1.04714h-6.59702v9.31951h15.39302v-5.7592c0-.5791.468-1.0472 1.0471-1.0472s1.0471.4681 1.0471 1.0472v6.8064c0 .5791-.468 1.0471-1.0471 1.0471h-17.48726c-.57907 0-1.04714-.468-1.04714-1.0471v-11.41385c0-.57907.46807-1.04714 1.04714-1.04714zm5.8803-4.34459 4.7121 1.57071c.288.09529.5194.31205.6367.59268.1162.27959.1047.59687-.0314.86913l-2.4074 4.81686c-.1833.3675-.5539.5791-.9382.5791-.1571 0-.3173-.0356-.4671-.11-.5173-.2597-.7277-.888-.4691-1.40526l1.1889-2.37891c-4.0996 1.68797-6.3249 4.53987-6.3249 8.18697 0 .5791-.4681 1.0472-1.0471 1.0472-.5791 0-1.0472-.4681-1.0472-1.0472 0-4.4699 2.7028-8.04278 7.5143-10.07421l-1.9824-.66064c-.5477-.18221-.844-.77594-.6608-1.32464.1822-.54765.7717-.84609 1.3236-.66179z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/SoapboxIcon.tsx
import { jsx as jsx90 } from "react/jsx-runtime";
var SoapboxIcon = (props) => /* @__PURE__ */ jsx90("svg", { ...props, children: /* @__PURE__ */ jsx90(
  "path",
  {
    d: "m15.7829 19.9195c1.7831-.0396 2.688-.079 3.4524-.079 2.4101 0 2.4643-.5698 2.6291-2.2998.0161-.1681.0331-.3472.0535-.5377.1341-1.2162.0766-8.54397.0191-10.16543-.0064-.2209-.008-.42798-.0095-.62158-.0121-1.52821-.0174-2.21601-2.3473-2.21601-1.7437 0-4.6496.06881-7.1831.115-1.2804.02334-2.2443.04093-2.46946.04093-.67064.03118-.99638 1.029-1.0347 1.96448-.02098.51212-.02473 2.03357-.02069 3.53602-2.0997-1.99934-5.23506-4.27551-5.94338-4.62817-.23692-.17101-.5951.06006-.65895.36744-.51522 2.4804-.24785 10.79872.42166 14.00882.10701.5131.5848.5653.89181.352 1.82324-1.2674 3.87556-3.4426 5.27447-5.2548-.03781 1.4028-.06521 3.1549.15005 4.1851.21076 1.1226 1.20719 1.3097 2.05019 1.3097.4981.0169 2.6193-.0302 4.7248-.077z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/SortIcon.tsx
import { jsx as jsx91 } from "react/jsx-runtime";
var SortIcon = (props) => /* @__PURE__ */ jsx91("svg", { ...props, children: /* @__PURE__ */ jsx91(
  "path",
  {
    clipRule: "evenodd",
    d: "M2 6.28572C2 5.49674 2.63959 4.85715 3.42857 4.85715H20.5714C21.3604 4.85715 22 5.49674 22 6.28572C22 7.0747 21.3604 7.71429 20.5714 7.71429H3.42857C2.63959 7.71429 2 7.0747 2 6.28572ZM2 12C2 11.211 2.63959 10.5714 3.42857 10.5714H14.8571C15.6461 10.5714 16.2857 11.211 16.2857 12C16.2857 12.789 15.6461 13.4286 14.8571 13.4286H3.42857C2.63959 13.4286 2 12.789 2 12ZM3.42857 16.2857C2.63959 16.2857 2 16.9253 2 17.7143C2 18.5033 2.63959 19.1429 3.42857 19.1429H7.71429C8.50326 19.1429 9.14286 18.5033 9.14286 17.7143C9.14286 16.9253 8.50326 16.2857 7.71428 16.2857H3.42857Z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/SparkleIcon.tsx
import { jsx as jsx92 } from "react/jsx-runtime";
var SparkleIcon = (props) => /* @__PURE__ */ jsx92("svg", { ...props, children: /* @__PURE__ */ jsx92(
  "path",
  {
    clipRule: "evenodd",
    d: "m18.4814 8.67568-.6644 2.03912c-.1208.3764-.7329.3834-.8658.0139l-.8174-2.24473c-.1531-.4148-.4953-.75988-.9463-.9481l-1.9289-.80868c-.3584-.14988-.3342-.59953.0403-.72153l1.9449-.62742c.5316-.1708.9423-.54725 1.1074-1.01433l.741-2.08443c.1328-.38343.7651-.366.8738.0244l.5798 2.10534c.1289.46359.4954.85399.9947 1.05964l2.2027.90279c.3785.15337.3342.63091-.0685.73199l-1.9409.48103c-.6081.14988-1.0792.56467-1.2524 1.09101zm-11.00752 2.19872 1.03356-3.39854c.18792-.62742 1.14004-.63904 1.34675-.02324l1.27161 3.74128c.238.6913.7705 1.2665 1.472 1.5802l3.0005 1.3478c.5575.2498.5199.9992-.0627 1.2025l-3.0255 1.0457c-.8268.2847-1.4658.9121-1.7226 1.6906l-1.15255 3.474c-.20671.6391-1.19015.61-1.35928-.0406l-.90201-3.5089c-.20045-.7727-.77047-1.4234-1.54721-1.7661l-3.4264-1.5047c-.58881-.2556-.51991-1.0515.10649-1.2199l3.01924-.8018c.94586-.2498 1.67875-.9411 1.9481-1.8183z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/SpinnerIcon.tsx
import { jsx as jsx93 } from "react/jsx-runtime";
var SpinnerIcon = (props) => /* @__PURE__ */ jsx93("svg", { ...props, children: /* @__PURE__ */ jsx93(
  "path",
  {
    d: "m12 5.33333c-3.6819 0-6.66667 2.98475-6.66667 6.66667 0 3.6819 2.98477 6.6667 6.66667 6.6667s6.6667-2.9848 6.6667-6.6667c0-.9205.7461-1.6667 1.6666-1.6667s1.6667.7462 1.6667 1.6667c0 5.5228-4.4772 10-10 10-5.52285 0-10-4.4772-10-10 0-5.52283 4.47715-10 10-10 .9205 0 1.6667.74617 1.6667 1.66667s-.7462 1.66666-1.6667 1.66666z",
    fill: "currentColor",
    children: /* @__PURE__ */ jsx93(
      "animateTransform",
      {
        attributeName: "transform",
        dur: "1.5s",
        keyTimes: "0;1",
        repeatCount: "indefinite",
        type: "rotate",
        values: "0 12 12;360 12 12"
      }
    )
  }
) });

// src/components/Icon/icons/StatsIcon.tsx
import { jsx as jsx94 } from "react/jsx-runtime";
var StatsIcon = (props) => /* @__PURE__ */ jsx94("svg", { ...props, children: /* @__PURE__ */ jsx94(
  "path",
  {
    d: "m16.5566 10.0643c-.0138-1.76364-.0349-4.48222.0778-6.10188.0712-1.21794.7119-1.9487 2.3848-1.9487 1.4949-.09744 1.8509.34102 2.0644.77948 1.1747 2.63075.9255 14.9563.8187 16.9537-.0103.1819-.0095.3498-.0088.5049.0044.9247.0067 1.3953-2.3404 1.6874-2.3136.2923-2.5627-.4872-2.634-1.9487-.0711-1.1692-.3203-7.0153-.3558-8.7692 0-.2944-.0032-.6926-.0067-1.157zm-14.48339-1.50187c-.14486 1.45517-.03621 4.88197 0 5.86777.03621 1.2674.32593 5.3514.39835 6.1963.07243 1.0797.18107 1.5491 2.57119 1.3144 2.38974-.2414 2.38569-.931 2.38125-1.6846-.00073-.1258-.00149-.2533.00887-.3809.10864-1.4552-.21728-8.872-.39835-9.9986-.06291-.39135-.10395-.76005-.13985-1.08248-.06745-.60575-.11671-1.04819-.25851-1.17073-.28971-.28165-.76049-.70413-2.49876-.61025-1.77448.14083-1.95554.70413-2.06419 1.54909zm12.34129 3.27277c.1806.8473.2413 2.5838.2777 3.6225.0093.2658.0171.4859.0247.6338.0379.9189-.0378 3.9177-.0755 4.5464-.0379.7739-.1513 1.3059-2.6088 1.3543-2.49154.0828-2.51834-.5076-2.54541-1.104-.00455-.1004-.00912-.201-.02546-.2986-.18904-1.0157-.37808-7.8354.18903-8.9479.18904-.3385.64274-.7255 2.45744-.8706s2.155.4837 2.3063 1.0641z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/SwitchAccountsIcon.tsx
import { jsx as jsx95 } from "react/jsx-runtime";
var SwitchAccountsIcon = (props) => /* @__PURE__ */ jsx95("svg", { ...props, children: /* @__PURE__ */ jsx95(
  "path",
  {
    d: "m19.9646 3.56989c1.8831 1.70948 2.0107 4.32673 2.0173 6.99611l-.0001.9022c0 .5142.0048 1.0139.0095 1.4974.0315 3.247.0561 5.7743-1.4399 7.1725-1.292 1.2075-3.7521 1.737-6.1439 1.8298-.7898.0306-2.3731.0535-3.3766 0-2.50856-.1338-5.72463-.3411-7.52574-1.8764-1.58915-1.3545-1.45707-4.3243-1.46202-6.8769-.00066-.3406-.0013-.6675-.00565-.9747-.00365-.2567-.0094-.5236-.01531-.7981-.05418-2.51434-.12193-5.65891 1.29137-7.39433 1.56755-1.92485 6.85845-2.02051 8.54555-2.02051.1646 0 .3708-.00475.6101-.01026 1.8411-.04241 5.6413-.12995 7.4954 1.55319zm-13.46158 6.43131c-.27704.0188-.53391.1518-.70948.3673l-1.99272 2.4455c-.35041.43-.28643 1.0632.14291 1.4142s1.06144.2869 1.41186-.1431l.33308-.4088c.31302 1.3863 1.05786 2.648 2.14023 3.5944 1.3458 1.1767 3.1031 1.7698 4.8856 1.6487 1.7824-.1211 3.4436-.9464 4.6185-2.2944.3644-.4182.3215-1.0531-.0961-1.4182-.4174-.365-1.0513-.322-1.4157.0962-.8248.9464-1.9912 1.5258-3.2425 1.6109-1.2514.085-2.4852-.3314-3.43001-1.1576-.70847-.6194-1.21095-1.4312-1.45381-2.327l.49561.4632c.40517.3787 1.04013.3567 1.41822-.0492.3781-.4058.35612-1.0418-.04906-1.4205l-2.30414-2.1536c-.20301-.1898-.47546-.28678-.75249-.268zm5.45468-5.37285c-1.7866 0-3.49982.71082-4.76304 1.97611-.39187.39251-.39187 1.02889 0 1.42141.39187.3925 1.02721.3925 1.41908 0 .8869-.88831 2.08976-1.38736 3.34396-1.38736s2.457.49905 3.3438 1.38736c.6097.61063 1.0357 1.3708 1.2424 2.19443l-.3233-.30964c-.4006-.3836-1.0358-.36931-1.4187.03186-.383.40118-.3688 1.03748.0318 1.42108l2.0251 1.9394c.388.3715.9991.3715 1.387 0l2.0251-1.9394c.4006-.3836.4149-1.0199.0319-1.42108-.383-.40117-1.0181-.41546-1.4187-.03186l-.2948.28234c-.2399-1.35029-.8873-2.60548-1.8687-3.58853-1.2633-1.2653-2.9765-1.97612-4.7629-1.97612z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/TagIcon.tsx
import { jsx as jsx96 } from "react/jsx-runtime";
var TagIcon = (props) => /* @__PURE__ */ jsx96("svg", { ...props, children: /* @__PURE__ */ jsx96(
  "path",
  {
    d: "m4.11263 5.04041c-.18667 1.92432.04424 9.01979.00001 11.01579-.04423 1.9961.12249 4.3531.29625 5.0481.15491.6196 1.84727 1.5368 3.03625.3225 1.18899-1.2142 2.57456-2.7822 4.57456-4.1325 2.0186 1.5231 3.2093 2.9179 4.4324 4.1325 1.2231 1.2147 3.0966.6257 3.2493-.3225.3224-1.8964.2206-6.4423.2546-7.8088.0339-1.7012-.1019-7.13954-.1528-8.25509-.0339-1.11555.0771-1.94224-.6526-2.22113-3.0546-1.17133-9.17301-1.09763-12.39732-.53985-2.90187.502-2.52186 1.56177-2.64065 2.76098z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/TeamIcon.tsx
import { jsx as jsx97 } from "react/jsx-runtime";
var TeamIcon = (props) => /* @__PURE__ */ jsx97("svg", { ...props, children: /* @__PURE__ */ jsx97(
  "path",
  {
    clipRule: "evenodd",
    d: "M2 4a2 2 0 012-2h8a2 2 0 012 2v18H2V4zm5.5 3a1.5 1.5 0 100 3h1a1.5 1.5 0 100-3h-1zm0 7a1.5 1.5 0 000 3h1a1.5 1.5 0 000-3h-1zm8.5-4h5a1 1 0 011 1v11h-6V10z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/ThumbsDownIcon.tsx
import { jsx as jsx98 } from "react/jsx-runtime";
var ThumbsDownIcon = (props) => /* @__PURE__ */ jsx98("svg", { ...props, children: /* @__PURE__ */ jsx98(
  "path",
  {
    d: "m17.2519 13.3364-3.0265-11.67337c-.0339-.00525-.0679-.01016-.102-.01475-2.9405-.39243-6.71014-.39244-8.53416-.21004-2.95711.29848-2.5315 2.75261-2.5315 2.75261-1.58081 1.62503-.6467 3.6425-.6467 3.6425-2.023 2.24965-.4643 3.95755-.4643 3.95755-1.835066 1.6858-.45876 3.5817 1.111 3.7144 1.19943.105 4.08468.0663 5.3836.0442.33164-.0055.58591.2985.5251.6246-.87331 4.9193 1.00597 8.1196 3.03446 7.8045 1.0853-.1713 1.0845-1.7357 1.0838-3.0809-.0003-.6075-.0006-1.1702.0991-1.5399.3316-1.2326 1.614-2.9405 2.9295-4.444.4315-.4931.8118-1.0216 1.1386-1.5774zm-1.9146-11.36956c.0096.00386.0193.00776.0289.01168-.015-.10585-.0277-.20524-.0379-.29704-.0884-.818045.0608-.912012 1.4979-1.298924 1.4371-.38691271 1.8185-.4864 2.1004-.270833.1495.114118.2991.595245.4945 1.223977.079.25439.1656.53295.2627.82114.3372 1.00044 2.0341 7.87086 2.2054 9.24716.0154.1205.0372.2305.0575.3323.1255.6317.1892.952-1.7433 1.547-1.043.3216-1.5483.4667-1.8348.3757z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ThumbsUpIcon.tsx
import { jsx as jsx99 } from "react/jsx-runtime";
var ThumbsUpIcon = (props) => /* @__PURE__ */ jsx99("svg", { ...props, children: /* @__PURE__ */ jsx99(
  "path",
  {
    d: "m5.81817 10.6635 3.02644 11.6734c.0339.0053.06791.0102.10202.0148 2.94057.3924 6.71017.3924 8.53417.21 2.9571-.2985 2.5315-2.7526 2.5315-2.7526 1.5808-1.625.6467-3.6425.6467-3.6425 2.023-2.2496.4643-3.9576.4643-3.9576 1.8351-1.6858.4588-3.58165-1.111-3.71431-1.1994-.10502-4.0847-.06633-5.3836-.04422-.3316.00553-.5859-.29848-.5251-.62459.8733-4.91931-1.006-8.119626-3.0345-7.8045688-1.08524.1713558-1.08442 1.7357188-1.08372 3.0809688.00032.60744.00061 1.1702-.09912 1.53987-.33164 1.23259-1.61397 2.94053-2.92947 4.44396-.43149.49313-.81189 1.02159-1.13862 1.57739zm1.91462 11.3696c-.00967-.0038-.01932-.0077-.02895-.0117.01497.1059.02772.2053.03792.2971.08843.818-.06082.912-1.49792 1.2989s-1.81848.4864-2.10037.2708c-.14954-.1141-.29907-.5952-.49448-1.2239-.07906-.2544-.16564-.533-.26276-.8212-.33717-1.0004-2.03406-7.8709-2.20541-9.2472-.01535-.1205-.0372-.2304-.05744-.3323-.12554-.6316-.189207-.952 1.74328-1.547 1.04301-.3216 1.54824-.4667 1.83482-.3757z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/TriangleIcon.tsx
import { jsx as jsx100 } from "react/jsx-runtime";
var TriangleIcon = (props) => /* @__PURE__ */ jsx100("svg", { ...props, children: /* @__PURE__ */ jsx100(
  "path",
  {
    d: "m20.2254 20.7499h-16.26331c-.7475 0-1.30834-.37-1.6825-.9258-.37334-.5559-.37334-1.2967 0-1.8517l8.03831-13.88917c.7484-1.11083 2.8042-1.11083 3.365 0l8.0375 13.88917c.3734.555.3734 1.2958 0 1.8517-.3741.5558-.935.9258-1.4958.9258zm-12.89831-3.7033h9.71921l-5.0459-8.33337-4.67248 8.33337z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/UndoIcon.tsx
import { jsx as jsx101 } from "react/jsx-runtime";
var UndoIcon = (props) => /* @__PURE__ */ jsx101("svg", { ...props, children: /* @__PURE__ */ jsx101(
  "path",
  {
    d: "m16.2028 20.4397c1.8016-.6674 3.3424-1.8936 4.3969-3.4997 1.0545-1.6059 1.5675-3.5072 1.4639-5.4256-.1037-1.91847-.8185-3.7534-2.04-5.23636s-2.8854-2.53613-4.7486-3.00542c-1.863-.46929-3.8273-.33007-5.60553.39728-1.77825.72735-3.27706 2.00467-4.27719 3.64507-.74181 1.21674-1.18038 2.58587-1.28892 3.99293l-.64191-.536c-.63589-.531-1.58182-.4459-2.11279.1899-.530981.6359-.44593 1.5819.18996 2.1128l2.99601 2.5018c.55666.4648 1.36615.4648 1.92282 0l2.99601-2.5018c.63594-.5309.72094-1.4769.18997-2.1128-.53098-.6358-1.47691-.7209-2.11281-.1899l-.385.3215c.11931-.7805.39223-1.53458.80815-2.21675.66674-1.0936 1.66595-1.94515 2.85143-2.43006 1.1855-.4849 2.4951-.57771 3.7371-.26485 1.242.31287 2.3514 1.01497 3.1657 2.00362.8142.98864 1.2909 2.21194 1.3599 3.49094.0692 1.2789-.2728 2.5464-.9759 3.617-.703 1.0707-1.7302 1.8882-2.9313 2.3331-1.201.4449-2.513.4938-3.7438.1394-.7961-.2291-1.62728.2304-1.85645 1.0266-.22918.796.2304 1.6272 1.02645 1.8564 1.8463.5314 3.8142.4582 5.6159-.2091z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/UnlockIcon.tsx
import { jsx as jsx102 } from "react/jsx-runtime";
var UnlockIcon = (props) => /* @__PURE__ */ jsx102("svg", { ...props, children: /* @__PURE__ */ jsx102(
  "path",
  {
    clipRule: "evenodd",
    d: "m18.4 8.86859h-.3298c.1092-2.46877-1.1518-4.89701-3.4269-6.11864-2.6473-1.42146-5.82043-.75428-7.72874 1.42683-.59024.67464-.27474 1.6864.50816 2.10678.78292.42037 1.74028.0553 2.47566-.44737.94172-.64368 2.18772-.75397 3.26102-.1777 1.1838.63567 1.8185 1.92477 1.7035 3.2101h-9.26289c-.88367 0-1.60001.73488-1.60001 1.64141v9.8486c0 .9065.71634 1.6414 1.60001 1.6414h12.79999c.8837 0 1.6-.7349 1.6-1.6414v-9.8486c0-.90653-.7163-1.64141-1.6-1.64141zm-6.4 9.84861c1.7674 0 3.2-1.4698 3.2-3.2829s-1.4326-3.2828-3.2-3.2828c-1.7673 0-3.19998 1.4697-3.19998 3.2828s1.43268 3.2829 3.19998 3.2829z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/UploadIcon.tsx
import { jsx as jsx103 } from "react/jsx-runtime";
var UploadIcon = (props) => /* @__PURE__ */ jsx103("svg", { ...props, children: /* @__PURE__ */ jsx103(
  "path",
  {
    d: "m15.4036 20.3766c0 .8975-.7301 1.625-1.6307 1.625h-3.2614c-.9006 0-1.63067-.7275-1.63067-1.625l.07361-5.3782h-2.17666c-1.50644 0-2.20732-1.8615-1.07251-2.849l5.43783-4.73245c.6145-.53462 1.5309-.53462 2.1453 0l5.438 4.73245c1.1347.9875.4338 2.849-1.0727 2.849h-2.1765zm4.9657-15.12818c.9006 0 1.6307-.72751 1.6307-1.625s-.7301-1.625-1.6307-1.625h-16.73859c-.90064 0-1.63071.72751-1.63071 1.625s.73007 1.625 1.63071 1.625z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/UsersPermissionsIcon.tsx
import { jsx as jsx104 } from "react/jsx-runtime";
var UsersPermissionsIcon = (props) => /* @__PURE__ */ jsx104("svg", { ...props, children: /* @__PURE__ */ jsx104(
  "path",
  {
    d: "m11.9795 11.586c5.5082.0762 6.1048-5.99821 3.1455-8.47622-2.0947-1.75393-5.74531-1.37583-7.21932.75941s-1.43432 7.64051 4.07382 7.71681zm-9.91151 9.511c.04238.425.50422.7755 1.01871.7622l17.7118.1408c.6229.0035 1.1358-.382 1.161-.843.1556-1.6495-.1357-3.3218-.8998-4.7419-1.8017-3.4404-12.22326-5.5665-16.7581-1.992-2.15941 1.711-2.47302 4.4518-2.23361 6.6739z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ViewStreamIcon.tsx
import { jsx as jsx105 } from "react/jsx-runtime";
var ViewStreamIcon = (props) => /* @__PURE__ */ jsx105("svg", { ...props, children: /* @__PURE__ */ jsx105(
  "path",
  {
    d: "m18.9737 7.86457c1.8137 1.68881 3.0919 3.22143 3.0237 4.17933.0942 1.3301-2.3864 3.6597-4.787 5.4039-3.6869 2.679-9.13851 1.4637-12.1841-1.3722-1.81366-1.6888-3.0919-3.2215-3.0237-4.1793-.09423-1.3301 2.38644-3.65971 4.78695-5.40395 3.68695-2.67898 9.13855-1.46365 12.18415 1.37222zm-4.5254.78409c-1.8472-1.58151-5.06662-1.24058-6.36652.68474-1.29991 1.9253-1.26491 6.8893 3.59262 6.9581 4.8576.0688 5.3837-5.4084 2.7739-7.64284zm-2.7724 5.79044c2.8097.0393 3.1141-3.09 1.6045-4.3666-1.0685-.90364-2.9307-.70885-3.68257.3912-.7519 1.1-.73165 3.9362 2.07807 3.9754z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/WandIcon.tsx
import { jsx as jsx106 } from "react/jsx-runtime";
var WandIcon = (props) => /* @__PURE__ */ jsx106("svg", { ...props, children: /* @__PURE__ */ jsx106(
  "path",
  {
    d: "m14.48 4.5c-.6775 0-1.23-.55333-1.23-1.23v-.04c0-.67583.5525-1.23 1.23-1.23s1.23.55333 1.23 1.23v.04c0 .67667-.5525 1.23-1.23 1.23zm5 1.25c-.6775 0-1.23-.55333-1.23-1.23v-.04c0-.67667.5525-1.23 1.23-1.23s1.23.55333 1.23 1.23v.04c0 .67667-.5525 1.23-1.23 1.23zm.0583 3.73083v-.03916c0-.6775.5542-1.23084 1.23-1.23084.6775 0 1.2317.55334 1.2317 1.23084v.03916c0 .67587-.5542 1.22997-1.2317 1.22997-.6758 0-1.23-.5541-1.23-1.22997zm-.0583 3.76917c.6775 0 1.23.5542 1.23 1.23v.04c0 .6767-.5525 1.23-1.23 1.23s-1.23-.5542-1.23-1.23v-.04c0-.6767.5525-1.23 1.23-1.23zm-9.96083-7.5c-.6775 0-1.23-.55333-1.23-1.23v-.04c0-.67667.5525-1.23 1.23-1.23.67753 0 1.23003.55333 1.23003 1.23v.04c0 .67667-.5525 1.23-1.23003 1.23zm-.01917 3.73083v.03834c0 .67663-.55417 1.23163-1.23 1.23163-.6775 0-1.23167-.555-1.23167-1.23163v-.03834c0-.6775.55417-1.23 1.23167-1.23.67583-.00083 1.23.5525 1.23 1.23zm4.98 5.01917c.6775 0 1.23.5542 1.23 1.23v.04c0 .6758-.5525 1.23-1.23 1.23s-1.23-.5542-1.23-1.23v-.04c0-.6758.5525-1.23 1.23-1.23zm-1.23-5c0 .6883.5608 1.25 1.25 1.25.6883 0 1.25-.5617 1.25-1.25 0-.69-.5617-1.25-1.25-1.25-.6892 0-1.25.56-1.25 1.25zm-10.885 12.135c-.48667-.4867-.48667-1.2817 0-1.7675l8.76-8.76c-.2333-.4892-.375-1.03-.375-1.6075 0-2.07167 1.6783-3.75 3.75-3.75 2.0708 0 3.75 1.67833 3.75 3.75 0 2.0717-1.6792 3.75-3.75 3.75-.5775 0-1.1192-.1417-1.6083-.375l-8.75837 8.76c-.2425.2425-.56416.365-.88333.365-.32083 0-.64167-.1225-.88417-.365z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/ZoomInIcon.tsx
import { jsx as jsx107 } from "react/jsx-runtime";
var ZoomInIcon = (props) => /* @__PURE__ */ jsx107("svg", { ...props, children: /* @__PURE__ */ jsx107(
  "path",
  {
    d: "m4.34613 4.96934c4.1006-4.848919 14.15257-3.87134 14.02637 5.15386-.0251 1.7929-.4336 3.2246-1.0999 4.3489 1.4827 1.707 4.4974 5.2485 4.5332 5.8655.0475.8186-.696 1.5695-1.6161 1.576-.6833.0048-3.0833-2.6413-4.7621-4.4923-.1897-.2092-.3703-.4082-.538-.5921-3.2216 1.8911-7.83109 1.273-10.01973-.2231-3.53338-2.4152-3.42617-8.20463-.52374-11.63676zm4.93672 1.64027c0-.55228.44772-1 1.00005-1 .5522 0 1 .44772 1 1v2.43714h2.437c.5523 0 1 .44771 1 .99995 0 .5523-.4477 1-1 1h-2.437v2.4371c0 .5523-.4478 1-1 1-.55233 0-1.00005-.4477-1.00005-1v-2.4371h-2.4371c-.55229 0-1-.4477-1-1 0-.55224.44771-.99995 1-.99995h2.4371z",
    fill: "currentColor",
    fillRule: "evenodd"
  }
) });

// src/components/Icon/icons/ZoomOutIcon.tsx
import { jsx as jsx108 } from "react/jsx-runtime";
var ZoomOutIcon = (props) => /* @__PURE__ */ jsx108("svg", { ...props, children: /* @__PURE__ */ jsx108(
  "path",
  {
    d: "m4.34613 4.96934c4.10059-4.848919 14.15257-3.87134 14.02637 5.15386-.0251 1.7929-.4336 3.2246-1.0999 4.3489 1.4827 1.707 4.4974 5.2485 4.5332 5.8655.0475.8186-.696 1.5695-1.6161 1.576-.6833.0048-3.0833-2.6413-4.7621-4.4923-.1898-.2092-.3703-.4082-.538-.5921-3.2216 1.8911-7.83109 1.273-10.01973-.2231-3.53339-2.4152-3.42617-8.20463-.52374-11.63676zm1.49962 5.07736c0 .5523.44771 1 1 1h6.87415c.5523 0 1-.4477 1-1 0-.55224-.4477-.99995-1-.99995h-6.87415c-.55229 0-1 .44771-1 .99995z",
    fill: "currentColor"
  }
) });

// src/components/Icon/icons/GroupRecordIcon.tsx
import { jsx as jsx109, jsxs as jsxs6 } from "react/jsx-runtime";
var GroupRecordIcon = (props) => /* @__PURE__ */ jsxs6("svg", { ...props, children: [
  /* @__PURE__ */ jsx109(
    "path",
    {
      d: "M 7.6660156 13.333984 C 4.6033687 13.333984 1.8196082 15.261562 0.73828125 18.130859 C 0.52228147 18.702859 0.54974052 19.324766 0.81640625 19.884766 C 1.091068 20.460765 1.5970887 20.908628 2.2050781 21.111328 C 3.9717464 21.700628 5.8100275 22 7.6660156 22 C 9.4455743 22 11.208675 21.721851 12.908203 21.179688 C 12.33347 20.258643 12 19.170101 12 18 C 12 17.017544 12.235211 16.092267 12.650391 15.275391 C 11.311039 14.056643 9.5409138 13.333984 7.6660156 13.333984 z ",
      fill: "currentColor",
      id: "path1080"
    }
  ),
  /* @__PURE__ */ jsx109(
    "path",
    {
      d: "M 16 10 C 14.110702 10 12.33098 10.736657 10.988281 11.972656 C 12.030725 12.37129 12.995063 12.950091 13.835938 13.675781 C 14.913535 12.638129 16.378956 12 18 12 C 19.911892 12 21.607843 12.886783 22.705078 14.271484 C 21.496969 11.695436 18.871278 10 16 10 z ",
      fill: "currentColor",
      id: "path1078"
    }
  ),
  /* @__PURE__ */ jsx109(
    "path",
    {
      d: "M 11.3332,8.3333 C 11.3332,10.3583 9.69162,12 7.66659,12 5.64155,12 3.99993,10.3583 3.99993,8.3333 c 0,-2.02504 1.64162,-3.66666 3.66666,-3.66666 2.02503,0 3.66661,1.64162 3.66661,3.66666 z",
      fill: "currentColor",
      id: "path1076"
    }
  ),
  /* @__PURE__ */ jsx109(
    "path",
    {
      d: "m 19.6666,4.99997 c 0,2.02504 -1.6417,3.66666 -3.6667,3.66666 -2.025,0 -3.6667,-1.64162 -3.6667,-3.66666 0,-2.02504 1.6417,-3.66666 3.6667,-3.66666 2.025,0 3.6667,1.64162 3.6667,3.66666 z",
      fill: "currentColor",
      id: "path90"
    }
  ),
  /* @__PURE__ */ jsx109(
    "path",
    {
      clipRule: "evenodd",
      d: "M13.2 18C13.2 15.3534 15.3534 13.2 18 13.2C20.6466 13.2 22.8 15.3534 22.8 18C22.8 20.6466 20.6466 22.8 18 22.8C15.3534 22.8 13.2 20.6466 13.2 18ZM14.1 18C14.1 20.1504 15.8496 21.9 18 21.9C20.1504 21.9 21.9 20.1504 21.9 18C21.9 15.8496 20.1504 14.1 18 14.1C15.8496 14.1 14.1 15.8496 14.1 18ZM21 18C21 19.6569 19.6568 21 18 21C16.3431 21 15 19.6569 15 18C15 16.3432 16.3431 15 18 15C19.6568 15 21 16.3432 21 18Z",
      fill: "currentColor",
      fillRule: "evenodd",
      id: "path94"
    }
  )
] });

// src/components/Icon/icons/InviteToRecordIcon.tsx
import { jsx as jsx110, jsxs as jsxs7 } from "react/jsx-runtime";
var InviteToRecordIcon = (props) => /* @__PURE__ */ jsxs7("svg", { ...props, children: [
  /* @__PURE__ */ jsxs7(
    "g",
    {
      clipPath: "url(#clip0_23610_312)",
      id: "g8",
      children: [
        /* @__PURE__ */ jsx110(
          "path",
          {
            d: "M 3.8007812 2 C 2.8071822 2 2 2.8071822 2 3.8007812 L 2 5.5996094 L 2 15.199219 C 2 17.186417 3.6124214 18.800781 5.5996094 18.800781 L 12.054688 18.800781 C 12.019999 18.538708 12 18.271851 12 18 C 12 14.676003 14.676003 12 18 12 C 18.271851 12 18.538708 12.019999 18.800781 12.054688 L 18.800781 7.4003906 C 18.800781 6.4067916 17.993599 5.5996094 17 5.5996094 L 10.400391 5.5996094 L 8.3554688 2.5332031 C 8.133479 2.1996035 7.7582215 2 7.3574219 2 L 3.8007812 2 z ",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            clipRule: "evenodd",
            d: "M13.2 18C13.2 15.3534 15.3534 13.2 18 13.2C20.6466 13.2 22.8 15.3534 22.8 18C22.8 20.6466 20.6466 22.8 18 22.8C15.3534 22.8 13.2 20.6466 13.2 18ZM14.1 18C14.1 20.1504 15.8496 21.9 18 21.9C20.1504 21.9 21.9 20.1504 21.9 18C21.9 15.8496 20.1504 14.1 18 14.1C15.8496 14.1 14.1 15.8496 14.1 18ZM21 18C21 19.6569 19.6568 21 18 21C16.3431 21 15 19.6569 15 18C15 16.3432 16.3431 15 18 15C19.6568 15 21 16.3432 21 18Z",
            fill: "currentColor",
            fillRule: "evenodd",
            id: "path6"
          }
        )
      ]
    }
  ),
  /* @__PURE__ */ jsx110("defs", { id: "defs13", children: /* @__PURE__ */ jsx110("clipPath", { id: "clip0_23610_312", children: /* @__PURE__ */ jsx110(
    "rect",
    {
      fill: "white",
      height: "23.9999",
      id: "rect10",
      width: "23.9999"
    }
  ) }) })
] });

// src/components/Icon/icons/CollectionIcon.tsx
import { jsx as jsx111 } from "react/jsx-runtime";
var CollectionIcon = (props) => /* @__PURE__ */ jsx111(
  "svg",
  {
    ...props,
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx111(
      "path",
      {
        d: "M8.00037 13.111C8.61387 13.1112 9.11072 13.6088 9.11072 14.2224C9.11066 14.8358 8.61383 15.3335 8.00037 15.3337C7.38675 15.3337 6.88909 14.836 6.88904 14.2224C6.88904 13.6087 7.38672 13.111 8.00037 13.111ZM4.4447 13.3337C4.93552 13.3338 5.33337 13.7315 5.33337 14.2224C5.33332 14.7132 4.93549 15.1109 4.4447 15.111C3.95382 15.111 3.55608 14.7132 3.55603 14.2224C3.55603 13.7314 3.95378 13.3337 4.4447 13.3337ZM11.556 13.3337C12.0468 13.3339 12.4447 13.7316 12.4447 14.2224C12.4446 14.7131 12.0467 15.1108 11.556 15.111C11.0651 15.111 10.6664 14.7132 10.6664 14.2224C10.6664 13.7314 11.0651 13.3337 11.556 13.3337ZM12.6664 1.77802C14.0164 1.77802 15.1107 2.87232 15.1107 4.22235V9.55536C15.1107 10.9054 14.0164 11.9997 12.6664 11.9997H3.33337C1.98335 11.9997 0.889038 10.9054 0.889038 9.55536V4.22235C0.889038 2.87232 1.98335 1.77802 3.33337 1.77802H12.6664Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PlaylistIcon.tsx
import { jsx as jsx112 } from "react/jsx-runtime";
var PlaylistIcon = (props) => /* @__PURE__ */ jsx112(
  "svg",
  {
    ...props,
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M13.4127 11.582C13.8595 11.5821 14.2223 11.9801 14.2223 12.4707C14.2222 12.9612 13.8595 13.3593 13.4127 13.3594H5.23401C4.78722 13.3594 4.42455 12.9613 4.42444 12.4707C4.42444 11.98 4.78715 11.582 5.23401 11.582H13.4127ZM2.15588 5.62109C2.38676 5.51013 2.6605 5.54203 2.86096 5.70215L5.08362 7.47949C5.24149 7.60572 5.33362 7.79739 5.33362 8C5.3336 8.20261 5.24152 8.39421 5.08362 8.52051L2.86096 10.2979C2.6605 10.4579 2.38677 10.4899 2.15588 10.3789C1.92451 10.2682 1.77795 10.0334 1.77795 9.77734V6.22266C1.77795 5.96657 1.92451 5.73176 2.15588 5.62109ZM13.4127 7.33301C13.8596 7.33301 14.2223 7.73199 14.2223 8.22266C14.2221 8.71312 13.8594 9.11133 13.4127 9.11133H7.4762C7.02948 9.11132 6.66684 8.71312 6.66663 8.22266C6.66663 7.732 7.02935 7.33302 7.4762 7.33301H13.4127ZM13.4127 2.66699C13.8595 2.66704 14.2223 3.06503 14.2223 3.55566C14.2222 4.04625 13.8595 4.44428 13.4127 4.44434H5.23401C4.78718 4.44434 4.42449 4.04628 4.42444 3.55566C4.42444 3.065 4.78715 2.66699 5.23401 2.66699H13.4127Z",
        fill: "currentColor",
        id: "Vector"
      }
    )
  }
);

// src/components/Icon/Icon.tsx
import { jsx as jsx113 } from "react/jsx-runtime";
var iconSizeMap = {
  sm: "12",
  md: "16",
  lg: "24",
  xl: "32",
  xxl: "48"
};
var Icon = ({
  color = "currentColor",
  size = "lg",
  style,
  type,
  ...otherProps
}) => {
  if (isNil2(type)) {
    throw new Error("An Icon component requires a `type` prop to be provided");
  }
  if (isNil2(iconMap[type])) {
    throw new Error(`Type "${type}" does not exist, please update type prop in Icon component.`);
  }
  if (isNil2(iconSizeMap[size])) {
    throw new Error(`Size "${size}" does not exist, please update size prop in Icon component.`);
  }
  const IconElement = icons_exports[iconMap[type]];
  const defaultStyle = { verticalAlign: "middle", ...style };
  return /* @__PURE__ */ jsx113(
    IconElement,
    {
      "aria-hidden": "true",
      color,
      height: `${iconSizeMap[size]}px`,
      role: "presentation",
      style: defaultStyle,
      viewBox: "0 0 24 24",
      width: `${iconSizeMap[size]}px`,
      xmlns: "http://www.w3.org/2000/svg",
      ...otherProps
    }
  );
};
Icon.displayName = "Icon_VHS";

// src/components/Button/ButtonContent.tsx
import { Fragment as Fragment2, jsx as jsx114, jsxs as jsxs8 } from "react/jsx-runtime";
var ButtonLoadingContainer = styled.span`
  display: flex;
  justify-content: ${({ $textAlign }) => {
  if ($textAlign === "left") {
    return "start";
  }
  if ($textAlign === "right") {
    return "end";
  }
  return "center";
}};
  position: absolute;
`;
var ButtonLabelContainer = styled.div`
  align-items: center;
  display: inline-flex;
  flex-direction: ${({ $iconPosition }) => $iconPosition === "end" ? "row-reverse" : "row"};
  flex-grow: 1;
  gap: ${({ $size, theme: theme2 }) => {
  if ($size === "lg") {
    return theme2.spacing.space03;
  }
  return theme2.spacing.space02;
}};
  justify-content: center;
  opacity: ${({ $isLoading }) => $isLoading ? 0 : 1};
  position: relative;
`;
var ButtonContentLabel = styled.div`
  align-items: center;
  display: inline-flex;
  flex-grow: 1;
  justify-content: ${({ $textAlign }) => {
  if ($textAlign === "left") {
    return "start";
  }
  if ($textAlign === "right") {
    return "end";
  }
  return "center";
}};
`;
var IconContainer = styled.div`
  display: flex;

  & > svg {
    max-height: ${({ $size }) => $size};
    max-width: ${({ $size }) => $size};
  }
`;
var iconSizeMap2 = Object.freeze({
  lg: "md",
  md: "md",
  sm: "sm"
});
var iconContainerSizeMap = {
  lg: "16px",
  md: "16px",
  sm: "12px"
};
var ButtonContent = ({
  children,
  icon,
  iconName,
  iconPosition,
  isLoading,
  size,
  textAlign
}) => {
  const renderButtonIcon = () => {
    if (isNotNil2(icon) && isNotNil2(iconName)) {
      throw new Error(
        `Do not use both icon and iconName props. If you are using a VHS icon, pass its type as a string to iconName. If you are using a custom icon, pass it as a component to the icon prop. `
      );
    }
    if (isNotNil2(icon)) {
      return /* @__PURE__ */ jsx114(
        IconContainer,
        {
          $size: iconContainerSizeMap[size],
          "data-testid": "icon-container",
          children: icon
        }
      );
    }
    if (isNotNil2(iconName)) {
      return /* @__PURE__ */ jsx114(
        Icon,
        {
          "data-testid": "iconName-icon",
          size: iconSizeMap2[size],
          type: iconName
        }
      );
    }
    return null;
  };
  return /* @__PURE__ */ jsxs8(Fragment2, { children: [
    isLoading ? /* @__PURE__ */ jsxs8(
      ButtonLoadingContainer,
      {
        $textAlign: textAlign,
        "data-testid": "button-content-loading",
        children: [
          /* @__PURE__ */ jsx114(
            LiveMessage,
            {
              "aria-live": "assertive",
              message: "Button has been disabled while action is in progress"
            }
          ),
          /* @__PURE__ */ jsx114(
            Icon,
            {
              size: iconSizeMap2[size],
              type: "spinner"
            }
          )
        ]
      }
    ) : null,
    /* @__PURE__ */ jsxs8(
      ButtonLabelContainer,
      {
        $iconPosition: iconPosition,
        $isLoading: isLoading,
        $size: size,
        children: [
          /* @__PURE__ */ jsx114(Fragment2, { children: renderButtonIcon() }),
          isNotNil2(children) ? /* @__PURE__ */ jsx114(
            ButtonContentLabel,
            {
              $textAlign: textAlign,
              "data-testid": "button-content-label",
              children
            }
          ) : null
        ]
      }
    )
  ] });
};

// src/components/Button/ButtonStyledComponent.tsx
import { styled as styled2, css as css2 } from "styled-components";
import { darken, transparentize } from "polished";
var hoverDarken = "0.04";
var activeDarken = "0.08";
var hoverTransparency = 0.9;
var activeTransparency = 0.8;
var buttonResetStyle = css2`
  align-items: center;
  background-color: transparent;
  background-image: none;
  border: none;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  justify-content: center;
  line-height: 1;
  padding: 0;
  text-align: left;
  vertical-align: middle;

  /*
  * Fixes input types in iOS. Higher specificity for Safari to deal with
  * Normalize defaults
  */
  &,
  &[type='button'],
  &[type='reset'],
  &[type='submit'] {
    appearance: none;
  }

  /* Normalize focus behavior of Firefox */
  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  /* add some default affordance for a focused button */
  &:focus {
    outline: thin dotted #999999;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`;
var primaryHoverStyle = css2`
  background-color: ${({ $buttonColor }) => darken(hoverDarken, $buttonColor)};
  border-color: ${({ $buttonColor }) => darken(hoverDarken, $buttonColor)};
`;
var primaryActiveStyle = css2`
  background-color: ${({ $buttonColor }) => darken(activeDarken, $buttonColor)};
  border-color: ${({ $buttonColor }) => darken(activeDarken, $buttonColor)};
`;
var primaryFocusStyle = css2`
  box-shadow: inset 0 0 0 1px white;
`;
var primaryStyle = css2`
  background-color: ${({ $buttonColor }) => $buttonColor};
  border-color: ${({ $buttonColor }) => $buttonColor};
  color: white;

  &:focus-visible {
    ${primaryFocusStyle}
  }

  &:hover:not([aria-disabled='true']) {
    ${primaryHoverStyle}
  }

  &:active:not([aria-disabled='true']) {
    ${primaryActiveStyle}
  }
`;
var secondaryHoverStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(hoverTransparency, $buttonColor)};
`;
var secondaryActiveStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(activeTransparency, $buttonColor)};
`;
var secondaryFocusStyle = css2`
  box-shadow:
    inset 0 0 0 1px white,
    inset 0 0 0 2px ${({ $buttonColor }) => $buttonColor};
`;
var secondaryStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(1, $buttonColor)};
  border-color: ${({ $buttonColor }) => $buttonColor};
  color: ${({ $buttonColor }) => $buttonColor};

  &:focus-visible {
    ${secondaryFocusStyle}
  }

  &:hover:not([aria-disabled='true']) {
    ${secondaryHoverStyle}
  }

  &:active:not([aria-disabled='true']) {
    ${secondaryActiveStyle}
  }
`;
var tertiaryHoverStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(hoverTransparency, $buttonColor)};
`;
var tertiaryActiveStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(activeTransparency, $buttonColor)};
`;
var tertiaryFocusStyle = css2`
  border-color: ${({ $buttonColor }) => $buttonColor};
  box-shadow:
    inset 0 0 0 1px white,
    inset 0 0 0 2px ${({ $buttonColor }) => $buttonColor};
`;
var tertiaryStyle = css2`
  background-color: ${({ $buttonColor }) => transparentize(1, $buttonColor)};
  border-color: ${({ $buttonColor }) => transparentize(1, $buttonColor)};
  color: ${({ $buttonColor }) => $buttonColor};

  &:focus-visible {
    ${tertiaryFocusStyle}
  }

  &:hover:not([aria-disabled='true']) {
    ${tertiaryHoverStyle}
  }

  &:active:not([aria-disabled='true']) {
    ${tertiaryActiveStyle}
  }
`;
var squareStyle = css2`
  border-radius: 0;
`;
var disabledButtonStyle = css2`
  opacity: 0.4;
`;
var largeButtonStyle = css2`
  font-size: 16px;
  line-height: 24px;
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space02} ${theme2.spacing.space04}`};
`;
var mediumButtonStyle = css2`
  font-size: 14px;
  line-height: 20px;
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space02} ${theme2.spacing.space03}`};
`;
var smallButtonStyle = css2`
  font-size: 12px;
  line-height: 18px;
  padding: ${({ theme: theme2 }) => `2px ${theme2.spacing.space02}`};
`;
var fullWidthButtonStyle = css2`
  width: 100%;
`;
var buttonStyle = css2`
  ${buttonResetStyle}
  border-radius: ${({ theme: theme2 }) => theme2.button.borderRadius};
  border-style: solid;
  border-width: 1px;
  cursor: pointer;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  text-decoration: none;
  transition: all ${({ theme: theme2 }) => theme2.duration.productive.moderate};

  &:focus {
    outline: none;
  }

  &[aria-disabled='true'] {
    ${disabledButtonStyle}
  }

  /* border-radius */
  ${({ $square }) => $square === true && squareStyle}

  /* label wrapping */
  white-space: ${({ $labelWrap }) => $labelWrap === true ? "wrap" : "nowrap"};

  /* apply various button variant styles */
  ${({ $variant }) => {
  if ($variant === "primary" || $variant === "dangerous") {
    return primaryStyle;
  }
  if ($variant === "secondary") {
    return secondaryStyle;
  }
  if ($variant === "tertiary") {
    return tertiaryStyle;
  }
  return void 0;
}}

  /* primarily used for documentation/debugging */
  ${({ $variant, $forceState }) => {
  if ($variant === "primary" || $variant === "dangerous") {
    if ($forceState === "hover") {
      return primaryHoverStyle;
    }
    if ($forceState === "active") {
      return primaryActiveStyle;
    }
    if ($forceState === "focus") {
      return primaryFocusStyle;
    }
  }
  if ($variant === "secondary") {
    if ($forceState === "hover") {
      return secondaryHoverStyle;
    }
    if ($forceState === "active") {
      return secondaryActiveStyle;
    }
    if ($forceState === "focus") {
      return secondaryFocusStyle;
    }
  }
  if ($variant === "tertiary") {
    if ($forceState === "hover") {
      return tertiaryHoverStyle;
    }
    if ($forceState === "active") {
      return tertiaryActiveStyle;
    }
    if ($forceState === "focus") {
      return tertiaryFocusStyle;
    }
  }
  return void 0;
}}

  ${({ $size }) => {
  if ($size === "lg") {
    return largeButtonStyle;
  }
  if ($size === "md") {
    return mediumButtonStyle;
  }
  if ($size === "sm") {
    return smallButtonStyle;
  }
  return void 0;
}}

  ${({ $fullWidth }) => {
  if ($fullWidth) {
    return fullWidthButtonStyle;
  }
  return void 0;
}}
`;
var ButtonStyledComponent = styled2.button`
  ${({ $noStyle }) => $noStyle ? buttonResetStyle : buttonStyle}
`;

// src/components/Button/getButtonColor.ts
import { isNonEmptyString } from "@wistia/type-guards";
var getButtonColor = (colorOverride, variant, allThemeColors) => {
  if (isNonEmptyString(colorOverride)) {
    return colorOverride;
  }
  if (variant === "primary") {
    return allThemeColors.brandBlue500;
  }
  if (variant === "secondary") {
    return allThemeColors.grey900;
  }
  if (variant === "tertiary") {
    return allThemeColors.grey900;
  }
  if (variant === "dangerous") {
    return allThemeColors.red600;
  }
  return allThemeColors.brandBlue500;
};

// src/components/Button/Button.tsx
import { jsx as jsx115 } from "react/jsx-runtime";
var Button = forwardRef(
  ({
    children,
    disabled = false,
    forceState,
    colorOverride,
    fullWidth = false,
    icon,
    iconName,
    iconPosition = "start",
    label,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    labelWrap = false,
    isLoading = false,
    noStyle = false,
    onClick,
    size = "lg",
    square = false,
    textAlign = "center",
    type = "button",
    variant = "primary",
    ...otherProps
  }, ref) => {
    const isAriaDisabled = () => isLoading || disabled;
    const handleClick = (event) => {
      if (isAriaDisabled()) {
        event.preventDefault();
        return;
      }
      if (isNotNil3(onClick)) {
        onClick(event);
      }
    };
    const buttonColor = getButtonColor(colorOverride, variant, useThemeColor());
    const getContent = () => {
      if (isNotNil3(children)) {
        return children;
      }
      if (isNotNil3(label)) {
        return label;
      }
      return null;
    };
    return /* @__PURE__ */ jsx115(
      ButtonStyledComponent,
      {
        ref,
        $buttonColor: buttonColor,
        $forceState: forceState,
        $fullWidth: fullWidth,
        $iconPosition: iconPosition,
        $isLoading: isLoading,
        $labelWrap: labelWrap,
        $noStyle: noStyle,
        $size: size,
        $square: square,
        $textAlign: textAlign,
        $variant: variant,
        "aria-disabled": isAriaDisabled() || void 0,
        disabled: false,
        onClick: handleClick,
        type,
        ...otherProps,
        children: /* @__PURE__ */ jsx115(
          ButtonContent,
          {
            icon,
            iconName,
            iconPosition,
            isLoading,
            size,
            textAlign,
            children: getContent()
          }
        )
      }
    );
  }
);
Button.displayName = "Button_VHS";

// src/private/helpers/isServer/isServer.ts
var isServer = () => typeof window === "undefined" || typeof document === "undefined";

// src/components/Toast/Toast.tsx
import { jsx as jsx116, jsxs as jsxs9 } from "react/jsx-runtime";
var toastVariants = ["success", "warning", "error", "dangerous"];
var defaultVariant = "success";
var defaultActionLabel = "Dismiss";
var defaultTimeout = 3e3;
var getAccentColor = (colorOverride, variant, allThemeColors) => {
  if (isNonEmptyString2(colorOverride)) {
    return colorOverride;
  }
  if (!toastVariants.includes(variant)) {
    throw new Error(
      `Variant "${variant}" not supported, please update variant prop in Toast component.`
    );
  }
  if (variant === "dangerous") {
    return allThemeColors.error600;
  }
  if (variant === "error") {
    return allThemeColors.error600;
  }
  if (variant === "warning") {
    return allThemeColors.warning500;
  }
  return allThemeColors.brandBlue500;
};
var ToastComponent = styled3.div`
  align-items: center;
  background: white;
  border-left-color: ${({ $accentColor }) => $accentColor};
  border-left-style: solid;
  border-left-width: 8px; /* accent border */
  box-shadow: ${({ theme: theme2 }) => theme2.shadow.elevation2};
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space04};
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space04} ${theme2.spacing.space04} ${theme2.spacing.space04} ${theme2.spacing.space04}`};
`;
var ToastContent = styled3.div`
  align-self: center;
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  flex-grow: 1;
  font-size: 16px;

  /* in case a <p> tag is passed as Toast message this will
  remove any margins that may arrive with children */
  > * {
    margin: 0;
  }
`;
var ToastPortal = ({ children }) => {
  if (isServer()) {
    return null;
  }
  const container = document.getElementById("toast-portal");
  if (!container) {
    return null;
  }
  return createPortal(children, container);
};
var displayTimer = null;
var Toast = memo(
  ({
    actionInterceptor = void 0,
    actionLabel = defaultActionLabel,
    colorOverride = void 0,
    renderInPlace = false,
    isOpen = false,
    message,
    onClose,
    timeout: timeout2 = defaultTimeout,
    variant = defaultVariant,
    ...otherProps
  }) => {
    const [showToast, setShowToast] = useState2(isOpen);
    const isErrorOrWarning = variant === "error" || variant === "warning";
    const ariaLive = isErrorOrWarning ? "assertive" : "polite";
    const stopTimer = useCallback(() => {
      clearTimeout(displayTimer ?? void 0);
      displayTimer = null;
    }, []);
    const closeToast = () => {
      stopTimer();
      onClose();
    };
    const startTimer = () => {
      displayTimer = setTimeout(() => {
        closeToast();
      }, timeout2);
    };
    useEffect(() => {
      if (showToast && !renderInPlace) {
        startTimer();
      }
      return () => {
        if (coerceToBoolean(displayTimer)) {
          stopTimer();
        }
      };
    });
    useEffect(() => {
      setShowToast(isOpen);
      return () => {
        if (coerceToBoolean(displayTimer)) {
          stopTimer();
        }
      };
    }, [isOpen, stopTimer, setShowToast]);
    const accentColor = getAccentColor(colorOverride, variant, useThemeColor());
    const ToastWrapper = renderInPlace ? Fragment3 : ToastPortal;
    return /* @__PURE__ */ jsx116(ToastWrapper, { children: /* @__PURE__ */ jsx116(
      ToastAnimation,
      {
        in: showToast,
        renderInPlace,
        children: /* @__PURE__ */ jsxs9(
          ToastComponent,
          {
            $accentColor: accentColor,
            "aria-hidden": !showToast,
            onMouseEnter: stopTimer,
            onMouseLeave: startTimer,
            ...otherProps,
            children: [
              /* @__PURE__ */ jsxs9(ToastContent, { "data-testid": "toast-message", children: [
                /* @__PURE__ */ jsx116(
                  LiveMessage2,
                  {
                    "aria-live": ariaLive,
                    message
                  }
                ),
                message
              ] }),
              /* @__PURE__ */ jsx116(
                Button,
                {
                  colorOverride: accentColor,
                  "data-testid": "toast-action",
                  label: actionLabel,
                  onClick: actionInterceptor ?? closeToast,
                  variant: "tertiary"
                }
              )
            ]
          },
          message
        )
      }
    ) });
  }
);
Toast.displayName = "Toast_VHS";

// src/components/Toast/ToastContext.tsx
import { createContext as createContext2 } from "react";
var ToastContext = createContext2(null);

// src/components/Toast/ToastProvider.tsx
import { Fragment as Fragment4, jsx as jsx117, jsxs as jsxs10 } from "react/jsx-runtime";
var ToastProvider = ({ children }) => {
  const [toastActionInterceptor, setToastActionInterceptor] = useState3(
    void 0
  );
  const [toastActionLabel, setToastActionLabel] = useState3("");
  const [toastColorOverride, setToastColorOverride] = useState3(void 0);
  const [toastMessage, setToastMessage] = useState3("");
  const [toastShown, setToastShown] = useState3(false);
  const [toastTimeout, setToastTimeout] = useState3(void 0);
  const [toastVariant, setToastVariant] = useState3(void 0);
  const showToast = useCallback2(
    ({
      message,
      variant = defaultVariant,
      actionLabel = defaultActionLabel,
      actionInterceptor,
      timeout: timeout2 = defaultTimeout,
      colorOverride
    }) => {
      setToastActionInterceptor(actionInterceptor ? () => actionInterceptor : void 0);
      setToastActionLabel(actionLabel);
      setToastColorOverride(colorOverride);
      setToastMessage(message);
      setToastShown(true);
      setToastTimeout(timeout2);
      setToastVariant(variant);
    },
    []
  );
  return /* @__PURE__ */ jsxs10(Fragment4, { children: [
    /* @__PURE__ */ jsx117(ToastContext.Provider, { value: showToast, children }),
    /* @__PURE__ */ jsx117(
      Toast,
      {
        actionInterceptor: toastActionInterceptor,
        actionLabel: toastActionLabel,
        colorOverride: toastColorOverride,
        isOpen: toastShown,
        message: toastMessage,
        onClose: () => setToastShown(false),
        timeout: toastTimeout,
        variant: toastVariant
      }
    ),
    /* @__PURE__ */ jsx117("div", { id: "toast-portal" })
  ] });
};

// src/components/ModalBase/ModalProvider.tsx
import { useRef, useCallback as useCallback3, useMemo } from "react";
import { isFunction } from "@wistia/type-guards";

// src/components/ModalBase/ModalProviderContext.tsx
import { createContext as createContext3, useContext as useContext2 } from "react";
var ModalProviderContext = createContext3({});
var useModalProviderContext = () => useContext2(ModalProviderContext);

// src/components/ModalBase/ModalProvider.tsx
import { jsx as jsx118 } from "react/jsx-runtime";
var ModalProvider = ({ children }) => {
  const modalQueueRef = useRef(/* @__PURE__ */ new Set());
  const modalQueue = modalQueueRef.current;
  const isFirstCallback = useCallback3(
    (setIsShown) => modalQueue.values().next().value === setIsShown,
    [modalQueue]
  );
  const showFirstModal = useCallback3(() => {
    const setIsShown = modalQueue.values().next().value;
    if (isFunction(setIsShown)) {
      setIsShown(true);
    }
  }, [modalQueue]);
  const hideFirstModal = useCallback3(() => {
    const setIsShown = modalQueue.values().next().value;
    if (isFunction(setIsShown)) {
      setIsShown(false);
    }
  }, [modalQueue]);
  const attemptShowModal = useCallback3(
    (setIsShown) => {
      modalQueue.add(setIsShown);
      if (isFirstCallback(setIsShown)) {
        showFirstModal();
      }
    },
    [modalQueue, isFirstCallback, showFirstModal]
  );
  const attemptHideModal = useCallback3(
    (setIsShown) => {
      modalQueue.add(setIsShown);
      if (isFirstCallback(setIsShown)) {
        hideFirstModal();
        modalQueue.delete(setIsShown);
        showFirstModal();
      } else {
        modalQueue.delete(setIsShown);
      }
    },
    [modalQueue, isFirstCallback, hideFirstModal, showFirstModal]
  );
  const removeModalFromQueue = useCallback3(
    (setIsShown) => {
      modalQueue.delete(setIsShown);
    },
    [modalQueue]
  );
  const modalProviderCoordinator = useMemo(
    () => ({
      attemptShowModal,
      attemptHideModal,
      removeModalFromQueue
    }),
    [attemptShowModal, attemptHideModal, removeModalFromQueue]
  );
  return /* @__PURE__ */ jsx118(ModalProviderContext.Provider, { value: modalProviderCoordinator, children });
};

// src/css/GlobalStyle.tsx
import { createGlobalStyle } from "styled-components";

// src/css/normalize.tsx
import { css as css3 } from "styled-components";
var normalize = css3`
  /* normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */

  /* Document
    ========================================================================== */

  /**
  * 1. Correct the line height in all browsers.
  * 2. Prevent adjustments of font size after orientation changes in iOS.
  */

  html {
    line-height: 1.15; /* 1 */
    text-size-adjust: 100%; /* 2 */
  }

  /* Sections
    ========================================================================== */

  /**
  * Remove the margin in all browsers.
  */

  body {
    margin: 0;
  }

  /**
  * Render the 'main' element consistently in IE.
  */

  main {
    display: block;
  }

  /**
  * Correct the font size and margin on 'h1' elements within 'section' and
  * 'article' contexts in Chrome, Firefox, and Safari.
  */

  h1 {
    font-size: 2em;
    margin: 0.67em 0;
  }

  /* Grouping content
    ========================================================================== */

  /**
  * 1. Add the correct box sizing in Firefox.
  * 2. Show the overflow in Edge and IE.
  */

  hr {
    box-sizing: content-box; /* 1 */
    height: 0; /* 1 */
    overflow: visible; /* 2 */
  }

  /**
  * 1. Correct the inheritance and scaling of font size in all browsers.
  * 2. Correct the odd 'em' font sizing in all browsers.
  */
  /* stylelint-disable */
  pre {
    font-family: monospace, monospace; /* 1 */
    font-size: 1em; /* 2 */
  }
  /* stylelint-enable */

  /* Text-level semantics
    ========================================================================== */

  /**
  * Remove the gray background on active links in IE 10.
  */

  a {
    background-color: transparent;
  }

  /**
  * 1. Remove the bottom border in Chrome 57-
  * 2. Add the correct text decoration in Chrome, Edge, IE, Opera, and Safari.
  */

  abbr[title] {
    border-bottom: none; /* 1 */
    text-decoration: underline; /* 2 */
    text-decoration: underline dotted; /* 2 */
  }

  /**
  * Add the correct font weight in Chrome, Edge, and Safari.
  */

  b,
  strong {
    font-weight: bolder;
  }

  /**
  * 1. Correct the inheritance and scaling of font size in all browsers.
  * 2. Correct the odd 'em' font sizing in all browsers.
  */

  /* stylelint-disable */
  code,
  kbd,
  samp {
    font-family: monospace, monospace; /* 1 */
    font-size: 1em; /* 2 */
  }
  /* stylelint-enable */

  /**
  * Add the correct font size in all browsers.
  */

  small {
    font-size: 80%;
  }

  /**
  * Prevent 'sub' and 'sup' elements from affecting the line height in
  * all browsers.
  */

  sub,
  sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
  }

  sub {
    bottom: -0.25em;
  }

  sup {
    top: -0.5em;
  }

  /* Embedded content
    ========================================================================== */

  /**
  * Remove the border on images inside links in IE 10.
  */

  img {
    border-style: none;
  }

  /* Forms
    ========================================================================== */

  /**
  * 1. Change the font styles in all browsers.
  * 2. Remove the margin in Firefox and Safari.
  */

  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: inherit; /* 1 */
    font-size: 100%; /* 1 */
    line-height: 1.15; /* 1 */
    margin: 0; /* 2 */
  }

  /**
  * Show the overflow in IE.
  * 1. Show the overflow in Edge.
  */

  button,
  input {
    /* 1 */
    overflow: visible;
  }

  /**
  * Remove the inheritance of text transform in Edge, Firefox, and IE.
  * 1. Remove the inheritance of text transform in Firefox.
  */

  button,
  select {
    /* 1 */
    text-transform: none;
  }

  /**
  * Correct the inability to style clickable types in iOS and Safari.
  */

  button,
  [type='button'],
  [type='reset'],
  [type='submit'] {
    appearance: auto;
  }

  /**
  * Remove the inner border and padding in Firefox.
  */

  button::-moz-focus-inner,
  [type='button']::-moz-focus-inner,
  [type='reset']::-moz-focus-inner,
  [type='submit']::-moz-focus-inner {
    border-style: none;
    padding: 0;
  }

  /**
  * Restore the focus styles unset by the previous rule.
  */

  button:-moz-focusring,
  [type='button']:-moz-focusring,
  [type='reset']:-moz-focusring,
  [type='submit']:-moz-focusring {
    outline: 1px dotted ButtonText;
  }

  /**
  * Correct the padding in Firefox.
  */

  fieldset {
    padding: 0.35em 0.75em 0.625em;
  }

  /**
  * 1. Correct the text wrapping in Edge and IE.
  * 2. Correct the color inheritance from 'fieldset' elements in IE.
  * 3. Remove the padding so developers are not caught out when they zero out
  *    'fieldset' elements in all browsers.
  */

  legend {
    box-sizing: border-box; /* 1 */
    color: inherit; /* 2 */
    display: table; /* 1 */
    max-width: 100%; /* 1 */
    padding: 0; /* 3 */
    white-space: normal; /* 1 */
  }

  /**
  * Add the correct vertical alignment in Chrome, Firefox, and Opera.
  */

  progress {
    vertical-align: baseline;
  }

  /**
  * Remove the default vertical scrollbar in IE 10+.
  */

  textarea {
    overflow: auto;
  }

  /**
  * 1. Add the correct box sizing in IE 10.
  * 2. Remove the padding in IE 10.
  */

  [type='checkbox'],
  [type='radio'] {
    box-sizing: border-box; /* 1 */
    padding: 0; /* 2 */
  }

  /**
  * Correct the cursor style of increment and decrement buttons in Chrome.
  */

  [type='number']::-webkit-inner-spin-button,
  [type='number']::-webkit-outer-spin-button {
    height: auto;
  }

  /**
  * 1. Correct the odd appearance in Chrome and Safari.
  * 2. Correct the outline style in Safari.
  */

  [type='search'] {
    appearance: textfield; /* 1 */
    outline-offset: -2px; /* 2 */
  }

  /**
  * Remove the inner padding in Chrome and Safari on macOS.
  */

  [type='search']::-webkit-search-decoration {
    appearance: none;
  }

  /**
  * 1. Correct the inability to style clickable types in iOS and Safari.
  * 2. Change font properties to 'inherit' in Safari.
  */

  ::-webkit-file-upload-button {
    appearance: auto; /* 1 */
    font: inherit; /* 2 */
  }

  /* Interactive
    ========================================================================== */

  /*
  * Add the correct display in Edge, IE 10+, and Firefox.
  */

  details {
    display: block;
  }

  /*
  * Add the correct display in all browsers.
  */

  summary {
    display: list-item;
  }

  /* Misc
    ========================================================================== */

  /**
  * Add the correct display in IE 10+.
  */

  template {
    display: none;
  }

  /**
  * Add the correct display in IE 10.
  */

  [hidden] {
    display: none;
  }
`;

// src/css/baseline.tsx
import { css as css4 } from "styled-components";
var baseline = css4`
  /**
  * 1. Change from 'content-box' so that 'width' calculations are unaffected by 'padding' or 'border'
  * 2. Ensure the iOS tap highlight is transparent
  */
  html {
    box-sizing: border-box; /* 1 */
    -webkit-tap-highlight-color: rgb(0 0 0 / 0%); /* 2 */
  }

  /**
  * 1. See: https://css-tricks.com/inheriting-box-sizing-probably-slightly-better-best-practice/
  */
  *,
  *::before,
  *::after {
    box-sizing: inherit; /* 1 */
  }

  /**
  * 1. Without this fonts are too heavy weight in OS X Firefox
  * 2. Design decision
  * 3. Preferable for the body to fill viewport, even when empty
  */
  body {
    -moz-osx-font-smoothing: grayscale; /* 1 */
    -webkit-font-smoothing: antialiased; /* 2 */
    color: ${({ theme: theme2 }) => theme2.color.grey900};
    font-family: ${({ theme: theme2 }) => theme2.font.family.default};
    font-size: ${({ theme: theme2 }) => theme2.font.size.default};
    font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
    line-height: 1.5;
    min-height: 100vh; /* 3 */
  }
`;

// src/css/GlobalStyle.tsx
var GlobalStyle = createGlobalStyle`
  ${normalize}
  ${baseline}
`;

// src/providers/VHSProvider.tsx
import { jsx as jsx119, jsxs as jsxs11 } from "react/jsx-runtime";
var VHSProvider = ({
  children,
  theme: theme2 = {},
  globalStylesIncluded = true,
  skipAnimations = false,
  Link: Link3
}) => {
  const motionPreference = skipAnimations ? "never" : "user";
  return /* @__PURE__ */ jsxs11(VHSThemeProvider, { theme: theme2, children: [
    globalStylesIncluded ? /* @__PURE__ */ jsx119(GlobalStyle, {}) : null,
    /* @__PURE__ */ jsx119(LiveAnnouncer, { children: /* @__PURE__ */ jsx119(MotionConfig, { reducedMotion: motionPreference, children: /* @__PURE__ */ jsx119(WrapperProvider, { value: { Link: Link3 }, children: /* @__PURE__ */ jsx119(ModalProvider, { children: /* @__PURE__ */ jsx119(ToastProvider, { children }) }) }) }) })
  ] });
};

// src/hooks/useBoolean/useBoolean.ts
import { useState as useState4, useCallback as useCallback4 } from "react";
var useBoolean = (initialValue = false) => {
  const [value, setValue] = useState4(initialValue);
  const toggle = useCallback4(() => setValue((val) => !val), []);
  const setTrue = useCallback4(() => setValue(true), []);
  const setFalse = useCallback4(() => setValue(false), []);
  return [value, toggle, setTrue, setFalse, setValue];
};

// src/hooks/useClipboard/useClipboard.ts
import { useCallback as useCallback5 } from "react";

// src/private/hooks/useTimedToggle/useTimedToggle.ts
import { useEffect as useEffect2, useRef as useRef2, useState as useState5 } from "react";
var useTimedToggle = (initialValue) => {
  const [value, setValue] = useState5(false);
  const timeoutRef = useRef2(void 0);
  const initialValueRef = useRef2(initialValue);
  const toggleValue = (timeout2) => {
    clearTimeout(timeoutRef.current);
    setValue(!initialValueRef.current);
    timeoutRef.current = window.setTimeout(() => setValue(initialValueRef.current), timeout2);
  };
  useEffect2(() => () => clearTimeout(timeoutRef.current), []);
  return [value, toggleValue];
};

// src/hooks/useClipboard/useClipboard.ts
var useClipboard = (textToCopy, timeout2 = 1500) => {
  const [hasCopied, toggleHasCopied] = useTimedToggle(false);
  const [failedToCopy, toggleFailedToCopy] = useTimedToggle(false);
  const onCopy = useCallback5(async () => {
    try {
      await copyToClipboard(textToCopy);
      if (timeout2 && timeout2 > 0) {
        toggleHasCopied(timeout2);
      }
    } catch (error) {
      if (error instanceof Error) {
        toggleFailedToCopy(timeout2);
      }
    }
  }, [textToCopy, timeout2, toggleHasCopied, toggleFailedToCopy]);
  return [onCopy, hasCopied, failedToCopy];
};

// src/hooks/useElementObserver/useElementObserver.ts
import { useMemo as useMemo2, useState as useState6, useRef as useRef3, useLayoutEffect as useLayoutEffect2 } from "react";
var initialRect = {
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  x: 0,
  y: 0
};
var useElementObserver = () => {
  const ref = useRef3(null);
  const [rect, setRect] = useState6(initialRect);
  const observer = useMemo2(
    () => new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height, top, left, bottom, right, x, y } = entries[0].contentRect;
        setRect({ width, height, top, left, bottom, right, x, y });
      }
    }),
    []
  );
  useLayoutEffect2(() => {
    if (!ref.current) {
      return;
    }
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [observer]);
  return [ref, rect];
};

// src/hooks/useEvent/useEvent.ts
import { useEffect as useEffect3, useRef as useRef4 } from "react";
var isEventTargetSupported = (eventTarget) => (
  // @ts-expect-error the compiler thinks that addEventListener may not exist on eventTarget, but we still need to check it.
  Boolean(typeof eventTarget === "object" && eventTarget?.addEventListener)
);
var useEvent = (eventName, eventHandler, eventTarget = window, eventOptions = {}) => {
  const savedEventHandler = useRef4(void 0);
  const savedEventOptions = useRef4(void 0);
  useEffect3(() => {
    savedEventHandler.current = eventHandler;
  }, [eventHandler]);
  useEffect3(() => {
    savedEventOptions.current = eventOptions;
  }, [eventOptions]);
  useEffect3(() => {
    if (!eventName || !isEventTargetSupported(eventTarget)) {
      return;
    }
    const eventListener = (event) => {
      if (savedEventHandler.current !== void 0) {
        return savedEventHandler.current(event);
      }
      return () => {
      };
    };
    eventTarget.addEventListener(eventName, eventListener, savedEventOptions.current);
    return () => {
      eventTarget.removeEventListener(eventName, eventListener, savedEventOptions.current);
    };
  }, [eventName, eventTarget, savedEventOptions]);
};

// src/hooks/useFocusTrap/useFocusTrap.ts
import { useRef as useRef5, useCallback as useCallback6, useEffect as useEffect4 } from "react";
import { isNotUndefined } from "@wistia/type-guards";

// src/hooks/useFocusTrap/helpers.ts
var FOCUSABLE_ELEMENT_SELECTORS = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]';
var isHiddenElement = (element) => {
  const { display, visibility } = window.getComputedStyle(element);
  const isHidden = display === "none" || element.style.display === "none" || visibility === "none" || element.style.visibility === "hidden";
  return element.offsetWidth <= 0 && element.offsetHeight <= 0 || isHidden;
};
var isVisibleElement = (element) => {
  let parentElement = element;
  while (parentElement) {
    if (parentElement === document.body) {
      break;
    }
    if (isHiddenElement(parentElement)) {
      return false;
    }
    parentElement = parentElement.parentNode;
  }
  return true;
};
var getElementTabIndex = (element) => {
  const tabIndex = element.getAttribute("tabindex");
  return Number.parseInt(tabIndex ?? void 0, 10);
};
var isTabIndexNaN = (element) => {
  const tabIndex = getElementTabIndex(element);
  return Number.isNaN(tabIndex);
};
var isFocusableElement = (element) => {
  const tabbableNodeRegEx = /input|select|textarea|button|object/;
  const nodeName = element.nodeName.toLowerCase();
  const isTabIndexNotNaN = !isTabIndexNaN(element);
  const isFocusable = (
    // @ts-expect-error - Disabled is specific to buttons and inputs, but we could be dealing with any number of types here.  Disabled would be undefined for those, so ignoring.
    tabbableNodeRegEx.test(nodeName) && !element.disabled || (element instanceof HTMLAnchorElement ? element.href || isTabIndexNotNaN : isTabIndexNotNaN)
  );
  return Boolean(isFocusable) && isVisibleElement(element);
};
var isTabbableElement = (element) => {
  const tabIndex = getElementTabIndex(element);
  return (isTabIndexNaN(element) || tabIndex >= 0) && isFocusableElement(element);
};
var findTabbableDescendants = (element) => Array.from(element.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)).filter(
  isTabbableElement
);
var focusLaterElements = [];
var focusElement = null;
var needToFocus = false;
var handleBlur = () => {
  needToFocus = true;
};
var handleFocus = () => {
  if (needToFocus) {
    needToFocus = false;
    if (!focusElement) {
      return;
    }
    if (focusElement.contains(document.activeElement)) {
      return;
    }
    const element = findTabbableDescendants(focusElement)[0] ?? focusElement;
    element.focus();
  }
};
var markForFocusLater = () => {
  const element = document.activeElement;
  if (element !== null) {
    focusLaterElements.push(element);
  }
};
var returnFocus = () => {
  let toFocus = null;
  try {
    toFocus = focusLaterElements.pop();
    if (toFocus) {
      toFocus.focus();
    }
  } catch {
    console.warn(
      `You tried to return focus to ${coerceToString(toFocus)} but it is not in the DOM anymore`
    );
  }
};
var setupScopedFocus = (element) => {
  focusElement = element;
  document.addEventListener("focusout", handleBlur, false);
  document.addEventListener("focusin", handleFocus, true);
};
var teardownScopedFocus = () => {
  focusElement = null;
  document.removeEventListener("focusout", handleBlur);
  document.removeEventListener("focusin", handleFocus);
};
var scopeTab = (node, event) => {
  const tabbable = findTabbableDescendants(node);
  if (!tabbable.length) {
    event.preventDefault();
    return;
  }
  const finalTabbable = tabbable[event.shiftKey ? 0 : tabbable.length - 1];
  const leavingFinalTabbable = finalTabbable === document.activeElement || node === document.activeElement;
  if (!leavingFinalTabbable) {
    return;
  }
  event.preventDefault();
  const target = tabbable[event.shiftKey ? tabbable.length - 1 : 0];
  if (target) {
    target.focus();
  }
};
var createAriaHider = (containerNode, selector) => {
  selector ??= "body > :not(script)";
  const rootNodes = Array.from(document.querySelectorAll(selector)).map((node) => {
    if (node.contains(containerNode)) {
      return void 0;
    }
    const ariaHidden = node.getAttribute("aria-hidden");
    if (ariaHidden === null || ariaHidden === "false") {
      node.setAttribute("aria-hidden", "true");
    }
    return {
      node,
      ariaHidden
    };
  });
  return () => {
    rootNodes.forEach((item) => {
      if (!item) {
        return;
      }
      if (item.ariaHidden === null) {
        item.node.removeAttribute("aria-hidden");
      } else {
        item.node.setAttribute("aria-hidden", item.ariaHidden);
      }
    });
  };
};

// src/hooks/useFocusTrap/useFocusTrap.ts
var isRef = (val) => {
  return val !== null && typeof val === "object" && "current" in val;
};
var useFocusTrap = (active = true, options = {}) => {
  const ref = useRef5(null);
  const restoreAriaRef = useRef5(null);
  const setRef = useCallback6(
    (node) => {
      if (restoreAriaRef.current !== null) {
        restoreAriaRef.current();
      }
      if (ref.current) {
        returnFocus();
        teardownScopedFocus();
      }
      if (active && node !== null && node !== void 0) {
        setupScopedFocus(node);
        markForFocusLater();
        const processNode = (node2) => {
          restoreAriaRef.current = !(options.disableAriaHider ?? false) ? createAriaHider(node2) : null;
          let focusElement2 = null;
          if (isNotUndefined(options.focusSelector)) {
            if (isRef(options.focusSelector)) {
              focusElement2 = options.focusSelector.current;
            } else {
              focusElement2 = typeof options.focusSelector === "string" ? node2.querySelector(options.focusSelector) : options.focusSelector;
            }
          }
          if (!focusElement2) {
            const children = Array.from(
              node2.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)
            );
            focusElement2 = // Prefer tabbable elements, But fallback to any focusable element
            children.find(isTabbableElement) ?? // But fallback to any focusable element
            children.find(isFocusableElement) ?? // Nothing found
            null;
            if (!focusElement2 && isFocusableElement(node2)) {
              focusElement2 = node2;
            }
          }
          if (focusElement2) {
            focusElement2.focus();
          }
          if (!focusElement2 && process.env["NODE_ENV"] === "development") {
            console.warn(
              '[useFocusTrap]: Failed to find a focusable element after activating the focus trap. Make sure to include at an element that can recieve focus. As a fallback, you can also set "tabIndex={-1}" on the focus trap node.',
              node2
            );
          }
        };
        setTimeout(() => {
          if (node.ownerDocument) {
            processNode(node);
          }
          if (!node.ownerDocument && process.env["NODE_ENV"] === "development") {
            console.warn(
              "[useFocusTrap]: The focus trap is not part of the DOM yet, so it is unable to correctly set focus. Make sure to render the ref node.",
              node
            );
          }
        });
        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active, options.focusSelector, options.disableAriaHider]
  );
  useEffect4(() => {
    if (!active) {
      return void 0;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Tab" && ref.current) {
        scopeTab(ref.current, event);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active]);
  return setRef;
};

// src/hooks/useForceUpdate/useForceUpdate.ts
import { useCallback as useCallback7, useState as useState7 } from "react";
var useForceUpdate = () => {
  const [, dispatch] = useState7(/* @__PURE__ */ Object.create(null));
  return useCallback7(() => {
    dispatch(/* @__PURE__ */ Object.create(null));
  }, [dispatch]);
};

// src/hooks/useFormikContext/index.ts
import { useFormikContext } from "formik";

// src/hooks/useIsHovered/useIsHovered.ts
import { useState as useState8 } from "react";
var useIsHovered = () => {
  const [isHovered, setIsHovered] = useState8(false);
  const onFocus = () => setIsHovered(true);
  const onMouseEnter = () => setIsHovered(true);
  const onMouseMove = () => setIsHovered(true);
  const onBlur = () => setIsHovered(false);
  const onMouseLeave = () => setIsHovered(false);
  const hoverProps = {
    onFocus,
    onMouseEnter,
    onBlur,
    onMouseLeave
  };
  if (!isHovered) {
    hoverProps.onMouseMove = onMouseMove;
  }
  return [isHovered, hoverProps, setIsHovered];
};

// src/hooks/useKey/useKey.ts
import { useMemo as useMemo3 } from "react";
var createKeyPredicate = (keyFilter) => {
  if (typeof keyFilter === "function") {
    return keyFilter;
  }
  if (typeof keyFilter === "string") {
    return (event) => event.key === keyFilter;
  }
  if (keyFilter !== null && keyFilter !== void 0 && Boolean(keyFilter)) {
    return () => true;
  }
  return () => false;
};
var useKey = (key, eventHandler, options = {}, dependencies = [key]) => {
  const { eventName = "keydown", eventTarget, eventOptions } = options;
  const memoizedEventHandler = useMemo3(() => {
    const predicate = createKeyPredicate(key);
    return (handlerEvent) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.nodeName ?? "") || document.activeElement?.isContentEditable) {
        return;
      }
      if (predicate(handlerEvent)) {
        return eventHandler(handlerEvent);
      }
    };
  }, dependencies);
  useEvent(eventName, memoizedEventHandler, eventTarget, eventOptions);
};

// src/hooks/useKeyPress/useKeyPress.ts
import { useState as useState9 } from "react";
var useKeyPress = (keyFilter) => {
  const [state, setState] = useState9([false, null]);
  useKey(keyFilter, (event) => setState([true, event]), { eventName: "keydown" }, [
    state
  ]);
  useKey(keyFilter, (event) => setState([false, event]), { eventName: "keyup" }, [
    state
  ]);
  return state;
};

// src/private/hooks/useUpdateEffect/useUpdateEffect.ts
import { useEffect as useEffect5, useRef as useRef6 } from "react";
var useUpdateEffect = (effect, dependencies) => {
  const isInitialMount = useRef6(true);
  useEffect5(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return () => {
      };
    }
    return effect();
  }, dependencies);
};

// src/hooks/useKeyPressOnce/useKeyPressOnce.ts
var useKeyPressOnce = (key, keydownHandler = void 0, keyupHandler = void 0) => {
  const [pressed, event] = useKeyPress(key);
  useUpdateEffect(() => {
    if (!pressed && keyupHandler && event !== null) {
      keyupHandler(event);
    } else if (pressed && keydownHandler && event !== null) {
      keydownHandler(event);
    } else {
    }
    return () => {
    };
  }, [pressed]);
};

// src/hooks/useLocalStorage/useLocalStorage.ts
import { useState as useState10 } from "react";
var useLocalStorage = (key, initialValue, storage = window.localStorage) => {
  const [storedValue, setStoredValue] = useState10(() => {
    try {
      const item = storage.getItem(key);
      return item !== null && Boolean(item) ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      storage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };
  const removeValue = () => {
    storage.removeItem(key);
    setStoredValue(void 0);
  };
  return [storedValue, setValue, removeValue];
};

// src/hooks/useMergedRefs/useMergedRefs.ts
var useMergedRefs = (refs) => (element) => {
  refs.forEach((ref) => {
    if (typeof ref === "function") {
      ref(element);
    } else if (ref != null) {
      ref.current = element;
    } else {
    }
  });
};

// src/hooks/useOnClickOutside/useOnClickOutside.ts
import { useEffect as useEffect6 } from "react";
var useOnClickOutside = (ref, handler) => {
  useEffect6(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

// src/hooks/usePreventScroll/usePreventScroll.ts
import { useLayoutEffect as useLayoutEffect3 } from "react";
var usePreventScroll = (locked) => {
  useLayoutEffect3(() => {
    if (locked) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
    return () => {
    };
  }, [locked]);
};

// src/hooks/usePreviousValue/usePreviousValue.ts
import { useEffect as useEffect7, useRef as useRef7 } from "react";
var usePreviousValue = (value) => {
  const ref = useRef7(void 0);
  useEffect7(() => {
    ref.current = value;
  });
  return ref.current;
};

// src/components/ActionModal/ActionModal.tsx
import { styled as styled12 } from "styled-components";
import { isNotNil as isNotNil8 } from "@wistia/type-guards";

// src/components/ModalBase/ModalBase.tsx
import { useEffect as useEffect8, useRef as useRef8, useState as useState11, useCallback as useCallback8 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { createGlobalStyle as createGlobalStyle2 } from "styled-components";
import isEqual from "react-fast-compare";
import { isNotUndefined as isNotUndefined2, isUndefined, isNotNil as isNotNil5 } from "@wistia/type-guards";

// src/private/components/ModalAnimation/ModalAnimation.tsx
import { Children } from "react";
import { motion as motion2, AnimatePresence as AnimatePresence2 } from "motion/react";
import { isNotNil as isNotNil4 } from "@wistia/type-guards";
import { jsx as jsx120 } from "react/jsx-runtime";
var TRANSITION_TIME = 100;
var ModalAnimation = ({ children }) => {
  const kids = Children.map(children, (child) => child)?.filter(Boolean);
  return /* @__PURE__ */ jsx120(AnimatePresence2, { children: isNotNil4(kids) ? kids.map((child) => /* @__PURE__ */ jsx120(
    motion2.div,
    {
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      initial: { opacity: 0 },
      style: { willChange: "auto" },
      transition: { duration: TRANSITION_TIME / 1e3 },
      children: child
    },
    child.key
  )) : null });
};

// src/components/ModalBase/ModalBase.tsx
import { Fragment as Fragment5, jsx as jsx121, jsxs as jsxs12 } from "react/jsx-runtime";
var LOCKED_SCROLL_CLASSNAME = "is-scroll-locked";
var GlobalStyle2 = createGlobalStyle2`
  .${LOCKED_SCROLL_CLASSNAME} {
    overflow: hidden;
  }
`;
var getFocusableElements = (root = document) => {
  const potentialElements = isNotNil5(root) ? root.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ) : [];
  return Array.from(potentialElements).filter(
    (element) => window.getComputedStyle(element).getPropertyValue("display") !== "none"
  );
};
var keepFocusWithinModal = (modalRoot, event) => {
  const focusable = getFocusableElements(modalRoot.current);
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];
  if (focusable.length === 1) {
    event.preventDefault();
    firstFocusable.focus();
    return;
  }
  if (event.shiftKey) {
    if (document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    }
    return;
  }
  if (document.activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }
};
var ModalBaseContent = ({
  className,
  initialShownState = false,
  isDismissible = true,
  onBeforeHide,
  onHide,
  onShow,
  render,
  toggle
}) => {
  const { attemptShowModal, attemptHideModal, removeModalFromQueue } = useModalProviderContext();
  const [isShown, setIsShown] = useState11(false);
  const [isShownAttempted, setIsShownAttempted] = useState11(initialShownState);
  const wasJustShown = usePreviousValue(isShown);
  const hide = () => setIsShownAttempted(false);
  const show = useCallback8(() => setIsShownAttempted(true), []);
  const toggleButtonRef = useRef8(void 0);
  const modalEl = document.createElement("div");
  modalEl.classList.add("Modal");
  if (isNotUndefined2(className)) {
    modalEl.classList.add(className);
  }
  const modalRoot = useRef8(modalEl);
  const attemptHide = useCallback8(() => {
    if (isNotUndefined2(onBeforeHide)) {
      const { shouldShowMessage, message } = Object(onBeforeHide());
      if (shouldShowMessage) {
        const shouldProceed = window.confirm(message);
        if (!shouldProceed) {
          return;
        }
      }
    }
    hide();
    if (onHide) {
      onHide();
    }
  }, [onBeforeHide, onHide]);
  useEffect8(() => {
    const onKeyup = (event) => {
      if (event.key === "Escape" && isDismissible) {
        attemptHide();
      }
    };
    if (isShown) {
      document.addEventListener("keyup", onKeyup);
      document.querySelector("body")?.classList.add(LOCKED_SCROLL_CLASSNAME);
    }
    return () => {
      document.removeEventListener("keyup", onKeyup);
      document.querySelector("body")?.classList.remove(LOCKED_SCROLL_CLASSNAME);
    };
  }, [isShown, attemptHide, isDismissible]);
  useEffect8(() => {
    setIsShownAttempted(initialShownState);
  }, [setIsShownAttempted, initialShownState]);
  useEffect8(() => {
    const onKeyDown = (event) => {
      if (event.key === "Tab") {
        keepFocusWithinModal(modalRoot, event);
      }
    };
    if (isShown) {
      const focusable = getFocusableElements(modalRoot.current);
      if (!focusable.find((element) => element === document.activeElement)) {
        const firstFocusable = focusable[0];
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
      document.addEventListener("keydown", onKeyDown);
      if (onShow) {
        onShow();
      }
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isShown, onShow]);
  useEffect8(() => {
    if (!isShown && isNotNil5(wasJustShown) && wasJustShown && toggleButtonRef.current) {
      toggleButtonRef.current.focus();
    }
  }, [isShown, wasJustShown]);
  useEffect8(() => {
    const modalRootEl = modalRoot.current;
    document.body.appendChild(modalRootEl);
    return () => modalRootEl.remove();
  }, []);
  useEffect8(
    () => () => {
      if (isUndefined(removeModalFromQueue)) {
        return;
      }
      removeModalFromQueue(setIsShown);
    },
    [removeModalFromQueue]
  );
  useEffect8(() => {
    if (isShownAttempted && isNotUndefined2(attemptShowModal)) {
      attemptShowModal(setIsShown);
    } else {
      if (isUndefined(attemptHideModal)) {
        return;
      }
      attemptHideModal(setIsShown);
    }
  }, [attemptShowModal, attemptHideModal, isShownAttempted]);
  if (!attemptShowModal || !attemptHideModal || !removeModalFromQueue) {
    return null;
  }
  return /* @__PURE__ */ jsxs12(Fragment5, { children: [
    /* @__PURE__ */ jsx121(GlobalStyle2, {}),
    isNotUndefined2(toggle) && toggle(show, toggleButtonRef),
    createPortal2(
      /* @__PURE__ */ jsx121(ModalAnimation, { children: isShown && isNotUndefined2(render) ? render(attemptHide) : void 0 }),
      modalRoot.current
    )
  ] });
};
var ModalBase = (props) => {
  const modalProviderContext = useModalProviderContext();
  if (!isEqual(modalProviderContext, {})) {
    return /* @__PURE__ */ jsx121(ModalBaseContent, { ...props });
  }
  return /* @__PURE__ */ jsx121(ModalProvider, { children: /* @__PURE__ */ jsx121(ModalBaseContent, { ...props }) });
};
ModalBase.displayName = "ModalBase_VHS";

// src/components/ButtonGroup/ButtonGroup.tsx
import { styled as styled4 } from "styled-components";
import { isNil as isNil3 } from "@wistia/type-guards";
import { jsx as jsx122 } from "react/jsx-runtime";
var getAlignment = (align) => {
  if (align === "center") {
    return "center";
  }
  if (align === "left") {
    return "flex-start";
  }
  if (align === "right") {
    return "flex-end";
  }
  return void 0;
};
var ButtonGroupComponent = styled4.div`
  display: flex;

  /* this helps ensure that primary buttons appear at the top of the column */
  flex-direction: ${({ $align }) => $align === "right" ? "column-reverse" : "column"};
  gap: ${({ theme: theme2 }) => theme2.buttonGroup.gap};
  justify-content: ${({ $align }) => getAlignment($align)};
  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};

  ${mq.smAndUp} {
    flex-direction: row;
  }

  button,
  input,
  a,
  .MenuButton {
    width: 100%;

    ${mq.smAndUp} {
      width: auto;
    }
  }
`;
var ButtonGroup = ({
  children,
  align = "left",
  fullWidth = false,
  ...otherProps
}) => {
  if (isNil3(children)) {
    return null;
  }
  return /* @__PURE__ */ jsx122(
    ButtonGroupComponent,
    {
      $align: align,
      $fullWidth: fullWidth,
      ...otherProps,
      children
    }
  );
};
ButtonGroup.displayName = "ButtonGroup_VHS";

// src/components/Text/Text.tsx
import { forwardRef as forwardRef2 } from "react";
import { styled as styled6, css as css6 } from "styled-components";

// src/components/Ellipsis/Ellipsis.tsx
import { isNotNil as isNotNil6 } from "@wistia/type-guards";
import { styled as styled5, css as css5 } from "styled-components";
import { jsx as jsx123 } from "react/jsx-runtime";
var ellipsisStyle = css5`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /*
    Using feature detection (i.e., -webkit-named-image) we only target Safari browsers
    with this additional styling to prevent a bug that's exclusive to Safari.

    Note that Safari does not properly hide the truncated text for non-inline/inline-block
    elements so we need to apply display: inline to all the paragraphs in the description.
    see https://stackoverflow.com/a/70683084
  */
  @supports (background: - webkit-named-image(i)) {
    & > div > div:has(p) {
      margin-top: 16px;
    }

    & > div > div > p {
      display: inline;
    }
  }
`;
var ellipsisFlexParentStyle = css5`
  min-width: 0;
`;
var EllipsisComponent = styled5.div`
  ${ellipsisStyle};
  ${({ lines }) => {
  if (isNotNil6(lines)) {
    return css5`
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${lines};
        display: -webkit-box;
        white-space: initial;
      `;
  }
  return void 0;
}}
`;
var Ellipsis = ({ children, lines, ...otherProps }) => /* @__PURE__ */ jsx123(
  EllipsisComponent,
  {
    lines,
    ...otherProps,
    children
  }
);
Ellipsis.displayName = "Ellipsis_VHS";

// src/components/Text/Text.tsx
import { jsx as jsx124 } from "react/jsx-runtime";
var alignMap = {
  left: "left",
  right: "right",
  center: "center",
  justify: "justify"
};
var headline1TextStyle = css6`
  font-family: ${({ theme: theme2 }) => theme2.headline.family};
  font-size: 24px;
  font-weight: ${({ theme: theme2 }) => theme2.headline.headline1.weight};
  line-height: 1.25;
`;
var headline2TextStyle = css6`
  font-family: ${({ theme: theme2 }) => theme2.headline.family};
  font-size: 20px;
  font-weight: ${({ theme: theme2 }) => theme2.headline.headline2.weight};
  line-height: 1.25;
`;
var headline3TextStyle = css6`
  font-family: ${({ theme: theme2 }) => theme2.headline.family};
  font-size: 16px;
  font-weight: ${({ theme: theme2 }) => theme2.headline.headline3.weight};
  line-height: 1.25;
`;
var overlineTextStyle = css6`
  font-size: 12px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
  line-height: 1.25;
  text-transform: uppercase;
`;
var body1TextStyle = css6`
  font-size: 16px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 1.5;
`;
var body2TextStyle = css6`
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 1.5;
`;
var captionTextStyle = css6`
  font-size: 12px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 1.5;
`;
var subtitle1TextStyle = css6`
  font-size: 16px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  line-height: 1.5;
`;
var subtitle2TextStyle = css6`
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  line-height: 1.5;
`;
var monospaceTextStyle = css6`
  font-family: ${({ theme: theme2 }) => theme2.font.family.monospace};
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 1.5;
`;
var variantStyleMap = {
  headline1: headline1TextStyle,
  headline2: headline2TextStyle,
  headline3: headline3TextStyle,
  overline: overlineTextStyle,
  body1: body1TextStyle,
  body2: body2TextStyle,
  caption: captionTextStyle,
  subtitle1: subtitle1TextStyle,
  subtitle2: subtitle2TextStyle,
  monospace: monospaceTextStyle
};
var TextComponent = styled6.div`
  color: ${({ theme: theme2, $colorOverride }) => $colorOverride ?? theme2.color.grey900};
  font-style: normal;
  margin: 0;
  ${({ $variant }) => variantStyleMap[$variant]}
  ${({ $ellipsis }) => $ellipsis && ellipsisStyle};
  ${({ $bold }) => $bold && css6`
      font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold} !important;
    `}
  ${({ $italic }) => $italic && css6`
      font-style: italic;
    `}
  ${({ $inline }) => $inline && css6`
      display: inline-block;
    `}
  ${({ $disabled }) => $disabled && css6`
      opacity: 0.5;
    `}
  ${({ $preventUserSelect }) => $preventUserSelect && css6`
      user-select: none;
    `}
  ${({ $align }) => css6`
    text-align: ${alignMap[$align]};
  `}
  ${({ as, theme: theme2 }) => as === "p" && css6`
      display: block;
      margin-bottom: ${theme2.spacing.space05};
    `}
`;
var Text = forwardRef2(
  ({
    align = "left",
    bold = false,
    children,
    colorOverride,
    disabled = false,
    ellipsis = false,
    inline = false,
    italic = false,
    preventUserSelect = false,
    renderAs = "div",
    variant = "body1",
    ...otherProps
  }, ref) => /* @__PURE__ */ jsx124(
    TextComponent,
    {
      ref,
      $align: align,
      $bold: bold,
      $colorOverride: colorOverride,
      $disabled: disabled,
      $ellipsis: ellipsis,
      $inline: inline,
      $italic: italic,
      $preventUserSelect: preventUserSelect,
      $variant: variant,
      as: renderAs,
      ...otherProps,
      children
    }
  )
);
Text.displayName = "Text_VHS";

// src/components/Backdrop/Backdrop.tsx
import { styled as styled7 } from "styled-components";
import { jsx as jsx125 } from "react/jsx-runtime";
var alignVerticalMap = {
  normal: "normal",
  top: "start",
  center: "center",
  bottom: "end"
};
var alignHorizontalMap = {
  normal: "normal",
  left: "start",
  center: "center",
  right: "end"
};
var BackdropComponent = styled7.div`
  align-items: ${({ $alignVertical }) => alignVerticalMap[$alignVertical]};
  background: ${({ $backgroundColor }) => $backgroundColor};
  bottom: 0;
  display: grid;
  justify-content: ${({ $alignHorizontal }) => alignHorizontalMap[$alignHorizontal]};
  left: 0;
  overflow-y: auto;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${({ theme: theme2, $elevated }) => $elevated ? theme2.zIndex.BackdropElevated : theme2.zIndex.Backdrop};
`;
var Backdrop = ({
  alignHorizontal = "normal",
  alignVertical = "normal",
  backgroundColor = "rgba(0, 0, 0, 0.4)",
  children,
  elevated = false,
  ...otherProps
}) => /* @__PURE__ */ jsx125(
  BackdropComponent,
  {
    $alignHorizontal: alignHorizontal,
    $alignVertical: alignVertical,
    $backgroundColor: backgroundColor,
    $elevated: elevated,
    ...otherProps,
    children
  }
);
Backdrop.displayName = "Backdrop_VHS";

// src/components/IconButton/IconButton.tsx
import { forwardRef as forwardRef3, useMemo as useMemo5 } from "react";
import { styled as styled11, css as css9 } from "styled-components";
import { transparentize as transparentize2 } from "polished";
import { isNonEmptyString as isNonEmptyString3 } from "@wistia/type-guards";

// src/components/ScreenReaderOnly/ScreenReaderOnly.tsx
import { styled as styled8, css as css7 } from "styled-components";
import { isNotNil as isNotNil7 } from "@wistia/type-guards";
import { jsx as jsx126 } from "react/jsx-runtime";
var screenReaderOnlyStyle = css7`
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
var VisuallyHidden = styled8.div`
  ${screenReaderOnlyStyle}
`;
var VisuallyHiddenButFocusable = styled8.div`
  &:not(:focus-within) {
    ${screenReaderOnlyStyle}
  }
`;
var ScreenReaderOnly = ({
  text,
  children,
  focusable = false,
  ...otherProps
}) => {
  const accessibleText = isNotNil7(text) ? text : children;
  if (focusable) {
    return /* @__PURE__ */ jsx126(VisuallyHiddenButFocusable, { ...otherProps, children: accessibleText });
  }
  return /* @__PURE__ */ jsx126(VisuallyHidden, { ...otherProps, children: accessibleText });
};
ScreenReaderOnly.displayName = "ScreenReaderOnly_VHS";

// src/components/Tooltip/Tooltip.tsx
import { useEffect as useEffect9, useState as useState12, useRef as useRef9, useMemo as useMemo4, useId } from "react";
import { styled as styled10 } from "styled-components";
import { isString as isString3 } from "@wistia/type-guards";

// src/components/Tooltip/TooltipContent.tsx
import { styled as styled9, css as css8 } from "styled-components";
import { jsx as jsx127 } from "react/jsx-runtime";
var ARROW_SIZE = 8;
var StyledTooltipLabel = styled9.div`
  color: ${({ $fontColorOverride }) => $fontColorOverride ?? "white"};
  font-size: 12px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 18px;
  padding: ${({ theme: theme2 }) => theme2.spacing.space03};
  position: relative;

  a {
    color: white;
  }

  &::after {
    border: solid transparent;
    border-width: ${ARROW_SIZE}px;
    content: ' ';
    height: 0;
    margin-left: -${ARROW_SIZE}px;
    pointer-events: none;
    position: absolute;
    width: 0;
  }
`;
var tooltipTopStyle = css8`
  left: 50%;
  top: -10px;
  transform: translate(-50%, -100%);

  ${StyledTooltipLabel} {
    &::after {
      border-top-color: ${({ $backgroundColor }) => $backgroundColor};
      left: 50%;
      top: 100%;
    }
  }
`;
var tooltipBottomStyle = css8`
  bottom: -10px;
  left: 50%;
  transform: translate(-50%, 100%);

  ${StyledTooltipLabel} {
    &::after {
      border-bottom-color: ${({ $backgroundColor }) => $backgroundColor};
      left: 50%;
      top: -${ARROW_SIZE * 2}px;
    }
  }
`;
var tooltipLeftStyle = css8`
  left: calc(-${ARROW_SIZE}px - 5px);
  top: 50%;
  transform: translate(-100%, -50%);

  ${StyledTooltipLabel} {
    &::after {
      border-left-color: ${({ $backgroundColor }) => $backgroundColor};
      left: 100%;
      margin-left: 0;
      margin-top: -${ARROW_SIZE}px;
      top: 50%;
    }
  }
`;
var tooltipRightStyle = css8`
  right: calc(-${ARROW_SIZE}px - 5px);
  top: 50%;
  transform: translate(100%, -50%);

  ${StyledTooltipLabel} {
    &::after {
      border-right-color: ${({ $backgroundColor }) => $backgroundColor};
      margin-left: 0;
      margin-top: -${ARROW_SIZE}px;
      right: 100%;
      top: 50%;
    }
  }
`;
var StyledTooltipContent = styled9.div`
  background: ${({ $backgroundColor }) => $backgroundColor};
  border: solid 1px ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 4px;
  color: ${({ $fontColorOverride }) => $fontColorOverride ?? "white"};
  display: ${({ $active, $forceState }) => $active || $forceState === "open" ? "block" : "none"};
  position: absolute;
  transition: all ${({ theme: theme2 }) => theme2.duration.productive.moderate};
  z-index: ${({ theme: theme2 }) => theme2.zIndex.Tooltip};

  * {
    margin: 0;
    white-space: ${({ $shouldWrap }) => $shouldWrap ? "normal" : "nowrap"};
  }

  ${({ $direction }) => {
  switch ($direction) {
    case "top":
      return tooltipTopStyle;
    case "bottom":
      return tooltipBottomStyle;
    case "left":
      return tooltipLeftStyle;
    case "right":
      return tooltipRightStyle;
    default:
      return void 0;
  }
}}
`;
var TooltipContent = ({
  id,
  active,
  direction,
  children,
  forceState,
  shouldWrap: shouldWrap2,
  backgroundColorOverride,
  fontColorOverride
}) => {
  const backgroundColor = useThemeColor("grey900");
  return /* @__PURE__ */ jsx127(
    StyledTooltipContent,
    {
      $active: active,
      $backgroundColor: backgroundColorOverride ?? backgroundColor,
      $direction: direction,
      $fontColorOverride: fontColorOverride,
      $forceState: forceState,
      $shouldWrap: shouldWrap2,
      "aria-hidden": !active,
      "data-testid": "tooltip-content",
      id,
      children: /* @__PURE__ */ jsx127(StyledTooltipLabel, { $fontColorOverride: fontColorOverride, children })
    }
  );
};

// src/components/Truncate/Truncate.tsx
import { isString as isString2, isNil as isNil4 } from "@wistia/type-guards";
import { Fragment as Fragment6, jsx as jsx128 } from "react/jsx-runtime";
var Truncate = ({
  children,
  maxChars,
  terminator = "...",
  breakOnWords = false
}) => {
  if (isNil4(children)) {
    return null;
  }
  const canBeTruncated = children.length + terminator.length >= maxChars;
  if (!isString2(children) || maxChars <= 0 || !canBeTruncated) {
    return /* @__PURE__ */ jsx128(Fragment6, { children });
  }
  let truncatedChild = children.slice(0, maxChars - terminator.length);
  if (breakOnWords) {
    const index = Math.max(truncatedChild.lastIndexOf(" "), truncatedChild.lastIndexOf("\n"));
    truncatedChild = truncatedChild.slice(0, index);
  }
  truncatedChild = `${truncatedChild.trim()}${terminator}`;
  return /* @__PURE__ */ jsx128(Fragment6, { children: truncatedChild });
};
Truncate.displayName = "Truncate_VHS";

// src/components/Tooltip/Tooltip.tsx
import { Fragment as Fragment7, jsx as jsx129, jsxs as jsxs13 } from "react/jsx-runtime";
var TooltipWrapper = styled10.div`
  display: inline-block;
  position: relative;
  width: inherit;
`;
var Tooltip = ({
  label,
  direction = "top",
  duration = 2e3,
  eventType = "hover",
  forceState = void 0,
  maxChars = null,
  children,
  id = null,
  shouldWrap: shouldWrap2 = false,
  backgroundColorOverride,
  fontColorOverride,
  ...otherProps
}) => {
  const [active, setActive] = useState12(false);
  const generatedId = `vhs-tooltip-${useId()}`;
  const tooltipID = useMemo4(() => id ?? generatedId, [id, generatedId]);
  const tooltipEl = useRef9(null);
  const timer = useRef9(null);
  const startTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setActive(false);
    }, duration);
  };
  const handleClick = () => {
    setActive(true);
    if (duration) {
      clearTimeout(timer.current ?? void 0);
      startTimer();
    }
  };
  const handleMouseOver = () => setActive(true);
  const handleMouseLeave = () => setActive(false);
  const handleFocus2 = () => handleClick();
  const handleBlur2 = () => setActive(false);
  useEffect9(() => {
    const handleWindowClick = (event) => {
      if (active) {
        if (tooltipEl.current === null || tooltipEl.current.contains(event.target)) {
          return;
        }
        setActive(false);
      }
    };
    if (!duration) {
      document.addEventListener("mousedown", handleWindowClick, false);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      document.removeEventListener("mousedown", handleWindowClick, false);
    };
  }, [active, timer, duration]);
  if (isString3(label) && !label.length) {
    return /* @__PURE__ */ jsx129(Fragment7, { children });
  }
  let tooltipLabel = label;
  if (maxChars !== null) {
    tooltipLabel = /* @__PURE__ */ jsx129(
      Truncate,
      {
        breakOnWords: true,
        maxChars,
        children: label
      }
    );
  }
  const getProps = () => {
    const hoverProps = {
      onFocus: handleFocus2,
      onMouseLeave: handleMouseLeave,
      onMouseOver: handleMouseOver,
      onBlur: handleBlur2
    };
    const clickProps = {
      onClick: handleClick,
      ref: tooltipEl
    };
    if (eventType === "hover") {
      return hoverProps;
    }
    return clickProps;
  };
  return /* @__PURE__ */ jsxs13(
    TooltipWrapper,
    {
      "aria-labelledby": tooltipID,
      role: "tooltip",
      ...getProps(),
      ...otherProps,
      children: [
        /* @__PURE__ */ jsx129(
          TooltipContent,
          {
            active,
            backgroundColorOverride,
            direction,
            fontColorOverride,
            forceState,
            id: tooltipID,
            shouldWrap: shouldWrap2,
            children: tooltipLabel
          }
        ),
        children
      ]
    }
  );
};
Tooltip.displayName = "Tooltip_VHS";

// src/components/IconButton/IconButton.tsx
import { Fragment as Fragment8, jsx as jsx130, jsxs as jsxs14 } from "react/jsx-runtime";
var smSizeStyle = css9`
  max-height: 16px;
  max-width: 16px;
  min-height: 16px;
  min-width: 16px;

  svg {
    max-height: 12px;
    max-width: 12px;
    min-height: 12px;
    min-width: 12px;
  }
`;
var mdSizeStyle = css9`
  max-height: 24px;
  max-width: 24px;
  min-height: 24px;
  min-width: 24px;

  svg {
    max-height: 16px;
    max-width: 16px;
    min-height: 16px;
    min-width: 16px;
  }
`;
var lgSizeStyle = css9`
  max-height: 32px;
  max-width: 32px;
  min-height: 32px;
  min-width: 32px;

  svg {
    max-height: 24px;
    max-width: 24px;
    min-height: 24px;
    min-width: 24px;
  }
`;
var disabledStyle = css9`
  cursor: not-allowed;

  svg {
    color: ${({ $iconButtonColor }) => transparentize2(0.5, $iconButtonColor)};
  }

  &:hover,
  &:focus {
    background-color: transparent;

    svg {
      color: ${({ $iconButtonColor }) => transparentize2(0.5, $iconButtonColor)};
    }
  }
`;
var iconButtonStyle = css9`
  background-color: ${({ $iconButtonColor }) => transparentize2(1, $iconButtonColor)};
  border-radius: ${({ theme: theme2 }) => theme2.button.borderRadius};

  svg {
    color: ${({ $iconButtonColor }) => $iconButtonColor};
  }

  &:hover,
  &:focus-visible {
    background-color: ${({ $iconButtonColor }) => transparentize2(0.8, $iconButtonColor)};

    svg {
      color: ${({ $iconButtonColor }) => $iconButtonColor};
    }
  }

  &:active {
    background-color: ${({ $iconButtonColor }) => transparentize2(0.6, $iconButtonColor)};

    svg {
      color: ${({ $iconButtonColor }) => $iconButtonColor};
    }
  }
`;
var getIconButtonColor = (colorOverride, componentStyle, allThemeColors) => {
  if (isNonEmptyString3(colorOverride)) {
    return colorOverride;
  }
  if (componentStyle === "dark") {
    return allThemeColors.grey700;
  }
  return "#fff";
};
var IconButtonComponent = styled11.button`
  ${buttonResetStyle}
  ${iconButtonStyle}
  transition: all ${({ theme: theme2 }) => theme2.duration.productive.moderate};
  ${({ disabled }) => disabled ? disabledStyle : void 0}
  ${({ size }) => {
  if (size === "sm") {
    return smSizeStyle;
  }
  if (size === "md") {
    return mdSizeStyle;
  }
  return lgSizeStyle;
}};
`;
var IconButton = forwardRef3(
  ({
    colorOverride,
    componentStyle = "dark",
    disabled = false,
    icon,
    label,
    onClick,
    preventTooltip = false,
    size = "md",
    tooltipDirection = "top",
    ...otherProps
  }, ref) => {
    const iconButtonColor = getIconButtonColor(colorOverride, componentStyle, useThemeColor());
    const Button3 = useMemo5(
      () => /* @__PURE__ */ jsxs14(
        IconButtonComponent,
        {
          ref,
          $componentStyle: componentStyle,
          $iconButtonColor: iconButtonColor,
          disabled,
          onClick,
          size,
          ...otherProps,
          children: [
            /* @__PURE__ */ jsx130(Fragment8, { children: icon }),
            /* @__PURE__ */ jsx130(ScreenReaderOnly, { children: label })
          ]
        }
      ),
      [componentStyle, disabled, icon, iconButtonColor, label, onClick, otherProps, ref, size]
    );
    if (preventTooltip) {
      return Button3;
    }
    return /* @__PURE__ */ jsx130(
      Tooltip,
      {
        direction: tooltipDirection,
        label,
        children: Button3
      }
    );
  }
);
IconButton.displayName = "IconButton_VHS";

// src/private/components/ModalCloseButton/ModalCloseButton.tsx
import { jsx as jsx131 } from "react/jsx-runtime";
var ModalCloseButton = ({
  onClick,
  closeIconColorOverride
}) => /* @__PURE__ */ jsx131(
  IconButton,
  {
    "aria-label": "Close",
    colorOverride: closeIconColorOverride,
    icon: /* @__PURE__ */ jsx131(
      Icon,
      {
        size: "sm",
        type: "close"
      }
    ),
    onClick,
    preventTooltip: true,
    style: { position: "absolute", right: "16px", top: "16px", zIndex: 1 }
  }
);
ModalCloseButton.displayName = "ModalCloseButton";

// src/components/ActionModal/getModalBackgroundGradient.ts
import { css as css10 } from "styled-components";
var getModalBackgroundGradient = (illustrationBackgroundColor) => {
  switch (illustrationBackgroundColor) {
    case "green":
      return getBackgroundGradient("green");
    case "pink":
      return getBackgroundGradient("pink");
    case "purple":
      return getBackgroundGradient("purple");
    case "yellow":
      return getBackgroundGradient("yellow");
    case "brandBlue":
      return getBackgroundGradient("defaultLightOne");
    case "blue":
      return getBackgroundGradient("defaultLightOne");
    default:
      return css10`
        background-color: ${illustrationBackgroundColor};
      `;
  }
};

// src/components/ActionModal/ActionModal.tsx
import { Fragment as Fragment9, jsx as jsx132, jsxs as jsxs15 } from "react/jsx-runtime";
var defaultWidth = "532px";
var illustrationDiameter = 160;
var Dialogue = styled12.div`
  background-color: white;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto;
  position: relative;
  width: 100vw;

  ${mq.smAndUp} {
    border-radius: 16px;
    height: ${({ $fullHeight }) => $fullHeight ? "min-content" : "auto"};
    margin: ${({ theme: theme2 }) => theme2.spacing.space05};
    ${({ $fullHeight }) => $fullHeight ? "min-height: calc(100vh - 48px);" : ""}
    overflow: visible;
    width: min-content;
  }

  ${mq.mdAndUp} {
    flex-direction: row;
  }
`;
var Body = styled12.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme: theme2 }) => theme2.spacing.space06};

  ${mq.smAndUp} {
    width: ${({ width }) => width};
  }

  p {
    margin-top: 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
var Message = styled12.div`
  flex-grow: 1;
`;
var IllustrationWrapper = styled12.div`
  ${({ illustrationBackgroundColor }) => getModalBackgroundGradient(illustrationBackgroundColor)};
  align-items: center;
  background-position: center;
  background-size: cover;
  border-radius: 0;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  min-height: 192px;
  min-width: 192px;
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space04} ${theme2.spacing.space06}`};

  ${mq.smAndUp} {
    border-radius: 16px 16px 0 0;
  }

  ${mq.mdAndUp} {
    border-radius: 16px 0 0 16px;
    padding: ${({ theme: theme2 }) => theme2.spacing.space06};
  }

  img {
    height: ${illustrationDiameter}px;
    width: ${illustrationDiameter}px;
  }
`;
var ActionModal = ({
  alignHorizontal = "center",
  alignVertical = "center",
  buttons,
  children,
  className,
  elevated = true,
  fullHeight = false,
  closeIconColorOverride = void 0,
  illustration = null,
  illustrationBackgroundColor = "brandBlue",
  initialShownState = false,
  isDismissible = true,
  onBeforeHide,
  onHide,
  onShow,
  render,
  title,
  toggle,
  width = defaultWidth,
  ...otherProps
}) => {
  return /* @__PURE__ */ jsx132(
    ModalBase,
    {
      className,
      initialShownState,
      isDismissible,
      onBeforeHide,
      onHide,
      onShow,
      render: (hide) => /* @__PURE__ */ jsx132(
        Backdrop,
        {
          alignHorizontal,
          alignVertical,
          elevated,
          onClick: (event) => {
            if (!isDismissible) {
              return;
            }
            event.stopPropagation();
            hide();
          },
          children: /* @__PURE__ */ jsxs15(
            Dialogue,
            {
              $fullHeight: fullHeight,
              "aria-labelledby": "dialogTitle",
              "data-testid": "action-modal-dialogue",
              onClick: (event) => event.stopPropagation(),
              role: "dialog",
              ...otherProps,
              children: [
                isDismissible ? /* @__PURE__ */ jsx132(
                  ModalCloseButton,
                  {
                    closeIconColorOverride,
                    onClick: hide
                  }
                ) : null,
                isNotNil8(illustration) ? /* @__PURE__ */ jsx132(IllustrationWrapper, { illustrationBackgroundColor, children: typeof illustration === "string" ? /* @__PURE__ */ jsx132(
                  "img",
                  {
                    alt: "",
                    src: illustration
                  }
                ) : illustration }) : null,
                /* @__PURE__ */ jsxs15(Body, { width, children: [
                  /* @__PURE__ */ jsxs15(Message, { children: [
                    /* @__PURE__ */ jsx132(Fragment9, { children: title ? /* @__PURE__ */ jsx132(
                      Text,
                      {
                        id: "dialogTitle",
                        renderAs: "h2",
                        style: { marginBottom: "16px" },
                        variant: "headline1",
                        children: title
                      }
                    ) : null }),
                    /* @__PURE__ */ jsx132(Fragment9, { children: render === void 0 ? children : render(hide) })
                  ] }),
                  /* @__PURE__ */ jsx132(
                    ButtonGroup,
                    {
                      align: "right",
                      style: { flexGrow: "0", marginTop: "24px" },
                      children: typeof buttons === "function" ? buttons(hide) : buttons
                    }
                  )
                ] })
              ]
            }
          )
        }
      ),
      toggle
    }
  );
};
ActionModal.displayName = "ActionModal_VHS";

// src/components/Avatar/Avatar.tsx
import { useMemo as useMemo6, useState as useState13, useEffect as useEffect10 } from "react";
import { isNil as isNil6 } from "@wistia/type-guards";
import { styled as styled13 } from "styled-components";

// src/components/Avatar/generateContactAvatarColors.tsx
var colorVariants = {
  blue: ["blue600", "blue700", "blue800"],
  brandBlue: ["brandBlue600", "brandBlue700", "brandBlue800"],
  green: ["green600", "green700", "green800"],
  pink: ["pink600", "pink700", "pink800"],
  purple: ["purple600", "purple700", "purple800"],
  red: ["red600", "red700", "red800"],
  yellow: ["yellow700", "yellow800", "yellow900"]
};
var colorVariantKeys = Object.keys(colorVariants);
var multiplier = 31;
var hashCode = (str) => Array.from(str).reduce((sum, char) => Math.imul(multiplier, sum) + char.charCodeAt(0) | 0, 0);
var generateContactAvatarColors = ({
  seed,
  themeColors
}) => {
  const index = Math.abs(hashCode(seed)) % colorVariantKeys.length;
  const colorVariantsKeyFromIndex = colorVariantKeys[index];
  return colorVariants[colorVariantsKeyFromIndex].map((themeColorToken) => themeColors[themeColorToken]).filter((color) => color !== void 0);
};

// src/components/Avatar/formatInitialsForDisplay.tsx
import { isNil as isNil5, isString as isString4, isEmptyString } from "@wistia/type-guards";
var containsEmojiCharacter = (char) => {
  const emojiRegex = /<a?:.+?:\d{18}>|\p{Extended_Pictographic}/gu;
  return emojiRegex.test(char);
};
var formatInitialsForDisplay = (initials) => {
  if (isNil5(initials) || isEmptyString(initials) || isString4(initials) && containsEmojiCharacter(initials)) {
    return "U";
  }
  if (typeof initials !== "string") {
    return "U";
  }
  const firstChar = initials.substring(0, 1);
  return firstChar.toUpperCase();
};

// src/components/Avatar/Avatar.tsx
import { jsx as jsx133 } from "react/jsx-runtime";
var AvatarImage = styled13.img`
  align-items: center;
  background-color: white;
  border-radius: 50%;
  color: white;
  display: flex;
  height: ${({ $heightAndWidth }) => $heightAndWidth}px;
  justify-content: center;
  object-fit: cover;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  width: ${({ $heightAndWidth }) => $heightAndWidth}px;
  border: ${({ theme: theme2 }) => `2px solid ${theme2.color.grey200}`};

  a:hover & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 25%);
  }

  a:active & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 50%);
  }
`;
var fontSizeScaleMultiplier = 0.55;
var InitialsContainer = styled13.div`
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 50%;
  color: white;
  display: flex;
  font-size: ${({ $heightAndWidth }) => $heightAndWidth * fontSizeScaleMultiplier}px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
  height: ${({ $heightAndWidth }) => $heightAndWidth}px;
  justify-content: center;
  overflow: hidden;
  width: ${({ $heightAndWidth }) => $heightAndWidth}px;

  a:hover & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 25%);
  }

  a:active & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 50%);
  }
`;
var AvatarWrapper = styled13.div`
  max-height: ${({ $maxHeight }) => $maxHeight}px;
`;
var Avatar = ({
  imageUrl,
  heightAndWidth = 24,
  initials,
  name,
  onImageLoad,
  ...otherProps
}) => {
  const [imageLoadState, setImageLoadState] = useState13(null);
  const themeColors = useThemeColor();
  const avatarColor = useMemo6(() => {
    if (isNil6(name) || name === "Anonymous") {
      return themeColors.grey600;
    }
    return generateContactAvatarColors({
      seed: name,
      themeColors
    })[0];
  }, [name, themeColors]);
  useEffect10(() => {
    setImageLoadState(null);
  }, [imageUrl]);
  const handleImageLoad = () => {
    setImageLoadState("loaded");
    onImageLoad?.("image");
  };
  const handleImageLoadError = () => {
    setImageLoadState("error");
    onImageLoad?.("initials");
  };
  const showInitials = isNil6(imageUrl) || imageLoadState === "error";
  return /* @__PURE__ */ jsx133(
    AvatarWrapper,
    {
      ...otherProps,
      $maxHeight: heightAndWidth,
      children: showInitials ? /* @__PURE__ */ jsx133(
        InitialsContainer,
        {
          $backgroundColor: avatarColor ?? "",
          $heightAndWidth: heightAndWidth,
          children: formatInitialsForDisplay(initials)
        }
      ) : /* @__PURE__ */ jsx133(
        AvatarImage,
        {
          $heightAndWidth: heightAndWidth,
          alt: name ?? "Unavailable",
          onError: handleImageLoadError,
          onLoad: handleImageLoad,
          src: imageUrl
        }
      )
    }
  );
};
Avatar.displayName = "Avatar_VHS";

// src/components/BackgroundImage/BackgroundImage.tsx
import { styled as styled14 } from "styled-components";
import { isString as isString5 } from "@wistia/type-guards";
import { jsx as jsx134 } from "react/jsx-runtime";
var shouldWrap = (src) => {
  const imageFunctionList = [
    "cross-fade",
    "element",
    "image",
    "image-set",
    "linear-gradient",
    "radial-gradient",
    "conic-gradient",
    "repeating-linear-gradient",
    "repeating-radial-gradient",
    "repeating-conic-gradient",
    "paint",
    "url"
  ];
  return !imageFunctionList.some((imageFunction) => src.startsWith(imageFunction));
};
var getBackgroundImageValue = (src) => {
  if (src !== void 0 && Array.isArray(src)) {
    return src.map((imageSrc) => shouldWrap(imageSrc) ? `url('${imageSrc}')` : imageSrc).join(", ");
  }
  if (src !== void 0 && src !== "" && isString5(src)) {
    return shouldWrap(src) ? `url('${src}')` : src;
  }
  return void 0;
};
var getOverflowStyle = (overflow) => {
  if (overflow !== "") {
    if (isObject(overflow)) {
      Object.keys(overflow).forEach((key) => {
        if (!["horizontal", "vertical"].includes(key)) {
          throw new ReferenceError(
            `"${key}" is not a valid key for BackgroundImage component overflow prop; only "horizontal" or "vertical" allowed`
          );
        }
      });
      return Object.entries(overflow).map(([key, value]) => {
        if (key === "horizontal") {
          return `overflow-x: ${value};`;
        }
        if (key === "vertical") {
          return `overflow-y: ${value};`;
        }
        return void 0;
      }).join("");
    }
    return `overflow: ${overflow};`;
  }
  return void 0;
};
var BackgroundImageComponent = styled14.div`
  background-attachment: ${({ $bgAttachment }) => $bgAttachment};
  background-clip: ${({ $bgClip }) => $bgClip};
  background-color: ${({ $bgColor }) => $bgColor};
  background-image: ${({ $bgImage }) => getBackgroundImageValue($bgImage)};
  background-origin: ${({ $bgOrigin }) => $bgOrigin};
  background-position: ${({ $bgPosition }) => $bgPosition};
  background-repeat: ${({ $bgRepeat }) => $bgRepeat};
  background-size: ${({ $bgSize }) => $bgSize};
  display: flex;
  height: ${({ $elHeight }) => $elHeight};
  justify-content: center;
  ${({ $elOverflow }) => getOverflowStyle($elOverflow)};
  position: relative;
  width: ${({ $elWidth }) => $elWidth};
`;
var BackgroundImage = ({
  children = void 0,
  attachment: bgAttachment = "scroll",
  clip: bgClip = "border-box",
  color: bgColor = void 0,
  height: elHeight = "auto",
  origin: bgOrigin = void 0,
  overflow: elOverflow = "hidden",
  position: bgPosition = "center",
  repeat: bgRepeat = "no-repeat",
  size: bgSize = "cover",
  src: bgImage = void 0,
  width: elWidth = "100%",
  ...otherProps
}) => /* @__PURE__ */ jsx134(
  BackgroundImageComponent,
  {
    $bgAttachment: bgAttachment,
    $bgClip: bgClip,
    $bgColor: bgColor,
    $bgImage: bgImage,
    $bgOrigin: bgOrigin,
    $bgPosition: bgPosition,
    $bgRepeat: bgRepeat,
    $bgSize: bgSize,
    $elHeight: elHeight,
    $elOverflow: elOverflow,
    $elWidth: elWidth,
    ...otherProps,
    children
  }
);
BackgroundImage.displayName = "BackgroundImage_VHS";

// src/components/Badge/Badge.tsx
import { styled as styled15 } from "styled-components";
import { isNotNil as isNotNil9 } from "@wistia/type-guards";

// src/components/Badge/getBadgeColors.ts
var getBadgeColors = (badgeColor, allThemeColors) => {
  switch (badgeColor) {
    case "error":
      return {
        backgroundColor: allThemeColors.error300,
        textColor: allThemeColors.error800
      };
    case "green":
      return {
        backgroundColor: allThemeColors.green300,
        textColor: allThemeColors.green800
      };
    case "grey":
      return {
        backgroundColor: allThemeColors.grey300,
        textColor: allThemeColors.grey1000
      };
    case "orange":
      return {
        backgroundColor: allThemeColors.orange200,
        textColor: allThemeColors.orange800
      };
    case "pink":
      return {
        backgroundColor: allThemeColors.pink300,
        textColor: allThemeColors.pink800
      };
    case "purple":
      return {
        backgroundColor: allThemeColors.purple300,
        textColor: allThemeColors.purple800
      };
    case "red":
      return {
        backgroundColor: allThemeColors.red300,
        textColor: allThemeColors.red800
      };
    case "success":
      return {
        backgroundColor: allThemeColors.success200,
        textColor: allThemeColors.success800
      };
    case "warning":
      return {
        backgroundColor: allThemeColors.warning300,
        textColor: allThemeColors.warning800
      };
    case "yellow":
      return {
        backgroundColor: allThemeColors.yellow200,
        textColor: allThemeColors.yellow800
      };
    case "brandBlue":
    default:
      return {
        backgroundColor: allThemeColors.brandBlue200,
        textColor: allThemeColors.brandBlue700
      };
  }
};

// src/components/Badge/Badge.tsx
import { jsx as jsx135, jsxs as jsxs16 } from "react/jsx-runtime";
var BadgeComponent = styled15.div`
  align-items: center;
  background: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 999px;
  color: ${({ $textColor }) => $textColor};
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: ${({ theme: theme2 }) => theme2.spacing.space01}; /* space between icon and label */
  padding-left: ${({ $hasIcon }) => $hasIcon ? "4px" : "6px"};
  padding-right: 6px;
  width: fit-content;
`;
var Badge = ({
  colorScheme = "brandBlue",
  label,
  icon,
  ...otherProps
}) => {
  const { backgroundColor, textColor } = getBadgeColors(colorScheme, useThemeColor());
  return /* @__PURE__ */ jsxs16(
    BadgeComponent,
    {
      ...otherProps,
      $backgroundColor: backgroundColor,
      $hasIcon: isNotNil9(icon),
      $textColor: textColor,
      children: [
        isNotNil9(icon) && /* @__PURE__ */ jsx135(
          Icon,
          {
            "data-testid": "badge-icon",
            size: "sm",
            type: icon
          }
        ),
        /* @__PURE__ */ jsx135("span", { children: label })
      ]
    }
  );
};
Badge.displayName = "Badge_VHS";

// src/components/Banner/Banner.tsx
import { styled as styled17 } from "styled-components";
import { isNotNil as isNotNil10 } from "@wistia/type-guards";

// src/components/Banner/BannerChildComponents.tsx
import { styled as styled16, css as css11 } from "styled-components";
import { jsx as jsx136 } from "react/jsx-runtime";
var BodyComponent = styled16.div`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  flex: 1;
  font-size: 14px;
  line-height: 20px;
`;
var Body2 = ({ children }) => /* @__PURE__ */ jsx136(BodyComponent, { children });
var TitleComponent = styled16.h2`
  font-size: 16px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  line-height: 24px;
  margin: ${({ theme: theme2 }) => `0 0 ${theme2.spacing.space01}`};

  a {
    font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  }
`;
var Title = ({ children }) => /* @__PURE__ */ jsx136(TitleComponent, { children });
var RightGutterActions = css11`
  button,
  a {
    margin-top: ${({ theme: theme2 }) => theme2.spacing.space02};

    &:not([role='switch']) {
      width: 100%;
    }

    ${mq.smAndUp} {
      margin-left: ${({ theme: theme2 }) => theme2.spacing.space02};
      margin-top: 0;
    }
  }
`;
var getVerticalAlign = (align) => {
  switch (align) {
    case "center":
      return "center";
    case "top":
    case void 0:
    default:
      return "flex-start";
  }
};
var RightGutterComponent = styled16.div`
  align-items: ${({ $verticalAlign }) => getVerticalAlign($verticalAlign)};
  display: flex;
  ${RightGutterActions}

  ${mq.smAndUp} {
    margin-left: ${({ theme: theme2 }) => theme2.spacing.space05};
  }
`;
var RightGutter = ({ verticalAlign, children }) => /* @__PURE__ */ jsx136(RightGutterComponent, { $verticalAlign: verticalAlign, children });

// src/components/Banner/Banner.tsx
import { jsx as jsx137, jsxs as jsxs17 } from "react/jsx-runtime";
var BannerComponent = styled17.div`
  background: ${({ theme: theme2, backgroundColor }) => backgroundColor ?? theme2.color.grey200};
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  padding: 16px 24px;
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space04} ${theme2.spacing.space05}`};
`;
var IconWrapper = styled17.div`
  align-items: center;
  display: flex;
  margin-right: ${({ theme: theme2 }) => theme2.spacing.space05};
  margin-top: ${({ theme: theme2 }) => theme2.spacing.space01};
`;
var ChildrenWrapper = styled17.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mq.smAndUp} {
    flex-direction: row;
  }
`;
var Banner = ({ backgroundColor, icon, children, ...otherProps }) => /* @__PURE__ */ jsxs17(
  BannerComponent,
  {
    backgroundColor,
    ...otherProps,
    children: [
      isNotNil10(icon) ? /* @__PURE__ */ jsx137(IconWrapper, { "data-testid": "banner-icon", children: icon }) : null,
      /* @__PURE__ */ jsx137(ChildrenWrapper, { children })
    ]
  }
);
Banner.Body = Body2;
Banner.RightGutter = RightGutter;
Banner.Title = Title;
Banner.displayName = "Banner_VHS";

// src/components/Box/Box.tsx
import { forwardRef as forwardRef4 } from "react";
import { styled as styled18 } from "styled-components";
import { isNotNil as isNotNil11 } from "@wistia/type-guards";
import { jsx as jsx138 } from "react/jsx-runtime";
var ALIGN_CONTENT_MAP = {
  around: "space-around",
  between: "space-between",
  center: "center",
  end: "flex-end",
  start: "flex-start",
  stretch: "stretch"
};
var ALIGN_ITEMS_MAP = {
  baseline: "baseline",
  center: "center",
  end: "flex-end",
  start: "flex-start",
  stretch: "stretch"
};
var JUSTIFY_CONTENT_MAP = {
  around: "space-around",
  between: "space-between",
  center: "center",
  end: "flex-end",
  evenly: "space-evenly",
  start: "flex-start"
};
var ALIGN_SELF_MAP = {
  auto: "auto",
  baseline: "baseline",
  center: "center",
  end: "flex-end",
  start: "flex-start",
  stretch: "stretch"
};
var getFillStyle = (fill) => {
  if (fill === "horizontal") {
    return "width: 100%;";
  }
  if (fill === "vertical") {
    return "height: 100%;";
  }
  if (fill) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return void 0;
};
var getFillViewportStyle = (fillViewport) => {
  if (fillViewport === "horizontal") {
    return "width: 100vw;";
  }
  if (fillViewport === "vertical") {
    return "height: 100vh;";
  }
  if (fillViewport) {
    return `
      width: 100vw;
      height: 100vh;
    `;
  }
  return void 0;
};
var getGapStyle = (gap) => {
  if (isNotNil11(gap)) {
    if (isObject(gap)) {
      return Object.entries(gap).map(([key, value]) => `${key}-gap: ${value};`).join("");
    }
    return `gap: ${gap};`;
  }
  return void 0;
};
var BoxComponent = styled18.div`
  align-content: ${({ $alignContent }) => ALIGN_CONTENT_MAP[$alignContent]};
  align-items: ${({ $alignItems }) => ALIGN_ITEMS_MAP[$alignItems]};
  align-self: ${({ $alignSelf }) => $alignSelf ? ALIGN_SELF_MAP[$alignSelf] : null};
  display: ${({ $inline }) => isNotNil11($inline) && $inline ? "inline-flex" : "flex"};
  flex-basis: ${({ $basis }) => isNotNil11($basis) ? $basis : null};
  flex-direction: ${({ $flexDirection }) => $flexDirection};
  ${({ $fillBox }) => getFillStyle($fillBox)};
  ${({ $fillViewport }) => getFillViewportStyle($fillViewport)};

  /* Box children styles */
  flex-grow: ${({ $grow }) => isNotNil11($grow) ? $grow : null};
  flex-shrink: ${({ $shrink }) => isNotNil11($shrink) ? $shrink : null};
  flex-wrap: ${({ $wrapItems }) => $wrapItems ? "wrap" : "nowrap"};
  ${({ $gap }) => getGapStyle($gap)};
  justify-content: ${({ $justifyContent }) => JUSTIFY_CONTENT_MAP[$justifyContent]};
  order: ${({ $order }) => isNotNil11($order) ? $order : null};
`;
var Box = forwardRef4(
  ({
    alignContent = "stretch",
    alignItems = "start",
    alignSelf,
    basis,
    children,
    direction = "row",
    fill = false,
    fillViewport = false,
    gap,
    grow,
    inline = false,
    justifyContent = "start",
    order,
    renderAs = "div",
    shrink,
    wrapItems = false,
    ...otherProps
  }, ref) => {
    return /* @__PURE__ */ jsx138(
      BoxComponent,
      {
        ref,
        $alignContent: alignContent,
        $alignItems: alignItems,
        $alignSelf: alignSelf,
        $basis: basis,
        $fillBox: fill,
        $fillViewport: fillViewport,
        $flexDirection: direction,
        $gap: gap,
        $grow: grow,
        $inline: inline,
        $justifyContent: justifyContent,
        $order: order,
        $shrink: shrink,
        $wrapItems: wrapItems,
        as: renderAs,
        ...otherProps,
        children
      }
    );
  }
);
Box.displayName = "Box_VHS";

// src/components/ButtonLink/ButtonLink.tsx
import { forwardRef as forwardRef6 } from "react";
import { styled as styled20 } from "styled-components";
import { isNotNil as isNotNil12 } from "@wistia/type-guards";

// src/components/Link/Link.tsx
import { styled as styled19 } from "styled-components";
import { forwardRef as forwardRef5 } from "react";

// src/components/Link/linkStyle.tsx
import { css as css12 } from "styled-components";
import { darken as darken2 } from "polished";
var HOVER_DARKEN = 0.08;
var primaryHoverStyle2 = css12`
  color: ${({ theme: theme2 }) => darken2(HOVER_DARKEN, theme2.color.brandBlue500)};
`;
var primaryStyle2 = css12`
  color: ${({ theme: theme2 }) => theme2.color.brandBlue500};

  &:hover,
  &:focus {
    ${primaryHoverStyle2}
  }
`;
var secondaryHoverStyle2 = css12`
  color: rgb(255 255 255 / 80%);
`;
var secondaryStyle2 = css12`
  color: white;

  &:hover,
  &:focus {
    ${secondaryHoverStyle2}
  }
`;
var dangerousHoverStyle = css12`
  color: ${({ theme: theme2 }) => darken2(HOVER_DARKEN, theme2.color.red600)};
`;
var dangerousStyle = css12`
  color: ${({ theme: theme2 }) => theme2.color.red600};

  &:hover,
  &:focus {
    ${dangerousHoverStyle}
  }
`;
var disabledStyle2 = css12`
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  cursor: not-allowed;

  &:hover,
  &:focus {
    color: ${({ theme: theme2 }) => theme2.color.grey700};
  }
`;
var underlineOnHoverStyle = css12`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
var linkStyle = css12`
  cursor: pointer;
  font-weight: 400;
  text-decoration: underline;
  ${({ $underlineOnHover }) => $underlineOnHover && underlineOnHoverStyle}
  ${({ $disabledLink }) => $disabledLink && disabledStyle2}
  ${({ $variant }) => {
  if ($variant === "primary") {
    return primaryStyle2;
  }
  if ($variant === "secondary") {
    return secondaryStyle2;
  }
  if ($variant === "dangerous") {
    return dangerousStyle;
  }
  return primaryStyle2;
}}
  ${({ $variant, $forceState }) => {
  switch ($forceState) {
    case "hover":
    case "focus":
      if ($variant === "primary") {
        return primaryHoverStyle2;
      }
      if ($variant === "secondary") {
        return secondaryHoverStyle2;
      }
      if ($variant === "dangerous") {
        return dangerousHoverStyle;
      }
      break;
    /* no active styles yet */
    case "active":
    case void 0:
    default:
      return void 0;
  }
  return void 0;
}}
`;
var unstyledLinkStyle = css12`
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  ${({ $underlineOnHover }) => $underlineOnHover && underlineOnHoverStyle}
`;

// src/components/Link/Link.tsx
import { jsx as jsx139 } from "react/jsx-runtime";
var LinkComponent = styled19.a`
  ${linkStyle}
`;
var UnstyledLinkComponent = styled19.a`
  ${unstyledLinkStyle}
`;
var appendParams = (href, params) => {
  const keys2 = Object.keys(params);
  if (!keys2.length) {
    return href;
  }
  const url = new URL(href);
  keys2.forEach((key) => {
    url.searchParams.set(key, params[key]);
  });
  return url.toString();
};
var generateHref = (href, type, params, disabled) => {
  if (disabled) {
    return null;
  }
  let to = href;
  if (type === "phone") {
    to = `tel:${href}`;
  }
  if (type === "email") {
    to = `mailto:${href}`;
  }
  if (params !== void 0) {
    to = appendParams(to, params);
  }
  return to;
};
var DefaultLinkWrapper = ({
  RenderedLink,
  children,
  ...anchorProps
}) => /* @__PURE__ */ jsx139(RenderedLink, { ...anchorProps, children });
var Link = forwardRef5(
  ({
    href,
    beforeAction,
    children,
    disabled = false,
    forceState,
    nested = false,
    params,
    type = "default",
    unstyled = false,
    variant = "primary",
    underlineOnHover = false,
    ...otherProps
  }, ref) => {
    const to = generateHref(href, type, params, disabled);
    const externalLinkProps = type === "external" ? { target: "_blank", rel: "noopener noreferrer" } : null;
    const handleClick = async (event, routingCallback = null) => {
      if (disabled) {
        event.preventDefault();
      } else if (beforeAction) {
        event.preventDefault();
        try {
          await beforeAction();
        } catch (error) {
          console.error(error);
        } finally {
          if (routingCallback !== null) {
            routingCallback();
          } else {
            window.location.assign(to);
          }
        }
      } else {
      }
    };
    const RenderedLink = unstyled ? UnstyledLinkComponent : LinkComponent;
    const { Link: LinkWrapper = DefaultLinkWrapper } = useWrappers() || {};
    const component = /* @__PURE__ */ jsx139(
      LinkWrapper,
      {
        ref,
        $disabledLink: disabled,
        $forceState: forceState,
        $underlineOnHover: underlineOnHover,
        $unstyled: unstyled,
        $variant: variant,
        href: to,
        onClick: handleClick,
        RenderedLink,
        ...externalLinkProps,
        ...otherProps,
        children
      }
    );
    if (nested) {
      return /* @__PURE__ */ jsx139(
        "object",
        {
          "data-testid": "link-object",
          type: "hacky/hack",
          children: component
        }
      );
    }
    return component;
  }
);
Link.displayName = "Link_VHS";

// src/components/ButtonLink/ButtonLink.tsx
import { jsx as jsx140 } from "react/jsx-runtime";
var StyledButtonLink = styled20(Link)`
  ${buttonStyle};

  /* Links are disabled by removing their href */
  &:not([href]) {
    ${disabledButtonStyle};
  }
`;
var ButtonLink = forwardRef6(
  ({
    beforeAction,
    children,
    colorOverride,
    disabled = false,
    forceState,
    fullWidth = false,
    href,
    icon,
    iconName,
    iconPosition = "start",
    label,
    labelWrap = false,
    params,
    size = "lg",
    square = false,
    textAlign = "center",
    type = "default",
    variant = "primary",
    ...otherProps
  }, ref) => {
    const buttonColor = getButtonColor(colorOverride, variant, useThemeColor());
    const getContent = () => {
      if (isNotNil12(children)) {
        return children;
      }
      if (isNotNil12(label)) {
        return label;
      }
      return null;
    };
    const styledProps = {
      ref,
      $buttonColor: buttonColor,
      $forceState: forceState,
      $fullWidth: fullWidth,
      $icon: icon,
      $iconPosition: iconPosition,
      $isLoading: false,
      $labelWrap: labelWrap,
      $size: size,
      $square: square,
      $textAlign: textAlign,
      $variant: variant,
      beforeAction,
      disabled,
      href,
      params,
      type,
      unstyled: true,
      ...otherProps
    };
    return /* @__PURE__ */ jsx140(StyledButtonLink, { ...styledProps, children: /* @__PURE__ */ jsx140(
      ButtonContent,
      {
        icon,
        iconName,
        iconPosition,
        isLoading: false,
        size,
        textAlign,
        children: getContent()
      }
    ) });
  }
);
ButtonLink.displayName = "ButtonLink_VHS";

// src/components/Checkbox/Checkbox.tsx
import { forwardRef as forwardRef7, useState as useState14, isValidElement } from "react";
import { isNonEmptyString as isNonEmptyString4, isNotUndefined as isNotUndefined3 } from "@wistia/type-guards";

// src/components/Checkbox/OptionStyledComponent.tsx
import { styled as styled21, css as css13 } from "styled-components";
var OptionWrapper = styled21.input`
  align-self: flex-start;
  border-radius: 3px;
  box-shadow: ${({ type }) => type === "checkbox" ? "inset 0 0.5px 1.5px 0 rgba(0, 0, 0, 0.38)" : "none"};
  margin-top: ${({ theme: theme2 }) => theme2.spacing.space01};

  /*
   * There are some high-level styles for VMA modals that broadly target input[type="radio"]
   * and input[type="checkbox"], so this extra specificity is needed to override those styles.
   */
  &&& {
    margin: ${({ theme: theme2 }) => theme2.spacing.space01} 0 0;
  }
`;
var OptionLabel = styled21.div`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  font-size: 14px;
  line-height: 20px;
`;
var OptionSubLabel = styled21.div`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  font-size: 12px;
  line-height: 16px;
`;
var disabledStyle3 = css13`
  cursor: not-allowed;
  opacity: 0.5;
`;
var errorHoverStyle = css13`
  border-color: transparent;
`;
var errorFocusedStyle = css13`
  outline: none;
`;
var errorStyle = css13`
  background-color: ${({ theme: theme2 }) => theme2.color.error100};
  border: 1px solid ${({ theme: theme2 }) => theme2.color.error500};

  &:hover,
  &:focus,
  &:active {
    border-color: transparent !important;
  }
`;
var defaultHoverStyle = css13`
  background-color: ${({ theme: theme2 }) => theme2.color.grey300};
  cursor: pointer;
`;
var defaultFocusedStyle = css13`
  ${defaultHoverStyle}
`;
var Container = styled21.label`
  align-items: center;
  border: 1px solid transparent;
  border-radius: 4px;
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space04};
  padding: ${({ theme: theme2 }) => theme2.spacing.space02};
  width: ${({ $direction }) => $direction === "column" ? "100%" : "auto"};

  &:hover {
    ${defaultHoverStyle}
  }

  ${({ $hasError }) => $hasError && errorStyle};
  ${({ $disabled }) => $disabled && disabledStyle3};
  ${({ $isFocused }) => $isFocused && defaultFocusedStyle};
  ${({ $isFocused, $hasError }) => $isFocused && $hasError && errorFocusedStyle};
  ${({ $forceState }) => $forceState === "focus" && defaultFocusedStyle};
  ${({ $forceState }) => $forceState === "hover" && defaultHoverStyle};
  ${({ $forceState, $hasError }) => $forceState === "focus" && $hasError && errorFocusedStyle};
  ${({ $forceState, $hasError }) => $forceState === "hover" && $hasError && errorHoverStyle};
`;

// src/components/Checkbox/Checkbox.tsx
import { Fragment as Fragment10, jsx as jsx141, jsxs as jsxs18 } from "react/jsx-runtime";
var Checkbox = forwardRef7(
  ({
    checked,
    direction = "column",
    disabled = false,
    forceState,
    hasError = false,
    id = "default",
    label = "default",
    labelDescription,
    name,
    onChange,
    required = false,
    value = "false",
    ...otherProps
  }, ref) => {
    const [focused, setFocused] = useState14(false);
    return /* @__PURE__ */ jsxs18(
      Container,
      {
        ref,
        $direction: direction,
        $disabled: disabled,
        $forceState: forceState,
        $hasError: hasError,
        $isFocused: focused,
        "aria-invalid": isNotUndefined3(hasError) && hasError ? "true" : "false",
        ...otherProps,
        children: [
          /* @__PURE__ */ jsx141(
            OptionWrapper,
            {
              "aria-checked": checked,
              checked,
              "data-testid": "checkbox-option-wrapper",
              disabled,
              id,
              name,
              onBlur: () => setFocused(false),
              onChange,
              onFocus: () => setFocused(true),
              required,
              type: "checkbox",
              value
            }
          ),
          /* @__PURE__ */ jsxs18(OptionLabel, { children: [
            /* @__PURE__ */ jsx141(Fragment10, { children: label }),
            isNonEmptyString4(labelDescription) || isValidElement(labelDescription) ? /* @__PURE__ */ jsx141(OptionSubLabel, { "data-testid": "checkbox-option-sub-label", children: labelDescription }) : null
          ] })
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox_VHS";

// src/components/CheckboxGroup/CheckboxGroup.tsx
import { styled as styled26 } from "styled-components";
import { isNotNil as isNotNil13 } from "@wistia/type-guards";

// src/private/components/FormFieldWrapper/FormFieldWrapper.tsx
import { styled as styled25 } from "styled-components";

// src/components/Label/Label.tsx
import { styled as styled22, css as css14 } from "styled-components";
import { jsx as jsx142 } from "react/jsx-runtime";
var requiredStyle = css14`
  &::after {
    color: red;
    content: ' *';
  }
`;
var disabledStyle4 = css14`
  color: ${({ theme: theme2 }) => theme2.color.grey500};
`;
var SPACE_BETWEEN_LABEL_AND_INPUT = "8px";
var LabelComponent = styled22.label`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  display: block;
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.semiBold};
  margin-bottom: ${SPACE_BETWEEN_LABEL_AND_INPUT};
  width: 100%;

  /* if label is wrapping an input this ensures it appears beneath the label with a nice margin */
  > input,
  > textarea,
  > select {
    display: block;
    margin-top: ${SPACE_BETWEEN_LABEL_AND_INPUT};
  }

  > label {
    font-weight: normal;
  }

  ${({ $screenReaderOnly }) => $screenReaderOnly && screenReaderOnlyStyle}
  ${({ $disabled }) => $disabled && disabledStyle4}
  ${({ $required }) => $required && requiredStyle}
`;
var Label = ({
  renderAs = "label",
  children,
  disabled = false,
  htmlFor,
  required = false,
  screenReaderOnly = false,
  ...otherProps
}) => /* @__PURE__ */ jsx142(
  LabelComponent,
  {
    $disabled: disabled,
    $renderAs: renderAs,
    $required: required,
    $screenReaderOnly: screenReaderOnly,
    as: renderAs,
    htmlFor: renderAs === "label" ? htmlFor : "",
    ...otherProps,
    children
  }
);
Label.displayName = "Label_VHS";

// src/components/FormFieldError/FormFieldError.tsx
import { styled as styled23 } from "styled-components";
import { isNil as isNil7 } from "@wistia/type-guards";
import { jsx as jsx143 } from "react/jsx-runtime";
var FieldError = styled23.div`
  align-items: center;
  border-left: 4px solid ${({ theme: theme2 }) => theme2.color.error500};
  color: ${({ theme: theme2 }) => theme2.color.error600};
  font-size: 12px;
  line-height: 16px;
  margin-top: ${({ theme: theme2 }) => theme2.spacing.space02};
  padding-left: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var FormFieldError = ({
  children,
  ...otherProps
}) => {
  if (isNil7(children)) {
    return null;
  }
  return /* @__PURE__ */ jsx143(FieldError, { ...otherProps, children });
};
FormFieldError.displayName = "FormFieldError_VHS";

// src/components/LabelDescription/LabelDescription.tsx
import { styled as styled24, css as css15 } from "styled-components";
import { jsx as jsx144 } from "react/jsx-runtime";
var disabledStyle5 = css15`
  color: ${({ theme: theme2 }) => theme2.color.grey500};
`;
var LabelDescriptionComponent = styled24.div`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  display: block;
  font-size: 14px;
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space03};
  ${({ disabled }) => disabled && disabledStyle5};
`;
var LabelDescription = ({
  children,
  disabled = false,
  ...otherProps
}) => /* @__PURE__ */ jsx144(
  LabelDescriptionComponent,
  {
    disabled,
    ...otherProps,
    children
  }
);
LabelDescription.displayName = "LabelDescription_VHS";

// src/private/components/FormFieldWrapper/FormFieldWrapper.tsx
import { jsx as jsx145, jsxs as jsxs19 } from "react/jsx-runtime";
var StyledFormFieldWrapper = styled25.div`
  width: 100%;
`;
var FormFieldWrapper = ({
  disabled = false,
  error = null,
  label = null,
  labelDescription = null,
  labelProps = {},
  labelType = "label",
  name,
  children,
  labelId = null,
  ...otherProps
}) => {
  const computedId = labelId ?? `group-id-${name}`;
  const computedAriaLabelledby = labelType === "legend" ? computedId : null;
  const combinedLabelProps = {
    id: computedId,
    htmlFor: `field-${name}`,
    renderAs: labelType,
    disabled: labelProps.disabled ?? disabled,
    screenReaderOnly: labelProps.screenReaderOnly ?? false
  };
  return /* @__PURE__ */ jsxs19(
    StyledFormFieldWrapper,
    {
      ...otherProps,
      "aria-labelledby": computedAriaLabelledby ?? void 0,
      children: [
        label !== null ? /* @__PURE__ */ jsx145(Label, { ...combinedLabelProps, children: label }) : null,
        labelDescription !== null ? /* @__PURE__ */ jsx145(LabelDescription, { disabled, children: labelDescription }) : null,
        error !== null ? /* @__PURE__ */ jsx145(FormFieldError, { "data-testid": `form-field-error-${name}`, children: error }) : null,
        children
      ]
    }
  );
};
FormFieldWrapper.displayName = "FormFieldWrapper";

// src/components/CheckboxGroup/CheckboxGroup.tsx
import { jsx as jsx146 } from "react/jsx-runtime";
var StyledOptionContainer = styled26.div`
  display: flex;
  flex-direction: ${({ direction }) => direction === "row" ? "row" : "column"};
  gap: ${({ direction, theme: theme2 }) => direction === "row" ? theme2.spacing.space04 : theme2.spacing.space01};
`;
var CheckboxGroup = ({
  direction = "column",
  disabled = false,
  error,
  label,
  labelDescription,
  labelProps = {},
  name,
  onChange,
  options,
  value = false,
  ...otherProps
}) => /* @__PURE__ */ jsx146(
  FormFieldWrapper,
  {
    disabled,
    error,
    label,
    labelDescription,
    labelProps,
    labelType: "legend",
    name,
    role: "group",
    ...otherProps,
    children: /* @__PURE__ */ jsx146(StyledOptionContainer, { direction, children: options.map((option) => {
      const isChecked = Array.isArray(value) ? value.includes(option.name) : value;
      return /* @__PURE__ */ jsx146(
        Checkbox,
        {
          checked: isChecked,
          direction,
          disabled,
          onChange: (event) => isNotNil13(onChange) ? onChange(event, option.name) : void 0,
          ...option
        },
        option.name
      );
    }) })
  }
);
CheckboxGroup.displayName = "CheckboxGroup_VHS";

// src/components/ClickArea/ClickArea.tsx
import { forwardRef as forwardRef8, useCallback as useCallback9 } from "react";
import { styled as styled27 } from "styled-components";
import { jsx as jsx147 } from "react/jsx-runtime";
var ClickAreaComponent = styled27.div`
  cursor: pointer;
`;
var ClickArea = forwardRef8(
  ({ renderAs = "div", className, onClick, targetRef, ...props }, ref) => {
    const handleClick = useCallback9(
      (event) => {
        const node = targetRef.current;
        if (node === null) {
          return;
        }
        if (event.defaultPrevented) {
          return;
        }
        if (node.contains(event.target)) {
          if (onClick) {
            onClick(event);
          }
        } else {
          event.stopPropagation();
          event.preventDefault();
          node.focus();
          node.click();
        }
      },
      [onClick, targetRef]
    );
    return /* @__PURE__ */ jsx147(
      ClickAreaComponent,
      {
        ref,
        as: renderAs,
        role: "presentation",
        ...props,
        onClick: handleClick
      }
    );
  }
);
ClickArea.displayName = "ClickArea_VHS";

// src/components/CloseButton/CloseButton.tsx
import { styled as styled28 } from "styled-components";

// src/components/CloseButton/CloseIcon.tsx
import { jsx as jsx148 } from "react/jsx-runtime";
var CloseIcon2 = () => /* @__PURE__ */ jsx148(
  "svg",
  {
    height: "22",
    preserveAspectRatio: "xMidYMid meet",
    style: { display: "block" },
    viewBox: "0 0 20 20",
    width: "22",
    children: /* @__PURE__ */ jsx148(
      "path",
      {
        d: "M17 15.85289l-5.8533769-5.85435067L17 4.14710997 15.85289 3l-5.8514293 5.85240314L4.14710997 3 3 4.14710997l5.85240314 5.85240314L3 15.85289 4.14710997 17l5.85435073-5.8524031L15.8519163 17z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/CloseButton/CloseButton.tsx
import { jsx as jsx149 } from "react/jsx-runtime";
var Button2 = styled28.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  cursor: pointer;
  display: flex;
  height: 42px;
  justify-content: center;
  pointer-events: auto;
  width: 42px;

  /* Normalize focus behavior of Firefox */
  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  &:focus {
    outline: thin dotted ${({ theme: theme2 }) => theme2.color.grey500};
  }

  &:hover {
    color: ${({ theme: theme2 }) => theme2.color.grey900};
  }
`;
var CloseButton = ({ onClick, ...otherProps }) => /* @__PURE__ */ jsx149(
  Button2,
  {
    onClick,
    title: "Close",
    ...otherProps,
    children: /* @__PURE__ */ jsx149(CloseIcon2, {})
  }
);
CloseButton.displayName = "CloseButton_VHS";

// src/components/CollapsibleGroup/CollapsibleGroup.tsx
import { useEffect as useEffect11, useLayoutEffect as useLayoutEffect4, useState as useState15, useId as useId2 } from "react";
import { styled as styled29, css as css16 } from "styled-components";
import { darken as darken3 } from "polished";
import { isNil as isNil8, isNonEmptyString as isNonEmptyString5, isNotUndefined as isNotUndefined4 } from "@wistia/type-guards";
import { jsx as jsx150, jsxs as jsxs20 } from "react/jsx-runtime";
var DARKENING_AMOUNT = 0.08;
var BAR_HEIGHT = 48;
var BUTTON_OFFSET = 16;
var CARET_SIZE = 16;
var PADDING_X = 16;
var TRANSITION_TIME2 = "0.2s";
var VARIANT_BRAND_BLUE = "brandblue";
var CARET_DIRECTION_UP = "up";
var CARET_DIRECTION_DOWN = "down";
var CARET_DIRECTION_LEFT = "left";
var CARET_DIRECTION_RIGHT = "right";
var StyledCollapsibleGroup = styled29.div`
  & + & {
    margin-top: ${({ theme: theme2 }) => theme2.spacing.space01};
  }
`;
var computeBackgroundColor = ({
  backgroundColor,
  open: open2,
  theme: theme2,
  variant
}) => {
  const closedBgColor = backgroundColor ?? theme2.color.grey100;
  const openBgColor = variant === VARIANT_BRAND_BLUE ? theme2.color.brandBlue100 : closedBgColor;
  return open2 ? openBgColor : closedBgColor;
};
var StyledControlWrapper = styled29.div`
  align-items: center;
  background-color: ${({ theme: theme2, $backgroundColor, $open, $variant }) => computeBackgroundColor({
  backgroundColor: $backgroundColor,
  open: $open,
  theme: theme2,
  variant: $variant
})};
  color: ${({ $open, theme: theme2 }) => $open ? theme2.color.brandBlue600 : theme2.color.grey900};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  min-height: ${BAR_HEIGHT}px;
  padding-right: ${PADDING_X}px;

  &:hover {
    background-color: ${({ theme: theme2, $backgroundColor, $open, $variant }) => darken3(
  DARKENING_AMOUNT,
  computeBackgroundColor({
    backgroundColor: $backgroundColor,
    open: $open,
    theme: theme2,
    variant: $variant
  })
)};
  }
`;
var Caret = styled29.span`
  color: ${({ $isOpen, theme: theme2, $variant }) => $isOpen && $variant === VARIANT_BRAND_BLUE ? theme2.color.brandBlue600 : theme2.color.grey500};
  display: inline-flex;
  height: ${CARET_SIZE}px;
  transform: rotate(
    ${({ $direction }) => {
  switch ($direction) {
    case CARET_DIRECTION_UP:
      return "-180deg";
    case CARET_DIRECTION_DOWN:
      return "0deg";
    case CARET_DIRECTION_LEFT:
      return "90deg";
    case CARET_DIRECTION_RIGHT:
      return "-90deg";
    default:
      return "0deg";
  }
}}
  );
  transition: transform ${TRANSITION_TIME2} ease-out;
  width: ${CARET_SIZE}px;

  > svg {
    height: 100%;
    width: 100%;
  }
`;
var showOnHoverStyles = () => css16`
  opacity: 0;
  transition: opacity ${TRANSITION_TIME2} ease-out;

  ${StyledControlWrapper}:hover &,
  ${StyledControlWrapper}:focus &,
  ${StyledControlWrapper}:focus-within & {
    opacity: 1;
  }
`;
var StyledLabel = styled29(Text)`
  /*
  * This should be able to support an input field when used to rename itself
  */

  color: ${({ $open, theme: theme2, $styleVariant }) => $open && $styleVariant === VARIANT_BRAND_BLUE ? theme2.color.brandBlue600 : null};
  line-height: ${BAR_HEIGHT}px;
  margin: 0 auto 0 ${BUTTON_OFFSET}px;
  user-select: none;
  width: 100%;
`;
var StyledContent = styled29.div`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  overflow: ${({ $isOpen }) => $isOpen ? "visible" : "hidden"};
  padding: ${({ $contentPadding }) => $contentPadding ?? 0};
  transition: ${({ $showTransition }) => $showTransition ? `height ${TRANSITION_TIME2} ease-out` : void 0};
  visibility: ${({ $isOpen }) => $isOpen ? "visible" : "hidden"};
  ${({ $height, $isOpen }) => isNotUndefined4($height) && Number.isFinite($height) && `
    height: ${$isOpen ? $height : 0}px;
  `};
  ${({ $maxHeight }) => isNotUndefined4($maxHeight) && Number.isFinite($maxHeight) && `
    max-height: ${$maxHeight}px;
  `};
`;
var CaretButton = styled29(Button)`
  height: 24px;
  margin-left: ${BUTTON_OFFSET}px;
  order: ${({ $layout }) => $layout === "leading" ? -1 : 1};
  width: 24px;

  &:focus {
    outline: 1px dashed ${({ theme: theme2 }) => theme2.color.grey500};
  }
`;
var StyledActionControl = styled29.span`
  ${({ $showActionsOnHover }) => $showActionsOnHover && showOnHoverStyles()};
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  margin-right: -${PADDING_X}px;
  min-height: ${BAR_HEIGHT}px;
  width: ${BAR_HEIGHT}px;

  > * {
    flex-shrink: 0;
    min-width: 0;
  }
`;
var ActionControl = ({ children, showActionsOnHover }) => {
  if (isNil8(children)) {
    return null;
  }
  return (
    // eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions
    /* @__PURE__ */ jsx150(
      StyledActionControl,
      {
        $showActionsOnHover: showActionsOnHover,
        onClick: (event) => {
          event.stopPropagation();
        },
        children
      }
    )
  );
};
var CollapsibleGroup = ({
  actions,
  backgroundColor,
  caretDirection = { open: CARET_DIRECTION_DOWN, closed: CARET_DIRECTION_RIGHT },
  children = null,
  contentPadding = null,
  hideCaret = false,
  id,
  isOpen,
  label,
  layout = "leading",
  onOpenChange,
  showActionsOnHover = true,
  showTransition = false,
  variant,
  ...otherProps
}) => {
  const [open2, setOpen] = useState15(isOpen ?? false);
  const [dimensions, setDimensions] = useState15({
    width: void 0,
    height: void 0
  });
  const [wasEditingOnMouseDown, setWasEditingOnMouseDown] = useState15(false);
  const [targetRef, targetBox] = useElementObserver();
  const caretDirectionOpen = caretDirection.open;
  const caretDirectionClosed = caretDirection.closed;
  const generatedId = useId2();
  const computedId = isNonEmptyString5(id) ? id : `vhs-collapsible-group-${generatedId}`;
  useEffect11(() => {
    setDimensions({
      height: targetBox.height,
      width: targetBox.width
    });
  }, [targetBox]);
  useEffect11(() => {
    if (isOpen !== void 0) {
      setOpen(isOpen);
    }
  }, [isOpen]);
  useLayoutEffect4(() => {
    if (!targetRef.current) {
      return;
    }
    setDimensions({
      height: targetRef.current.offsetHeight,
      width: targetRef.current.offsetWidth
    });
  }, [targetRef]);
  const onClick = () => {
    if (wasEditingOnMouseDown) {
      setWasEditingOnMouseDown(false);
      return;
    }
    const newOpen = !open2;
    setOpen(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };
  const caret = /* @__PURE__ */ jsx150(
    CaretButton,
    {
      $layout: layout,
      "aria-expanded": open2,
      "aria-labelledby": computedId,
      noStyle: true,
      onClick,
      children: /* @__PURE__ */ jsx150(
        Caret,
        {
          $direction: open2 ? caretDirectionOpen : caretDirectionClosed,
          $isOpen: open2,
          $layout: layout,
          $variant: variant,
          children: /* @__PURE__ */ jsx150(Icon, { type: "caret-down" })
        }
      )
    }
  );
  return /* @__PURE__ */ jsxs20(StyledCollapsibleGroup, { ...otherProps, children: [
    /* @__PURE__ */ jsxs20(
      StyledControlWrapper,
      {
        $backgroundColor: backgroundColor,
        $open: open2,
        $variant: variant,
        onClick,
        children: [
          /* @__PURE__ */ jsx150(
            StyledLabel,
            {
              $open: open2,
              $styleVariant: variant,
              id: computedId,
              renderAs: "div",
              variant: "subtitle1",
              children: typeof label === "function" ? label({ open: open2, caret }) : label
            }
          ),
          !hideCaret && caret,
          /* @__PURE__ */ jsx150(ActionControl, { showActionsOnHover, children: actions })
        ]
      }
    ),
    /* @__PURE__ */ jsx150(
      StyledContent,
      {
        $backgroundColor: backgroundColor,
        $contentPadding: contentPadding,
        $height: dimensions.height,
        $isOpen: open2,
        $maxHeight: dimensions.height,
        $showTransition: showTransition,
        $variant: variant,
        "data-testid": "collapsible-group-content",
        children: /* @__PURE__ */ jsx150(
          "div",
          {
            ref: targetRef,
            style: { width: "100%", display: "inline-block" },
            children
          }
        )
      }
    )
  ] });
};
CollapsibleGroup.displayName = "CollapsibleGroup_VHS";

// src/components/Divider/Divider.tsx
import { styled as styled30 } from "styled-components";
import { jsx as jsx151 } from "react/jsx-runtime";
var DividerComponent = styled30.hr`
  background: none;
  border-bottom: none;
  border-left: none;
  border-right: none;
  border-top-color: ${({ $fillColor }) => $fillColor};
  border-top-style: ${({ $variant }) => $variant};
  border-top-width: ${({ $borderHeight }) => `${$borderHeight}px`};
  clear: both;
  height: 0;
  line-height: 0;
  margin: 0;
`;
var Divider = ({
  colorOverride = void 0,
  height = 1,
  variant = "dashed",
  ...otherProps
}) => {
  const defaultFillColor = useThemeColor("grey300");
  const fillColor = colorOverride ?? defaultFillColor;
  return /* @__PURE__ */ jsx151(
    DividerComponent,
    {
      $borderHeight: height,
      $fillColor: fillColor,
      $variant: variant,
      ...otherProps
    }
  );
};
Divider.displayName = "Divider_VHS";

// src/components/FileSelect/FileSelect.tsx
import { forwardRef as forwardRef9 } from "react";
import { styled as styled31 } from "styled-components";
import { jsx as jsx152, jsxs as jsxs21 } from "react/jsx-runtime";
var maxFileSizeInGigs = 26;
var defaultMaximumFileSize = maxFileSizeInGigs * 1024;
var FileSelectComponent = styled31.label`
  display: inline-block;
  overflow: hidden;
  position: relative;

  /* capture clicks with a large positioned file input with 0 opacity */
  input[type='file'] {
    &::-webkit-file-upload-button {
      cursor: ${({ $disabled }) => $disabled ? "not-allowed" : "pointer"};
    }

    bottom: 0;
    cursor: ${({ $disabled }) => $disabled ? "not-allowed" : "pointer"};
    font-size: 10rem;
    left: 0;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;
var FileSelect = forwardRef9(
  ({
    accept,
    children,
    disabled = false,
    id,
    maxSize = defaultMaximumFileSize,
    multiple = false,
    name = "file-upload",
    onClick,
    onChange,
    onError,
    ...otherProps
  }, ref) => {
    const handleOnChange = (event) => {
      const { files } = event.target;
      if (maxSize && files) {
        const maxSizeInBytes = maxSize * 1024 * 1024;
        const inputFiles = Array.from(files);
        if (inputFiles.some((file) => file.size > maxSizeInBytes)) {
          if (onError) {
            onError(new Error(`File not accepted. Please choose a file under ${maxSize}MB.`));
          }
          return false;
        }
      }
      if (onChange) {
        onChange(event);
      }
      return true;
    };
    return /* @__PURE__ */ jsxs21(
      FileSelectComponent,
      {
        $disabled: disabled,
        "aria-controls": "filename",
        htmlFor: id,
        ...otherProps,
        children: [
          children,
          /* @__PURE__ */ jsx152(
            "input",
            {
              ref,
              accept,
              "data-testid": "file-select-input",
              disabled,
              id,
              multiple,
              name,
              onChange: handleOnChange,
              onClick,
              tabIndex: -1,
              type: "file"
            }
          )
        ]
      }
    );
  }
);
FileSelect.displayName = "FileSelect_VHS";

// src/components/Form/Form.tsx
import { useState as useState16, useEffect as useEffect14 } from "react";
import { Formik, Form as FormikForm } from "formik";
import { isNotNil as isNotNil15, isNotUndefined as isNotUndefined5, isUndefined as isUndefined2 } from "@wistia/type-guards";

// src/components/FormGlobalError/FormGlobalError.tsx
import { forwardRef as forwardRef10 } from "react";
import { useFormikContext as useFormikContext2 } from "formik";
import { styled as styled32 } from "styled-components";
import { isNonEmptyString as isNonEmptyString6, isNotNil as isNotNil14 } from "@wistia/type-guards";
import { jsx as jsx153, jsxs as jsxs22 } from "react/jsx-runtime";
var GlobalErrorsContainer = styled32.div`
  border-left: 4px solid;
  border-left-color: ${({ theme: theme2 }) => theme2.color.error500};
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space07};
  padding-left: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var ErrorsText = styled32.p`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var ErrorOrderedList = styled32.ol`
  list-style: none;
  margin: 0;
  padding-left: 0;
`;
var ErrorText = styled32.span`
  color: ${({ theme: theme2 }) => theme2.color.error600};
  line-height: 1.5;
`;
var ErrorLink = styled32(ErrorText)`
  cursor: pointer;
  text-decoration: underline;
`;
var errorsTitle = (count) => {
  let errorText = "error";
  if (count > 1) {
    errorText += "s";
  }
  return `${count} ${errorText} found on page`;
};
var scrollToField = (fieldName) => {
  const globalErrorElement = document.getElementById(`fieldset-${fieldName}`);
  globalErrorElement?.scrollIntoView({ behavior: "smooth" });
};
var FieldErrorsList = ({ errors = {}, statusError }) => /* @__PURE__ */ jsxs22(ErrorOrderedList, { children: [
  isNonEmptyString6(statusError) ? /* @__PURE__ */ jsx153(ErrorText, { children: statusError }) : null,
  Object.keys(errors).map((field) => /* @__PURE__ */ jsx153("li", { children: /* @__PURE__ */ jsx153(
    ErrorLink,
    {
      "data-testid": `form-global-error-${field}`,
      onClick: (event) => {
        event.preventDefault();
        scrollToField(field);
      },
      children: errors[field]
    }
  ) }, field))
] });
var FormGlobalError = forwardRef10((_, ref) => {
  const formikContext = useFormikContext2();
  const { status, errors } = formikContext;
  let errorsCount = Object.keys(errors).length;
  if (isNotNil14(status) && status["length"] > 0) {
    errorsCount += 1;
  }
  const showErrorContainer = isNotNil14(status) && !status["success"] || errorsCount;
  if (coerceToBoolean(showErrorContainer)) {
    return /* @__PURE__ */ jsxs22(GlobalErrorsContainer, { ref, children: [
      /* @__PURE__ */ jsx153(ErrorsText, { children: errorsTitle(errorsCount) }),
      /* @__PURE__ */ jsx153(
        FieldErrorsList,
        {
          errors,
          statusError: status
        }
      )
    ] });
  }
  return null;
});
FormGlobalError.displayName = "FormGlobalError_VHS";

// src/components/Toast/useToast.ts
import { useContext as useContext3 } from "react";
var useToast = () => useContext3(ToastContext);

// src/private/hooks/useDebounce/useDebounce.ts
import { useEffect as useEffect13 } from "react";

// src/private/hooks/useTimeoutFunc/useTimeoutFunc.ts
import { useCallback as useCallback10, useEffect as useEffect12, useRef as useRef10 } from "react";
var useTimeoutFunc = (func, timeout2 = 0) => {
  const readyRef = useRef10(false);
  const timeoutRef = useRef10(void 0);
  const callbackRef = useRef10(func);
  const isReady = useCallback10(() => readyRef.current, []);
  const set = useCallback10(() => {
    readyRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      readyRef.current = true;
      callbackRef.current();
    }, timeout2);
  }, [timeout2]);
  const clear = useCallback10(() => {
    readyRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);
  useEffect12(() => {
    callbackRef.current = func;
  }, [func]);
  useEffect12(() => {
    set();
    return clear;
  }, [timeout2]);
  return [isReady, clear, set];
};

// src/private/hooks/useDebounce/useDebounce.ts
var useDebounce = (func, timeout2 = 0, dependencies = []) => {
  const [isReady, cancel, reset] = useTimeoutFunc(func, timeout2);
  useEffect13(reset, dependencies);
  return [isReady, cancel];
};

// src/components/Form/Form.tsx
import { jsx as jsx154, jsxs as jsxs23 } from "react/jsx-runtime";
var didValuesChange = (newValues, oldValues) => JSON.stringify(newValues) !== JSON.stringify(oldValues);
var handleInitialValues = (initialValues, validationSchema) => {
  const filteredInitialValues = {};
  Object.keys(initialValues).forEach((key) => {
    if (initialValues[key] === null) {
      if (isNotUndefined5(validationSchema) && isNotUndefined5(validationSchema.fields) && isNotUndefined5(validationSchema.fields[key]) && // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      validationSchema.fields[key]._type === "boolean") {
        filteredInitialValues[key] = false;
      } else {
        filteredInitialValues[key] = void 0;
      }
    } else {
      filteredInitialValues[key] = initialValues[key];
    }
  });
  return filteredInitialValues;
};
var Form = ({
  children,
  className,
  formId,
  initialValues,
  onSubmit,
  scrollToFormErrorsOnSubmit = true,
  shareFormContext,
  style,
  submitOnChange = false,
  validate,
  validateOnBlur = false,
  validateOnChange = false,
  validationSchema,
  globalErrorRef,
  showGlobalError = true,
  innerRef
}) => {
  const [isSubmitting, setIsSubmitting] = useState16();
  const [isValid3, setIsValid] = useState16();
  const [errorCount, setErrorCount] = useState16(0);
  const showToast = useToast();
  const handleSubmit = (values, ref) => {
    const filteredValues = {};
    Object.keys(values).forEach((key) => {
      const keyValue = values[key];
      const isStringType = isNotUndefined5(validationSchema) && isNotUndefined5(validationSchema.fields[key]) && // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      validationSchema.fields[key]._type === "string";
      if (keyValue === null || keyValue === void 0) {
        filteredValues[key] = null;
        return;
      }
      if (typeof keyValue === "number") {
        filteredValues[key] = keyValue;
        return;
      }
      if (keyValue === false) {
        filteredValues[key] = false;
      }
      if (isStringType && !keyValue) {
        filteredValues[key] = null;
      }
      if (keyValue) {
        filteredValues[key] = isStringType ? keyValue.trim() : keyValue;
      }
    });
    onSubmit(filteredValues, ref);
  };
  const shouldScrollToFormErrorsOnSubmit = scrollToFormErrorsOnSubmit && (isUndefined2(isSubmitting) || !isSubmitting) && (isUndefined2(isValid3) || !isValid3) && // eslint-disable-next-line @typescript-eslint/unbound-method
  isNotUndefined5(globalErrorRef.current?.scrollIntoView);
  useEffect14(() => {
    const formElement = document.querySelector(`form[data-testid="${formId}"]`);
    if ((isUndefined2(isSubmitting) || !isSubmitting) && isNotUndefined5(isValid3) && isValid3 && formElement?.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    if (shouldScrollToFormErrorsOnSubmit) {
      const errorMessage = errorCount > 1 ? `there are ${errorCount} errors` : "there's an error!";
      showToast({
        variant: "error",
        message: `Uh oh, ${errorMessage}`
      });
      if (globalErrorRef.current) {
        globalErrorRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [
    errorCount,
    globalErrorRef,
    isSubmitting,
    isValid3,
    shouldScrollToFormErrorsOnSubmit,
    showToast,
    formId
  ]);
  const configuredInitialValues = handleInitialValues(initialValues, validationSchema);
  const debounceDelay = 750;
  return /* @__PURE__ */ jsx154(
    Formik,
    {
      enableReinitialize: true,
      initialValues: configuredInitialValues,
      innerRef: isNotNil15(innerRef) ? innerRef : () => {
      },
      onSubmit: handleSubmit,
      validate: isNotNil15(validate) ? validate : () => {
      },
      validateOnBlur,
      validateOnChange,
      validationSchema,
      children: (props) => {
        const [values, setValues] = useState16(props.values);
        useEffect14(() => {
          if (isNotUndefined5(shareFormContext)) {
            shareFormContext(props);
          }
          setIsSubmitting(props.isSubmitting);
          setIsValid(props.isValid);
          setErrorCount(Object.keys(props.errors).length);
          if (submitOnChange && didValuesChange(props.values, values)) {
            setValues(props.values);
          }
        }, [props, values]);
        if (submitOnChange) {
          const [,] = useDebounce(
            () => {
              if (didValuesChange(initialValues, values)) {
                void props.submitForm();
              }
            },
            debounceDelay,
            [values]
          );
        }
        return /* @__PURE__ */ jsxs23(
          FormikForm,
          {
            className,
            "data-testid": formId,
            noValidate: true,
            style,
            children: [
              showGlobalError ? /* @__PURE__ */ jsx154(FormGlobalError, { ref: globalErrorRef }) : null,
              children
            ]
          }
        );
      }
    }
  );
};
Form.displayName = "Form_VHS";

// src/components/FormButtons/FormButtons.tsx
import { useFormikContext as useFormikContext3 } from "formik";

// src/private/components/FormConnectorButton/FormConnectorButton.tsx
import { jsx as jsx155 } from "react/jsx-runtime";
var FormConnectorButton = ({
  type = "submit",
  isSubmitting = false,
  children,
  disabled = false,
  ...props
}) => {
  const isDisabled = isSubmitting || disabled;
  return /* @__PURE__ */ jsx155(
    Button,
    {
      disabled: isDisabled,
      type,
      ...props,
      children
    }
  );
};
FormConnectorButton.displayName = "FormConnectorButton";

// src/components/FormButtons/FormButtons.tsx
import { jsx as jsx156 } from "react/jsx-runtime";
var handleMouseDown = (event) => event.preventDefault();
var FormButtons = ({
  buttons,
  align = "left",
  ...props
}) => {
  const { isSubmitting } = useFormikContext3();
  return /* @__PURE__ */ jsx156(
    ButtonGroup,
    {
      align,
      ...props,
      children: buttons.map(({ name, type, ...buttonProps }) => /* @__PURE__ */ jsx156(
        FormConnectorButton,
        {
          isSubmitting,
          type,
          ...buttonProps,
          onMouseDown: handleMouseDown,
          children: name
        },
        name
      ))
    }
  );
};
FormButtons.displayName = "FormButtons_VHS";

// src/components/FormField/FormField.tsx
import { forwardRef as forwardRef14 } from "react";
import { styled as styled43 } from "styled-components";
import { Field } from "formik";

// src/components/FormField/inputTypeMap.ts
var inputTypeMap = {
  checkbox: {
    component: "FormConnectorCheckboxGroup",
    inputType: null
  },
  custom: {
    component: "FormConnectorCustomField",
    inputType: null
  },
  email: {
    component: "FormConnectorInput",
    inputType: "email"
  },
  hidden: {
    component: "FormConnectorHidden",
    inputType: "hidden"
  },
  multiline: {
    component: "FormConnectorInput",
    inputType: null
    // we render as `textarea` so no type needed
  },
  number: {
    component: "FormConnectorInput",
    inputType: "number"
  },
  password: {
    component: "FormConnectorInput",
    inputType: "password"
  },
  phone: {
    component: "FormConnectorInput",
    inputType: "tel"
  },
  radio: {
    component: "FormConnectorRadioGroup",
    inputType: null
  },
  search: {
    component: "FormConnectorInput",
    inputType: "search"
  },
  select: {
    component: "FormConnectorSelect",
    inputType: null
  },
  text: {
    component: "FormConnectorInput",
    inputType: "text"
  },
  time: {
    component: "FormConnectorInput",
    inputType: "time"
  },
  timePosition: {
    component: "FormConnectorInput",
    inputType: "text"
  },
  url: {
    component: "FormConnectorInput",
    inputType: "url"
  },
  richTextEditor: {
    component: "FormConnectorRichTextEditor",
    inputType: null
  }
};

// src/private/components/FormConnectorCheckboxGroup/FormConnectorCheckboxGroup.tsx
import { isArray, isNotNil as isNotNil16 } from "@wistia/type-guards";
import { jsx as jsx157 } from "react/jsx-runtime";
var FormConnectorCheckboxGroup = ({
  direction = "column",
  disabled = false,
  field: { name, value },
  form: { errors, setFieldValue },
  label,
  labelDescription,
  labelProps = {},
  onChangeCustom,
  options = []
}) => {
  const onChangeSingle = (event, optionValue) => {
    setFieldValue(optionValue, event.target.checked);
  };
  const onChangeMultiple = (event, optionValue) => {
    const fieldValues = value;
    if (isArray(fieldValues)) {
      if (event.target.checked) {
        fieldValues.push(optionValue);
      } else {
        const index = fieldValues.indexOf(optionValue);
        if (index > -1) {
          fieldValues.splice(index, 1);
        }
      }
    }
    setFieldValue(optionValue, fieldValues);
  };
  const onChange = Array.isArray(value) && options.length > 1 ? onChangeMultiple : onChangeSingle;
  const safeOptions = Array.isArray(options) ? options : [options];
  return /* @__PURE__ */ jsx157(
    CheckboxGroup,
    {
      direction,
      disabled,
      error: isNotNil16(errors) ? errors[name] : void 0,
      label,
      labelDescription,
      labelProps,
      name,
      onChange: (event, optionValue) => {
        onChange(event, optionValue);
        if (onChangeCustom) {
          onChangeCustom(event.target.checked);
        }
      },
      options: safeOptions,
      value
    }
  );
};
FormConnectorCheckboxGroup.displayName = "FormConnectorCheckboxGroup";

// src/private/components/FormConnectorCustomField/FormConnectorCustomField.tsx
import { isNotUndefined as isNotUndefined6 } from "@wistia/type-guards";
import { jsx as jsx158 } from "react/jsx-runtime";
var FormConnectorCustomField = ({
  customComponent: Element8,
  field,
  form,
  label = "",
  labelDescription,
  labelProps = {},
  ...otherProps
}) => {
  const { name } = field;
  const { errors } = form;
  return /* @__PURE__ */ jsx158(
    FormFieldWrapper,
    {
      error: isNotUndefined6(errors) ? errors[name] : void 0,
      label,
      labelDescription,
      labelProps,
      name,
      children: /* @__PURE__ */ jsx158(
        Element8,
        {
          field,
          form,
          ...otherProps
        }
      )
    }
  );
};
FormConnectorCustomField.displayName = "FormConnectorCustomField";

// src/private/components/FormConnectorInput/FormConnectorInput.tsx
import { isNotUndefined as isNotUndefined8 } from "@wistia/type-guards";

// src/components/Input/Input.tsx
import { forwardRef as forwardRef11, useEffect as useEffect16, useRef as useRef11 } from "react";
import { styled as styled35 } from "styled-components";
import { isNotNil as isNotNil18 } from "@wistia/type-guards";

// src/components/Input/InputStyledComponent.tsx
import { styled as styled33, css as css17 } from "styled-components";
var focusStyle = css17`
  border-color: ${({ theme: theme2 }) => theme2.color.brandBlue500};
  outline: none;
`;
var errorStyle2 = css17`
  border-color: ${({ theme: theme2 }) => theme2.color.error500};

  &:focus {
    border-color: ${({ theme: theme2 }) => theme2.color.error600};
  }
`;
var disabledStyle6 = css17`
  -webkit-opacity: 0.5;
  -webkit-text-fill-color: ${({ theme: theme2 }) => theme2.color.grey700};
  color: ${({ theme: theme2 }) => theme2.color.grey900}; /* iOS */
  cursor: not-allowed;
  opacity: 0.5 !important;

  &:focus {
    border-color: ${({ theme: theme2 }) => theme2.color.grey300};
  }
`;
var readOnlyStyle = css17`
  border: dashed 1px ${({ theme: theme2 }) => theme2.color.grey400};
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  outline: none;

  &:focus {
    border-color: ${({ theme: theme2 }) => theme2.color.grey300} !important;
  }
`;
var clickToCopyStyle = css17`
  ${readOnlyStyle}
  flex: 1;
`;
var textareaStyle = css17`
  line-height: 20px;
  min-height: 80px;
`;
var inputStyle = css17`
  background-color: white;
  border: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  border-radius: 4px;
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  display: block;
  font-family: ${({ theme: theme2, $monospace }) => $monospace && theme2.font.family.monospace};
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.regular};
  line-height: 20px;
  padding: ${({ theme: theme2 }) => theme2.spacing.space02};
  width: 100%;

  /* prevent text from appearing underneath icon */
  ${({ $hasIcon, $iconPosition }) => {
  if ($hasIcon) {
    if ($iconPosition === "start") {
      return `padding-left: 36px;`;
    }
    return `padding-right: 36px;`;
  }
  return void 0;
}}

  &::placeholder {
    color: ${({ theme: theme2 }) => theme2.color.grey500};
    opacity: 1; /* Firefox */
  }

  /* Microsoft Edge */
  /* stylelint-disable-next-line selector-no-vendor-prefix */
  &::-ms-input-placeholder {
    color: ${({ theme: theme2 }) => theme2.color.grey500};
  }

  &:focus {
    ${focusStyle}
  }

  /* Handle unwanted zoom into inputs on iOS devices
   * https://uxcellence.com/2014/fix-ios-input-zoom
   */
  @media (max-width: 450px) {
    input[type='email'],
    input[type='number'],
    input[type='password'],
    input[type='search'],
    input[type='tel'],
    input[type='text'],
    input[type='url'],
    textarea,
    select {
      font-size: 16px;
    }
  }
`;
var InputStyledComponent = styled33.input`
  ${inputStyle}
  ${({ as }) => as === "textarea" && textareaStyle};
  ${({ as }) => as !== "textarea" && ellipsisStyle};
  ${({ $clickToCopy }) => $clickToCopy && clickToCopyStyle};
  ${({ disabled }) => disabled && disabledStyle6};
  ${({ $forceState }) => $forceState === "focus" && focusStyle};
  ${({ $hasError }) => $hasError && errorStyle2};
  ${({ readOnly }) => readOnly && readOnlyStyle};
`;

// src/components/Input/ClickToCopy.tsx
import { useState as useState17, useEffect as useEffect15 } from "react";
import { styled as styled34, css as css18 } from "styled-components";
import { isFunction as isFunction2 } from "@wistia/type-guards";
import { darken as darken4 } from "polished";
import { jsx as jsx159, jsxs as jsxs24 } from "react/jsx-runtime";
var clickToCopyWrapperEnabledStyles = css18`
  input {
    cursor: pointer;
  }

  textarea {
    cursor: pointer;
  }
`;
var ClickToCopyWrapper = styled34.div`
  align-items: center;
  display: flex;
  position: relative;
  ${({ $disabled }) => !$disabled && clickToCopyWrapperEnabledStyles}
`;
var disabledClickToCopyButtonStyles = css18`
  cursor: not-allowed;
`;
var enabledClickToCopyButtonStyles = css18`
  &:hover {
    color: ${({ theme: theme2 }) => darken4("4%", theme2.color.grey700)};
  }

  &:active {
    color: ${({ theme: theme2 }) => darken4("8%", theme2.color.grey700)};
  }

  &:focus {
    outline: none;
  }
`;
var ClickToCopyButtonComponent = styled34(Button)`
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  position: absolute;
  right: ${({ theme: theme2 }) => `calc(${theme2.spacing.space02} + 1px)`}; /* space + border width */
  transition: color 0.1s ease;
  ${({ disabled }) => disabled ? disabledClickToCopyButtonStyles : enabledClickToCopyButtonStyles}
`;
var ClickToCopyIcon = styled34(Icon)`
  ${({ disabled }) => disabled ? `cursor: not-allowed;` : "cursor: pointer;"}
`;
var ClickToCopyButton = ({ onClick, disabled = false }) => {
  const disabledColor = useThemeColor("grey500");
  return /* @__PURE__ */ jsx159(
    ClickToCopyButtonComponent,
    {
      "aria-disabled": disabled,
      "aria-label": "Click to Copy",
      disabled,
      noStyle: true,
      onClick,
      children: /* @__PURE__ */ jsx159(
        ClickToCopyIcon,
        {
          color: disabled ? disabledColor : "currentColor",
          disabled,
          size: "md",
          type: disabled ? "copy-disabled" : "copy"
        }
      )
    }
  );
};
var ClickToCopy = ({
  inputComponent: InputStyledComponent2,
  inputProps,
  onClick
}) => {
  const showToast = useToast();
  const [focusTime, setFocusTime] = useState17(0);
  const { value, disabled } = inputProps;
  const disabledValue = disabled ?? false;
  useEffect15(() => {
    if (focusTime > 0) {
      const timer = setTimeout(() => setFocusTime(focusTime - 1), 1e3);
      return () => clearTimeout(timer);
    }
    return () => {
    };
  }, [focusTime]);
  const handleOnClick = () => {
    void (async () => {
      if (disabledValue) {
        return;
      }
      await copyToClipboard(value);
      if (isFunction2(onClick)) {
        onClick();
      }
      showToast({ message: "Copied to clipboard" });
      setFocusTime(5);
    })();
  };
  const forceState = focusTime > 0 ? {
    $forceState: "focus"
  } : void 0;
  const inputComponentProps = {
    ...inputProps,
    ...forceState
  };
  const buttonProps = {
    onClick: handleOnClick,
    disabled: disabledValue,
    "aria-disabled": disabledValue
  };
  return (
    // eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions
    /* @__PURE__ */ jsxs24(
      ClickToCopyWrapper,
      {
        $disabled: disabledValue,
        onClick: handleOnClick,
        children: [
          /* @__PURE__ */ jsx159(InputStyledComponent2, { ...inputComponentProps }),
          /* @__PURE__ */ jsx159(ClickToCopyButton, { ...buttonProps })
        ]
      }
    )
  );
};

// src/components/Input/timePositionUtils.ts
import { isNil as isNil9, isNotNil as isNotNil17, isNotUndefined as isNotUndefined7, isString as isString6 } from "@wistia/type-guards";
var SEC_AND_MIN_MAX = 60;
var stripInvalidCharacters = (string) => string.replace(/[^0-9:]/g, "");
var parseNumber = (string) => string ? Number.parseFloat(string) : 0;
var timeUnitsToString = (seconds, minutes, hours) => {
  let formattedString = isNotNil17(hours) && Number(hours) > 0 ? `${hours}:` : "";
  formattedString += `${minutes}:${seconds}`;
  return formattedString;
};
var formatString = (string) => {
  const stringArray = string.split(":");
  const [initialSeconds, initialMinutes, initialHours] = stringArray.reverse().map((stringNumber) => parseNumber(stringNumber));
  let seconds = 0;
  let minutes = 0;
  let hours = 0;
  if (isNotNil17(initialSeconds)) {
    minutes = Math.floor(initialSeconds / SEC_AND_MIN_MAX);
    if (isNotNil17(initialMinutes)) {
      minutes += initialMinutes;
    }
  }
  if (isNotNil17(initialSeconds)) {
    seconds = initialSeconds % SEC_AND_MIN_MAX;
  }
  const secondsString = seconds.toString();
  if (secondsString.length === 1) {
    seconds = secondsString.padStart(2, "0");
  }
  const initialHoursOr0 = isNotUndefined7(initialHours) ? initialHours : 0;
  hours = Math.floor(minutes / SEC_AND_MIN_MAX) + initialHoursOr0;
  minutes %= SEC_AND_MIN_MAX;
  const minutesString = minutes.toString();
  if (hours && minutesString.length === 1) {
    minutes = minutesString.padStart(2, "0");
  }
  return timeUnitsToString(seconds, minutes, hours);
};
var timePositionFormat = (value) => {
  if (isNil9(value) || !isString6(value)) {
    return "0:00";
  }
  const cleanString = stripInvalidCharacters(value);
  return formatString(cleanString);
};

// src/components/Input/Input.tsx
import { jsx as jsx160, jsxs as jsxs25 } from "react/jsx-runtime";
var INPUT_TYPES_WITH_ICONS = /* @__PURE__ */ new Set(["search"]);
var InputWrapper = styled35.div`
  position: relative;

  svg {
    color: ${({ theme: theme2, $disabled }) => $disabled ? theme2.color.grey300 : theme2.color.grey500};
    cursor: text;
    pointer-events: none;
    position: absolute;
    top: 11px;
    ${({ $iconPosition, theme: theme2 }) => {
  if ($iconPosition === "start") {
    return `left: ${theme2.spacing.space03};`;
  }
  return `right: ${theme2.spacing.space03};`;
}}
  }

  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};
`;
var renderIcon = (icon, type) => {
  if (isNotNil18(icon)) {
    return icon;
  }
  if (type === "search") {
    return /* @__PURE__ */ jsx160(
      Icon,
      {
        size: "md",
        type: "search"
      }
    );
  }
  return null;
};
var Input = forwardRef11(
  ({
    clickToCopy = false,
    disabled = false,
    forceState,
    fullWidth = true,
    hasError = false,
    icon,
    iconPosition = "end",
    innerRef,
    monospace = false,
    onBlur,
    onChange,
    onClickToCopy,
    onFocus,
    onFocusToSelect,
    placeholder,
    readOnly = false,
    required = false,
    type = "text",
    value,
    ...otherProps
  }, ref) => {
    const defaultRef = useRef11(null);
    const mergedRefs = useMergedRefs([
      innerRef,
      ref,
      defaultRef
    ]);
    const elementType = type === "multiline" ? "textarea" : "input";
    const isReadOnly = readOnly || clickToCopy;
    const hasIcon = isNotNil18(icon) || clickToCopy || INPUT_TYPES_WITH_ICONS.has(type);
    useEffect16(() => {
      if (type === "timePosition" && isNotNil18(defaultRef.current)) {
        defaultRef.current.value = timePositionFormat(value ?? 0);
      }
    }, [type, value]);
    const handleOnFocus = (event) => {
      if (onFocusToSelect) {
        event.target.select();
        onFocusToSelect(event);
      }
      if (onFocus) {
        onFocus(event);
      }
    };
    const handleOnBlur = (event) => {
      if (type === "timePosition" && isNotNil18(defaultRef.current)) {
        defaultRef.current.value = timePositionFormat(event.target.value);
      }
      if (onBlur) {
        onBlur(event);
      }
    };
    const baseProps = {
      "aria-invalid": hasError ? "true" : "false",
      as: elementType,
      $clickToCopy: clickToCopy,
      disabled,
      $forceState: forceState,
      $hasError: hasError,
      $hasIcon: hasIcon,
      $iconPosition: type === "search" ? "start" : iconPosition,
      $monospace: monospace,
      onBlur: handleOnBlur,
      onChange,
      onFocus: handleOnFocus,
      placeholder,
      readOnly: isReadOnly,
      ref: mergedRefs,
      required,
      type
    };
    const clickToCopyProps = {
      value: coerceToString(value),
      ...baseProps,
      ...otherProps
    };
    const inputProps = {
      value,
      ...baseProps,
      ...otherProps
    };
    if (clickToCopy) {
      return /* @__PURE__ */ jsx160(
        ClickToCopy,
        {
          inputComponent: InputStyledComponent,
          inputProps: clickToCopyProps,
          onClick: onClickToCopy
        }
      );
    }
    return /* @__PURE__ */ jsxs25(
      InputWrapper,
      {
        $disabled: disabled,
        $fullWidth: fullWidth,
        $iconPosition: inputProps.$iconPosition,
        children: [
          /* @__PURE__ */ jsx160(InputStyledComponent, { ...inputProps }),
          renderIcon(icon, type)
        ]
      }
    );
  }
);
Input.displayName = "Input_VHS";

// src/private/components/FormConnectorInput/FormConnectorInput.tsx
import { jsx as jsx161 } from "react/jsx-runtime";
var TRIMMABLE_INPUTS = /* @__PURE__ */ new Set(["text", "multiline", "url", "email"]);
var FormConnectorInput = ({
  disabled,
  field: { name, value, onChange, onBlur },
  form: { errors, setFieldValue },
  label,
  labelDescription,
  labelProps,
  onChangeCustom,
  placeholder,
  type,
  onFocusToSelectValue,
  ...otherProps
}) => {
  const handleChange = (event) => {
    onChange(event);
    if (onChangeCustom) {
      onChangeCustom(event.target.value);
    }
  };
  const handleBlur2 = (event) => {
    onBlur(event);
    if (TRIMMABLE_INPUTS.has(type)) {
      const trimmedValue = (event.target.value || "").trim();
      setFieldValue(name, trimmedValue);
    }
  };
  const handleFocusToSelect = value === onFocusToSelectValue ? () => {
  } : void 0;
  return /* @__PURE__ */ jsx161(
    FormFieldWrapper,
    {
      disabled,
      error: isNotUndefined8(errors) ? errors[name] : void 0,
      label,
      labelDescription,
      labelProps,
      name,
      children: /* @__PURE__ */ jsx161(
        Input,
        {
          "data-testid": `input-${name}`,
          disabled,
          name,
          onBlur: handleBlur2,
          onChange: handleChange,
          onFocusToSelect: handleFocusToSelect,
          placeholder,
          type,
          value,
          ...otherProps
        }
      )
    }
  );
};
FormConnectorInput.displayName = "FormConnectorInput";

// src/private/components/FormConnectorRadioGroup/FormConnectorRadioGroup.tsx
import { useCallback as useCallback11 } from "react";

// src/components/RadioGroup/RadioGroup.tsx
import { styled as styled36 } from "styled-components";
import { isNotNil as isNotNil20 } from "@wistia/type-guards";

// src/components/Radio/Radio.tsx
import { forwardRef as forwardRef12, useState as useState18, isValidElement as isValidElement2 } from "react";
import { isNotNil as isNotNil19 } from "@wistia/type-guards";
import { Fragment as Fragment11, jsx as jsx162, jsxs as jsxs26 } from "react/jsx-runtime";
var Radio = forwardRef12(
  ({
    checked,
    direction = "column",
    disabled = false,
    forceState,
    hasError = false,
    id,
    label,
    labelDescription,
    name,
    onChange,
    value = "false",
    ...otherProps
  }, ref) => {
    const [focused, setFocused] = useState18(false);
    return /* @__PURE__ */ jsxs26(
      Container,
      {
        ref,
        $direction: direction,
        $disabled: disabled,
        $forceState: forceState,
        $hasError: hasError,
        $isFocused: focused,
        children: [
          /* @__PURE__ */ jsx162(
            OptionWrapper,
            {
              "aria-checked": checked,
              checked,
              "data-testid": "radio-option-wrapper",
              disabled,
              id,
              name,
              onBlur: () => setFocused(false),
              onChange,
              onFocus: () => setFocused(true),
              role: "radio",
              type: "radio",
              value,
              ...otherProps
            }
          ),
          /* @__PURE__ */ jsxs26(OptionLabel, { children: [
            /* @__PURE__ */ jsx162(Fragment11, { children: label }),
            isNotNil19(labelDescription) || isValidElement2(labelDescription) ? /* @__PURE__ */ jsx162(OptionSubLabel, { "data-testid": "radio-option-sub-label", children: labelDescription }) : null
          ] })
        ]
      }
    );
  }
);
Radio.displayName = "Radio_VHS";

// src/components/RadioGroup/RadioGroup.tsx
import { jsx as jsx163 } from "react/jsx-runtime";
var StyledOptionContainer2 = styled36.div`
  display: flex;
  flex-direction: ${({ direction }) => direction === "row" ? "row" : "column"};
  gap: ${({ direction, theme: theme2 }) => direction === "row" ? theme2.spacing.space04 : theme2.spacing.space01};
`;
var RadioGroup = ({
  direction = "column",
  disabled = false,
  error,
  label,
  labelDescription,
  labelProps = {},
  name,
  onChange,
  options,
  value,
  ...otherProps
}) => /* @__PURE__ */ jsx163(
  FormFieldWrapper,
  {
    disabled,
    error,
    label,
    labelDescription,
    labelProps,
    labelType: "legend",
    name,
    role: "radiogroup",
    ...otherProps,
    children: /* @__PURE__ */ jsx163(StyledOptionContainer2, { direction, children: options.map((option) => /* @__PURE__ */ jsx163(
      Radio,
      {
        checked: value === option.value,
        direction,
        disabled,
        labelDescription: option.labelDescription,
        name,
        onChange: (event) => isNotNil20(onChange) ? onChange(event, option.value) : void 0,
        ...option
      },
      option.value
    )) })
  }
);
RadioGroup.displayName = "RadioGroup_VHS";

// src/private/components/FormConnectorRadioGroup/FormConnectorRadioGroup.tsx
import { jsx as jsx164 } from "react/jsx-runtime";
var FormConnectorRadioGroup = ({
  direction = "column",
  disabled = false,
  field: { name },
  form: { errors, values, setFieldValue },
  label = "",
  labelDescription,
  labelProps = {},
  onChangeCustom,
  options
}) => {
  const handleOnChange = useCallback11(
    (_event, optionValue) => {
      setFieldValue(name, optionValue);
      if (onChangeCustom) {
        onChangeCustom(optionValue);
      }
    },
    [setFieldValue, name, onChangeCustom]
  );
  return /* @__PURE__ */ jsx164(
    RadioGroup,
    {
      direction,
      disabled,
      error: errors[name],
      label,
      labelDescription,
      labelProps,
      name,
      onChange: handleOnChange,
      options,
      value: values[name]
    }
  );
};
FormConnectorRadioGroup.displayName = "FormConnectorRadioGroup";

// src/private/components/FormConnectorSelect/FormConnectorSelect.tsx
import { isNil as isNil10, isBoolean as isBoolean2 } from "@wistia/type-guards";

// src/components/Select/Select.tsx
import { forwardRef as forwardRef13 } from "react";
import { styled as styled38, css as css20 } from "styled-components";
import { isBoolean, isNonEmptyString as isNonEmptyString7, isNotNil as isNotNil21 } from "@wistia/type-guards";

// src/components/Select/SelectStyledComponent.tsx
import { styled as styled37, css as css19 } from "styled-components";
var focusStyle2 = css19`
  border: 1px solid ${({ theme: theme2 }) => theme2.color.brandBlue500};
`;
var disabledStyle7 = css19`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  cursor: not-allowed;
  opacity: 0.5;

  &:focus {
    border-color: ${({ theme: theme2 }) => theme2.color.grey300};
  }
`;
var errorStyle3 = css19`
  border: 1px solid ${({ theme: theme2 }) => theme2.color.error500};
`;
var SelectStyledComponent = styled37.select`
  ${ellipsisStyle}
  appearance: none;
  background-color: white;
  border: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  border-radius: 4px;
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  cursor: pointer;
  display: block;
  font-size: 14px;
  height: ${({ multiple }) => multiple ? "66px" : "auto"};
  line-height: 20px;
  margin: 0;
  outline: none;

  /* selects w/ multiple need to be able to scroll */
  overflow-y: ${({ multiple }) => multiple ? "scroll" : "hidden"};

  /* right padding is to prevent text collision with caret icon */
  padding: ${({ theme: theme2 }) => `7px ${theme2.spacing.space06} 7px ${theme2.spacing.space02}`};
  width: 100%;

  &:focus {
    ${focusStyle2}
  }

  /* this removes a dotted border around select text when focusing in Firefox */
  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000000;
  }

  /* remove drop-down arrow in IE */
  &::-ms-expand {
    display: none;
  }

  ${({ disabled }) => disabled && disabledStyle7}
  ${({ $hasError }) => $hasError && errorStyle3}
  ${({ $forceState }) => $forceState === "focus" && focusStyle2};
`;

// src/components/Select/Select.tsx
import { jsx as jsx165, jsxs as jsxs27 } from "react/jsx-runtime";
var getSvgColor = (theme2, disabled, focused) => {
  if (disabled) {
    return theme2.color.grey300;
  }
  if (focused) {
    return theme2.color.brandBlue500;
  }
  return theme2.color.grey700;
};
var SelectWrapper = styled38.div`
  position: relative;

  /* svg here is referring to the caret that appears on the left */
  svg {
    color: ${({ theme: theme2, $disabled }) => getSvgColor(theme2, $disabled, false)};
    cursor: pointer;
    pointer-events: none;
    position: absolute;
    right: ${({ theme: theme2 }) => theme2.spacing.space02};
    top: ${({ theme: theme2 }) => theme2.spacing.space03};

    /* fakes the focus state of the svg for documentation purposes */
    ${({ theme: theme2, $forceState }) => $forceState === "focus" && css20`
        color: ${getSvgColor(theme2, false, true)};
      `}
  }

  /* match border focus color */
  ${SelectStyledComponent}:focus + svg {
    color: ${({ theme: theme2 }) => getSvgColor(theme2, false, true)};
  }

  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};
`;
var renderOptions = (options, labelName, valueName) => {
  const renderOptionGroup = ({
    groupLabel,
    groupOptions,
    disabled = false
  }) => /* @__PURE__ */ jsx165(
    "optgroup",
    {
      "data-testid": groupLabel,
      disabled,
      label: groupLabel,
      children: renderOptions(groupOptions)
    },
    groupLabel
  );
  return options.map((option) => {
    if (Object.prototype.hasOwnProperty.call(option, "groupLabel")) {
      return renderOptionGroup(option);
    }
    const { label, value, disabled = false } = option;
    let optionValue = isNonEmptyString7(valueName) ? option[valueName] : value;
    const optionLabel = isNonEmptyString7(labelName) ? option[labelName] : label;
    if (isBoolean(optionValue)) {
      optionValue = coerceToString(optionValue);
    }
    return /* @__PURE__ */ jsx165(
      "option",
      {
        disabled,
        value: optionValue,
        children: optionLabel
      },
      `${coerceToString(optionLabel)}--${coerceToString(optionValue)}`
    );
  });
};
var renderPlaceholder = (placeholder, preventPlaceholderSelection, multiple) => {
  if (isNotNil21(placeholder) && !multiple) {
    return renderOptions([
      { label: placeholder, value: "", disabled: preventPlaceholderSelection }
    ]);
  }
  return null;
};
var renderIcon2 = (multiple) => {
  if (multiple) {
    return null;
  }
  return /* @__PURE__ */ jsx165(
    Icon,
    {
      size: "md",
      type: "caret-down"
    }
  );
};
var Select = forwardRef13(
  ({
    ariaLabel,
    disabled = false,
    forceState,
    fullWidth = true,
    hasError = false,
    labelName = "label",
    multiple = false,
    options,
    placeholder = "Please select...",
    preventPlaceholderSelection = false,
    required = false,
    valueName = "value",
    ...otherProps
  }, ref) => /* @__PURE__ */ jsxs27(
    SelectWrapper,
    {
      $disabled: disabled,
      $forceState: forceState,
      $fullWidth: fullWidth,
      children: [
        /* @__PURE__ */ jsxs27(
          SelectStyledComponent,
          {
            ref,
            $forceState: forceState,
            $hasError: hasError,
            "aria-invalid": hasError ? "true" : "false",
            "aria-label": ariaLabel,
            disabled,
            multiple,
            required,
            ...otherProps,
            children: [
              renderPlaceholder(placeholder, preventPlaceholderSelection, multiple),
              renderOptions(options, labelName, valueName)
            ]
          }
        ),
        renderIcon2(multiple)
      ]
    }
  )
);
Select.displayName = "Select_VHS";

// src/private/components/FormConnectorSelect/FormConnectorSelect.tsx
import { jsx as jsx166 } from "react/jsx-runtime";
var FormConnectorSelect = ({
  disabled = false,
  field: { name, value, onChange, onBlur },
  form: { errors },
  label,
  labelDescription,
  labelProps = {},
  options,
  placeholder,
  onChangeCustom,
  ...otherProps
}) => {
  const getValue = (val) => {
    if (isNil10(val)) {
      return "";
    }
    if (isBoolean2(val)) {
      return coerceToString(value);
    }
    return val;
  };
  return /* @__PURE__ */ jsx166(
    FormFieldWrapper,
    {
      disabled,
      error: errors[name],
      label,
      labelDescription,
      labelProps,
      name,
      children: /* @__PURE__ */ jsx166(
        Select,
        {
          "data-testid": `select-${name}`,
          disabled,
          name,
          onBlur,
          onChange: (event) => {
            onChange(event);
            if (onChangeCustom) {
              onChangeCustom(event.target.value);
            }
          },
          options,
          placeholder,
          value: getValue(value),
          ...otherProps
        }
      )
    }
  );
};
FormConnectorSelect.displayName = "FormConnectorSelect";

// src/private/components/FormConnectorHidden/FormConnectorHidden.tsx
import { useEffect as useEffect17 } from "react";
import { isNil as isNil11, isNotUndefined as isNotUndefined9 } from "@wistia/type-guards";
import { jsx as jsx167 } from "react/jsx-runtime";
var FormConnectorHidden = ({
  field: { name, value, onChange },
  form: { setFieldValue } = {},
  onChangeCustom,
  valueState = "",
  ...otherProps
}) => {
  useEffect17(() => {
    if (!valueState && isNil11(value)) {
      return;
    }
    if (valueState !== value && isNotUndefined9(setFieldValue)) {
      setFieldValue(name, valueState);
    }
  }, [setFieldValue, name, valueState, value]);
  return /* @__PURE__ */ jsx167(
    Input,
    {
      "data-testid": `input-${name}`,
      name,
      onChange: (event) => {
        onChange(event);
        if (isNotUndefined9(onChangeCustom)) {
          onChangeCustom(event.target.value);
        }
      },
      type: "hidden",
      value: isNil11(value) ? "" : value,
      ...otherProps
    }
  );
};
FormConnectorHidden.displayName = "FormConnectorHidden";

// src/private/components/FormConnectorRichTextEditor/FormConnectorRichTextEditor.tsx
import { useState as useState19, useEffect as useEffect19 } from "react";
import { styled as styled42 } from "styled-components";
import { isNotUndefined as isNotUndefined11 } from "@wistia/type-guards";

// src/components/RichTextEditor/RichTextEditor.tsx
import { useCallback as useCallback12, useMemo as useMemo7, useEffect as useEffect18 } from "react";
import { styled as styled41 } from "styled-components";
import { Editable, withReact, Slate, ReactEditor } from "slate-react";
import { createEditor, Transforms as Transforms5, Element as Element7, Text as Text4 } from "slate";
import { withHistory } from "slate-history";
import { isHotkey } from "is-hotkey";
import { isNil as isNil14, isNotNil as isNotNil26, isNonEmptyString as isNonEmptyString11 } from "@wistia/type-guards";
import isEqual2 from "react-fast-compare";

// src/components/RichTextEditor/serializers.ts
import { Element, Text as Text2 } from "slate";
import { jsx as jsx168 } from "slate-hyperscript";
import { isNotNil as isNotNil22, isNil as isNil12, isString as isString7, isArray as isArray2 } from "@wistia/type-guards";

// src/private/helpers/escapeHtml/escapeHtml.ts
var matchHtmlRegExp = /["'&<>]/;
var doubleQuoteChar = 34;
var ampersandChar = 38;
var singleQuoteChar = 39;
var lessThanChar = 60;
var greaterThanChar = 62;
var escapeHtml = (stringToEscape) => {
  const str = `${stringToEscape}`;
  const match = matchHtmlRegExp.exec(str);
  if (!match) {
    return str;
  }
  let escape = "";
  let html = "";
  let index = 0;
  let lastIndex = 0;
  for (index = match.index; index < str.length; index++) {
    const charCode = str.charCodeAt(index);
    if (charCode === doubleQuoteChar) {
      escape = "&quot;";
    } else if (charCode === ampersandChar) {
      escape = "&amp;";
    } else if (charCode === singleQuoteChar) {
      escape = "&#39;";
    } else if (charCode === lessThanChar) {
      escape = "&lt;";
    } else if (charCode === greaterThanChar) {
      escape = "&gt;";
    } else {
      escape = "";
    }
    if (escape !== "") {
      if (lastIndex !== index) {
        html += str.substring(lastIndex, index);
      }
      lastIndex = index + 1;
      html += escape;
    }
  }
  return lastIndex !== index ? html + str.substring(lastIndex, index) : html;
};

// src/components/RichTextEditor/serializers.ts
var emptyTextNode = { text: "" };
var rteIsNonImageNodeType = (node) => node.type !== "image";
var rteIsInvalidImage = (node) => node.type === "image" && node.url === void 0;
var rteHasOnlyOneChild = (node) => isNotNil22(node.children) && node.children.length === 1;
var rteIsEmptyTextNode = (node) => Text2.isText(node) && node.text?.trim() === "";
var rteIsOnlyChildWithEmptyText = (node) => (
  // We know that node.children[0] exists bcause of rteHasOnlyOneChild
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  rteHasOnlyOneChild(node) && rteIsEmptyTextNode(node.children[0])
);
var isValueNull = (value) => value.every((val) => isNil12(val));
var sanitizedValue = (value) => isValueNull(value) ? null : value.join("");
var addAttrsToChildren = (child, attrs) => {
  const childCopy = child;
  if (childCopy.children) {
    childCopy.children = childCopy.children.map((item) => {
      const itemWithAttrs = addAttrsToChildren(item, attrs);
      return { ...itemWithAttrs, ...attrs };
    });
  }
  return childCopy;
};
var serializeText = (textNode) => {
  const text = escapeHtml(textNode.text);
  const newStringArr = [text];
  if (isNotNil22(textNode.bold) && textNode.bold) {
    newStringArr.push("</strong>");
    newStringArr.unshift("<strong>");
  }
  if (isNotNil22(textNode.italic) && textNode.italic) {
    newStringArr.push("</em>");
    newStringArr.unshift("<em>");
  }
  if (isNotNil22(textNode.underline) && textNode.underline) {
    newStringArr.push("</u>");
    newStringArr.unshift("<u>");
  }
  if (isNotNil22(textNode.strikethrough) && textNode.strikethrough) {
    newStringArr.push("</s>");
    newStringArr.unshift("<s>");
  }
  if (isNotNil22(textNode.code) && textNode.code) {
    newStringArr.push("</code>");
    newStringArr.unshift("<code>");
  }
  return newStringArr.join("");
};
var serializeNode = (node) => {
  if (rteIsNonImageNodeType(node) && rteIsOnlyChildWithEmptyText(node) || rteIsInvalidImage(node)) {
    return null;
  }
  if (Text2.isText(node)) {
    return serializeText(node);
  }
  const children = isNotNil22(node.children) ? node.children.map((node_) => serializeNode(node_)).join("") : "";
  switch (node.type) {
    case "span":
      return `<span>${children}</span>`;
    case "blockquote":
      return `<blockquote>${children}</blockquote>`;
    case "paragraph":
      return `<p>${children}</p>`;
    case "heading-one":
      return `<h1>${children}</h1>`;
    case "heading-two":
      return `<h2>${children}</h2>`;
    // We preserve the original headers "under the hood" in the HTML
    // Even though we render h4-h6 as h3s in the editor UI per Design request.
    case "heading-three":
      return `<h3>${children}</h3>`;
    case "heading-four":
      return `<h4>${children}</h4>`;
    case "heading-five":
      return `<h5>${children}</h5>`;
    case "heading-six":
      return `<h6>${children}</h6>`;
    case "link":
      return `<a rel="noopener noreferrer" target="_blank" href="${escapeHtml(
        node.url
      )}">${children}</a>`;
    case "numbered-list":
      return `<ol>${children}</ol>`;
    case "bulleted-list":
      return `<ul>${children}</ul>`;
    case "list-item":
      return `<li>${children}</li>`;
    case "image":
      return `<img src="${escapeHtml(node.url)}" />`;
    case "code":
      return `<code>${children}</code>`;
    case void 0:
    default:
      return children;
  }
};
var rteSerializeToHTML = (value) => {
  const mappedValue = value.map((node) => serializeNode(node));
  return sanitizedValue(mappedValue);
};
var ELEMENT_TAGS = {
  SPAN: () => ({ type: "span" }),
  A: (el) => ({ type: "link", url: el.getAttribute("href") }),
  // eslint-disable-line id-length
  BLOCKQUOTE: () => ({ type: "blockquote" }),
  H1: () => ({ type: "heading-one" }),
  H2: () => ({ type: "heading-two" }),
  H3: () => ({ type: "heading-three" }),
  H4: () => ({ type: "heading-four" }),
  H5: () => ({ type: "heading-five" }),
  H6: () => ({ type: "heading-six" }),
  LI: () => ({ type: "list-item" }),
  OL: () => ({ type: "numbered-list" }),
  P: () => ({ type: "paragraph" }),
  // eslint-disable-line id-length
  UL: () => ({ type: "bulleted-list" }),
  IMG: (el) => ({ type: "image", url: el.getAttribute("src") }),
  CODE: () => ({ type: "code" })
};
var TEXT_TAGS = {
  DEL: () => ({ strikethrough: true }),
  S: () => ({ strikethrough: true }),
  // eslint-disable-line id-length
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  // eslint-disable-line id-length
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
  // eslint-disable-line id-length
  CODE: () => ({ code: true })
};
var isTextNode = (el) => el.nodeType === 3;
var isElement = (el) => el.nodeType === 1;
var isCodeBlck = (element) => element.nodeName === "CODE";
var isInlineCodeBlck = (element, elementChildren) => (
  // @ts-expect-error I don't want to narrow the type too far and miss something that should be matched here.
  isCodeBlck(element) && isNotNil22(elementChildren[0]) && !elementChildren[0].match(/\n/g)
);
var shouldCreateBlck = (el, children) => !isInlineCodeBlck(el, children) && isNotNil22(ELEMENT_TAGS[el.nodeName]);
var shouldCreateText = (el, children) => isInlineCodeBlck(el, children) || isNotNil22(TEXT_TAGS[el.nodeName]);
var htmlChildIsEmptyText = (child) => isNotNil22(child) && !isString7(child) && child.text?.trim() === "";
var createEditorBlockElements = (children, el) => {
  const { nodeName } = el;
  if (nodeName !== "IMG" && children.length === 1 && !isString7(children[0]) && // @ts-expect-error - text is a property on NodeLeafObject.
  isNotNil22(children[0].text) && // Casting to Text as I don't want to further narrow the type in case I miss another type where it's valid. RM 2021-03-31
  children[0].text?.trim() === "") {
    return jsx168("element", ELEMENT_TAGS.P(), emptyTextNode);
  }
  const attrs = ELEMENT_TAGS[nodeName](el);
  return jsx168("element", attrs, children);
};
var createEditorTextElements = (children, el) => {
  const { nodeName } = el;
  const attrs = TEXT_TAGS[nodeName]();
  return children.map((child) => {
    if (!isString7(child) && isNotNil22(child) && isNotNil22(child.children)) {
      return addAttrsToChildren(child, attrs);
    }
    if (htmlChildIsEmptyText(child)) {
      return jsx168("text", {}, emptyTextNode);
    }
    return jsx168("text", attrs, child);
  });
};
var blockWithOnlyTextChildren = (el) => el.nodeName === "BLOCKQUOTE" || el.nodeName === "OL" || el.nodeName === "UL";
var getNodeChildren = (el, mapper) => {
  let children = [];
  if (isCodeBlck(el)) {
    children = [...el.childNodes].map(
      (node) => (
        // This helps us distinguish between code block content that should be treated as HTML vs. as text content
        // @ts-expect-error - TODO: I can't get into this conditional click testing, and I can't see how outerHTML would be valid from reading the code here.
        isTextNode(node) ? node.textContent : node.outerHTML
      )
      // eslint-disable-line @typescript-eslint/no-unsafe-return
    );
  } else if (blockWithOnlyTextChildren(el)) {
    children = [...el.childNodes].filter((node) => node.textContent?.trim() !== "").flatMap(mapper);
  } else {
    children = [...el.childNodes].flatMap(mapper);
  }
  return children;
};
var deserializeHTMLNode = (el) => {
  if (isTextNode(el)) {
    if (el.nodeValue?.trim() === "") {
      return null;
    }
    return el.textContent;
  }
  if (el.nodeName === "BR") {
    return "\n";
  }
  if (!isElement(el)) {
    return null;
  }
  const children = getNodeChildren(el, deserializeHTMLNode);
  const filteredChildren = children.filter(
    (child) => child === null || typeof child === "string" || typeof child === "object" && ("text" in child || "type" in child)
  );
  const safeChildren = filteredChildren.length > 0 && filteredChildren[0] != null ? filteredChildren : [emptyTextNode];
  if (el.nodeName === "BODY") {
    return jsx168("fragment", {}, safeChildren);
  }
  if (shouldCreateBlck(el, children)) {
    return createEditorBlockElements(safeChildren, el);
  }
  if (shouldCreateText(el, safeChildren)) {
    return createEditorTextElements(safeChildren, el);
  }
  return children;
};
var rteDeserializeHTML = (htmlString) => {
  if (!htmlString) {
    return void 0;
  }
  const parsedEl = new DOMParser().parseFromString(htmlString, "text/html").body;
  const fragment = deserializeHTMLNode(parsedEl);
  if (isNotNil22(fragment) && isArray2(fragment) && isNotNil22(fragment[0]) && // @ts-expect-error - I don't want to risk narrowing too far during the TS conversion. RM.
  isNotNil22(fragment[0].text)) {
    return [{ type: "paragraph", children: [{ text: fragment[0].text }] }];
  }
  const lastNodeOfFragment = isArray2(fragment) ? fragment[fragment.length - 1] : void 0;
  if (isArray2(fragment) && Element.isElement(lastNodeOfFragment) && lastNodeOfFragment.type !== "paragraph") {
    return [...fragment, { type: "paragraph", children: [{ text: "" }] }];
  }
  return fragment;
};

// src/components/RichTextEditor/Leaf.tsx
import { isNotNil as isNotNil23 } from "@wistia/type-guards";
import { jsx as jsx169 } from "react/jsx-runtime";
var Leaf = ({
  attributes = { "data-slate-leaf": true },
  children,
  leaf = {}
}) => {
  let kids = children;
  if (isNotNil23(leaf.bold)) {
    kids = /* @__PURE__ */ jsx169("strong", { children: kids });
  }
  if (isNotNil23(leaf.italic)) {
    kids = /* @__PURE__ */ jsx169("em", { children: kids });
  }
  if (isNotNil23(leaf.underline)) {
    kids = /* @__PURE__ */ jsx169("u", { children: kids });
  }
  if (isNotNil23(leaf.strikethrough)) {
    kids = /* @__PURE__ */ jsx169("s", { children: kids });
  }
  if (isNotNil23(leaf.code)) {
    kids = /* @__PURE__ */ jsx169("code", { children: kids });
  }
  return /* @__PURE__ */ jsx169("span", { ...attributes, children: kids });
};

// src/components/RichTextEditor/Element.tsx
import { useSelected, useFocused } from "slate-react";
import { styled as styled39, css as css21 } from "styled-components";
import { jsx as jsx170, jsxs as jsxs28 } from "react/jsx-runtime";
var Wrapper = styled39.span`
  display: inline-block;
  user-select: none;
`;
var Image = styled39.img`
  box-shadow: ${({ selected, focused, theme: theme2 }) => selected && focused ? `0 0 0 3px ${theme2.color.brandBlue500}` : "none"};
  max-height: 10em;
  max-width: 100%;
  user-select: none;
`;
var listStyle = css21`
  padding: 0 ${({ theme: theme2 }) => theme2.spacing.space05};

  li {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    margin: ${({ theme: theme2 }) => `${theme2.spacing.space02} 0`};
  }
`;
var EditorOrderedList = styled39.ol`
  ${listStyle}
`;
var EditorUnorderedList = styled39.ul`
  ${listStyle}
`;
var headerStyle = css21`
  font-weight: 500;
`;
var EditorHeading1 = styled39.h1`
  ${headerStyle}
  font-size: 18px;
`;
var EditorHeading2 = styled39.h2`
  ${headerStyle}
  font-size: 16px;
`;
var EditorHeading3 = styled39.h3`
  ${headerStyle}
  font-size: 14px;
`;
var EditorParagraph = styled39.p`
  font-weight: 400;
`;
var EditorLink = styled39.a`
  color: ${({ theme: theme2 }) => theme2.color.brandBlue500};
  font-weight: 300;
`;
var EditorSpan = styled39.span`
  font-weight: 400;
`;
var Element2 = ({
  attributes = {
    "data-slate-node": "element",
    ref: void 0
  },
  children = void 0,
  element = {}
}) => {
  const selected = useSelected();
  const focused = useFocused();
  switch (element.type) {
    case "span":
      return /* @__PURE__ */ jsx170(EditorSpan, { ...attributes, children });
    case "blockquote":
      return /* @__PURE__ */ jsx170("blockquote", { ...attributes, children });
    case "bulleted-list":
      return /* @__PURE__ */ jsx170(EditorUnorderedList, { ...attributes, children });
    case "heading-one":
      return /* @__PURE__ */ jsx170(EditorHeading1, { ...attributes, children });
    case "heading-two":
      return /* @__PURE__ */ jsx170(EditorHeading2, { ...attributes, children });
    // We render h4-h6 as h3s in the editor UI per Design request
    // (Though we preserve the original headers "under the hood" in the HTML).
    case "heading-three":
    case "heading-four":
    case "heading-five":
    case "heading-six":
      return /* @__PURE__ */ jsx170(EditorHeading3, { ...attributes, children });
    case "list-item":
      return /* @__PURE__ */ jsx170("li", { ...attributes, children });
    case "numbered-list":
      return /* @__PURE__ */ jsx170(EditorOrderedList, { ...attributes, children });
    case "image":
      return /* @__PURE__ */ jsxs28(Wrapper, { ...attributes, children: [
        /* @__PURE__ */ jsx170(Wrapper, { contentEditable: false, children: /* @__PURE__ */ jsx170(
          Image,
          {
            alt: "",
            contentEditable: false,
            focused,
            selected,
            src: element.url
          }
        ) }),
        children
      ] });
    case "link":
      return /* @__PURE__ */ jsx170(
        EditorLink,
        {
          ...attributes,
          href: element.url,
          rel: "noopener noreferrer",
          target: "_blank",
          children
        }
      );
    case "code":
      return /* @__PURE__ */ jsx170("code", { ...attributes, children });
    case void 0:
    default:
      return /* @__PURE__ */ jsx170(EditorParagraph, { ...attributes, children });
  }
};

// src/components/RichTextEditor/EditorButtons.tsx
import { useSlate } from "slate-react";
import { styled as styled40 } from "styled-components";
import { Node as Node2 } from "slate";
import { isNonEmptyString as isNonEmptyString10 } from "@wistia/type-guards";

// src/components/RichTextEditor/utilities/withImagesPlugin.ts
import { Transforms as Transforms2, Element as Element4, Text as Text3 } from "slate";
import { isNotUndefined as isNotUndefined10, isNotNil as isNotNil25 } from "@wistia/type-guards";

// src/components/RichTextEditor/utilities/toggle.ts
import { Element as Element3, Transforms, Editor as SlateEditor } from "slate";
import { isNotNil as isNotNil24, isNonEmptyString as isNonEmptyString8 } from "@wistia/type-guards";
var isBlockActive = (editor, format) => {
  const [match] = SlateEditor.nodes(editor, {
    match: (node) => Element3.isElement(node) && node.type === format
  });
  return Boolean(match);
};
var LIST_TYPES = /* @__PURE__ */ new Set(["numbered-list", "bulleted-list"]);
var toggleBlck = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.has(format);
  Transforms.unwrapNodes(editor, {
    match: (node) => Element3.isElement(node) && isNotNil24(node.type) && LIST_TYPES.has(node.type),
    split: true
  });
  const formatting = isList ? "list-item" : format;
  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : formatting
  });
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};
var isMarkActive = (editor, format) => {
  const valueMarks = SlateEditor.marks(editor);
  return isNonEmptyString8(valueMarks && Object.keys(valueMarks).find((mark) => mark === format));
};
var toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    SlateEditor.removeMark(editor, format);
  } else {
    SlateEditor.addMark(editor, format, true);
  }
};

// src/components/RichTextEditor/utilities/withImagesPlugin.ts
var emptyTextNode2 = { text: "" };
var imageExtensions = ["jpg", "png", "gif", "tiff", "bmp", "dcm"];
var isImageUrl = (url) => {
  if (!isUrl(url)) {
    return false;
  }
  const hasImageExtension = imageExtensions.find((event) => url.includes(event));
  return isNotUndefined10(hasImageExtension) && Boolean(hasImageExtension);
};
var insertImage = (editor, url) => {
  if (!url) {
    toggleBlck(editor, "image");
    return;
  }
  if (!isImageUrl(url)) {
    toggleBlck(editor, "image");
    return;
  }
  const image = { type: "image", url, children: [emptyTextNode2] };
  Transforms2.insertNodes(editor, image);
};
var withImages = (editor) => {
  const { normalizeNode, isVoid } = editor;
  editor.isVoid = (element) => element.type === "image" ? true : isVoid(element);
  editor.normalizeNode = ([node, path]) => {
    if (Element4.isElement(node) && node.type === "image" && node.url !== void 0) {
      const paragraph = {
        type: "paragraph",
        children: [emptyTextNode2]
      };
      const pathIndex = isNotUndefined10(path[0]) ? path[0] + 1 : 1;
      Transforms2.insertNodes(editor, paragraph, { at: [pathIndex] });
      Transforms2.move(editor);
    }
    if (Element4.isElement(node) && node.type === "image" && node.url === void 0 && isNotUndefined10(node.children) && isNotUndefined10(node.children[0]) && isNotUndefined10(node.children[0].text) && node.children[0].text.trim() !== "") {
      Transforms2.setNodes(
        editor,
        { type: "span" },
        {
          match: (matchNode) => Element4.isElement(matchNode) && matchNode.type === "image" && matchNode.url === void 0 && isNotUndefined10(matchNode.children) && Text3.isText(matchNode.children[0]) && isNotNil25(matchNode.children[0].text) && matchNode.children[0].text.trim() !== "",
          split: true,
          at: path,
          mode: "highest"
        }
      );
    }
    normalizeNode([node, path]);
  };
  return editor;
};

// src/components/RichTextEditor/utilities/withLinksPlugin.ts
import { Transforms as Transforms3, Element as Element5, Node, Editor, Range, Path } from "slate";
import { isNonEmptyString as isNonEmptyString9 } from "@wistia/type-guards";
var isHashUrl = (urlString) => {
  const urlPattern = /^#.+$/i;
  return Boolean(urlPattern.test(urlString));
};
var isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: (node) => Element5.isElement(node) && node.type === "link"
  });
  return Boolean(link);
};
var unwrapLink = (editor) => {
  Transforms3.unwrapNodes(editor, {
    match: (node) => Element5.isElement(node) && node.type === "link"
  });
};
var wrapLink = (editor, url) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: "link",
    url,
    children: isCollapsed === true ? [{ text: url }] : []
  };
  if (isCollapsed === true) {
    Transforms3.insertNodes(editor, link);
  } else {
    Transforms3.wrapNodes(editor, link, { split: true });
    Transforms3.collapse(editor, { edge: "end" });
  }
};
var withLinks = (editor) => {
  const { insertData, insertText, isInline, normalizeNode } = editor;
  editor.isInline = (element) => element.type === "link" ? true : isInline(element);
  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };
  editor.insertData = (data) => {
    const text = data.getData("text/plain");
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };
  editor.normalizeNode = ([node, path]) => {
    if (Element5.isElement(node) && node.type === "link" && Node.string(node) === "") {
      Transforms3.unwrapNodes(editor, { at: path });
      Transforms3.select(editor, Path.next(path));
    }
    normalizeNode([node, path]);
  };
  return editor;
};
var insertLink = (editor) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  } else {
    const url = prompt("Enter the URL of the link:");
    if (!isNonEmptyString9(url)) {
      toggleMark(editor, "link");
      return;
    }
    const urlHasSpaces = url.includes(" ");
    if (urlHasSpaces) {
      alert("The URL provided had spaces. Try again without spaces.");
      toggleMark(editor, "link");
      return;
    }
    if (!isUrl(url) && !isHashUrl(url)) {
      alert(
        "The URL provided looks invalid. Try again. Make sure to include the protocol (ex. https://) in the URL."
      );
      toggleMark(editor, "link");
      return;
    }
    if (editor.selection) {
      wrapLink(editor, url);
    }
  }
};

// src/components/RichTextEditor/utilities/handleListEscape.ts
import { Transforms as Transforms4, Range as Range2, Editor as Editor2, Element as Element6 } from "slate";
var OverriddenEditor = {
  ...Editor2,
  isBlck: Editor2.isBlock
};
var newLine = {
  type: "paragraph",
  children: [
    {
      text: "",
      marks: []
    }
  ]
};
var isLastChildBlankText = (listChildren) => {
  if (listChildren.length === 1) {
    return listChildren[listChildren.length - 1]?.text?.trim() === "";
  }
  const link = listChildren.find((el) => el.type === "link");
  return link?.children[0]?.text?.trim() === "";
};
var handleListEscape = (editor) => {
  const { selection } = editor;
  const textIsNotSelected = selection && Range2.isCollapsed(selection);
  const selectedElement = editor.children[selection.anchor.path[0]];
  const isList = selectedElement.type === "bulleted-list" || selectedElement.type === "numbered-list";
  const blockAbove = OverriddenEditor.above(editor, {
    match: (node) => Element6.isElement(node) && OverriddenEditor.isBlck(editor, node)
  });
  if ((textIsNotSelected ?? false) && isList && isLastChildBlankText(blockAbove[0].children)) {
    Transforms4.removeNodes(editor, { hanging: true });
    Transforms4.insertNodes(editor, newLine);
    Transforms4.unwrapNodes(editor, {
      // @ts-expect-error - type does exist on some sub-types of nodes.
      match: ({ type }) => type === "bulleted-list" || type === "numbered-list",
      all: true,
      split: true
    });
  }
};

// src/components/RichTextEditor/utilities/rteFormatTypes.ts
var rteFormatTypes = Object.freeze({
  BOLD: "BOLD",
  ITALIC: "ITALIC",
  UNDERLINE: "UNDERLINE",
  STRIKETHROUGH: "STRIKETHROUGH",
  HEADING_1: "HEADING_1",
  HEADING_2: "HEADING_2",
  BLOCKQUOTE: "BLOCKQUOTE",
  NUMBERED_LIST: "NUMBERED_LIST",
  BULLETED_LIST: "BULLETED_LIST",
  LINK: "LINK",
  IMAGE: "IMAGE"
});

// src/private/components/Icon/Icon.tsx
import { isNil as isNil13 } from "@wistia/type-guards";

// src/private/components/Icon/iconMap.ts
var iconMap2 = {
  "block-quote": "BlockQuote",
  bold: "Bold",
  "bulleted-list": "BulletedList",
  heading: "Heading",
  "heading-two": "HeadingTwo",
  image: "Image",
  italic: "Italic",
  link: "Link",
  "numbered-list": "NumberedList",
  strikethrough: "Strikethrough",
  underline: "Underline"
};

// src/private/components/Icon/icons/index.ts
var icons_exports2 = {};
__export(icons_exports2, {
  BlockQuote: () => BlockQuote,
  Bold: () => Bold,
  BulletedList: () => BulletedList,
  Heading: () => Heading,
  HeadingTwo: () => HeadingTwo,
  Image: () => Image2,
  Italic: () => Italic,
  Link: () => Link2,
  NumberedList: () => NumberedList,
  Strikethrough: () => Strikethrough,
  Underline: () => Underline
});

// src/private/components/Icon/icons/BlockQuote.tsx
import { jsx as jsx171 } from "react/jsx-runtime";
var BlockQuote = (props) => /* @__PURE__ */ jsx171("svg", { ...props, children: /* @__PURE__ */ jsx171(
  "path",
  {
    d: "M4.2125 11.0375C4.35417 11.1792 4.53333 11.25 4.75 11.25H19.25C19.4667 11.25 19.6458 11.1792 19.7875 11.0375C19.9292 10.8958 20 10.7167 20 10.5C20 10.2833 19.9292 10.1042 19.7875 9.9625C19.6458 9.82083 19.4667 9.75 19.25 9.75H4.75C4.53333 9.75 4.35417 9.82083 4.2125 9.9625C4.07083 10.1042 4 10.2833 4 10.5C4 10.7167 4.07083 10.8958 4.2125 11.0375ZM4.2125 14.0375C4.35417 14.1792 4.53333 14.25 4.75 14.25H12.975C13.1917 14.25 13.3708 14.1792 13.5125 14.0375C13.6542 13.8958 13.725 13.7167 13.725 13.5C13.725 13.2833 13.6542 13.1042 13.5125 12.9625C13.3708 12.8208 13.1917 12.75 12.975 12.75H4.75C4.53333 12.75 4.35417 12.8208 4.2125 12.9625C4.07083 13.1042 4 13.2833 4 13.5C4 13.7167 4.07083 13.8958 4.2125 14.0375ZM1.5 10C1.22386 10 1 10.2239 1 10.5V14C1 14.2761 1.22386 14.5 1.5 14.5C1.77614 14.5 2 14.2761 2 14V10.5C2 10.2239 1.77614 10 1.5 10Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Bold.tsx
import { jsx as jsx172 } from "react/jsx-runtime";
var Bold = (props) => /* @__PURE__ */ jsx172("svg", { ...props, children: /* @__PURE__ */ jsx172(
  "path",
  {
    d: "M16.9995 11.7C18.354 10.743 19.302 9.17101 19.302 7.71451C19.302 4.48501 16.86 1.99951 13.722 1.99951H4.998V22.0005H14.823C17.7375 22.0005 19.998 19.5705 19.998 16.5855C19.998 14.415 18.798 12.5565 16.998 11.7H16.9995ZM9.186 5.57101H13.371C14.5305 5.57101 15.465 6.52801 15.465 7.71451C15.465 8.89951 14.5305 9.85651 13.3725 9.85651H9.186V5.57101ZM14.07 18.429H9.186V14.1435H14.07C15.228 14.1435 16.1625 15.1005 16.1625 16.2855C16.1625 17.4705 15.228 18.429 14.07 18.429V18.429Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/BulletedList.tsx
import { jsx as jsx173 } from "react/jsx-runtime";
var BulletedList = (props) => /* @__PURE__ */ jsx173("svg", { ...props, children: /* @__PURE__ */ jsx173(
  "path",
  {
    d: "M4.0005 10.5C3.1695 10.5 2.5005 11.1705 2.5005 12C2.5005 12.8295 3.1695 13.5 4.0005 13.5C4.83 13.5 5.5005 12.8295 5.5005 12C5.5005 11.1705 4.83 10.5 4.0005 10.5ZM4.0005 4.5C3.1695 4.5 2.5005 5.1705 2.5005 6C2.5005 6.8295 3.1695 7.5 4.0005 7.5C4.83 7.5 5.5005 6.8295 5.5005 6C5.5005 5.1705 4.83 4.5 4.0005 4.5ZM4.0005 16.5C3.1695 16.5 2.5005 17.1795 2.5005 18C2.5005 18.8205 3.18 19.5 4.0005 19.5C4.8195 19.5 5.5005 18.8205 5.5005 18C5.5005 17.1795 4.83 16.5 4.0005 16.5ZM7.0005 19.0005H21V16.9995H7.0005V19.0005ZM7.0005 13.0005H21V10.9995H7.0005V13.0005ZM7.0005 4.9995V7.0005H21V4.9995H7.0005Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Heading.tsx
import { jsx as jsx174 } from "react/jsx-runtime";
var Heading = (props) => /* @__PURE__ */ jsx174("svg", { ...props, children: /* @__PURE__ */ jsx174(
  "path",
  {
    d: "M7.9995 3V6.7995H14.154V22.0005H17.8455V6.7995H24V3H7.9995ZM0 12.9H3.666V22.0005H7.335V12.9H11.001V9H0V12.9Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/HeadingTwo.tsx
import { jsx as jsx175 } from "react/jsx-runtime";
var HeadingTwo = (props) => /* @__PURE__ */ jsx175("svg", { ...props, children: /* @__PURE__ */ jsx175(
  "path",
  {
    d: "M3 17V7H4.5V11.25H9V7H10.5V17H9V12.75H4.5V17H3ZM13.5 17V12.75C13.5 12.3333 13.6458 11.9792 13.9375 11.6875C14.2292 11.3958 14.5833 11.25 15 11.25H19.5V8.5H13.5V7H19.5C19.9167 7 20.2708 7.14583 20.5625 7.4375C20.8542 7.72917 21 8.08333 21 8.5V11.25C21 11.6667 20.8542 12.0208 20.5625 12.3125C20.2708 12.6042 19.9167 12.75 19.5 12.75H15V15.5H21V17H13.5Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Image.tsx
import { jsx as jsx176 } from "react/jsx-runtime";
var Image2 = (props) => /* @__PURE__ */ jsx176("svg", { ...props, children: /* @__PURE__ */ jsx176(
  "path",
  {
    d: "M21 19.0005V4.9995C21 3.9 20.1 3 19.0005 3H4.9995C3.9 3 3 3.9 3 4.9995V19.0005C3 20.1 3.9 21 4.9995 21H19.0005C20.1 21 21 20.1 21 19.0005ZM8.5005 13.5L10.9995 16.5105L14.5005 12L19.0005 18H4.9995L8.5005 13.5Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Italic.tsx
import { jsx as jsx177 } from "react/jsx-runtime";
var Italic = (props) => /* @__PURE__ */ jsx177("svg", { ...props, children: /* @__PURE__ */ jsx177(
  "path",
  {
    d: "M8.8335 1.99951V6.28501H12.0555L7.0695 17.715H3V22.0005H14.667V17.715H11.4435L16.431 6.28501H20.5005V1.99951H8.8335Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Link.tsx
import { jsx as jsx178 } from "react/jsx-runtime";
var Link2 = (props) => /* @__PURE__ */ jsx178("svg", { ...props, children: /* @__PURE__ */ jsx178(
  "path",
  {
    d: "M2.28 12C2.28 9.948 3.948 8.28 6 8.28H10.8V6H6C2.688 6 0 8.688 0 12C0 15.312 2.688 18 6 18H10.8V15.72H6C3.948 15.72 2.28 14.052 2.28 12ZM7.2 13.2H16.8V10.8H7.2V13.2ZM18 6H13.2V8.28H18C20.052 8.28 21.72 9.948 21.72 12C21.72 14.052 20.052 15.72 18 15.72H13.2V18H18C21.312 18 24 15.312 24 12C24 8.688 21.312 6 18 6Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/NumberedList.tsx
import { jsx as jsx179 } from "react/jsx-runtime";
var NumberedList = (props) => /* @__PURE__ */ jsx179("svg", { ...props, children: /* @__PURE__ */ jsx179(
  "path",
  {
    d: "M3.425 20C3.30833 20 3.20833 19.9583 3.125 19.875C3.04167 19.7917 3 19.6917 3 19.575C3 19.4583 3.04167 19.3583 3.125 19.275C3.20833 19.1917 3.30833 19.15 3.425 19.15H5.1V18.5H4.475C4.35833 18.5 4.25833 18.4583 4.175 18.375C4.09167 18.2917 4.05 18.1917 4.05 18.075C4.05 17.9583 4.09167 17.8583 4.175 17.775C4.25833 17.6917 4.35833 17.65 4.475 17.65H5.1V17H3.425C3.30833 17 3.20833 16.9583 3.125 16.875C3.04167 16.7917 3 16.6917 3 16.575C3 16.4583 3.04167 16.3583 3.125 16.275C3.20833 16.1917 3.30833 16.15 3.425 16.15H5.525C5.64167 16.15 5.74167 16.1917 5.825 16.275C5.90833 16.3583 5.95 16.4583 5.95 16.575V19.575C5.95 19.6917 5.90833 19.7917 5.825 19.875C5.74167 19.9583 5.64167 20 5.525 20H3.425ZM4.475 7.9C4.35833 7.9 4.25833 7.85833 4.175 7.775C4.09167 7.69167 4.05 7.59167 4.05 7.475V4.85H3.425C3.30833 4.85 3.20833 4.80833 3.125 4.725C3.04167 4.64167 3 4.54167 3 4.425C3 4.30833 3.04167 4.20833 3.125 4.125C3.20833 4.04167 3.30833 4 3.425 4H4.475C4.59167 4 4.69167 4.04167 4.775 4.125C4.85833 4.20833 4.9 4.30833 4.9 4.425V7.475C4.9 7.59167 4.85833 7.69167 4.775 7.775C4.69167 7.85833 4.59167 7.9 4.475 7.9V7.9ZM3.425 13.925C3.30833 13.925 3.20833 13.8833 3.125 13.8C3.04167 13.7167 3 13.6167 3 13.5V13.275C3 13.2583 3.03333 13.1667 3.1 13L4.875 10.925H3.425C3.30833 10.925 3.20833 10.8833 3.125 10.8C3.04167 10.7167 3 10.6167 3 10.5C3 10.3833 3.04167 10.2833 3.125 10.2C3.20833 10.1167 3.30833 10.075 3.425 10.075H5.525C5.64167 10.075 5.74167 10.1167 5.825 10.2C5.90833 10.2833 5.95 10.3833 5.95 10.5V10.725C5.95 10.7417 5.91667 10.8333 5.85 11L4.05 13.075H5.525C5.64167 13.075 5.74167 13.1167 5.825 13.2C5.90833 13.2833 5.95 13.3833 5.95 13.5C5.95 13.6167 5.90833 13.7167 5.825 13.8C5.74167 13.8833 5.64167 13.925 5.525 13.925H3.425ZM8.975 18.775C8.75833 18.775 8.57917 18.7042 8.4375 18.5625C8.29583 18.4208 8.225 18.2417 8.225 18.025C8.225 17.8083 8.29583 17.6292 8.4375 17.4875C8.57917 17.3458 8.75833 17.275 8.975 17.275H20.25C20.4667 17.275 20.6458 17.3458 20.7875 17.4875C20.9292 17.6292 21 17.8083 21 18.025C21 18.2417 20.9292 18.4208 20.7875 18.5625C20.6458 18.7042 20.4667 18.775 20.25 18.775H8.975ZM8.975 12.7C8.75833 12.7 8.57917 12.6292 8.4375 12.4875C8.29583 12.3458 8.225 12.1667 8.225 11.95C8.225 11.7333 8.29583 11.5542 8.4375 11.4125C8.57917 11.2708 8.75833 11.2 8.975 11.2H20.25C20.4667 11.2 20.6458 11.2708 20.7875 11.4125C20.9292 11.5542 21 11.7333 21 11.95C21 12.1667 20.9292 12.3458 20.7875 12.4875C20.6458 12.6292 20.4667 12.7 20.25 12.7H8.975ZM8.975 6.625C8.75833 6.625 8.57917 6.55417 8.4375 6.4125C8.29583 6.27083 8.225 6.09167 8.225 5.875C8.225 5.65833 8.29583 5.47917 8.4375 5.3375C8.57917 5.19583 8.75833 5.125 8.975 5.125H20.25C20.4667 5.125 20.6458 5.19583 20.7875 5.3375C20.9292 5.47917 21 5.65833 21 5.875C21 6.09167 20.9292 6.27083 20.7875 6.4125C20.6458 6.55417 20.4667 6.625 20.25 6.625H8.975Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Strikethrough.tsx
import { jsx as jsx180 } from "react/jsx-runtime";
var Strikethrough = (props) => /* @__PURE__ */ jsx180("svg", { ...props, children: /* @__PURE__ */ jsx180(
  "path",
  {
    d: "M2 13.75V12.25H22V13.75H2ZM10.75 10.75V6.5H5V4H19V6.5H13.25V10.75H10.75ZM10.75 20V15.25H13.25V20H10.75Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/icons/Underline.tsx
import { jsx as jsx181 } from "react/jsx-runtime";
var Underline = (props) => /* @__PURE__ */ jsx181("svg", { ...props, children: /* @__PURE__ */ jsx181(
  "path",
  {
    d: "M5 21V19.5H19V21H5ZM12 17C10.3167 17 8.89583 16.425 7.7375 15.275C6.57917 14.125 6 12.7167 6 11.05V3H8V11.1C8 12.2 8.38333 13.125 9.15 13.875C9.91667 14.625 10.8667 15 12 15C13.1333 15 14.0833 14.625 14.85 13.875C15.6167 13.125 16 12.2 16 11.1V3H18V11.05C18 12.7167 17.4208 14.125 16.2625 15.275C15.1042 16.425 13.6833 17 12 17Z",
    fill: "currentColor"
  }
) });

// src/private/components/Icon/Icon.tsx
import { jsx as jsx182 } from "react/jsx-runtime";
var iconSizeMap3 = {
  sm: "12",
  md: "16",
  lg: "24",
  xl: "32",
  xxl: "48"
};
var Icon2 = ({
  color = "currentColor",
  size = "lg",
  style,
  type,
  ...otherProps
}) => {
  if (isNil13(type)) {
    throw new Error("An Icon component requires a `type` prop to be provided");
  }
  if (isNil13(iconMap2[type])) {
    throw new Error(`Type "${type}" does not exist, please update type prop in Icon component.`);
  }
  if (isNil13(iconSizeMap3[size])) {
    throw new Error(`Size "${size}" does not exist, please update size prop in Icon component.`);
  }
  const IconElement = icons_exports2[iconMap2[type]];
  const defaultStyle = { verticalAlign: "middle", ...style };
  return /* @__PURE__ */ jsx182(
    IconElement,
    {
      "aria-hidden": "true",
      color,
      height: `${iconSizeMap3[size]}px`,
      role: "presentation",
      style: defaultStyle,
      viewBox: "0 0 24 24",
      width: `${iconSizeMap3[size]}px`,
      xmlns: "http://www.w3.org/2000/svg",
      ...otherProps
    }
  );
};
Icon2.displayName = "Icon";

// src/components/RichTextEditor/EditorButtons.tsx
import { jsx as jsx183, jsxs as jsxs29 } from "react/jsx-runtime";
var ButtonContainer = styled40.div`
  background: white;
  border-bottom: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space02};
  padding: ${({ theme: theme2 }) => theme2.spacing.space02} 0;
  position: sticky;
  top: 0;

  /*
   * needs a stacking context to stay above RTE content
   * did not add to theme because it doesn't interact with any other components
   */
  z-index: 1;
`;
var EditorButton = ({
  format = "",
  icon,
  disabled = false,
  onCustomClick,
  buttonType = "block"
}) => {
  const editor = useSlate();
  const activeButtonColor = useThemeColor("brandBlue500");
  const isActive = buttonType === "block" ? isBlockActive(editor, format) : isMarkActive(editor, format);
  const handleClick = (event) => {
    event.preventDefault();
    if (buttonType === "block") {
      toggleBlck(editor, format);
    } else {
      toggleMark(editor, format);
    }
    if (onCustomClick) {
      onCustomClick(event);
    }
  };
  const buttonColor = isActive ? activeButtonColor : void 0;
  return /* @__PURE__ */ jsx183(
    IconButton,
    {
      "aria-pressed": isActive,
      colorOverride: buttonColor,
      disabled,
      icon,
      label: format,
      onClick: handleClick,
      preventTooltip: true
    }
  );
};
var getSelectedText = (editor) => {
  if (!editor.selection) {
    return "";
  }
  const nodeFragment = Node2.fragment(editor, editor.selection);
  if (!nodeFragment[0]) {
    return "";
  }
  return Node2.string(nodeFragment[0]);
};
var noTextSelected = (editor) => {
  const userSelection = getSelectedText(editor);
  return userSelection.trim() === "";
};
var EditorButtons = ({
  enabledFormats = ["BOLD", "ITALIC", "HEADING_1", "LINK", "BULLETED_LIST", "IMAGE"],
  disabled = false
}) => {
  const editor = useSlate();
  return /* @__PURE__ */ jsxs29(ButtonContainer, { children: [
    enabledFormats.includes(rteFormatTypes.BOLD) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "mark",
        disabled,
        format: "bold",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "bold" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.ITALIC) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "mark",
        disabled,
        format: "italic",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "italic" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.HEADING_1) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "heading-one",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "heading" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.HEADING_2) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "heading-two",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "heading-two" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.BULLETED_LIST) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "bulleted-list",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "bulleted-list" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.NUMBERED_LIST) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "numbered-list",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "numbered-list" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.BLOCKQUOTE) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "blockquote",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "block-quote" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.UNDERLINE) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "mark",
        disabled,
        format: "underline",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "underline" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.STRIKETHROUGH) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "mark",
        disabled,
        format: "strikethrough",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "strikethrough" })
      }
    ),
    enabledFormats.includes(rteFormatTypes.LINK) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "mark",
        disabled: disabled || noTextSelected(editor),
        format: "link",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "link" }),
        onCustomClick: (event) => {
          event.preventDefault();
          insertLink(editor);
        }
      }
    ),
    enabledFormats.includes(rteFormatTypes.IMAGE) && /* @__PURE__ */ jsx183(
      EditorButton,
      {
        buttonType: "block",
        disabled,
        format: "image",
        icon: /* @__PURE__ */ jsx183(Icon2, { type: "image" }),
        onCustomClick: (event) => {
          event.preventDefault();
          const promptUserForImageUrl = prompt("Enter the URL of the image:");
          if (!isNonEmptyString10(promptUserForImageUrl)) {
            return;
          }
          insertImage(editor, promptUserForImageUrl);
        }
      }
    )
  ] });
};

// src/components/RichTextEditor/RichTextEditor.tsx
import { jsx as jsx184, jsxs as jsxs30 } from "react/jsx-runtime";
var EditableWrapper = styled41(Editable)`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  outline: none; /* prevent blue accessibility outline from appearing on focus */
`;
var defaultHeight = "300px";
var EditorWrapper = styled41.div`
  background: white;
  border: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  border-radius: 4px;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "auto"};
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  height: ${({ height }) => height ?? defaultHeight};
  overflow: auto;
  padding: 0 ${({ theme: theme2 }) => theme2.spacing.space04};

  strong {
    font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
  }
`;
var HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline"
};
var emptyTextNode3 = { text: "" };
var defaultValue = [
  {
    type: "paragraph",
    children: [emptyTextNode3]
  }
];
var RichTextEditor = ({
  enabledFormats = Object.values(rteFormatTypes),
  value = defaultValue,
  onChange,
  onBlur,
  placeholder = "Enter some rich text...",
  disabled = false,
  ariaLabelledBy,
  useExternalValue,
  "data-testid": dataTestId = "vhs-rich-text-editor",
  height,
  ...otherProps
}) => {
  const renderElement = useCallback12(
    (props) => /* @__PURE__ */ jsx184(Element2, { ...props }),
    []
  );
  const renderLeaf = useCallback12((props) => /* @__PURE__ */ jsx184(Leaf, { ...props }), []);
  const editor = useMemo7(
    () => withImages(withLinks(withHistory(withReact(createEditor())))),
    []
  );
  const isTextValid = (rteValue) => {
    if (isNil14(rteValue) || isNil14(rteValue[0]) || isNil14(rteValue[0].children)) {
      return false;
    }
    const { text } = rteValue[0].children[0];
    return isNonEmptyString11(text);
  };
  useEffect18(() => {
    const isDefaultValue = isEqual2(value, defaultValue);
    const valueString = rteSerializeToHTML(value);
    const editorChildrenString = rteSerializeToHTML(editor.children);
    if (isDefaultValue || isTextValid(value) && useExternalValue && valueString !== editorChildrenString) {
      editor.children = value;
      editor.selection = null;
      editor.onChange();
    }
  }, [value, editor, useExternalValue]);
  const handleEditorBlur = (_event) => {
    const updatedValue = editor.children;
    if (isNotNil26(onBlur)) {
      onBlur(updatedValue);
    }
  };
  const handleRemoveImageIfSelected = useCallback12(() => {
    const { selection } = editor;
    if (isNil14(selection) || isNil14(selection.anchor.path[0])) {
      return;
    }
    const selectedElement = editor.children[selection.anchor.path[0]];
    if (Element7.isElement(selectedElement) && selectedElement.type === "image") {
      toggleBlck(editor, "image");
      Transforms5.removeNodes(editor, { at: selection });
      Transforms5.move(editor, { reverse: true });
      const spanElement = editor.children.filter(
        (child) => Element7.isElement(child) && child.type === "span"
      )[0];
      if (Element7.isElement(spanElement) && spanElement.type === "span" && isNotNil26(spanElement.children) && isNotNil26(spanElement.children[0]) && isNotNil26(spanElement.children[0].text) && spanElement.children[0].text.trim() !== "") {
        Transforms5.setNodes(
          editor,
          { type: "paragraph" },
          {
            match: (node) => Element7.isElement(node) && node.type === "span" && isNotNil26(node.children) && Text4.isText(node.children[0]) && isNotNil26(node.children[0].text) && node.children[0].text.trim() !== "",
            split: true,
            mode: "highest"
          }
        );
      }
    }
  }, [editor]);
  return (
    // eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions
    /* @__PURE__ */ jsx184(
      EditorWrapper,
      {
        "data-testid": dataTestId,
        disabled,
        height,
        onClick: () => {
          ReactEditor.focus(editor);
        },
        children: /* @__PURE__ */ jsxs30(
          Slate,
          {
            editor,
            initialValue: value,
            onChange,
            ...otherProps,
            children: [
              /* @__PURE__ */ jsx184(
                EditorButtons,
                {
                  disabled,
                  enabledFormats
                }
              ),
              /* @__PURE__ */ jsx184(
                EditableWrapper,
                {
                  "aria-labelledby": ariaLabelledBy,
                  autoFocus: false,
                  "data-testid": "editor-content",
                  onBlur: handleEditorBlur,
                  onKeyDown: (event) => {
                    Object.keys(HOTKEYS).forEach((hotKey) => {
                      if (isHotkey(hotKey, event)) {
                        event.preventDefault();
                        toggleMark(editor, HOTKEYS[hotKey]);
                      }
                    });
                    if (event.key === "Enter" && !event.shiftKey) {
                      handleListEscape(editor);
                    }
                    if (event.key === "Backspace" || event.key === "Clear" || event.key === "Delete") {
                      handleRemoveImageIfSelected();
                    }
                  },
                  placeholder,
                  readOnly: disabled,
                  renderElement,
                  renderLeaf,
                  spellCheck: true
                }
              )
            ]
          }
        )
      }
    )
  );
};
RichTextEditor.displayName = "RichTextEditor_VHS";

// src/private/components/FormConnectorRichTextEditor/FormConnectorRichTextEditor.tsx
import { jsx as jsx185 } from "react/jsx-runtime";
var Wrapper2 = styled42.div`
  margin-top: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var FormConnectorRichTextEditor = ({
  field: { name, value, onChange },
  form: { errors },
  label,
  labelDescription,
  labelProps = {},
  placeholder = "",
  disabled = false,
  enabledFormats = Object.values(rteFormatTypes),
  useExternalValue = false,
  height = void 0,
  ...otherProps
}) => {
  const [rteValue, setRTEValue] = useState19(rteDeserializeHTML(coerceToString(value)));
  const labelId = isNotUndefined11(label) ? `${name}-label-id` : void 0;
  useEffect19(() => {
    if (useExternalValue) {
      setRTEValue(rteDeserializeHTML(coerceToString(value)));
    }
  }, [useExternalValue, value]);
  return /* @__PURE__ */ jsx185(
    FormFieldWrapper,
    {
      disabled,
      error: errors[name],
      label,
      labelDescription,
      labelId,
      labelProps,
      name,
      children: /* @__PURE__ */ jsx185(Wrapper2, { "data-testid": `richTextEditor-${name}`, children: /* @__PURE__ */ jsx185(
        RichTextEditor,
        {
          ariaLabelledBy: labelId,
          disabled,
          enabledFormats,
          height,
          onChange: (event) => {
            setRTEValue(event);
            const serializedValue = rteSerializeToHTML(event) ?? "";
            onChange(name)(serializedValue);
          },
          placeholder,
          useExternalValue,
          value: rteValue,
          ...otherProps
        }
      ) })
    }
  );
};
FormConnectorRichTextEditor.displayName = "FormConnectorRichTextEditor";

// src/components/FormField/FormField.tsx
import { jsx as jsx186 } from "react/jsx-runtime";
var FormFieldSet = styled43.fieldset`
  border: 0;
  flex-grow: 1;
  margin: ${({ theme: theme2 }) => `0 0 ${theme2.spacing.space05}`};
  min-width: 0;
  padding: 0;

  &[disabled] {
    label {
      pointer-events: none;
    }
  }
`;
var FieldWrapper = styled43(Field)`
  resize: ${({ $resize }) => $resize};
`;
var fieldsMap = {
  FormConnectorCheckboxGroup,
  FormConnectorCustomField,
  FormConnectorInput,
  FormConnectorRadioGroup,
  FormConnectorSelect,
  FormConnectorHidden,
  FormConnectorRichTextEditor
};
var FormField = forwardRef14(
  ({
    resize = "vertical",
    customComponent,
    direction = "column",
    disabled = false,
    label,
    labelDescription,
    labelProps = {},
    name = "",
    onChangeCustom,
    options,
    placeholder = null,
    type = "text",
    ...otherProps
  }, ref) => /* @__PURE__ */ jsx186(
    FormFieldSet,
    {
      disabled,
      id: `fieldset-${name}`,
      children: /* @__PURE__ */ jsx186(
        FieldWrapper,
        {
          $resize: resize,
          component: fieldsMap[inputTypeMap[type].component],
          customComponent,
          direction,
          disabled,
          id: `field-${name}`,
          innerRef: ref,
          label,
          labelDescription,
          labelProps,
          name,
          onChangeCustom,
          options,
          placeholder,
          type,
          ...otherProps
        }
      )
    }
  )
);
FormField.displayName = "FormField_VHS";

// src/components/FullScreenModal/FullScreenModal.tsx
import { styled as styled44 } from "styled-components";
import { isNonEmptyString as isNonEmptyString12 } from "@wistia/type-guards";
import { jsx as jsx187, jsxs as jsxs31 } from "react/jsx-runtime";
var CloseButton2 = styled44(Button)`
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  height: 42px;
  position: fixed;
  right: ${({ theme: theme2 }) => theme2.spacing.space05};
  top: ${({ theme: theme2 }) => theme2.spacing.space05};
  width: 42px;

  &:hover {
    color: ${({ theme: theme2 }) => theme2.color.grey900};
  }
`;
var FullScreenModal = ({
  content,
  toggle,
  backgroundColor,
  initialShownState = false,
  onBeforeHide,
  onHide
}) => {
  const defaultBackgroundColor = useThemeColor("grey100");
  return /* @__PURE__ */ jsx187(
    ModalBase,
    {
      initialShownState,
      onBeforeHide,
      onHide,
      render: (hide) => /* @__PURE__ */ jsxs31(
        Backdrop,
        {
          backgroundColor: isNonEmptyString12(backgroundColor) ? backgroundColor : defaultBackgroundColor,
          children: [
            /* @__PURE__ */ jsx187(
              CloseButton2,
              {
                noStyle: true,
                onClick: hide,
                title: "Close",
                children: /* @__PURE__ */ jsx187(CloseIcon2, {})
              }
            ),
            content(hide)
          ]
        }
      ),
      toggle
    }
  );
};
FullScreenModal.displayName = "FullScreenModal_VHS";

// src/components/Image/Image.tsx
import { styled as styled45 } from "styled-components";
import { jsx as jsx188 } from "react/jsx-runtime";
var getFillStyle2 = ($fillContainer) => {
  if ($fillContainer === "horizontal") {
    return "width: 100%;";
  }
  if ($fillContainer === "vertical") {
    return "height: 100%;";
  }
  if ($fillContainer === true) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return void 0;
};
var ImageComponent = styled45.img`
  ${({ $fillContainer }) => getFillStyle2($fillContainer)};
  object-fit: ${({ $objFit }) => $objFit};
  object-position: ${({ $objPosition }) => $objPosition};
`;
var Image3 = ({
  src,
  alt,
  fill: fillContainer = false,
  fit: objFit,
  loading = "lazy",
  position: objPosition,
  ...otherProps
}) => /* @__PURE__ */ jsx188(
  ImageComponent,
  {
    $fillContainer: fillContainer,
    $objFit: objFit,
    $objPosition: objPosition,
    alt,
    loading,
    src,
    ...otherProps
  }
);
Image3.displayName = "Image_VHS";

// src/components/KeyboardShortcut/KeyboardShortcut.tsx
import { styled as styled46 } from "styled-components";
import { isNotNil as isNotNil27, isArray as isArray3 } from "@wistia/type-guards";
import { jsx as jsx189, jsxs as jsxs32 } from "react/jsx-runtime";
var KeyboardShortcutComponent = styled46.div`
  align-items: center;
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space02};
  ${({ $fullWidth }) => $fullWidth && "width: 100%; justify-content: space-between;"}
`;
var StyledKey = styled46.kbd`
  align-items: center;
  background: ${({ $componentStyle, theme: theme2 }) => $componentStyle === "dark" ? theme2.color.grey200 : theme2.color.grey600};
  border-bottom: 1px solid
    ${({ $componentStyle, theme: theme2 }) => $componentStyle === "dark" ? theme2.color.grey400 : theme2.color.grey800};
  border-radius: 4px;
  color: ${({ $componentStyle, theme: theme2 }) => $componentStyle === "dark" ? theme2.color.grey700 : theme2.color.grey100};
  display: flex;
  font-family: InterVariable, sans-serif;

  /* Adds serif to uppercase "I" to distinguish it from "l": https://rsms.me/inter/glyphs/?g=I.1 */
  font-feature-settings: 'cv08' on;
  font-size: 10px;

  /* Adds a slash to the zero to distinguish it from the letter "O": https://rsms.me/inter/glyphs/?g=zero.tf.slash */

  /* Adds tabular numbers to ensure the width of the key is consistent */
  font-variant-numeric: slashed-zero tabular-nums;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  height: 20px;
  justify-content: center;
  line-height: 20px;
  min-width: 20px;
  padding: 0 ${({ theme: theme2 }) => theme2.spacing.space01};
`;
var Label2 = styled46.span`
  color: ${({ $componentStyle, theme: theme2 }) => $componentStyle === "dark" ? theme2.color.grey700 : theme2.color.grey100};
  font-size: 12px;
`;
var KeysContainer = styled46.div`
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space01};
`;
var keyToString = (key) => {
  switch (key) {
    // Arrow Keys
    case "ArrowDown":
      return "\u2193";
    case "ArrowLeft":
      return "\u2190";
    case "ArrowRight":
      return "\u2192";
    case "ArrowUp":
      return "\u2191";
    // Modifier Keys
    case "Alt":
      return "Alt";
    case "Cmd":
    case "Meta":
      if (/Mac|iPhone|iPad/.test(window.navigator.userAgent)) {
        return "\u2318";
      }
      if (window.navigator.userAgent.includes("Win")) {
        return "\u229E";
      }
      return "^";
    case "Ctrl":
      return "Ctrl";
    case "Option":
      return "\u2325";
    case "Shift":
      return "\u21E7";
    // Whitespace Keys
    case "Enter":
      return "\u21B5";
    case "Space":
      return "Space";
    case "Tab":
      return "Tab";
    case "Backspace":
      return "Del";
    case "Esc":
      return "Esc";
    default:
      return key.toUpperCase();
  }
};
var KeyboardShortcut = ({
  label,
  keyboardKeys,
  fullWidth = false,
  componentStyle = "dark",
  ...otherProps
}) => {
  const kbKeys = isArray3(keyboardKeys) ? keyboardKeys : [keyboardKeys];
  return /* @__PURE__ */ jsxs32(
    KeyboardShortcutComponent,
    {
      $componentStyle: componentStyle,
      $fullWidth: fullWidth,
      ...otherProps,
      children: [
        isNotNil27(label) && /* @__PURE__ */ jsx189(Label2, { $componentStyle: componentStyle, children: label }),
        /* @__PURE__ */ jsx189(KeysContainer, { children: kbKeys.map((keyboardKey, index) => /* @__PURE__ */ jsx189(
          StyledKey,
          {
            $componentStyle: componentStyle,
            children: keyToString(keyboardKey)
          },
          index
        )) })
      ]
    }
  );
};
KeyboardShortcut.displayName = "KeyboardShortcut_VHS";

// src/components/LinkButton/LinkButton.tsx
import { forwardRef as forwardRef15 } from "react";
import { styled as styled47 } from "styled-components";
import { isNotNil as isNotNil28, isNil as isNil15 } from "@wistia/type-guards";
import { jsx as jsx190 } from "react/jsx-runtime";
var isEnterOrSpace = (event) => event.key === "Enter" || event.key === " " || event.key === "Spacebar";
var LinkButtonStyledComponent = styled47.span`
  ${({ $noStyle }) => !$noStyle ? linkStyle : void 0};

  &[tabindex] {
    ${({ $underlineOnHover }) => $underlineOnHover ? underlineOnHoverStyle : void 0};
  }
`;
var LinkButton = forwardRef15(
  ({
    children,
    disabled = false,
    forceState,
    label,
    onClick,
    noStyle = false,
    underlineOnHover = false,
    variant = "primary",
    ...otherProps
  }, ref) => {
    const handleLinkClick = (event) => {
      if (isNil15(onClick)) {
        return;
      }
      if (disabled) {
        return;
      }
      onClick(event);
    };
    const handleKeypress = (event) => {
      if (isNil15(onClick)) {
        return;
      }
      if (isEnterOrSpace(event)) {
        event.preventDefault();
        onClick(event);
      }
    };
    const computedTabIndex = disabled ? void 0 : 0;
    const getContent = () => {
      if (isNotNil28(children)) {
        return children;
      }
      if (isNotNil28(label)) {
        return label;
      }
      return null;
    };
    return /* @__PURE__ */ jsx190(
      LinkButtonStyledComponent,
      {
        ref,
        $disabledLink: disabled,
        $forceState: forceState,
        $noStyle: noStyle,
        $underlineOnHover: underlineOnHover,
        $variant: variant,
        "aria-disabled": disabled,
        onClick: handleLinkClick,
        onKeyPress: handleKeypress,
        role: "button",
        tabIndex: computedTabIndex,
        ...otherProps,
        children: getContent()
      }
    );
  }
);
LinkButton.displayName = "LinkButton_VHS";

// src/components/List/List.tsx
import { isNotNil as isNotNil29 } from "@wistia/type-guards";
import { styled as styled49, css as css22 } from "styled-components";

// src/components/List/ListItem.tsx
import { styled as styled48 } from "styled-components";
import { isNil as isNil16 } from "@wistia/type-guards";
import { jsx as jsx191 } from "react/jsx-runtime";
var ListItemComponent = styled48.li`
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var ListItem = ({ children }) => {
  if (isNil16(children)) {
    return null;
  }
  return /* @__PURE__ */ jsx191(ListItemComponent, { children });
};
ListItem.displayName = "ListItem_VHS";

// src/components/List/List.tsx
import { jsx as jsx192, jsxs as jsxs33 } from "react/jsx-runtime";
var spacesStyle = css22`
  overflow: hidden;
  padding-left: 0;
  vertical-align: bottom;

  li {
    display: block;
    float: left;

    &::after {
      content: ' ';
      padding-right: ${({ theme: theme2 }) => theme2.spacing.space03};
    }

    &:last-child {
      &::after {
        content: '' !important;
        padding-right: 0;
      }
    }
  }
`;
var commasStyle = css22`
  ${spacesStyle};

  li {
    &::after {
      content: ',' !important;
      padding-right: ${({ theme: theme2 }) => theme2.spacing.space01};
    }
  }
`;
var slashesStyle = css22`
  ${spacesStyle};

  li {
    &::after {
      content: '/' !important;
      padding-left: ${({ theme: theme2 }) => theme2.spacing.space01};
      padding-right: ${({ theme: theme2 }) => theme2.spacing.space01};
    }
  }
`;
var breadcrumbsStyle = css22`
  ${spacesStyle};

  li {
    &::after {
      content: '>' !important;
      padding-left: ${({ theme: theme2 }) => theme2.spacing.space01};
      padding-right: ${({ theme: theme2 }) => theme2.spacing.space01};
    }
  }
`;
var unbulletedStyle = css22`
  list-style: none;
  padding-left: 0;
`;
var ListComponent = styled49.ul`
  list-style-position: outside;
  margin: ${({ theme: theme2 }) => `0 0 ${theme2.spacing.space01}`};
  padding: ${({ theme: theme2 }) => `0 0 0 ${theme2.spacing.space04}`};
  ${({ variant }) => variant === "unbulleted" && unbulletedStyle};
  ${({ variant }) => variant === "spaces" && spacesStyle};
  ${({ variant }) => variant === "commas" && commasStyle};
  ${({ variant }) => variant === "slashes" && slashesStyle};
  ${({ variant }) => variant === "breadcrumbs" && breadcrumbsStyle};

  > li:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin-top: ${({ theme: theme2 }) => theme2.spacing.space01};
  }

  /* nested lists should not have bottom margins because their container <li> will have one */
  li ul,
  li ol {
    margin-bottom: 0;
  }
`;
var renderListComponent = (listItems, variant, { ...otherProps }) => {
  const elementType = variant === "ordered" ? "ol" : "ul";
  return /* @__PURE__ */ jsx192(
    ListComponent,
    {
      as: elementType,
      variant,
      ...otherProps,
      children: listItems
    }
  );
};
var renderListFromArray = (listItems, variant, otherProps) => {
  let itemCount = 0;
  const items = listItems.map((listItem, i) => {
    const item = listItem;
    const nextItem = listItems[i + 1];
    const key = `item-${itemCount += 1}`;
    if (Array.isArray(nextItem)) {
      return /* @__PURE__ */ jsxs33(ListItem, { children: [
        item,
        renderListFromArray(nextItem, variant, otherProps)
      ] }, key);
    }
    if (Array.isArray(item)) {
      return null;
    }
    return /* @__PURE__ */ jsx192(ListItem, { children: item }, key);
  });
  return renderListComponent(items, variant, otherProps);
};
var List = ({
  children,
  items,
  variant,
  ...otherProps
}) => {
  const listVariant = variant ?? "bullets";
  if (isNotNil29(children)) {
    if (Array.isArray(children) && !children.length) {
      return null;
    }
    return renderListComponent(children, listVariant, otherProps);
  }
  if (isNotNil29(items)) {
    if (!items.length) {
      return null;
    }
    return renderListFromArray(items, listVariant, otherProps);
  }
  return null;
};
List.displayName = "List_VHS";

// src/components/Menu/index.ts
import { DropdownMenuRadioGroup } from "@radix-ui/react-dropdown-menu";

// src/components/Menu/Menu.tsx
import { styled as styled50, css as css23, keyframes } from "styled-components";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent
} from "@radix-ui/react-dropdown-menu";
import { isNotNil as isNotNil30, isNotUndefined as isNotUndefined12 } from "@wistia/type-guards";
import { jsx as jsx193, jsxs as jsxs34 } from "react/jsx-runtime";
var gutterWidth = "8px";
var open = keyframes`
  from {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var close = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
`;
var menuContentCss = css23`
  animation: ${open} ${({ theme: theme2 }) => theme2.duration.productive.moderate}
    ${({ theme: theme2 }) => theme2.easing.entrance.productive};
  background: white;
  border-radius: 4px;
  box-shadow: ${({ theme: theme2 }) => theme2.shadow.elevation2};
  max-height: var(--radix-dropdown-menu-content-available-height);
  max-width: 100vw;
  overflow: auto;
  padding: ${gutterWidth} ${gutterWidth} ${({ theme: theme2 }) => theme2.spacing.space04} ${gutterWidth};
  transform-origin: var(--radix-dropdown-menu-content-transform-origin);
  z-index: ${({ theme: theme2 }) => theme2.zIndex.MenuComponent};

  &[data-state='closed'] {
    animation: ${close} ${({ theme: theme2 }) => theme2.duration.productive.moderate}
      ${({ theme: theme2 }) => theme2.easing.exit.productive};
  }

  ${mq.smAndUp} {
    bottom: 0;
    padding: ${gutterWidth};
    right: 0;
    top: 0;
    transform: initial;
  }

  hr {
    margin: ${({ theme: theme2 }) => theme2.spacing.space02} 0;
  }
`;
var MenuContent = styled50(DropdownMenuContent)`
  ${menuContentCss}
`;
var Menu = ({
  align = "start",
  children,
  trigger,
  label,
  isOpen,
  triggerProps = {},
  // TODO - come back to this. This error is bizarre
  // eslint-disable-next-line @typescript-eslint/unbound-method
  onOpenChange,
  onInteractOutside,
  ...props
}) => {
  return /* @__PURE__ */ jsxs34(
    DropdownMenu,
    {
      modal: false,
      ...isNotNil30(onOpenChange) && isNotNil30(isOpen) ? { open: isOpen, onOpenChange } : {},
      children: [
        /* @__PURE__ */ jsx193(DropdownMenuTrigger, { asChild: true, children: isNotUndefined12(trigger) ? trigger : /* @__PURE__ */ jsx193(
          Button,
          {
            "aria-expanded": isOpen,
            forceState: isOpen ? "active" : void 0,
            iconName: "caret-down",
            iconPosition: "end",
            ...triggerProps,
            children: label
          }
        ) }),
        /* @__PURE__ */ jsx193(DropdownMenuPortal, { children: /* @__PURE__ */ jsx193(
          MenuContent,
          {
            ...props,
            align,
            onInteractOutside,
            sideOffset: 6,
            children
          }
        ) })
      ]
    }
  );
};
Menu.displayName = "Menu_VHS";

// src/components/Menu/MenuItem.tsx
import {
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuItemIndicator,
  DropdownMenuSubTrigger
} from "@radix-ui/react-dropdown-menu";
import { isNonEmptyString as isNonEmptyString13, isNotNil as isNotNil31, isNotUndefined as isNotUndefined13 } from "@wistia/type-guards";
import { styled as styled51, css as css24 } from "styled-components";
import { darken as darken5 } from "polished";
import { jsx as jsx194, jsxs as jsxs35 } from "react/jsx-runtime";
var hoverDarken2 = "0.04";
var focusDarken = "0.08";
var dangerousMenuItemStyles = css24`
  color: ${({ theme: theme2 }) => theme2.color.error700};

  &:focus,
  [data-state='highlighted'] {
    background-color: ${({ theme: theme2 }) => darken5(focusDarken, theme2.color.error100)};
  }

  &:hover:not([aria-disabled]) {
    background-color: ${({ theme: theme2 }) => darken5(hoverDarken2, theme2.color.error100)};
  }
`;
var pricingMenuItemStyles = css24`
  color: ${({ theme: theme2 }) => theme2.color.purple600};

  &:focus,
  [data-state='highlighted'] {
    background-color: ${({ theme: theme2 }) => darken5(focusDarken, theme2.color.purple100)};
  }

  &:hover:not([aria-disabled]) {
    background-color: ${({ theme: theme2 }) => darken5(hoverDarken2, theme2.color.purple100)};
  }
`;
var getMenuItemStyles = (variant) => {
  switch (variant) {
    case "dangerous":
      return dangerousMenuItemStyles;
    case "pricing":
      return pricingMenuItemStyles;
    case "primary":
    default:
      return null;
  }
};
var StyledMenuItem = styled51.div`
  ${buttonResetStyle};
  border-radius: 4px;
  ${ellipsisStyle};
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space02};
  justify-content: space-between;
  padding: ${({ theme: theme2 }) => theme2.spacing.space02};
  text-decoration: none;

  &:hover,
  &:focus {
    border: none;
    color: ${({ theme: theme2 }) => theme2.color.grey900};
    outline: none;
  }

  &:focus,
  [data-state='highlighted'] {
    background-color: ${({ theme: theme2 }) => darken5(focusDarken, theme2.color.grey200)};
  }

  &:hover:not([aria-disabled]) {
    background-color: ${({ theme: theme2 }) => theme2.color.grey200};
  }

  /* Precedence boost: https://styled-components.com/docs/basics#pseudoelements-pseudoselectors-and-nesting */
  && {
    ${({ $variant }) => isNotNil31($variant) && getMenuItemStyles($variant)};
  }
`;
var StyledMenuItemContent = styled51.div`
  align-items: center;
  display: grid;
  gap: ${({ theme: theme2 }) => theme2.spacing.space03};
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  row-gap: 0;
  width: 100%;
`;
var StyledMenuItemLabel = styled51.div`
  display: flex;
  font-size: 14px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.medium};
  justify-content: space-between;
  line-height: 1.5;
  min-width: 100%;
`;
var IconContainer2 = styled51.div`
  grid-row: span 2;
  text-align: center;
  width: 24px;
`;
var NoIconContainer = styled51.div`
  grid-row: span 2;
  text-align: center;
  width: 0;
`;
var MenuItem = ({
  children,
  label,
  description,
  badge,
  icon,
  renderAs,
  href,
  target,
  variant = "primary",
  ...props
}) => {
  const as = renderAs ?? (isNotUndefined13(href) ? "a" : "div");
  const targetProp = as === "a" && isNonEmptyString13(target) ? { target } : {};
  return /* @__PURE__ */ jsx194(
    DropdownMenuItem,
    {
      style: { outline: "none" },
      ...props,
      asChild: true,
      children: /* @__PURE__ */ jsx194(
        StyledMenuItem,
        {
          $variant: variant,
          as,
          href,
          ...targetProp,
          children: isNotUndefined13(children) ? children : /* @__PURE__ */ jsxs35(StyledMenuItemContent, { children: [
            isNotUndefined13(icon) && /* @__PURE__ */ jsx194(IconContainer2, { children: icon }),
            /* @__PURE__ */ jsxs35(StyledMenuItemLabel, { children: [
              label,
              " ",
              badge
            ] }),
            isNotUndefined13(description) ? /* @__PURE__ */ jsx194(Text, { variant: "caption", children: description }) : null
          ] })
        }
      )
    }
  );
};
var CheckboxMenuItem = ({
  children,
  label,
  description,
  badge,
  icon,
  indicator = /* @__PURE__ */ jsx194(
    Icon,
    {
      size: "sm",
      type: "checkmark"
    }
  ),
  ...props
}) => {
  return /* @__PURE__ */ jsx194(
    DropdownMenuCheckboxItem,
    {
      ...props,
      asChild: true,
      children: /* @__PURE__ */ jsxs35(StyledMenuItem, { children: [
        isNotUndefined13(children) ? children : /* @__PURE__ */ jsxs35(StyledMenuItemContent, { children: [
          isNotUndefined13(icon) ? /* @__PURE__ */ jsx194(IconContainer2, { children: icon }) : /* @__PURE__ */ jsx194(NoIconContainer, {}),
          /* @__PURE__ */ jsxs35(StyledMenuItemLabel, { children: [
            label,
            " ",
            badge
          ] }),
          isNotUndefined13(description) ? /* @__PURE__ */ jsx194(Text, { variant: "caption", children: description }) : null
        ] }),
        /* @__PURE__ */ jsx194(DropdownMenuItemIndicator, { children: indicator })
      ] })
    }
  );
};
var RadioMenuItem = ({
  children,
  label,
  badge,
  description,
  icon,
  indicator = /* @__PURE__ */ jsx194(
    Icon,
    {
      size: "sm",
      type: "checkmark"
    }
  ),
  ...props
}) => {
  return /* @__PURE__ */ jsx194(
    DropdownMenuRadioItem,
    {
      ...props,
      asChild: true,
      children: /* @__PURE__ */ jsxs35(StyledMenuItem, { children: [
        isNotUndefined13(children) ? children : /* @__PURE__ */ jsxs35(StyledMenuItemContent, { children: [
          isNotUndefined13(icon) ? /* @__PURE__ */ jsx194(IconContainer2, { children: icon }) : /* @__PURE__ */ jsx194(NoIconContainer, {}),
          /* @__PURE__ */ jsxs35(StyledMenuItemLabel, { children: [
            label,
            " ",
            badge
          ] }),
          " ",
          isNotUndefined13(description) ? /* @__PURE__ */ jsx194(Text, { variant: "caption", children: description }) : null
        ] }),
        /* @__PURE__ */ jsx194(DropdownMenuItemIndicator, { children: indicator })
      ] })
    }
  );
};
var SubTrigger = styled51(DropdownMenuSubTrigger)`
  outline: none;

  &[data-state='open'],
  &[data-highlighted] {
    background-color: ${({ theme: theme2 }) => theme2.color.grey200};
  }
`;
var SubMenuTrigger = ({
  children,
  label,
  badge,
  description,
  icon,
  onClick
}) => {
  const { isSmAndUp } = useMq();
  const Trigger = isSmAndUp ? SubTrigger : DropdownMenuItem;
  return /* @__PURE__ */ jsx194(
    Trigger,
    {
      onClick: (event) => {
        if (isNotUndefined13(onClick)) {
          onClick(event);
        }
      },
      children: /* @__PURE__ */ jsxs35(StyledMenuItem, { children: [
        isNotUndefined13(children) ? children : /* @__PURE__ */ jsxs35(StyledMenuItemContent, { children: [
          isNotUndefined13(icon) ? /* @__PURE__ */ jsx194(IconContainer2, { children: icon }) : /* @__PURE__ */ jsx194(NoIconContainer, {}),
          /* @__PURE__ */ jsxs35(StyledMenuItemLabel, { children: [
            label,
            " ",
            badge
          ] }),
          isNotUndefined13(description) ? /* @__PURE__ */ jsx194(Text, { variant: "caption", children: description }) : null
        ] }),
        /* @__PURE__ */ jsx194(
          Icon,
          {
            size: "md",
            type: "caret-right"
          }
        )
      ] })
    }
  );
};

// src/components/Menu/MenuLabel.tsx
import { styled as styled52 } from "styled-components";
import { DropdownMenuLabel } from "@radix-ui/react-dropdown-menu";
import { jsx as jsx195 } from "react/jsx-runtime";
var StyledMenuLabel = styled52(DropdownMenuLabel)`
  /* TODO - this should be in theme */
  padding: 8px;
`;
var MenuLabel = ({ children, ...props }) => {
  const grey700 = useThemeColor("grey700");
  return /* @__PURE__ */ jsx195(StyledMenuLabel, { asChild: true, children: /* @__PURE__ */ jsx195(
    Text,
    {
      colorOverride: grey700,
      variant: "overline",
      ...props,
      children
    }
  ) });
};

// src/components/Menu/SubMenu.tsx
import { useState as useState20 } from "react";
import { styled as styled53 } from "styled-components";
import {
  DropdownMenuPortal as DropdownMenuPortal2,
  DropdownMenuSub,
  DropdownMenuGroup,
  DropdownMenuSubContent
} from "@radix-ui/react-dropdown-menu";
import { jsx as jsx196, jsxs as jsxs36 } from "react/jsx-runtime";
var SubMenuContent = styled53(DropdownMenuSubContent)`
  ${menuContentCss}

  ${mq.smAndDown} {
    transform: translateX(-100%) !important;
  }
`;
var StyledMobileSubMenuItems = styled53.div`
  margin-left: ${({ theme: theme2 }) => theme2.spacing.space03};
`;
var SubMenu = ({ label, icon, description, children }) => {
  const { isSmAndUp } = useMq();
  const [isExpanded, setIsExpanded] = useState20(false);
  return isSmAndUp ? /* @__PURE__ */ jsxs36(DropdownMenuSub, { children: [
    /* @__PURE__ */ jsx196(
      SubMenuTrigger,
      {
        description,
        icon,
        label
      }
    ),
    /* @__PURE__ */ jsx196(DropdownMenuPortal2, { children: /* @__PURE__ */ jsx196(SubMenuContent, { children }) })
  ] }) : /* @__PURE__ */ jsxs36(DropdownMenuGroup, { children: [
    /* @__PURE__ */ jsx196(
      SubMenuTrigger,
      {
        description,
        icon,
        label,
        onClick: (event) => {
          event.preventDefault();
          setIsExpanded((prev) => !prev);
        }
      }
    ),
    /* @__PURE__ */ jsx196(StyledMobileSubMenuItems, { children: isExpanded ? children : null })
  ] });
};

// src/components/MenuButton/MenuButton.tsx
import { useCallback as useCallback13, cloneElement, useState as useState21, useLayoutEffect as useLayoutEffect5, useRef as useRef13 } from "react";
import { isFunction as isFunction3, isNotNil as isNotNil34 } from "@wistia/type-guards";
import { styled as styled56, css as css26 } from "styled-components";

// src/private/components/Menu/Menu.tsx
import { forwardRef as forwardRef16, useRef as useRef12, useEffect as useEffect20 } from "react";
import { styled as styled54, css as css25 } from "styled-components";
import { darken as darken6 } from "polished";
import { isNotNil as isNotNil32, isNonEmptyString as isNonEmptyString14 } from "@wistia/type-guards";
import { Fragment as Fragment12, jsx as jsx197, jsxs as jsxs37 } from "react/jsx-runtime";
var MENU_WIDTH_OPTIONS = ["fullWidth", "maxItem"];
var GUTTER_WIDTH = "8px";
var FOCUS_DARKEN = "0.08";
var getMenuWidth = (width) => {
  if (MENU_WIDTH_OPTIONS.includes(width)) {
    return null;
  }
  return width;
};
var MenuComponent = styled54.div`
  background: white;
  border-radius: 4px 4px 0 0;
  box-shadow: ${({ theme: theme2 }) => theme2.shadow.elevation2};
  display: ${({ $menuWidth }) => $menuWidth === "maxItem" ? "inline-block" : "block"};
  padding: ${GUTTER_WIDTH} ${GUTTER_WIDTH} ${({ theme: theme2 }) => theme2.spacing.space04} ${GUTTER_WIDTH};
  width: ${({ $menuWidth }) => getMenuWidth($menuWidth)};
  z-index: ${({ theme: theme2 }) => theme2.zIndex.MenuComponent};

  ${mq.smAndUp} {
    border-radius: 4px;
    padding: ${GUTTER_WIDTH};
  }
`;
var dangerousMenuItemStyles2 = css25`
  /* stylelint-disable no-descending-specificity */
  button,
  a {
    &:hover {
      background-color: ${({ theme: theme2 }) => theme2.color.red100};
      color: ${({ theme: theme2 }) => theme2.color.red600};
    }

    &:focus {
      background-color: ${({ theme: theme2 }) => darken6(FOCUS_DARKEN, theme2.color.red100)};
      color: ${({ theme: theme2 }) => theme2.color.red600};
    }
  }
`;
var disabledMenuItemStyles = css25`
  button,
  a {
    background-color: transparent;
    color: ${({ theme: theme2 }) => theme2.color.grey500};
    cursor: not-allowed;
    opacity: 1;

    &:hover,
    &:focus {
      background-color: transparent;
      color: ${({ theme: theme2 }) => theme2.color.grey500};
      opacity: 1;
    }
  }
`;
var MenuItem2 = styled54.div`
  /* divider */
  hr {
    border-top: 1px solid ${({ theme: theme2 }) => theme2.color.grey300};
    margin: ${({ theme: theme2 }) => theme2.spacing.space01} ${GUTTER_WIDTH};
  }

  button {
    ${buttonResetStyle}
  }

  a {
    text-decoration: none;
    vertical-align: middle;
  }

  /* NOTE: some styles that may seem unnecessary or redundant are probably to override styles
     that come along with the Button, Link & FileSelect components that are allowed in the items array */
  button,
  a,
  label {
    border-radius: 4px;
    color: ${({ theme: theme2 }) => theme2.color.grey900};
    display: block;
    font-size: 18px;
    height: 44px;
    line-height: 40px;
    padding: 0 ${GUTTER_WIDTH};
    width: 100%;
    ${ellipsisStyle};

    ${mq.smAndUp} {
      font-size: 16px;
      height: 40px;
    }

    /* support FileSelect; this prevents an inner button or link from inheriting unwanted space */
    button,
    a {
      padding: 0;
    }

    &:hover,
    &:focus {
      border: none;
      color: ${({ theme: theme2 }) => theme2.color.grey900};
      outline: none;
    }

    &:hover:not([aria-disabled]) {
      background-color: ${({ theme: theme2 }) => theme2.color.grey200};
    }

    &:focus {
      background-color: ${({ theme: theme2 }) => darken6(FOCUS_DARKEN, theme2.color.grey200)};
    }
  }

  /* Precedence boost: https://styled-components.com/docs/basics#pseudoelements-pseudoselectors-and-nesting */
  && {
    ${({ variant }) => variant === "dangerous" ? dangerousMenuItemStyles2 : null}
    ${({ disabled }) => isNotNil32(disabled) && disabled ? disabledMenuItemStyles : null}
  }
`;
var Menu2 = forwardRef16(
  ({
    items,
    menuHeader,
    width = "fullWidth",
    onClick,
    onClickOutside,
    onPressEsc,
    onOpen,
    onClose,
    ...otherProps
  }, ref) => {
    const menuRef = useRef12(void 0);
    const onClickOutsideFn = isNotNil32(onClickOutside) ? onClickOutside : () => {
    };
    useOnClickOutside(menuRef, onClickOutsideFn);
    const menuRefs = useMergedRefs([menuRef, ref]);
    useEffect20(() => {
      if (onOpen) {
        onOpen();
      }
      return () => {
        if (onClose) {
          onClose();
        }
      };
    }, [onOpen, onClose]);
    useEffect20(() => {
      const onKeyup = (event) => {
        if (event.key === "Escape" && onPressEsc) {
          onPressEsc(event);
        }
      };
      document.addEventListener("keyup", onKeyup);
      return () => {
        document.removeEventListener("keyup", onKeyup);
      };
    }, [onPressEsc]);
    if (items?.length == null) {
      return null;
    }
    const itemsToRender = items.map((item) => {
      const { disabled, variant } = item.props;
      const disabledValue = isNotNil32(disabled) ? disabled : false;
      const variantValue = isNonEmptyString14(variant) ? variant : void 0;
      const key = `wrapper-${item.key}`;
      return /* @__PURE__ */ jsx197(
        MenuItem2,
        {
          disabled: disabledValue,
          variant: variantValue,
          children: item
        },
        key
      );
    });
    const handleOnClick = onClick;
    return (
      // eslint-disable-next-line styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions
      /* @__PURE__ */ jsxs37(
        MenuComponent,
        {
          ref: menuRefs,
          $menuWidth: width,
          onClick: handleOnClick,
          ...otherProps,
          children: [
            isNotNil32(menuHeader) ? /* @__PURE__ */ jsxs37(Fragment12, { children: [
              menuHeader,
              /* @__PURE__ */ jsx197(MenuItem2, { children: /* @__PURE__ */ jsx197(Divider, {}) })
            ] }) : null,
            itemsToRender
          ]
        }
      )
    );
  }
);
Menu2.displayName = "Menu";

// src/components/MenuButton/MenuHeader.tsx
import { styled as styled55 } from "styled-components";
import { isNotNil as isNotNil33 } from "@wistia/type-guards";
import { jsx as jsx198, jsxs as jsxs38 } from "react/jsx-runtime";
var HeaderAndButton = styled55.div`
  align-items: center;
  display: flex;
  gap: ${({ theme: theme2 }) => theme2.spacing.space02};
  justify-content: space-between;
  padding: ${({ theme: theme2 }) => theme2.spacing.space02};
`;
var HeaderText = styled55.span`
  color: ${({ theme: theme2 }) => theme2.color.grey900};
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
`;
var MenuHeader = ({
  label,
  onCloseClick,
  overrideMenuHeader = void 0
}) => /* @__PURE__ */ jsxs38(HeaderAndButton, { children: [
  isNotNil33(overrideMenuHeader) ? overrideMenuHeader : /* @__PURE__ */ jsx198(HeaderText, { children: label || "Menu" }),
  /* @__PURE__ */ jsx198(ModalCloseButton, { onClick: onCloseClick })
] });

// src/components/MenuButton/MenuButton.tsx
import { jsx as jsx199, jsxs as jsxs39 } from "react/jsx-runtime";
var MARGIN_BETWEEN_BUTTON_AND_MENU = 8;
var menuPositionAboveCss = css26`
  bottom: 0;
  margin-bottom: ${({ $dropdownButtonHeight }) => $dropdownButtonHeight + MARGIN_BETWEEN_BUTTON_AND_MENU}px;
`;
var menuPositionBelowCss = css26`
  margin-top: ${({ $dropdownButtonHeight }) => $dropdownButtonHeight + MARGIN_BETWEEN_BUTTON_AND_MENU}px;
  top: 0;
`;
var MenuComponent2 = styled56(Menu2)`
  animation-duration: ${({ theme: theme2 }) => theme2.duration.productive.fast};
  animation-timing-function: ${({ theme: theme2 }) => theme2.easing.entrance.productive};
  bottom: 0;
  display: ${({ $isMenuVisible }) => $isMenuVisible ? "block" : "none"};
  left: 0;
  position: fixed;
  width: 100%;

  ${mq.smAndUp} {
    animation: unset;
    bottom: initial;
    left: initial;
    position: absolute;
    width: initial;
    ${({ $menuAlignment }) => $menuAlignment === "left" ? "left: 0;" : "left: unset; right: 0"};
    ${({ $menuPosition }) => $menuPosition === "above" ? menuPositionAboveCss : menuPositionBelowCss};
  }
`;
var MenuButtonContainer = styled56.div`
  align-items: stretch;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;

  /* prevent dropdown button from wrapping underneath main button */
  position: relative;
`;
var MenuButton = ({
  label,
  menuAlignment = "right",
  menuItems,
  menuPosition = "below",
  menuWidth = "maxItem",
  onMenuClick,
  onMenuClosed,
  disabled = false,
  iconButtonComponent,
  overrideMenuHeader,
  size = "lg",
  ...otherProps
}) => {
  const [isMenuVisible, toggleMenu, , hideMenu] = useBoolean(false);
  const [shouldRenderItems, setShouldRenderItems] = useState21(false);
  const [dropdownButtonHeight, setDropdownButtonHeight] = useState21(0);
  const dropdownButton = useRef13(null);
  const { isXsAndDown } = useMq();
  useLayoutEffect5(() => {
    if (!dropdownButton.current) {
      return;
    }
    setDropdownButtonHeight(dropdownButton.current.offsetHeight);
  }, [dropdownButton]);
  const dismissMenu = (event) => {
    if (!isMenuVisible || isNotNil34(event) && coerceToBoolean(dropdownButton.current?.contains(event.target))) {
      return;
    }
    hideMenu();
    if (isFunction3(onMenuClosed)) {
      onMenuClosed();
    }
    dropdownButton.current?.focus();
  };
  const toggleMenuAndRenderItems = useCallback13(() => {
    setShouldRenderItems(true);
    toggleMenu();
  }, [toggleMenu]);
  const onClickButton = useCallback13(
    (event) => {
      if (onMenuClick) {
        onMenuClick(event);
      }
      toggleMenuAndRenderItems();
    },
    [onMenuClick, toggleMenuAndRenderItems]
  );
  let MenuTriggerButton = () => /* @__PURE__ */ jsx199(
    Button,
    {
      ref: dropdownButton,
      "aria-expanded": isMenuVisible,
      disabled,
      forceState: isMenuVisible ? "active" : void 0,
      iconName: "caret-down",
      iconPosition: "end",
      label,
      onClick: onClickButton,
      size,
      ...otherProps
    }
  );
  if (isNotNil34(iconButtonComponent)) {
    MenuTriggerButton = () => cloneElement(iconButtonComponent, {
      label,
      onClick: onClickButton,
      ref: dropdownButton,
      disabled,
      "aria-expanded": isMenuVisible,
      ...otherProps
    });
  }
  return /* @__PURE__ */ jsxs39(MenuButtonContainer, { children: [
    /* @__PURE__ */ jsx199(MenuTriggerButton, {}),
    /* @__PURE__ */ jsx199(
      MenuComponent2,
      {
        $dropdownButtonHeight: dropdownButtonHeight,
        $isMenuVisible: isMenuVisible,
        $menuAlignment: menuAlignment,
        $menuPosition: menuPosition,
        items: shouldRenderItems ? menuItems : [],
        menuHeader: isXsAndDown ? /* @__PURE__ */ jsx199(
          MenuHeader,
          {
            label,
            onCloseClick: dismissMenu,
            overrideMenuHeader
          }
        ) : null,
        onClick: dismissMenu,
        onClickOutside: dismissMenu,
        onPressEsc: dismissMenu,
        width: menuWidth
      }
    ),
    isXsAndDown && isMenuVisible ? /* @__PURE__ */ jsx199(Backdrop, {}) : null
  ] });
};
MenuButton.displayName = "MenuButton_VHS";

// src/components/Modal/Modal.tsx
import { forwardRef as forwardRef17, useEffect as useEffect21, useRef as useRef14 } from "react";
import { styled as styled58, css as css27 } from "styled-components";
import { hasKey, isNotNil as isNotNil35, isNotUndefined as isNotUndefined15 } from "@wistia/type-guards";
import { createPortal as createPortal3 } from "react-dom";

// src/components/Modal/Dialog.tsx
import { styled as styled57 } from "styled-components";
var Dialog = styled57.div`
  background-color: white;
  box-sizing: border-box;
  display: flex;

  /* latest versions of FF, Safari, and Chrome support dynamic viewport units; 100vh left as a fallback */
  height: 100vh;
  overflow: ${({ $overflow }) => $overflow ?? "auto"};
  position: relative;
  width: 100vw;

  ${mq.smAndUp} {
    border-radius: 16px;
    height: ${({ $fullHeight }) => $fullHeight ? "min-content" : "auto"};
    margin: ${({ theme: theme2 }) => theme2.spacing.space05};
    max-height: 90vh;
    ${({ $fullHeight }) => $fullHeight ? "min-height: calc(100vh - 48px);" : ""}
    max-width: 90vw;
    min-width: ${({ $width }) => $width ?? "480px"};
    width: min-content;
  }
`;

// src/components/Modal/ModalCloseContext.tsx
import { isNotUndefined as isNotUndefined14 } from "@wistia/type-guards";
import { createContext as createContext4, useContext as useContext4 } from "react";
var ModalCloseContext = createContext4(void 0);
var ModalCloseProvider = ModalCloseContext.Provider;
var useModalClose = () => {
  const modalClose = useContext4(ModalCloseContext);
  if (isNotUndefined14(modalClose)) {
    return modalClose;
  }
  throw new Error("useModalClose must be used within a ModalCloseProvider");
};

// src/components/Modal/Modal.tsx
import { jsx as jsx200 } from "react/jsx-runtime";
var StyledBackdrop = styled58(Backdrop)`
  display: flex;

  ${({ $allowModalToExceedViewportBounds }) => $allowModalToExceedViewportBounds && css27`
      ${mq.smAndUp} {
        padding: ${({ theme: theme2 }) => theme2.spacing.space05};
      }
    `}
`;
var StyledDialog = styled58(Dialog)`
  ${({ $allowModalToExceedViewportBounds }) => $allowModalToExceedViewportBounds && css27`
      ${mq.smAndUp} {
        max-height: min-content;
        margin: auto;
      }
    `}
`;
var ModalInner = styled58.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  width: 100%;
`;
var isMutableRef = (ref) => {
  return isNotNil35(ref) && hasKey(ref, "current") && isNotNil35(ref.current);
};
var useFocusRestore = (ref) => {
  useEffect21(() => {
    const previousFocus = document.activeElement;
    if (isMutableRef(ref)) {
      const el = ref.current;
      if (isNotNil35(el)) {
        el.focus();
      }
    }
    return () => {
      if (previousFocus !== null) {
        setTimeout(() => {
          previousFocus.focus();
        }, 0);
      }
    };
  }, [ref]);
};
var ModalContent = forwardRef17(
  ({
    alignHorizontal = "center",
    alignVertical = "center",
    allowModalToExceedViewportBounds = false,
    children,
    className,
    fullHeight = false,
    initialFocusRef,
    isOpen = false,
    shouldTrapFocus = true,
    onRequestClose,
    shouldCloseOnClickOutside = true,
    width,
    overflow = "auto",
    ...props
  }, ref) => {
    useFocusRestore(ref);
    const outerRef = useRef14(null);
    const focusRef = useFocusTrap(shouldTrapFocus, {
      focusSelector: isNotUndefined15(initialFocusRef) ? initialFocusRef : null
    });
    useOnClickOutside(outerRef, () => {
      if (onRequestClose && shouldCloseOnClickOutside) {
        onRequestClose();
      }
    });
    usePreventScroll(isOpen);
    useKeyPressOnce("Escape", (event) => {
      if (onRequestClose) {
        onRequestClose();
      }
      event.preventDefault();
    });
    return /* @__PURE__ */ jsx200(ModalCloseProvider, { value: onRequestClose, children: /* @__PURE__ */ jsx200(
      StyledBackdrop,
      {
        $allowModalToExceedViewportBounds: allowModalToExceedViewportBounds,
        alignHorizontal,
        alignVertical,
        children: /* @__PURE__ */ jsx200(
          StyledDialog,
          {
            ref: focusRef,
            $allowModalToExceedViewportBounds: allowModalToExceedViewportBounds,
            $fullHeight: fullHeight,
            $overflow: overflow,
            $width: width,
            "aria-modal": "true",
            role: "dialog",
            tabIndex: -1,
            ...props,
            children: /* @__PURE__ */ jsx200(ModalInner, { ref: outerRef, children })
          }
        )
      }
    ) });
  }
);
var Modal = (props) => {
  const { id, ...rest } = props;
  const modalEl = document.createElement("div");
  modalEl.classList.add("Modal");
  if (isNotUndefined15(props.className)) {
    modalEl.classList.add(props.className);
  }
  if (isNotUndefined15(id)) {
    modalEl.id = id;
  }
  const modalRoot = useRef14(modalEl);
  useEffect21(() => {
    const modalRootEl = modalRoot.current;
    document.body.appendChild(modalRootEl);
    return () => modalRootEl.remove();
  }, []);
  return createPortal3(
    /* @__PURE__ */ jsx200(ModalAnimation, { children: props.isOpen ? /* @__PURE__ */ jsx200(
      ModalContent,
      {
        ref: modalRoot,
        ...rest
      }
    ) : null }),
    modalRoot.current
  );
};
Modal.displayName = "Modal_VHS";
ModalContent.displayName = "ModalContent_VHS";

// src/components/Modal/ModalParts.tsx
import { styled as styled59 } from "styled-components";
import { jsx as jsx201 } from "react/jsx-runtime";
var ModalHeader = styled59.div`
  align-items: center;
  display: flex;
  padding: ${({ theme: theme2 }) => theme2.spacing.space04} ${({ theme: theme2 }) => theme2.spacing.space04};
`;
var ModalBody = styled59.div`
  flex: auto;
  height: 100%;
  margin-top: ${({ theme: theme2 }) => theme2.spacing.space04};
  overflow: auto;
  padding: ${({ theme: theme2 }) => theme2.spacing.space04};
`;
var ModalFooter = styled59.div`
  padding: ${({ theme: theme2 }) => theme2.spacing.space04};
`;
var StyledIconButton = styled59(IconButton)`
  position: absolute;
  right: ${({ theme: theme2 }) => theme2.spacing.space04};
  top: ${({ theme: theme2 }) => theme2.spacing.space04};
  z-index: ${({ theme: theme2 }) => theme2.zIndex.ModalCloseButton};
`;
var ModalCloseButton2 = ({ onClick, ...props }) => {
  const onRequestClose = useModalClose();
  return /* @__PURE__ */ jsx201(
    StyledIconButton,
    {
      "aria-label": "Close",
      icon: /* @__PURE__ */ jsx201(
        Icon,
        {
          size: "sm",
          type: "close"
        }
      ),
      onClick: onRequestClose,
      preventTooltip: true,
      ...props
    }
  );
};

// src/components/Paper/Paper.tsx
import { styled as styled60 } from "styled-components";
import { jsx as jsx202 } from "react/jsx-runtime";
var PaperComponent = styled60.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: ${({ theme: theme2, $elevationToken }) => theme2.paper[$elevationToken]};
`;
var Paper = ({ children, elevation = 1, ...otherProps }) => {
  const elevationToken = `elevation${elevation}`;
  return /* @__PURE__ */ jsx202(
    PaperComponent,
    {
      ...otherProps,
      $elevationToken: elevationToken,
      children
    }
  );
};
Paper.displayName = "Paper_VHS";

// src/components/ProgressBar/ProgressBar.tsx
import { styled as styled61 } from "styled-components";
import { jsx as jsx203, jsxs as jsxs40 } from "react/jsx-runtime";
var progressBarHeight = "8px";
var ProgressBarContainer = styled61.div`
  display: flex;
`;
var ProgressBarMeter = styled61.div`
  align-self: center;
  background: ${({ theme: theme2, $meterBgColorOverride }) => $meterBgColorOverride ?? theme2.color.grey300};
  border-radius: ${progressBarHeight};
  flex: 1;
  height: ${progressBarHeight};
`;
var ProgressBarLevel = styled61.div`
  background: ${({ theme: theme2, $colorOverride }) => $colorOverride ?? theme2.color.brandBlue500};
  border-radius: ${progressBarHeight};
  height: 100%;
  transition: width 0.2s ease-out;
  width: ${({ $progress }) => $progress * 100}%;
`;
var ProgressBarLabel = styled61.div`
  color: ${({ theme: theme2, $labelColorOverride }) => $labelColorOverride ?? theme2.color.grey900};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  line-height: 6px;
  margin-right: ${({ theme: theme2 }) => theme2.spacing.space04};
  text-align: right;
  width: 5ch;
`;
var ProgressBar = ({
  hasLabel = true,
  progress = 0,
  colorOverride = void 0,
  labelColorOverride = void 0,
  meterBgColorOverride = void 0,
  ...otherProps
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);
  const currentProgress = Math.round(clampedProgress * 100);
  return /* @__PURE__ */ jsxs40(
    ProgressBarContainer,
    {
      "aria-label": "Progress",
      "aria-valuemax": "100",
      "aria-valuemin": "0",
      "aria-valuenow": currentProgress,
      "data-testid": "progress-bar",
      role: "progressbar",
      ...otherProps,
      children: [
        hasLabel ? /* @__PURE__ */ jsxs40(
          ProgressBarLabel,
          {
            $labelColorOverride: labelColorOverride,
            "data-testid": "progress-bar-label",
            children: [
              currentProgress,
              "%"
            ]
          }
        ) : null,
        /* @__PURE__ */ jsx203(ProgressBarMeter, { $meterBgColorOverride: meterBgColorOverride, children: /* @__PURE__ */ jsx203(
          ProgressBarLevel,
          {
            $colorOverride: colorOverride,
            $progress: clampedProgress
          }
        ) })
      ]
    }
  );
};
ProgressBar.displayName = "ProgressBar_VHS";

// src/components/RangeSelector/RangeSelector.tsx
import { useEffect as useEffect24, useState as useState24 } from "react";
import { styled as styled66 } from "styled-components";
import { isSameDay as isSameDay3 } from "date-fns";

// src/private/components/RangeSelectorCalendar/RangeSelectorCalendar.tsx
import { useEffect as useEffect22, useMemo as useMemo8, useState as useState22 } from "react";
import { isNotNil as isNotNil37, isNil as isNil17 } from "@wistia/type-guards";
import { styled as styled63 } from "styled-components";
import { debounce as debounce2 } from "throttle-debounce";
import {
  addDays,
  compareAsc,
  differenceInDays,
  endOfDay,
  isBefore as isBefore2,
  isEqual as isEqual3,
  isSameDay,
  isWithinInterval,
  startOfMonth as startOfMonth2
} from "date-fns";

// src/private/components/Calendar/Calendar.tsx
import { styled as styled62 } from "styled-components";
import {
  isBefore,
  isAfter,
  isFuture,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  startOfDay,
  startOfMonth,
  endOfMonth,
  getDay,
  addMonths
} from "date-fns";
import { isNotNil as isNotNil36 } from "@wistia/type-guards";
import { jsx as jsx204, jsxs as jsxs41 } from "react/jsx-runtime";
var dayDimension = 45;
var daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
var daysInAWeek = 7;
var hasDates = (week) => week[0] instanceof Date || week[6] instanceof Date;
var daysOfMonth = (dateInMonth) => {
  const interval = {
    start: startOfMonth(startOfDay(dateInMonth)),
    end: endOfMonth(startOfDay(dateInMonth))
  };
  return eachDayOfInterval(interval);
};
var makeEmptyWeek = () => Array.from({ length: daysInAWeek }, () => []);
var monthLayout = (days) => {
  const month = [];
  const dayOfStart = isNotNil36(days[0]) ? getDay(days[0]) : 0;
  let currentWeek = makeEmptyWeek();
  for (let i = 0; i < days.length; i++) {
    const positionInWeek = (i + dayOfStart) % daysInAWeek;
    if (positionInWeek === 0 && hasDates(currentWeek)) {
      month.push(currentWeek);
      currentWeek = makeEmptyWeek();
    }
    currentWeek[positionInWeek] = days[i];
  }
  if (hasDates(currentWeek)) {
    month.push(currentWeek);
  }
  return month;
};
var Month = styled62.div`
  cursor: not-allowed;
  padding-top: ${({ theme: theme2 }) => theme2.spacing.space04};
  width: ${dayDimension * daysInAWeek}px;
`;
var MonthHeader = styled62.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space04};
`;
var MonthLayout = styled62.div`
  display: grid;
  grid-template-columns: repeat(7, 14.28% [col-start]);
  width: 100%;
`;
var Day = styled62.button`
  background: none;
  border: 0 solid white;
  border-radius: 50%;
  color: ${({ theme: theme2 }) => theme2.color.grey800};
  cursor: pointer;
  font-size: 14px;
  height: ${dayDimension}px;
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space01};
  position: relative;
  text-align: center;
  user-select: none;
  width: ${dayDimension}px;

  &:disabled {
    border: 0 solid white;
    color: ${({ theme: theme2 }) => theme2.color.grey300};
    cursor: not-allowed;
  }
`;
var NullDay = styled62.div`
  height: ${dayDimension}px;
`;
var DayLabel = styled62.span`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space02};
  text-align: center;
  user-select: none;
`;
var DayNumber = styled62.span`
  position: relative;
  z-index: 1;
`;
var MonthButton = styled62.button`
  background: none;
  border: none;
  color: ${({ theme: theme2 }) => theme2.color.grey500};
  cursor: pointer;

  &:disabled {
    color: ${({ theme: theme2 }) => theme2.color.grey300};
    cursor: not-allowed;
  }
`;
var Calendar = ({
  displayDate,
  earliestDate,
  latestDate,
  onDisplayDateChange,
  onMouseLeave,
  shouldPreventFuture = false,
  renderDay
}) => {
  const month = monthLayout(daysOfMonth(displayDate));
  const nextMonth = startOfMonth(addMonths(displayDate, 1));
  const prevMonth = endOfMonth(addMonths(displayDate, -1));
  const isAfterLatest = (date) => {
    if (!latestDate) {
      return false;
    }
    return isAfter(date, latestDate);
  };
  const isInFuture = (date) => {
    if (!shouldPreventFuture) {
      return false;
    }
    return isFuture(date);
  };
  const isBeforeEarliest = (date) => {
    if (!earliestDate) {
      return false;
    }
    return isBefore(date, earliestDate);
  };
  const isDisabled = (date) => isAfterLatest(date) || isInFuture(date) || isBeforeEarliest(date);
  const formatDay = (date) => date.toLocaleDateString("en-US", { day: "numeric" });
  const renderDayFunc = (day) => renderDay ? renderDay({
    day,
    isDisabled: isDisabled(day),
    formattedDay: formatDay(day)
  }) : /* @__PURE__ */ jsx204(
    Day,
    {
      "data-day": formatDay(day),
      disabled: isDisabled(day),
      children: /* @__PURE__ */ jsx204(DayNumber, { children: formatDay(day) })
    },
    day
  );
  const possibleRange = {
    start: earliestDate ?? addYears(/* @__PURE__ */ new Date(), -5),
    // eslint-disable-line @typescript-eslint/no-magic-numbers
    end: shouldPreventFuture ? /* @__PURE__ */ new Date() : addYears(/* @__PURE__ */ new Date(), 5)
  };
  const currentYear = displayDate.getFullYear();
  const getMonthOptions = () => {
    const formatter = new Intl.DateTimeFormat("en", { month: "long" });
    return eachMonthOfInterval(possibleRange).filter((date) => date.getFullYear() === displayDate.getFullYear()).map((date) => ({ value: date.getMonth(), label: formatter.format(date) }));
  };
  const getYearOptions = () => Object.values(
    eachMonthOfInterval(possibleRange).map((date) => ({ value: date.getFullYear(), label: date.getFullYear() })).reduce((memo2, entry) => {
      if (isNotNil36(memo2[entry.value])) {
        return memo2;
      }
      return { ...memo2, [entry.value]: entry };
    }, {})
  );
  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();
  return /* @__PURE__ */ jsxs41(Month, { "data-testid": "calendar-test-identifier", children: [
    /* @__PURE__ */ jsxs41(MonthHeader, { children: [
      /* @__PURE__ */ jsx204(
        MonthButton,
        {
          "aria-label": "Previous month",
          "data-testid": "month-prev",
          disabled: isBeforeEarliest(prevMonth),
          onClick: () => {
            if (onDisplayDateChange) {
              onDisplayDateChange(prevMonth);
            }
          },
          style: { transform: "translateX(-5px)" },
          type: "button",
          children: /* @__PURE__ */ jsx204(
            Icon,
            {
              size: "md",
              type: "caret-left"
            }
          )
        }
      ),
      /* @__PURE__ */ jsx204(
        Select,
        {
          "aria-label": "Select month",
          "data-testid": "monthSelect",
          fullWidth: false,
          onChange: (event) => {
            const selectedMonth = event.target.value;
            const nextDate = new Date(currentYear, Number(selectedMonth));
            if (onDisplayDateChange) {
              if (isInFuture(nextDate)) {
                onDisplayDateChange(/* @__PURE__ */ new Date());
              } else if (isBeforeEarliest(nextDate)) {
                onDisplayDateChange(earliestDate);
              } else {
                onDisplayDateChange(nextDate);
              }
            }
          },
          options: monthOptions,
          placeholder: null,
          value: displayDate.getMonth()
        }
      ),
      /* @__PURE__ */ jsx204(
        Select,
        {
          "aria-label": "Select year",
          "data-testid": "yearSelect",
          fullWidth: false,
          onChange: (event) => {
            const currentMonth = displayDate.getMonth();
            const selectedYear = event.target.value;
            const nextDate = new Date(Number(selectedYear), currentMonth);
            if (onDisplayDateChange) {
              if (isInFuture(nextDate)) {
                onDisplayDateChange(/* @__PURE__ */ new Date());
              } else if (isBeforeEarliest(nextDate)) {
                onDisplayDateChange(earliestDate);
              } else {
                onDisplayDateChange(nextDate);
              }
            }
          },
          options: yearOptions,
          placeholder: null,
          value: displayDate.getFullYear()
        }
      ),
      /* @__PURE__ */ jsx204(
        MonthButton,
        {
          "aria-label": "Next month",
          "data-testid": "month-next",
          disabled: isInFuture(nextMonth),
          onClick: () => {
            if (onDisplayDateChange) {
              onDisplayDateChange(nextMonth);
            }
          },
          style: { transform: "translateX(4px)" },
          type: "button",
          children: /* @__PURE__ */ jsx204(
            Icon,
            {
              size: "md",
              type: "caret-right"
            }
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsxs41(
      MonthLayout,
      {
        "aria-label": "Day selection grid",
        onMouseLeave,
        role: "button",
        children: [
          daysOfWeek.map((day) => /* @__PURE__ */ jsx204(DayLabel, { children: day }, day)),
          month.map(
            (week) => week.map(
              (day) => day instanceof Date ? renderDayFunc(day) : /* @__PURE__ */ jsx204(NullDay, {}, Math.random())
            )
          )
        ]
      }
    )
  ] });
};
Calendar.displayName = "Calendar";

// src/private/components/RangeSelectorCalendar/RangeSelectorCalendar.tsx
import { jsx as jsx205 } from "react/jsx-runtime";
var EnhancedDay = styled63(Day)`
  ${({ $isBeginning, $isInRange }) => $isBeginning && !$isInRange && `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `};
  ${({ $isEnd, $isInRange }) => $isEnd && !$isInRange && `
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  `};
  ${({ $isEnd, $isBeginning }) => $isEnd && $isBeginning && `
    border-radius: 50%;
  `};
  ${({ theme: theme2, $isInRange }) => $isInRange && `
    background: ${theme2.color.brandBlue200};
    border: 0px solid ${theme2.color.grey300};
    border-radius: 0;
  `};
  ${({ theme: theme2, $isInTempRange }) => $isInTempRange && `
    border-width: 1px 0;
    border-style: solid;
    border-color: ${theme2.color.brandBlue500};
    border-radius: 0;
  `};
  ${({ theme: theme2, $isActive }) => $isActive && `
    background: ${theme2.color.blue500};
    background: ${theme2.color.brandBlue500};
    border: none;
    color: white;
  `};
  ${({ theme: theme2, $isHovered }) => $isHovered && `
    &::after {
      border: 1px solid ${theme2.color.brandBlue500};
      border-radius: 50%;
      content: '';
      display: block;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 0;
    };

    &:disabled:after {
      display: none;
    };
  `};
  ${({ theme: theme2, $isBeginning, $isEnd, $isHovered }) => $isBeginning && !$isEnd && $isHovered && `
    color: white;

    &::after {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-left: 0px;
      background: ${theme2.color.brandBlue500};
      color: white;
    }
  `};
  ${({ theme: theme2, disabled, $isEnd, $isBeginning, $isHovered }) => isNotNil37($isEnd) && !$isBeginning && !disabled && $isHovered && `
    color: white;

    &::after {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-right: 0px;
      background: ${theme2.color.brandBlue500};
    }
  `}
`;
var isValidRange = (range) => {
  if (isNil17(range)) {
    return false;
  }
  const { start, end } = range;
  return start instanceof Date && end instanceof Date && isBefore2(start, end);
};
var isInRange = (range, day) => {
  if (isNil17(range)) {
    return false;
  }
  const diff = differenceInDays(range.end, range.start);
  if (diff < 2) {
    return false;
  }
  return isWithinInterval(day, { start: addDays(range.start, 1), end: addDays(range.end, -1) });
};
var makeRange = (firstDate, secondDate) => {
  if (firstDate instanceof Date && isNotNil37(secondDate)) {
    const [start, end] = [firstDate, secondDate].sort(compareAsc);
    if (isNotNil37(start) && isNotNil37(end)) {
      return { start, end: endOfDay(end) };
    }
  }
  return void 0;
};
var useRange = (firstDate, secondDate) => {
  const [range, setRange] = useState22();
  useEffect22(() => {
    setRange(makeRange(firstDate, secondDate));
  }, [firstDate, secondDate, setRange]);
  return range;
};
var RangeSelectorCalendar = ({
  range = {
    end: /* @__PURE__ */ new Date(),
    start: /* @__PURE__ */ new Date()
  },
  rangePartToShow = "",
  onChange,
  onIsValidChange = () => {
  },
  // eslint-disable-line @typescript-eslint/no-empty-function
  earliestDate,
  latestDate,
  shouldPreventFuture = false
}) => {
  const thisMonth = useMemo8(() => startOfMonth2(/* @__PURE__ */ new Date()), []);
  const initialDisplayDate = isNotNil37(range.end) ? range.end : thisMonth;
  const [displayDate, setDisplayDate] = useState22(initialDisplayDate);
  const [{ firstDate, secondDate }, setDates] = useState22({});
  const [hoveredDate, setHoveredDate] = useState22();
  const internalRange = useRange(firstDate, secondDate);
  const priorInternalRange = usePreviousValue(internalRange);
  const tempRange = useRange(!isValidRange(internalRange) && firstDate, hoveredDate);
  const onIsValidChangeDebounced = useMemo8(() => debounce2(100, onIsValidChange), [onIsValidChange]);
  const setDisplayDateDebounced = useMemo8(() => debounce2(0, setDisplayDate), []);
  useEffect22(() => {
    setDisplayDateDebounced(range.end);
  }, []);
  useEffect22(() => {
    if (!internalRange || !priorInternalRange) {
      return;
    }
    if (rangePartToShow) {
      const date = internalRange[rangePartToShow];
      setDisplayDateDebounced(date);
    }
  }, [rangePartToShow, internalRange, priorInternalRange, setDisplayDateDebounced]);
  useEffect22(() => {
    onIsValidChangeDebounced(isValidRange(internalRange));
  }, [internalRange, onIsValidChangeDebounced]);
  useEffect22(() => {
    if (isValidRange(range)) {
      let date;
      if (priorInternalRange && !isEqual3(range.start, priorInternalRange.start)) {
        date = range.start;
      }
      if (priorInternalRange && !isEqual3(range.end, priorInternalRange.end)) {
        date = range.end;
      }
      if (date) {
        setDisplayDateDebounced(date);
      }
      setDates({ firstDate: range.start, secondDate: range.end });
    }
  }, [range, priorInternalRange, setDisplayDateDebounced]);
  const dayProps = (day) => {
    const isActive = isNotNil37(firstDate) && isSameDay(firstDate, day) || isNotNil37(secondDate) && isSameDay(secondDate, day);
    const isInR = isInRange(internalRange, day);
    const isInTempRange = !internalRange && isInRange(tempRange, day);
    const val = day.toLocaleDateString("en-US");
    const isHovered = isNotNil37(hoveredDate) ? isSameDay(day, hoveredDate) : false;
    const isBeginning = Boolean(
      internalRange && isSameDay(internalRange.start, day) || tempRange && isSameDay(tempRange.start, day)
    );
    const isEnd = Boolean(
      internalRange && isSameDay(internalRange.end, day) || tempRange && isSameDay(tempRange.end, day)
    );
    return {
      $isActive: isActive,
      $isBeginning: isBeginning,
      $isEnd: isEnd,
      $isInRange: isInR,
      $isInTempRange: isInTempRange,
      $isHovered: isHovered,
      $val: val
    };
  };
  return /* @__PURE__ */ jsx205(
    Calendar,
    {
      displayDate,
      earliestDate,
      latestDate,
      onDisplayDateChange: setDisplayDateDebounced,
      onMouseLeave: () => {
        setHoveredDate(void 0);
      },
      renderDay: ({ day, isDisabled, formattedDay }) => {
        const propsForDay = dayProps(day);
        return /* @__PURE__ */ jsx205(
          EnhancedDay,
          {
            ...propsForDay,
            "data-day": formattedDay,
            "data-is-beginning": propsForDay.$isBeginning,
            "data-is-end": propsForDay.$isEnd,
            "data-is-in-temp-range": propsForDay.$isInTempRange,
            disabled: isDisabled,
            onClick: () => {
              let nextFirst = day;
              let nextSecond = day;
              if (firstDate && secondDate) {
                nextSecond = void 0;
              } else if (!firstDate) {
                nextSecond = secondDate;
              } else {
                nextFirst = firstDate;
              }
              setDates({ firstDate: nextFirst, secondDate: nextSecond });
              const nextRange = makeRange(nextFirst, nextSecond);
              if (isValidRange(nextRange) && onChange) {
                onChange(nextRange);
              }
              setHoveredDate(void 0);
            },
            onMouseOver: () => {
              if (firstDate && !isDisabled) {
                setHoveredDate(day);
              }
            },
            children: /* @__PURE__ */ jsx205(DayNumber, { children: formattedDay })
          },
          day
        );
      },
      shouldPreventFuture
    }
  );
};
RangeSelectorCalendar.displayName = "RangeSelectorCalendar";

// src/private/components/RangeSelectorInput/RangeSelectorInput.tsx
import { useCallback as useCallback14, useEffect as useEffect23, useRef as useRef15, useState as useState23 } from "react";
import { styled as styled65 } from "styled-components";
import { endOfDay as endOfDay3 } from "date-fns";
import { isNotUndefined as isNotUndefined17, isUndefined as isUndefined4, isNotNil as isNotNil41 } from "@wistia/type-guards";

// src/private/components/RangeSelectorInput/ErrorMessages.tsx
import { styled as styled64 } from "styled-components";
import { jsx as jsx206 } from "react/jsx-runtime";
var ErrorMessagesList = styled64.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
var ErrorMessage = styled64.li`
  color: ${({ theme: theme2 }) => theme2.color.error600};
  font-size: 12px;
`;
var ErrorMessages = ({ messages = [] }) => /* @__PURE__ */ jsx206(ErrorMessagesList, { children: messages.map((message) => /* @__PURE__ */ jsx206(ErrorMessage, { children: message }, message)) });

// src/private/components/RangeSelectorInput/constants.ts
var DIVIDER_STRING = "       -       ";
var INITIAL_VALUE = `mm/dd/yyyy${DIVIDER_STRING}mm/dd/yyyy`;
var positionElements = [
  { type: "month", id: "month1" },
  { type: "day", id: "day1" },
  { type: "year", id: "year1" },
  { type: "skip" },
  { type: "month", id: "month2" },
  { type: "day", id: "day2" },
  { type: "year", id: "year2" }
];
var createPositionsFromElements = (elements) => {
  const spaces = {
    month: 2,
    day: 2,
    year: 4,
    skip: DIVIDER_STRING.length - 2
  };
  let currentPosition = 0;
  return elements.map(({ type, id }, index) => {
    const start = currentPosition + (index === 0 ? 0 : 1);
    const end = start + spaces[type];
    currentPosition = end;
    return { start, end, type, id };
  });
};
var DATE_PART_POSITIONS = createPositionsFromElements(positionElements);

// src/private/components/RangeSelectorInput/eventTypes.ts
var keys = /* @__PURE__ */ new Set([
  "Enter",
  "Backspace",
  "Tab",
  "ArrowRight",
  "ArrowLeft",
  "ArrowUp",
  "ArrowDown",
  "/"
]);
var isANum = (event) => /^[0-9]$/i.test(event.key);
var isAModifier = (event) => keys.has(event.key);
var isPermissableKey = (event) => isANum(event) || isAModifier(event);
var isMoveRightChar = ({ key, shiftKey }) => key === "Tab" && !shiftKey || key === "/" || key === "ArrowRight";
var isMoveLeftChar = ({ key, shiftKey }) => key === "ArrowLeft" || key === "Tab" && shiftKey;
var isArrowUpDown = (event) => event.key === "ArrowUp" || event.key === "ArrowDown";
var isBackspace = (event) => event.key === "Backspace";
var isValueChangeEvent = (event) => isANum(event) || isArrowUpDown(event) || isBackspace(event);
var isMoveEvent = (event) => isMoveLeftChar(event) || isMoveRightChar(event);

// src/private/components/RangeSelectorInput/conversions.ts
import { startOfDay as startOfDay2, endOfDay as endOfDay2, isValid } from "date-fns";
import { isNotNil as isNotNil38, isNonEmptyString as isNonEmptyString15 } from "@wistia/type-guards";
var getValueParts = (value) => {
  const [start, end] = value.split(DIVIDER_STRING);
  let month1Val = "";
  let day1Val = "";
  let year1Val = "";
  let month2Val = "";
  let day2Val = "";
  let year2Val = "";
  if (isNotNil38(start)) {
    [month1Val, day1Val, year1Val] = start.split("/");
  }
  if (isNotNil38(end)) {
    [month2Val, day2Val, year2Val] = end.split("/");
  }
  const month1 = isNonEmptyString15(month1Val) ? month1Val : "";
  const day1 = isNonEmptyString15(day1Val) ? day1Val : "";
  const year1 = isNonEmptyString15(year1Val) ? year1Val : "";
  const month2 = isNonEmptyString15(month2Val) ? month2Val : "";
  const day2 = isNonEmptyString15(day2Val) ? day2Val : "";
  const year2 = isNonEmptyString15(year2Val) ? year2Val : "";
  return { month1, day1, year1, month2, day2, year2 };
};
var dateOnlyStringNumeric2 = (date) => {
  if (!isValid(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(["en-US"], {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(date);
  } catch {
    return "";
  }
};
var datePartsToString = (parts) => {
  const { month1, day1, year1, month2, day2, year2 } = parts;
  return `${month1}/${day1}/${year1}${DIVIDER_STRING}${month2}/${day2}/${year2}`;
};
var stringToRange = (value) => {
  const { month1, day1, year1, month2, day2, year2 } = getValueParts(value);
  try {
    const start = startOfDay2(new Date(Number(year1), Number(month1) - 1, Number(day1)));
    const end = endOfDay2(new Date(Number(year2), Number(month2) - 1, Number(day2)));
    return { start, end };
  } catch (error) {
    return void 0;
  }
};
var rangeToString = ({ start, end }) => {
  const opts = { month: "2-digit", day: "2-digit", year: "numeric" };
  const startString = start.toLocaleDateString("en-US", opts);
  const endString = end.toLocaleDateString("en-US", opts);
  return `${startString}${DIVIDER_STRING}${endString}`;
};

// src/private/components/RangeSelectorInput/iterators.ts
import isEqual4 from "react-fast-compare";

// src/private/components/RangeSelectorInput/utils.ts
import { isValid as isValid2, getDaysInMonth, getYear, isBefore as isBefore3, isSameDay as isSameDay2, compareAsc as compareAsc2 } from "date-fns";
import { isNotNil as isNotNil39 } from "@wistia/type-guards";
var timeout;
var preventDoubleClick = (event) => {
  const delay = 200;
  if (isNotNil39(timeout)) {
    event.preventDefault();
    clearTimeout(timeout);
    timeout = void 0;
  }
  timeout = setTimeout(() => {
    clearTimeout(timeout);
    timeout = void 0;
  }, delay);
};
var isValidRange2 = ({ start, end }) => isValid2(start) && isValid2(end);
var getFieldId = ([start], positions) => {
  const currentPosition = positions.find((pos) => pos.start === start);
  return isNotNil39(currentPosition) && isNotNil39(currentPosition.id) ? currentPosition.id : "";
};
var getMaxValue = (valueParts, part) => {
  const partType = part.substring(0, part.length - 1);
  const index = part.charAt(part.length - 1);
  const monthsInYear = 12;
  const maxDaysInMonth = 31;
  switch (partType) {
    case "month":
      return monthsInYear;
    case "year":
      return getYear(/* @__PURE__ */ new Date());
    case "day":
      if (valueParts[`month${index}`] !== "mm") {
        const monthAsNumber = valueParts[`month${index}`];
        const month = monthAsNumber - 1;
        if (valueParts[`year${index}`] !== "yyyy") {
          const year = valueParts[`year${index}`];
          return getDaysInMonth(new Date(year, month));
        }
        return getDaysInMonth(new Date(getYear(/* @__PURE__ */ new Date()), month));
      }
      return maxDaysInMonth;
    default:
      return 0;
  }
};
var getErrors = ({ range, maxDate, minDate }) => {
  const startString = dateOnlyStringNumeric2(range.start);
  const endString = dateOnlyStringNumeric2(range.end);
  const errorMessages = [];
  if (isNotNil39(maxDate)) {
    const maxString = dateOnlyStringNumeric2(maxDate);
    if (isBefore3(maxDate, range.start)) {
      errorMessages.push(`${startString} is after maximum of ${maxString}`);
    }
    if (isBefore3(maxDate, range.end)) {
      errorMessages.push(`${endString} is after maximum of ${maxString}`);
    }
  }
  if (minDate) {
    const minString = dateOnlyStringNumeric2(minDate);
    if (isBefore3(range.start, minDate)) {
      errorMessages.push(`${startString} is before minimum of ${minString}`);
    }
    if (isBefore3(range.end, minDate)) {
      errorMessages.push(`${endString} is before minimum of ${minString}`);
    }
  }
  if (isBefore3(range.end, range.start) || isSameDay2(range.end, range.start)) {
    errorMessages.push("start date must come before end date");
  }
  return errorMessages;
};
var orderRange = (range) => {
  const [start, end] = [range.start, range.end].sort(compareAsc2);
  return { start, end };
};

// src/private/components/RangeSelectorInput/selection.ts
import { isNotUndefined as isNotUndefined16, isUndefined as isUndefined3, isNotNil as isNotNil40 } from "@wistia/type-guards";
var getCursorPosition = (ref) => {
  const startPos = ref.current?.selectionStart;
  const endPos = ref.current?.selectionEnd;
  return { startPos, endPos };
};
var setCursorPosition = (ref, start, end) => {
  const elem = ref.current;
  if (!elem) {
    return;
  }
  elem.setSelectionRange(start, end);
};
var getSelectionFromMove = (selection, move, positions) => {
  const currentIndex = positions.findIndex((pos) => pos.start === selection[0]);
  let nextPosition = positions[currentIndex + move];
  if (nextPosition?.type === "skip") {
    nextPosition = positions[currentIndex + move * 2];
  }
  if (nextPosition) {
    return [nextPosition.start, nextPosition.end];
  }
  return selection;
};
var getSelectionFromCursor = (ref, positions) => {
  const pos = getCursorPosition(ref);
  for (let i = 0; i < positions.length; i++) {
    if (isUndefined3(positions[i])) {
      return [];
    }
    const { start, end, type } = positions[i];
    if (isNotNil40(pos.startPos) && pos.startPos > start - 1 && pos.startPos <= end) {
      if (type === "skip" && isNotUndefined16(positions[i + 1])) {
        return [positions[i + 1].start, positions[i + 1].end];
      }
      return [start, end];
    }
  }
  return [];
};
var getFirstDefaultPosition = (value) => {
  const vp = getValueParts(value);
  const dvp = getValueParts(INITIAL_VALUE);
  const parts = ["month1", "day1", "year1", "month2", "day2", "year2"];
  for (let i = 0; i < parts.length; i++) {
    if (isUndefined3(parts[i])) {
      return -1;
    }
    if (vp[parts[i]] === dvp[parts[i]]) {
      return i;
    }
  }
  return -1;
};
var getRangePartFromSelection = ([, end]) => {
  const indexOfDivider = INITIAL_VALUE.indexOf(DIVIDER_STRING);
  if (end === -1) {
    return "";
  }
  if (isNotUndefined16(end) && end <= indexOfDivider) {
    return "start";
  }
  return "end";
};

// src/private/components/RangeSelectorInput/iterators.ts
var validateDays = (parts) => {
  const nextParts = { ...parts };
  const pad = (val) => `${val}`.padStart(2, "0");
  if (parts.day1 !== "dd") {
    nextParts.day1 = pad(Math.min(Number(parts.day1), getMaxValue(parts, "day1")));
  }
  if (parts.day2 !== "dd") {
    nextParts.day2 = pad(Math.min(Number(parts.day2), getMaxValue(parts, "day2")));
  }
  return nextParts;
};
var applyNumKey = (valueParts, part, type, key, keyCount) => {
  const defaultValue2 = getValueParts(INITIAL_VALUE)[part];
  const len = defaultValue2.length;
  const pad = (val) => `${val}`.padStart(len, "0");
  const merge2 = (val) => ({ ...valueParts, [part]: pad(val) });
  const value = valueParts[part];
  const isDefault = value === defaultValue2;
  if (keyCount === 0) {
    return merge2(pad(key));
  }
  const maxValue = getMaxValue(valueParts, part);
  const priorValue = `${Number.parseInt(value, 10)}`;
  const nextValue = `${priorValue}${key}`;
  const exceedsLength = priorValue.length === len;
  if (isDefault && type !== "year") {
    return merge2(key);
  }
  if (exceedsLength) {
    return merge2(key);
  }
  if (Number.parseInt(nextValue, 10) > maxValue) {
    return merge2(maxValue);
  }
  return merge2(nextValue);
};
var inc = (value, max, diff) => {
  let intValue = Number.parseInt(value, 10);
  if (Number.isNaN(intValue)) {
    intValue = 0;
  }
  const nextValue = intValue + diff;
  if (nextValue <= 0 || nextValue > max) {
    return intValue;
  }
  return nextValue;
};
var applyUpDownArrow = (valueParts, part, diff) => {
  const defaultValue2 = getValueParts(INITIAL_VALUE)[part];
  const len = defaultValue2.length;
  const pad = (val) => `${val}`.padStart(len, "0");
  const merge2 = (val) => ({ ...valueParts, [part]: pad(val) });
  const value = valueParts[part];
  const maxValue = getMaxValue(valueParts, part);
  return merge2(pad(inc(value, maxValue, diff)));
};
var getNextValue = ({
  event,
  currentSelection,
  keyCount,
  valueParts,
  selectionLength
}) => {
  let nextValue = "";
  let nextKeyCount = keyCount;
  let shouldApply = false;
  const id = getFieldId(currentSelection, DATE_PART_POSITIONS);
  const type = id.substring(0, id.length - 1);
  if (isANum(event)) {
    event.preventDefault();
    const nextPotentialParts = applyNumKey(
      valueParts,
      id,
      type,
      event.key,
      keyCount
    );
    const validatedParts = validateDays(nextPotentialParts);
    nextValue = datePartsToString(validatedParts);
    nextKeyCount += 1;
    shouldApply = selectionLength === nextKeyCount;
  }
  if (isArrowUpDown(event)) {
    event.preventDefault();
    const diff = event.key === "ArrowUp" ? 1 : -1;
    const nextPotentialParts = applyUpDownArrow(valueParts, id, diff);
    const validatedParts = validateDays(nextPotentialParts);
    nextValue = datePartsToString(validatedParts);
    nextKeyCount = 0;
    shouldApply = true;
  }
  if (isBackspace(event)) {
    event.preventDefault();
    const initialValueParts = getValueParts(INITIAL_VALUE);
    nextValue = datePartsToString({
      ...valueParts,
      [id]: initialValueParts[id]
    });
    nextKeyCount = 0;
  }
  return { nextValue, nextKeyCount, shouldApply };
};
var getNextSelection = ({ event, currentSelection }) => {
  let nextSelection;
  if (isMoveRightChar(event)) {
    nextSelection = getSelectionFromMove(currentSelection, 1, DATE_PART_POSITIONS);
    if (!isEqual4(nextSelection, currentSelection)) {
      event.preventDefault();
    }
  }
  if (isMoveLeftChar(event)) {
    nextSelection = getSelectionFromMove(currentSelection, -1, DATE_PART_POSITIONS);
    if (!isEqual4(nextSelection, currentSelection)) {
      event.preventDefault();
    }
  }
  return { nextSelection, nextKeyCount: 0 };
};

// src/private/components/RangeSelectorInput/RangeSelectorInput.tsx
import { jsx as jsx207, jsxs as jsxs42 } from "react/jsx-runtime";
var Input2 = styled65.input`
  border: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  border-radius: 4px;
  color: ${({ theme: theme2 }) => theme2.color.grey800};
  outline: none;
  padding: ${({ theme: theme2 }) => theme2.spacing.space03};
  resize: none;
  text-align: center;
  width: ${({ $width }) => $width};

  &:focus {
    border: 1px solid ${({ theme: theme2 }) => theme2.color.brandBlue500};
  }
`;
var getMaxDate = ({ latestDate, shouldPreventFuture }) => {
  if (latestDate) {
    return latestDate;
  }
  return shouldPreventFuture ? endOfDay3(/* @__PURE__ */ new Date()) : void 0;
};
var RangeSelectorInput = ({
  onChange,
  onSelectedRangePartChange,
  range,
  earliestDate,
  latestDate,
  shouldPreventFuture = false,
  width = "100%",
  onErrorChange
}) => {
  const [value, setValue] = useState23(INITIAL_VALUE);
  const [keyCount, setKeyCount] = useState23(0);
  const [errorMessages, setErrorMessages] = useState23([]);
  const ref = useRef15(null);
  const [currentSelection, setCurrentSelection] = useState23([0, -1]);
  const selectionLength = isUndefined4(currentSelection) || isUndefined4(currentSelection[1]) || isUndefined4(currentSelection[0]) ? 0 : currentSelection[1] - currentSelection[0];
  const valueParts = getValueParts(value);
  const applyChange = useCallback14(
    (nextRange) => {
      const errors = getErrors({
        range: nextRange,
        maxDate: getMaxDate({ latestDate, shouldPreventFuture }),
        minDate: earliestDate
      });
      if (isValidRange2(nextRange)) {
        setValue(rangeToString(nextRange));
        if (!errors.length && isNotUndefined17(onChange)) {
          onChange(orderRange(nextRange));
        }
      }
      setErrorMessages(errors);
    },
    [earliestDate, latestDate, shouldPreventFuture, onChange]
  );
  useEffect23(() => {
    setErrorMessages([]);
    setValue(rangeToString(range));
  }, [range]);
  useEffect23(() => {
    if (isNotUndefined17(onErrorChange)) {
      onErrorChange(errorMessages);
    }
  }, [errorMessages, onErrorChange]);
  useEffect23(() => {
    if (selectionLength === keyCount) {
      setKeyCount(0);
      const nextSelection = getSelectionFromMove(currentSelection, 1, DATE_PART_POSITIONS);
      setCurrentSelection(nextSelection);
    }
  }, [keyCount, currentSelection, selectionLength]);
  useEffect23(() => {
    setCursorPosition(ref, currentSelection[0], currentSelection[1]);
    if (isNotUndefined17(onSelectedRangePartChange)) {
      onSelectedRangePartChange(getRangePartFromSelection(currentSelection));
    }
  }, [currentSelection, value, ref, onSelectedRangePartChange]);
  return /* @__PURE__ */ jsxs42("div", { children: [
    Boolean(errorMessages.length) && /* @__PURE__ */ jsx207(ErrorMessages, { messages: errorMessages }),
    /* @__PURE__ */ jsx207(
      Input2,
      {
        ref,
        $width: width,
        "data-testid": "range-selector-input",
        onBlur: () => {
          if (keyCount !== selectionLength && keyCount !== 0) {
            const stringToRangeValue = stringToRange(value);
            if (isNotNil41(stringToRangeValue)) {
              applyChange(stringToRangeValue);
            }
          }
          setCurrentSelection([0, -1]);
        },
        onFocus: () => {
          const index = getFirstDefaultPosition(value);
          if (index !== -1) {
            const firstPosition = DATE_PART_POSITIONS[0];
            if (isNotNil41(firstPosition)) {
              const { start, end } = firstPosition;
              setCurrentSelection([start, end]);
            }
          }
        },
        onKeyDown: (event) => {
          const args = {
            event,
            currentSelection,
            keyCount,
            valueParts,
            selectionLength
          };
          if (!isPermissableKey(event)) {
            event.preventDefault();
            return;
          }
          if (isValueChangeEvent(event)) {
            const { nextValue, nextKeyCount, shouldApply } = getNextValue(args);
            const nextRange = stringToRange(nextValue);
            if (shouldApply && isNotUndefined17(nextRange)) {
              applyChange(nextRange);
            } else {
              setValue(nextValue);
            }
            setKeyCount(nextKeyCount);
          }
          if (isMoveEvent(event)) {
            const { nextSelection, nextKeyCount } = getNextSelection(args);
            if (isNotUndefined17(nextSelection)) {
              setCurrentSelection(nextSelection);
            }
            setKeyCount(nextKeyCount);
            const stringToRangeValue = stringToRange(value);
            if (isNotUndefined17(stringToRangeValue)) {
              applyChange(stringToRangeValue);
            }
          }
        },
        onMouseDown: preventDoubleClick,
        onMouseUp: () => {
          setKeyCount(0);
          setCurrentSelection(getSelectionFromCursor(ref, DATE_PART_POSITIONS));
        },
        readOnly: true,
        type: "text",
        value
      }
    )
  ] });
};
RangeSelectorInput.displayName = "RangeSelectorInput";

// src/components/RangeSelector/RangeSelector.tsx
import { jsx as jsx208, jsxs as jsxs43 } from "react/jsx-runtime";
var TooltipWrapper2 = styled66.div`
  text-align: left;
  width: 240px;
`;
var PresetTooltip = ({ tooltipText }) => {
  return /* @__PURE__ */ jsx208(
    Tooltip,
    {
      direction: "right",
      label: /* @__PURE__ */ jsx208(TooltipWrapper2, { children: tooltipText }),
      shouldWrap: true,
      children: /* @__PURE__ */ jsx208(
        Icon,
        {
          size: "md",
          style: { verticalAlign: "text-bottom" },
          type: "question-mark"
        }
      )
    }
  );
};
var formatDate = (date) => date.toLocaleDateString("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
var formatFunc = ({ start, end }) => `${formatDate(start)} - ${formatDate(end)}`;
var isSameRange = (r1, r2) => isSameDay3(r1.start, r2.start) && isSameDay3(r1.end, r2.end);
var CalendarPadding = styled66.div`
  padding: ${({ theme: theme2 }) => `0 ${theme2.spacing.space03}`};
`;
var RangeSelectorWrapper = styled66.div`
  position: relative;
  width: ${({ $width }) => $width ?? "auto"};
`;
var StyledButton = styled66(Button)`
  ${({ theme: theme2, $inputButtonOverride }) => $inputButtonOverride && `
    && {
      background-color: white;
      border: 1px solid ${theme2.color.grey300};
      border-radius: 4px;
      color: ${theme2.color.grey900};
      font-size: 14px;
      height: 36px;

      &:hover,
      &:focus,
      &:active {
        background-color: white;
        box-shadow: 0 0 0 1.5px ${theme2.color.brandBlue500};
      }
    }
  `}
`;
var Item = styled66.button`
  background: white;
  border: none;
  color: ${({ theme: theme2 }) => theme2.color.grey800};
  cursor: pointer;
  display: flex;
  font-size: 15px;
  justify-content: space-between;
  padding: ${({ theme: theme2 }) => theme2.spacing.space03};
  width: 100%;

  &:hover {
    background: ${({ theme: theme2 }) => theme2.color.grey100};
  }

  ${({ theme: theme2, $isActive }) => $isActive && `
    color: ${theme2.color.blue600};
    font-weight: 600;
  `};
`;
var Break = styled66.div`
  flex-basis: 100%;
  height: 0;
`;
var Selector = styled66.div`
  background: white;
  border: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  position: absolute;
  top: ${({ theme: theme2 }) => theme2.spacing.space07};
  ${({ align }) => align === "left" && `
    left: 0;
  `};
  ${({ align }) => align === "right" && `
    right: 0;
  `};
  z-index: ${({ theme: theme2 }) => theme2.zIndex.RangeSelector};
`;
var SelectorInner = styled66.div`
  display: flex;
`;
var SelectorSection = styled66.div`
  padding: ${({ theme: theme2 }) => `${theme2.spacing.space05} ${theme2.spacing.space03}`};
`;
var Footer = styled66.div`
  border-top: 1px solid ${({ theme: theme2 }) => theme2.color.grey400};
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: ${({ theme: theme2 }) => theme2.spacing.space05};
  width: 100%;
`;
var Label3 = styled66.span`
  text-align: left;
  white-space: nowrap;

  &&::before {
    content: attr(data-title);
    display: block;
    font-weight: bold;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    width: calc(100% + 40px);
  }
`;
var RangeSelector = ({
  align = "left",
  buttonVariant = "secondary",
  earliestDate,
  latestDate,
  format = formatFunc,
  fullWidth = false,
  footerText,
  onChange,
  presets = [],
  range = {
    end: /* @__PURE__ */ new Date(),
    start: /* @__PURE__ */ new Date()
  },
  shouldPreventFuture = false,
  customTriggerComponent,
  width,
  ...props
}) => {
  const [internalRange, setInternalRange] = useState24(range);
  const [isOpen, setIsOpen] = useState24(false);
  const [isCurrentCalendarValid, setIsCurrentCalendarValid] = useState24(true);
  const [rangePartToShow, setRangePartToShow] = useState24("end");
  const [errors, setErrors] = useState24([]);
  const theme2 = useTheme();
  useEffect24(() => {
    setInternalRange(range);
  }, [range]);
  useEffect24(() => {
    const onClick = () => {
      setIsOpen(false);
      setInternalRange(range);
    };
    if (isOpen) {
      window.addEventListener("click", onClick);
    }
    return () => {
      window.removeEventListener("click", onClick);
    };
  }, [isOpen, range, setInternalRange]);
  const handleClick = (event) => event.stopPropagation();
  const commonTriggerProps = {
    "data-testid": "trigger",
    fullWidth,
    onClick: () => setIsOpen(!isOpen),
    children: format(range),
    // spread all props except data-testid to avoid duplicate test IDs
    ...Object.fromEntries(Object.entries(props).filter(([key]) => key !== "data-testid"))
  };
  const CustomTrigger = customTriggerComponent;
  return (
    // eslint-disable-next-line styled-components-a11y/no-static-element-interactions, styled-components-a11y/click-events-have-key-events
    /* @__PURE__ */ jsxs43(
      RangeSelectorWrapper,
      {
        $width: width,
        "data-testid": "rangeselector-test-identifier",
        onClick: handleClick,
        children: [
          CustomTrigger ? /* @__PURE__ */ jsx208(CustomTrigger, { ...commonTriggerProps }) : /* @__PURE__ */ jsx208(
            StyledButton,
            {
              ...commonTriggerProps,
              $inputButtonOverride: buttonVariant === "input",
              icon: /* @__PURE__ */ jsx208(
                Icon,
                {
                  size: "sm",
                  type: "caret-down"
                }
              ),
              iconPosition: "end",
              textAlign: "left",
              variant: buttonVariant === "input" ? "tertiary" : buttonVariant
            }
          ),
          isOpen ? /* @__PURE__ */ jsxs43(
            Selector,
            {
              $hasPresets: Boolean(presets.length),
              align,
              children: [
                /* @__PURE__ */ jsxs43(SelectorInner, { children: [
                  Boolean(presets.length) && /* @__PURE__ */ jsx208(
                    List,
                    {
                      "data-testid": "presets",
                      style: {
                        marginBottom: 0,
                        borderRight: `1px solid ${theme2.color.grey400}`,
                        padding: "12px"
                      },
                      variant: "unbulleted",
                      children: presets.map(({ label, value, tooltipText }) => /* @__PURE__ */ jsx208(ListItem, { children: /* @__PURE__ */ jsx208(
                        Item,
                        {
                          $isActive: isSameRange(internalRange, value),
                          "data-testid": label?.replaceAll(" ", "_"),
                          onClick: () => {
                            setInternalRange(value);
                          },
                          children: /* @__PURE__ */ jsxs43(Label3, { "data-title": `${label}___`, children: [
                            label,
                            " ",
                            tooltipText !== void 0 ? /* @__PURE__ */ jsx208(PresetTooltip, { tooltipText }) : null
                          ] })
                        }
                      ) }, label))
                    }
                  ),
                  /* @__PURE__ */ jsx208(SelectorSection, { children: /* @__PURE__ */ jsxs43(CalendarPadding, { children: [
                    /* @__PURE__ */ jsx208(
                      RangeSelectorInput,
                      {
                        earliestDate,
                        latestDate,
                        onChange: (next) => {
                          setInternalRange(next);
                        },
                        onErrorChange: setErrors,
                        onSelectedRangePartChange: setRangePartToShow,
                        range: internalRange,
                        shouldPreventFuture,
                        width: "100%"
                      }
                    ),
                    /* @__PURE__ */ jsx208(
                      RangeSelectorCalendar,
                      {
                        earliestDate,
                        latestDate,
                        onChange: (next) => {
                          setInternalRange(next);
                        },
                        onIsValidChange: setIsCurrentCalendarValid,
                        range: internalRange,
                        rangePartToShow,
                        shouldPreventFuture
                      }
                    )
                  ] }) })
                ] }),
                /* @__PURE__ */ jsx208(Break, {}),
                /* @__PURE__ */ jsxs43(Footer, { children: [
                  /* @__PURE__ */ jsx208("div", { children: footerText }),
                  /* @__PURE__ */ jsxs43(ButtonGroup, { align: "right", children: [
                    /* @__PURE__ */ jsx208(
                      Button,
                      {
                        onClick: () => {
                          setInternalRange(range);
                          setIsOpen(false);
                        },
                        style: { marginLeft: 0 },
                        variant: "secondary",
                        children: "Cancel"
                      }
                    ),
                    /* @__PURE__ */ jsx208(
                      Button,
                      {
                        "data-testid": "apply-button",
                        disabled: Boolean(errors.length) || !isCurrentCalendarValid,
                        onMouseUp: () => {
                          if (onChange) {
                            onChange(internalRange);
                          }
                          setIsOpen(false);
                        },
                        children: "Apply"
                      }
                    )
                  ] })
                ] })
              ]
            }
          ) : null
        ]
      }
    )
  );
};
RangeSelector.displayName = "RangeSelector_VHS";

// src/components/Slider/Slider.tsx
import { styled as styled67, css as css28 } from "styled-components";
import ReactSlider from "react-slider";
import {
  isFunction as isFunction4,
  isNonEmptyArray,
  isNonEmptyString as isNonEmptyString16,
  isNotNil as isNotNil42,
  isNumber,
  isString as isString8
} from "@wistia/type-guards";
import { jsx as jsx209 } from "react/jsx-runtime";
var TRACK_SIZE = "4px";
var DisabledSliderStyles = css28`
  opacity: 0.5;

  & * {
    pointer-events: none;
  }
`;
var SliderContainer = styled67.div`
  padding-bottom: ${TRACK_SIZE};
  width: 100%;
  ${({ disabled }) => disabled ? DisabledSliderStyles : null}
`;
var StyledThumb = styled67.div`
  background-color: ${({ theme: theme2 }) => theme2.color.brandBlue100};
  border-color: ${({ theme: theme2 }) => theme2.color.brandBlue500};
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  box-shadow: ${({ theme: theme2 }) => theme2.shadow.elevation1};
  cursor: grab;
  height: 20px;
  transform: translateY(-8px);
  transition-duration: ${({ theme: theme2 }) => theme2.duration.productive.fast};
  transition-property: border-width, transform;
  transition-timing-function: ${({ theme: theme2 }) => theme2.easing.exit.productive};
  width: 20px;

  &:hover,
  &:active {
    transform: translateY(-8px) scale(1.2);
    transform-origin: center;
    transition-timing-function: ${({ theme: theme2 }) => theme2.easing.entrance.productive};
  }

  &:focus-visible {
    border-width: 3px;
    outline: none;
  }
`;
var checkIfFilledTrack = (trackState) => {
  let isFilledTrack = trackState.index === 0;
  if (Array.isArray(trackState.value)) {
    const valueArray = trackState.value;
    if (valueArray.length > 1) {
      isFilledTrack = trackState.index > 0 && trackState.index < valueArray.length;
    }
  }
  return isFilledTrack;
};
var StyledTrack = styled67.div`
  background: ${({ $isFilledTrack, theme: theme2 }) => $isFilledTrack ? theme2.color.brandBlue500 : theme2.color.grey400};
  border-radius: 999px;
  height: ${TRACK_SIZE};
`;
var Slider = ({
  ariaLabel,
  ariaLabelledby,
  ariaValuetext,
  disabled = false,
  initialValue = 0,
  max = 100,
  min = 0,
  onChange,
  step = 1,
  value,
  "data-testid": passedDataTestId,
  ...otherProps
}) => {
  if (!(isNonEmptyString16(ariaLabel) || isNonEmptyString16(ariaLabelledby))) {
    console.warn(
      "VHS Slider: Sliders are required to have an accessible name. Add a label using the ariaLabel or ariaLabelledby prop."
    );
  }
  const dataTestId = isNotNil42(passedDataTestId) ? passedDataTestId : "vhs-slider";
  return /* @__PURE__ */ jsx209(
    SliderContainer,
    {
      ...otherProps,
      "data-testid": dataTestId,
      disabled,
      children: /* @__PURE__ */ jsx209(
        ReactSlider,
        {
          ariaLabel: isString8(ariaLabel) || isNonEmptyArray(ariaLabel) ? ariaLabel : void 0,
          ariaLabelledby: isString8(ariaLabelledby) || isNonEmptyArray(ariaLabelledby) ? ariaLabelledby : void 0,
          ariaValuetext: isFunction4(ariaValuetext) ? (state) => ariaValuetext(state) : ariaValuetext,
          defaultValue: isNumber(initialValue) || isNonEmptyArray(initialValue) ? initialValue : void 0,
          disabled,
          marks: step,
          max,
          min,
          onChange,
          renderMark: (markProps) => {
            const { key, ...restProps } = markProps;
            return /* @__PURE__ */ jsx209(
              "span",
              {
                ...restProps,
                "data-testid": `${dataTestId}-mark-${markProps.key + min}`
              },
              key
            );
          },
          renderThumb: (thumbProps, thumbState) => {
            const { key, as, ...restProps } = thumbProps;
            return /* @__PURE__ */ jsx209(
              StyledThumb,
              {
                ...restProps,
                "data-testid": `${dataTestId}-thumb-${thumbState.index}`
              },
              key
            );
          },
          renderTrack: (trackProps, trackState) => {
            const { key, as, ...restProps } = trackProps;
            return /* @__PURE__ */ jsx209(
              StyledTrack,
              {
                ...restProps,
                $isFilledTrack: checkIfFilledTrack(trackState),
                "data-testid": `${dataTestId}-track-${trackState.index}`
              },
              key
            );
          },
          step,
          value: isNumber(value) || isNonEmptyArray(value) ? value : void 0
        }
      )
    }
  );
};
Slider.displayName = "Slider_VHS";

// src/components/Stack/Stack.tsx
import { forwardRef as forwardRef18 } from "react";
import { styled as styled68 } from "styled-components";
import { jsx as jsx210 } from "react/jsx-runtime";
var StyledStack = styled68.div`
  display: flex;
  flex-direction: ${({ $direction }) => $direction === "horizontal" ? "row" : "column"};
  gap: ${({ $gap, theme: theme2 }) => theme2.spacing[$gap]};

  > * {
    flex: 1;
  }
`;
var Stack = forwardRef18(
  ({ renderAs = "div", direction = "vertical", gap = "space02", ...props }, ref) => {
    return /* @__PURE__ */ jsx210(
      StyledStack,
      {
        ...props,
        ref,
        $direction: direction,
        $gap: gap,
        as: renderAs
      }
    );
  }
);
Stack.displayName = "Stack_VHS";

// src/components/Switch/Switch.tsx
import { styled as styled69, css as css29 } from "styled-components";
import { jsx as jsx211 } from "react/jsx-runtime";
var SwitchComponent = styled69.button`
  background-color: white;
  border-color: ${({ theme: theme2 }) => theme2.color.grey700};
  border-radius: 20px;
  border-style: solid;
  border-width: 1px;
  color: ${({ theme: theme2 }) => theme2.color.grey700};
  cursor: pointer;
  display: inline-block;
  height: 20px;
  margin: 0;
  overflow: hidden;
  padding: 0;
  transition: background 175ms ease-out;
  vertical-align: middle;
  width: ${({ $hideText }) => $hideText ? "40px" : "48px"};

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme: theme2 }) => theme2.color.brandBlue500};
    outline-offset: 1px;
  }

  &[aria-checked='true'] {
    background-color: ${({ $colorOverride, theme: theme2 }) => $colorOverride ?? theme2.color.success600};
    border-color: ${({ $colorOverride, theme: theme2 }) => $colorOverride ?? theme2.color.success600};
    color: white;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
var ShowTextStyles = css29`
  &::before,
  &::after {
    display: inline-block;
    font-size: 10px;
    font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
    line-height: 20px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  &::before {
    content: 'ON';
    left: -24px;
  }

  &::after {
    color: ${({ theme: theme2 }) => theme2.color.grey700};
    content: 'OFF';
    font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
    left: 16px;

    ${SwitchComponent}[aria-checked="true"] & {
      left: 20px;
    }
  }
`;
var SwitchKnob = styled69.div`
  background-color: ${({ theme: theme2 }) => theme2.color.grey700};
  border-radius: 50%;
  height: 12px;
  position: relative;
  transform: translateY(0%) translateX(4px);
  transition:
    background 175ms ease-out,
    color 175ms ease-out,
    transform 175ms ease-out;
  width: 12px;

  --switch-knob-x-offset: ${({ $hideText }) => $hideText ? "22px" : "31px"};

  ${SwitchComponent}[aria-checked="true"] & {
    background-color: white;
    transform: translateY(0%) translateX(var(--switch-knob-x-offset));
  }

  ${({ $hideText }) => !$hideText && ShowTextStyles}
`;
var Switch = ({
  disabled = false,
  isChecked = false,
  onChange,
  colorOverride,
  hideText = false,
  ...otherProps
}) => {
  const ariaLabel = isChecked ? "on" : "off";
  const handleClick = () => {
    if (onChange) {
      onChange(!isChecked);
    }
  };
  return /* @__PURE__ */ jsx211(
    SwitchComponent,
    {
      $colorOverride: colorOverride,
      $hideText: hideText,
      "aria-checked": isChecked,
      "aria-disabled": disabled,
      "aria-label": ariaLabel,
      onClick: !disabled ? handleClick : void 0,
      role: "switch",
      type: "button",
      ...otherProps,
      children: /* @__PURE__ */ jsx211(SwitchKnob, { $hideText: hideText })
    }
  );
};
Switch.displayName = "Switch_VHS";

// src/components/Thumbnail/Thumbnail.tsx
import { styled as styled70 } from "styled-components";
import { isNotNil as isNotNil43 } from "@wistia/type-guards";
import { jsx as jsx212, jsxs as jsxs44 } from "react/jsx-runtime";
var ThumbnailComponent = styled70.div`
  ${({ $gradientBackground }) => getBackgroundGradient($gradientBackground)};
  aspect-ratio: 16 / 9;
  background-image: ${({ $backgroundUrl }) => $backgroundUrl};
  background-position: center;
  background-size: cover;
  border-radius: 4px;
  display: flex;
  overflow: hidden;
  position: relative;
  width: ${({ $width }) => $width};
`;
var WideThumbnailImage = styled70.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;
var SquareThumbnailImage = styled70.img`
  backdrop-filter: blur(8px);
  object-fit: contain;
  width: 100%;
`;
var BottomRightText = styled70.div`
  align-items: center;
  background-color: rgb(0 0 0 / 50%);
  border-radius: 2px;
  bottom: 0;
  color: white;
  display: flex;
  font-size: 12px;
  font-weight: ${({ theme: theme2 }) => theme2.font.weight.bold};
  gap: ${({ theme: theme2 }) => theme2.spacing.space01};
  margin-bottom: ${({ theme: theme2 }) => theme2.spacing.space01};
  margin-right: ${({ theme: theme2 }) => theme2.spacing.space01};
  padding: 0 ${({ theme: theme2 }) => theme2.spacing.space01};
  position: absolute;
  right: 0;
  z-index: 1;
`;
var IconContainer3 = styled70.div`
  align-self: center;
  aspect-ratio: 1;
  margin: 30%;
  width: 40%;

  > * {
    height: 100%;
    width: 100%;
  }
`;
var ThumbnailImage = ({ $thumbnailImageType, $thumbnailUrl }) => {
  if ($thumbnailImageType === "wide") {
    return /* @__PURE__ */ jsx212(
      WideThumbnailImage,
      {
        alt: "",
        src: $thumbnailUrl
      }
    );
  }
  if ($thumbnailImageType === "square") {
    return /* @__PURE__ */ jsx212(
      SquareThumbnailImage,
      {
        alt: "",
        src: $thumbnailUrl
      }
    );
  }
  return null;
};
var Collage = styled70.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: 50% 50%;
  width: 100%;
`;
var CollageImage = styled70.div`
  background-image: ${({ $thumbnailUrl }) => `url(${$thumbnailUrl})`};
  background-position: center;
  background-size: cover;
  min-width: 100px;

  &:nth-child(1) {
    grid-column: 1;
    grid-row: 1 / 3;
  }

  &:nth-child(2) {
    grid-column: 2;
    ${({ total }) => total === 2 && "grid-row: 1 / 3;"}
  }
`;
var Thumbnail = ({
  bottomRightText,
  thumbnailUrl,
  thumbnailImageType = "square",
  icon,
  bottomRightIcon,
  width = "auto",
  gradientBackground = "defaultMidOne",
  iconColor = "white",
  ...otherProps
}) => {
  const thumbnailUrlToUse = Array.isArray(thumbnailUrl) ? thumbnailUrl[0] : thumbnailUrl;
  return /* @__PURE__ */ jsxs44(
    ThumbnailComponent,
    {
      $backgroundUrl: thumbnailImageType === "square" && isNotNil43(thumbnailUrlToUse) ? `url(${thumbnailUrlToUse})` : "",
      $gradientBackground: gradientBackground,
      $width: width,
      ...otherProps,
      children: [
        isNotNil43(bottomRightText) ? /* @__PURE__ */ jsxs44(BottomRightText, { children: [
          bottomRightIcon ? /* @__PURE__ */ jsx212(
            Icon,
            {
              size: "sm",
              type: bottomRightIcon
            }
          ) : null,
          /* @__PURE__ */ jsx212("span", { children: bottomRightText })
        ] }) : null,
        isNotNil43(icon) && /* @__PURE__ */ jsx212(IconContainer3, { children: /* @__PURE__ */ jsx212(
          Icon,
          {
            color: iconColor,
            type: icon
          }
        ) }),
        !Array.isArray(thumbnailUrl) && isNotNil43(thumbnailImageType) && isNotNil43(thumbnailUrl) && /* @__PURE__ */ jsx212(
          ThumbnailImage,
          {
            $thumbnailImageType: thumbnailImageType,
            $thumbnailUrl: thumbnailUrl
          }
        ),
        Array.isArray(thumbnailUrl) && isNotNil43(thumbnailImageType) && isNotNil43(thumbnailUrl) && /* @__PURE__ */ jsx212(Collage, { "data-testid": "thumbnail-collage", children: thumbnailUrl.map((url, index) => /* @__PURE__ */ jsx212(
          CollageImage,
          {
            $thumbnailUrl: url,
            "data-testid": `collage-image-${index}`,
            total: thumbnailUrl.length
          },
          `${url}${index}`
        )) })
      ]
    }
  );
};
Thumbnail.displayName = "Thumbnail_VHS";

// src/components/ThumbnailCollage/ThumbnailCollage.tsx
import { styled as styled71 } from "styled-components";
import { isNotNil as isNotNil44 } from "@wistia/type-guards";
import { jsx as jsx213 } from "react/jsx-runtime";
var ThumbnailCollageContainer = styled71.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-template-rows: 1fr 1fr;
  width: 100%;
  gap: ${({ theme: theme2 }) => theme2.spacing.space01};
  height: 100%;
`;
var CollageImage2 = styled71.div`
  ${({ $thumbnailUrl }) => isNotNil44($thumbnailUrl) && `background-image: url(${$thumbnailUrl})`};
  ${({ $backgroundGradient }) => $backgroundGradient && getBackgroundGradient($backgroundGradient)};
  background-position: center;
  background-size: cover;
  border-radius: 8px;

  &:nth-child(1) {
    grid-column: 1;
    grid-row: 1 / 3;
  }

  &:only-child {
    grid-column: 1 / 3;
    grid-row: 1 / 3;
  }

  &:nth-child(2) {
    grid-column: 2;

    &:last-child {
      grid-row: 1 / 3;
    }
  }
`;
var ThumbnailCollage = ({
  thumbnailUrls,
  gradientBackground = "defaultMidOne",
  ...props
}) => {
  return /* @__PURE__ */ jsx213(
    ThumbnailCollageContainer,
    {
      role: "presentation",
      ...props,
      children: thumbnailUrls.length ? thumbnailUrls.map((thumbnailUrl) => /* @__PURE__ */ jsx213(
        CollageImage2,
        {
          $thumbnailUrl: thumbnailUrl
        },
        thumbnailUrl
      )) : /* @__PURE__ */ jsx213(CollageImage2, { $backgroundGradient: gradientBackground })
    }
  );
};

// src/components/WistiaLogo/WistiaLogo.tsx
import { styled as styled72 } from "styled-components";
import { isNotNil as isNotNil45 } from "@wistia/type-guards";
import { jsx as jsx214, jsxs as jsxs45 } from "react/jsx-runtime";
var renderBrandmark = (brandmarkColor, iconOnly) => {
  if (iconOnly) {
    return /* @__PURE__ */ jsx214("g", { fill: brandmarkColor, children: /* @__PURE__ */ jsx214("path", { d: "M16.09 17.1h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 26.53c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87ZM32.14 0c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" }) });
  }
  return /* @__PURE__ */ jsx214("g", { fill: brandmarkColor, children: /* @__PURE__ */ jsx214("path", { d: "M16.09 21.37h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 30.8c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87Zm16.05-17.1c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" }) });
};
var renderLogotype = (logotypeColor, iconOnly) => {
  if (iconOnly) {
    return null;
  }
  return /* @__PURE__ */ jsx214("g", { fill: logotypeColor, children: /* @__PURE__ */ jsx214("path", { d: "M70.16 8.66v15.18c0 1.68-.35 3.09-1.05 4.23a6.612 6.612 0 0 1-2.85 2.54c-1.19.55-2.52.82-4.01.82s-2.8-.28-4.01-.85a6.655 6.655 0 0 1-3.11-2.96c-.08.15-.16.29-.24.42a6.552 6.552 0 0 1-2.87 2.54c-1.2.56-2.54.85-4.01.85s-2.8-.27-3.94-.82a6.214 6.214 0 0 1-2.71-2.52c-.67-1.14-1.01-2.56-1.02-4.25l-.22-15.18h7.34V22.3c0 .82.19 1.37.56 1.67.39.28.85.42 1.38.42s1.02-.15 1.45-.45c.43-.3.65-.85.65-1.65V8.65h7.3v13.64c0 .8.22 1.35.65 1.65.43.3.91.45 1.45.45s.99-.14 1.36-.42c.39-.3.58-.85.58-1.67V8.66h7.34Zm2.45 0v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89A3.43 3.43 0 0 0 78.28.44c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.13 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm8.86 1.96c-1.42.4-2.6 1.11-3.54 2.14-.93 1.02-1.4 2.4-1.4 4.12 0 1.11.17 2.09.51 2.94.36.85.82 1.62 1.38 2.34.22.28.55.65.98 1.11.37.4.64.72.8.96.18.24.27.47.27.69 0 .5-.4.87-1.2 1.09-.8.21-1.62.31-2.47.31-.1-.01-.22-.02-.33-.02l1.02 6.94c.42.07.92.11 1.51.11 1.9 0 3.6-.28 5.1-.85 1.5-.56 2.68-1.42 3.54-2.56.88-1.14 1.31-2.55 1.31-4.23 0-.68-.07-1.31-.22-1.87-.13-.58-.32-1.09-.56-1.54a6.64 6.64 0 0 0-.8-1.27c-.3-.37-.74-.82-1.34-1.36-.39-.33-.67-.59-.85-.8-.18-.22-.27-.45-.27-.67 0-.46.26-.79.78-.98.53-.19 1.17-.29 1.91-.29.25 0 .51.01.78.04l-.71-6.88a10.4 10.4 0 0 0-1.56-.11c-1.66 0-3.21.21-4.65.62Zm19.54 15.71c-.99 0-1.71-.23-2.14-.69-.42-.47-.62-1.18-.62-2.11v-6.57h4.21V8.66h-4.21V3.38l-7.34 1.85V24.1c0 2.45.47 4.29 1.4 5.52.95 1.22 2.45 1.83 4.49 1.83.95 0 1.86-.07 2.74-.22.88-.13 1.62-.34 2.25-.62l1.38-6.3c-.55.1-1.27.16-2.16.16Zm4.13-15.8v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89a3.43 3.43 0 0 0-1.42-1.27c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.14 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm27.51 1.87v22.26h-7.34v-2.28c-.41.43-.85.83-1.34 1.19-1.44 1.07-3.12 1.6-5.05 1.6s-3.61-.52-5.1-1.56c-1.48-1.05-2.63-2.47-3.45-4.25-.82-1.78-1.22-3.73-1.22-5.85s.41-4.07 1.22-5.83c.82-1.78 1.97-3.19 3.45-4.23 1.48-1.05 3.18-1.58 5.1-1.58s3.61.53 5.05 1.6c.48.36.93.75 1.34 1.19V8.66h7.34Zm-7.1 11.11c0-.8-.19-1.53-.56-2.18-.37-.67-.88-1.19-1.54-1.58-.64-.39-1.34-.58-2.09-.58s-1.45.19-2.09.58c-.64.39-1.15.91-1.54 1.58-.37.67-.56 1.39-.56 2.18s.19 1.51.56 2.18c.39.67.9 1.19 1.54 1.58.64.39 1.34.58 2.09.58s1.45-.19 2.09-.58c.65-.39 1.16-.91 1.54-1.56.37-.67.56-1.4.56-2.2Z" }) });
};
var computedViewBox = (iconOnly) => {
  if (iconOnly) {
    return "0 0 34.4 26.78";
  }
  return "0 0 144 31.47";
};
var WistiaLogoComponent = styled72.svg`
  height: ${({ height }) => `${height}px`};

  /* ensure it will always fit on mobile */
  max-height: 22vw;
  max-width: 100%;

  &:hover {
    path {
      ${({ $hoverColor }) => $hoverColor !== void 0 ? `fill: ${$hoverColor}` : null};
    }
  }
`;
var WistiaLogo = ({
  description = void 0,
  height = 100,
  hoverColor = void 0,
  href = void 0,
  iconOnly = false,
  title = "Wistia Logo",
  variant = "standard",
  ...otherProps
}) => {
  const primaryColor = "#2949e5";
  const darkColor = "#000934";
  const lightColor = "#ffffff";
  const VARIANT_COLORS = {
    standard: {
      brandmark: primaryColor,
      logotype: darkColor
    },
    dark: {
      brandmark: darkColor,
      logotype: darkColor
    },
    light: {
      brandmark: lightColor,
      logotype: lightColor
    }
  };
  const brandmarkColor = VARIANT_COLORS[variant].brandmark;
  const logotypeColor = VARIANT_COLORS[variant].logotype;
  const Logo = /* @__PURE__ */ jsxs45(
    WistiaLogoComponent,
    {
      $hoverColor: hoverColor,
      height,
      role: "img",
      viewBox: computedViewBox(iconOnly),
      xmlns: "http://www.w3.org/2000/svg",
      ...otherProps,
      children: [
        /* @__PURE__ */ jsx214("title", { children: title }),
        isNotNil45(description) ? /* @__PURE__ */ jsx214("desc", { children: description }) : null,
        renderBrandmark(brandmarkColor, iconOnly),
        renderLogotype(logotypeColor, iconOnly)
      ]
    }
  );
  return href !== void 0 ? /* @__PURE__ */ jsx214("a", { href, children: Logo }) : Logo;
};
WistiaLogo.displayName = "WistiaLogo_VHS";
export {
  ActionModal,
  Avatar,
  Backdrop,
  BackgroundImage,
  Badge,
  Banner,
  Box,
  Button,
  ButtonGroup,
  ButtonLink,
  Checkbox,
  CheckboxGroup,
  CheckboxMenuItem,
  ClickArea,
  CloseButton,
  CollapsibleGroup,
  Divider,
  Ellipsis,
  FileSelect,
  Form,
  FormButtons,
  FormField,
  FormFieldError,
  FormGlobalError,
  FullScreenModal,
  Icon,
  IconButton,
  Image3 as Image,
  Input,
  KeyboardShortcut,
  Label,
  LabelDescription,
  Link,
  LinkButton,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuLabel,
  DropdownMenuRadioGroup as MenuRadioGroup,
  Modal,
  ModalBase,
  ModalBody,
  ModalCloseButton2 as ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Paper,
  ProgressBar,
  Radio,
  RadioGroup,
  RadioMenuItem,
  RangeSelector,
  RichTextEditor,
  ScreenReaderOnly,
  Select,
  Slider,
  Stack,
  SubMenu,
  Switch,
  Text,
  Thumbnail,
  ThumbnailCollage,
  Toast,
  ToastProvider,
  Tooltip,
  Truncate,
  VHSProvider,
  WistiaLogo,
  copyToClipboard,
  datetimeHelpers,
  ellipsisFlexParentStyle,
  ellipsisStyle,
  getBackgroundGradient,
  getSemiRandomBackgroundGradient,
  iconSizeMap,
  mq,
  objectHelpers,
  rteDeserializeHTML,
  rteFormatTypes,
  rteSerializeToHTML,
  screenReaderOnlyStyle,
  stringHelpers,
  useBoolean,
  useClipboard,
  useElementObserver,
  useEvent,
  useFocusTrap,
  useForceUpdate,
  useFormikContext,
  useIsHovered,
  useKey,
  useKeyPress,
  useKeyPressOnce,
  useLocalStorage,
  useMergedRefs,
  useMq,
  useOnClickOutside,
  usePreventScroll,
  usePreviousValue,
  useTheme,
  useThemeColor,
  useToast,
  useWindowSize
};
//# sourceMappingURL=index.mjs.map